import {
  DEFAULT_INTERFACE_LANGUAGE,
  getInterfaceLanguageCatalogue,
  interfaceLanguageCatalogueResponseSchema,
  type ApiError,
  type InterfaceLanguageCatalogueResponse,
  type InterfaceMessage,
} from '@molt/shared';
import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import type { ReactElement } from 'react';
import type { UseQueryOptions } from '@tanstack/react-query';
import { configureApiErrorMessages, get } from './api';
import { queryKeys } from './query-keys';

const fallbackCatalogue = getInterfaceLanguageCatalogue(DEFAULT_INTERFACE_LANGUAGE);

/**
 * FE-80.1 — exported for the KIND AUDIT in the per-section test files, and for
 * nothing else. Product code reaches these values through `t`, never through
 * this record.
 *
 * ⚠ The export exists because a kind audit written against `t` alone CANNOT
 * see one real mutation, and the card-stickers slice measured it: re-declaring
 * a `kind: 'text'` entry as `kind: 'plural'` with two IDENTICAL forms renders
 * byte-identically at every arity, so `t(key, values, 1) === t(key, values, 0)`
 * stays true and the whole suite stays green. The declared shape has drifted
 * and nothing can tell. `interfaceMessage` selects `forms.other` whenever
 * `count !== 1`, so the divergence is unobservable until a call site later
 * passes a count — and `packages/shared` types the plural arm as
 * `strictObject({ one, other })`, so what enters is an unreachable `one` form.
 * Reading `.kind` off the declaration is the only assertion that closes it.
 */
export const WEB_INTERFACE_MESSAGES: Record<string, InterfaceMessage> = {
  'shell.signIn': { kind: 'text', value: 'Sign in' },
  'shell.inboxOpened': { kind: 'text', value: 'Inbox opened.' },
  'shell.plannerOpened': { kind: 'text', value: 'Planner opened.' },
  'account.trigger': { kind: 'text', value: 'Your account, {name}' },
  'account.inbox': { kind: 'text', value: 'Inbox' },
  'account.planner': { kind: 'text', value: 'Planner' },
  'account.myCards': { kind: 'text', value: 'My Cards' },
  'account.profile': { kind: 'text', value: 'Profile' },
  'account.signOut': { kind: 'text', value: 'Sign out' },
  'myCards.title': { kind: 'text', value: 'My Cards' },
  'myCards.description': {
    kind: 'text',
    value: 'Cards assigned to you across boards.',
  },
  'myCards.workspaceLabel': { kind: 'text', value: 'Workspace' },
  'myCards.allWorkspaces': { kind: 'text', value: 'All workspaces' },
  'myCards.sortLabel': { kind: 'text', value: 'Sort' },
  'myCards.sort.due': { kind: 'text', value: 'Due date' },
  'myCards.sort.board': { kind: 'text', value: 'Board' },
  'myCards.loadFailedTitle': { kind: 'text', value: 'My Cards could not load' },
  'myCards.assignedCards': { kind: 'text', value: 'Assigned cards' },
  'myCards.noAssignedTitle': { kind: 'text', value: 'No assigned cards' },
  'myCards.noAssignedBody': {
    kind: 'text',
    value: 'Only cards where you are a card member appear here.',
  },
  'myCards.signInTitle': { kind: 'text', value: 'Sign in to see My Cards' },
  'myCards.signInBody': {
    kind: 'text',
    value: 'Only your assigned cards appear here.',
  },
  'myCards.signInAction': { kind: 'text', value: 'Sign in' },
  'myCards.genericError': {
    kind: 'text',
    value: 'Something went wrong. Try again.',
  },
  'myCards.untitledWorkspace': { kind: 'text', value: 'Untitled workspace' },
  'myCards.untitledBoard': { kind: 'text', value: 'Untitled board' },
  'myCards.untitledList': { kind: 'text', value: 'Untitled list' },
  'myCards.untitledCard': { kind: 'text', value: 'Untitled card' },
  'myCards.noDueDate': { kind: 'text', value: 'No due date' },
  'myCards.dueDate': { kind: 'text', value: 'Due {date}' },
  /* FE-80.1 workspace switcher — the shell control that sits above every board
     frame, and the last unrouted thing in the shell.

     ⚠ THIS NAMESPACE'S `All workspaces` IS A THIRD AUTHOR OF THOSE BYTES,
     DECLARED KNOWINGLY. `myCards.allWorkspaces` (just above) names the My Cards
     page's scope `<option>`; `home.allWorkspaces` (just below) names the Home
     page's Highlights filter `<option>`; THIS one names a radio item inside the
     shell's dropdown AND the trigger's own label, which is not a `<select>`
     option at all. Three DOM nodes on three surfaces. Sharing one key would let
     a translation of the My Cards filter rename a control in the header above
     every board — the namespace decides, not the bytes. This is the
     `boardTable.heading` ruling (a third `Table` declared knowingly), not the
     `boardMap.viewLabel` defect. The catalogue's own convention already settles
     it: `Untitled workspace` is declared once per namespace and `Untitled card`
     six times.

     ⚠ `workspaceSwitcher.allWorkspaces` is the one key in this block that NO
     `t('…')` grep can find. `ALL_WORKSPACES_LABEL` was a module-level constant
     and a module-level constant cannot call `t`, so `WorkspaceSwitcher.tsx`
     now holds the KEY in `ALL_WORKSPACES_KEY` and resolves it at render — the
     `BoardMapView.tsx:80-84` shape. `workspace-switcher.routing.test.ts` pins
     the constant and its two `t(ALL_WORKSPACES_KEY)` call sites instead. */
  'workspaceSwitcher.allWorkspaces': { kind: 'text', value: 'All workspaces' },
  'workspaceSwitcher.untitledWorkspace': {
    kind: 'text',
    value: 'Untitled workspace',
  },
  /* The trigger's accessible name. It stays fixed at every viewport while the
     VISIBLE label is hidden below 640px (`D2-BRIEF.md` §6.6 rule 4), so a
     locator that works at one width keeps working at another. `{name}` carries
     the resolved workspace name — user data — and is never translated. */
  'workspaceSwitcher.trigger': { kind: 'text', value: 'Workspace: {name}' },
  'workspaceSwitcher.newWorkspace': { kind: 'text', value: 'New workspace…' },
  /* ⚠ THREE ASCII DOTS, not the `…` its two siblings carry, and that is
     PRESERVED rather than fixed. The component shipped it this way and six
     assertions in `WorkspaceSwitcher.test.tsx` match the bytes exactly; a
     routing slice that normalises copy turns a file it does not own red for a
     reason that has nothing to do with routing, and byte-preservation is what
     keeps both sibling test files green UNEDITED. The catalogue already carries
     both forms (`inbox.addingCard` is `Adding card...`). Returned as a
     residual, not taken. */
  'workspaceSwitcher.activity': { kind: 'text', value: 'Activity...' },
  'workspaceSwitcher.members': { kind: 'text', value: 'Members…' },
  /* The shell's board switcher (`components/boards/BoardSwitcher.tsx`,
     `FR-BOARD-004`'s "board switcher", `FR-EXP-017`'s `B`). Its own namespace
     on the `workspaceSwitcher.*` rule above: `Starred`, `Recent` and the two
     `Untitled …` fallbacks repeat bytes other namespaces already declare, and
     the namespace decides, not the bytes. `boardSwitcher.trigger` is the
     button's FIXED accessible name; `triggerLabel` is the visible text, hidden
     below 640px. The name starts with the visible text (WCAG 2.5.3 Label in
     Name), so "click Boards" by speech input reaches the button. */
  'boardSwitcher.trigger': { kind: 'text', value: 'Boards: switch board' },
  'boardSwitcher.triggerLabel': { kind: 'text', value: 'Boards' },
  'boardSwitcher.title': { kind: 'text', value: 'Switch board' },
  'boardSwitcher.filterLabel': { kind: 'text', value: 'Find boards' },
  'boardSwitcher.filterPlaceholder': { kind: 'text', value: 'Find boards…' },
  'boardSwitcher.starredGroup': { kind: 'text', value: 'Starred' },
  'boardSwitcher.recentGroup': { kind: 'text', value: 'Recent' },
  'boardSwitcher.untitledWorkspace': { kind: 'text', value: 'Untitled workspace' },
  'boardSwitcher.untitledBoard': { kind: 'text', value: 'Untitled board' },
  'boardSwitcher.current': { kind: 'text', value: 'Current board' },
  'boardSwitcher.loading': { kind: 'text', value: 'Loading boards' },
  'boardSwitcher.loadFailedTitle': { kind: 'text', value: 'Boards could not load' },
  'boardSwitcher.loadFailedBody': {
    kind: 'text',
    value: 'Check your connection and try again.',
  },
  'boardSwitcher.retry': { kind: 'text', value: 'Try again' },
  'boardSwitcher.noBoards': { kind: 'text', value: 'No boards yet' },
  'boardSwitcher.noMatches': { kind: 'text', value: 'No boards match “{query}”' },
  /* FE-80.1 Home — the personal landing surface (`components/personal/HomePage.tsx`).

     ⚠ Every `Untitled …` fallback and `Due {date}` below is a per-namespace
     declaration rather than a reuse of the `myCards.*` family, on the same rule
     as `workspaceSwitcher.allWorkspaces` above: Home's card rows and My Cards'
     table rows are different surfaces, and a translator must be free to move
     one without the other. Only the FALLBACKS are here — every real card,
     board, list, checklist and workspace NAME stays user data. */
  'home.eyebrow': { kind: 'text', value: 'Personal landing' },
  'home.title': { kind: 'text', value: 'Home' },
  'home.workspaceFilterLabel': {
    kind: 'text',
    value: 'Filter Highlights by workspace',
  },
  'home.allWorkspaces': { kind: 'text', value: 'All workspaces' },
  'home.untitledWorkspace': { kind: 'text', value: 'Untitled workspace' },
  'home.loadFailedTitle': { kind: 'text', value: 'Home could not load' },
  'home.retry': { kind: 'text', value: 'Try again' },
  'home.dueDate': { kind: 'text', value: 'Due {date}' },
  'home.overdueDate': { kind: 'text', value: 'Overdue {date}' },
  'home.untitledCard': { kind: 'text', value: 'Untitled card' },
  'home.untitledBoard': { kind: 'text', value: 'Untitled board' },
  'home.untitledList': { kind: 'text', value: 'Untitled list' },
  /* ⚠ TWO KEYS, and the bytes already differ. `upNext.title` is the visible
     `<h2>` and the region's accessible name; `upNext.listLabel` names the `<ol>`
     inside it. This is the `boardFace.list.addCard` / `addCardTo` shape, and the
     relationship is the thing to keep: the long one BEGINS with the short one
     (WCAG 2.5.3, Label in Name), pinned by binding rather than by bytes. */
  'home.upNext.title': { kind: 'text', value: 'Up Next' },
  'home.upNext.listLabel': { kind: 'text', value: 'Up Next cards' },
  'home.upNext.emptyTitle': { kind: 'text', value: 'Nothing needs you next' },
  'home.upNext.emptyBody': {
    kind: 'text',
    value: 'Due cards and unread mentions appear here when they need attention.',
  },
  'home.upNext.mentioned': { kind: 'text', value: 'Mentioned you' },
  'home.upNext.addedToCard': { kind: 'text', value: 'Added you to the card' },
  /* ⚠ ONE KEY, TWO NODES, REUSED ON PURPOSE. `home.assigned.title` is both the
     section's `<h2>` and the `aria-label` on the `<ul>` inside it — one section,
     one name. A second key carrying the same English would let the heading speak
     the translation while the list inside it speaks English, with nothing red
     anywhere: the `boardTable.viewLabel` reuse ruling, not the `boardMap`
     defect. `home.highlights.title` below is the same decision. */
  'home.assigned.title': { kind: 'text', value: 'Assigned checklist items' },
  'home.assigned.emptyTitle': {
    kind: 'text',
    value: 'No assigned checklist items',
  },
  'home.assigned.emptyBody': {
    kind: 'text',
    value: 'Checklist items assigned to you appear here until they are checked.',
  },
  /* ⚠ `{count}` is a SLOT here, not a plural selector. `kind: 'text'` ignores
     `interfaceMessage`'s third `count` argument entirely, so the call site must
     pass `{ count, total }` in `values` — `board.card.position` already does
     exactly this. */
  'home.assigned.progress': { kind: 'text', value: '{count} of {total} checked' },
  'home.assigned.checkItem': { kind: 'text', value: '{item}, mark checked' },
  'home.assigned.itemFallback': { kind: 'text', value: 'Checklist item' },
  'home.assigned.untitledItem': {
    kind: 'text',
    value: 'Untitled checklist item',
  },
  'home.assigned.untitledChecklist': { kind: 'text', value: 'Checklist' },
  'home.highlights.title': { kind: 'text', value: 'Highlights' },
  'home.highlights.emptyTitle': { kind: 'text', value: 'No Highlights' },
  'home.highlights.emptyBody': {
    kind: 'text',
    value: 'Recent conversations from joined boards appear here.',
  },
  /* ⚠ ONE FLAT SENTENCE, and it costs two `font-medium` spans. `HomePage.tsx`
     rendered `<span>{actor}</span> on <span>{board}</span>`; `interfaceMessage`
     returns a STRING and the catalogue has no rich-text arm, so the byline is
     routed whole. Routing only the connective (`on`) was rejected: it freezes
     word order, and no locale could then put the board before the actor — which
     is the entire reason a template exists. Precedent `board.listEmpty.title`.
     The lost weight is deliberate and not compensated: the card-title link
     directly above the byline is already `font-semibold` and is the row's
     anchor, so a second competing weight makes the row harder to read at a
     glance. */
  'home.highlights.byline': { kind: 'text', value: '{actor} on {board}' },
  'home.highlights.noPreview': { kind: 'text', value: 'No comment preview' },
  'home.highlights.replyFormLabel': { kind: 'text', value: 'Reply to {title}' },
  /* The `{title}` slot's own fallback when a highlighted card has no title —
     `Reply to card`, lower-case, because it is read inside the sentence above
     and is not a label of its own. */
  'home.highlights.replyFormFallback': { kind: 'text', value: 'card' },
  /* ⚠ TWO KEYS FOR ONE ENGLISH WORD, and they must be free to diverge.
     `replyFieldLabel` is the NOUN naming the text field; `replySubmit` is the
     IMPERATIVE naming the button that sends it. Most languages inflect those
     differently, and one key would force a translator to pick a single part of
     speech for both. Neither reuses `notifications.replyButton`, which is the
     notification drawer's control on another surface entirely. */
  'home.highlights.replyFieldLabel': { kind: 'text', value: 'Reply' },
  'home.highlights.replySubmit': { kind: 'text', value: 'Reply' },
  'home.highlights.replyDisabled': { kind: 'text', value: 'Enter a reply first.' },
  /* The per-row toggle that opens the reply form. A Highlight carries its reply
     control (`FR-NOTIF-028` :388) but not an open textarea per row — twelve
     open forms were what pushed the boards off the page. Distinct from
     `replySubmit` so the toggle and the send button never share a name. */
  'home.highlights.replyOpen': { kind: 'text', value: 'Write a reply' },
  'home.highlights.replyHide': { kind: 'text', value: 'Hide reply' },
  /* ⚠ `{count}` is the number of rows HIDDEN in what is loaded, never a total:
     `/me/home` pages Highlights (`highlightsNextCursor`), so "Show all (25)"
     would claim a total the client does not know. */
  'home.showMore': { kind: 'text', value: 'Show {count} more' },
  'home.showFewer': { kind: 'text', value: 'Show fewer' },
  'home.error.requestFailed': { kind: 'text', value: 'Request failed' },
  'home.announce.welcomeBoardReady': {
    kind: 'text',
    value: 'Welcome board ready.',
  },
  'home.announce.replyPosted': { kind: 'text', value: 'Reply posted.' },
  'home.announce.checklistItemChecked': {
    kind: 'text',
    value: 'Checklist item checked.',
  },
  'home.announce.workspaceCreated': {
    kind: 'text',
    value: 'Workspace {workspace} created.',
  },
  'home.announce.boardCreated': {
    kind: 'text',
    value: 'Board {board} created.',
  },
  'inbox.title': { kind: 'text', value: 'Inbox' },
  'inbox.description': {
    kind: 'text',
    value: 'Capture work before it belongs to a board.',
  },
  'inbox.live': { kind: 'text', value: 'Live Inbox' },
  'inbox.archived': { kind: 'text', value: 'Archived' },
  'inbox.captureForm': { kind: 'text', value: 'Capture an Inbox card' },
  'inbox.cardTitle': { kind: 'text', value: 'Card title' },
  'inbox.addCard': { kind: 'text', value: 'Add card' },
  'inbox.addingCard': { kind: 'text', value: 'Adding card...' },
  'inbox.enterTitle': { kind: 'text', value: 'Enter a card title first.' },
  'inbox.organise': { kind: 'text', value: 'Organise' },
  'inbox.keyword': { kind: 'text', value: 'Keyword' },
  'inbox.sort': { kind: 'text', value: 'Sort' },
  'inbox.sort.newest': { kind: 'text', value: 'Newest' },
  'inbox.sort.oldest': { kind: 'text', value: 'Oldest' },
  'inbox.sort.alphabetical': { kind: 'text', value: 'Alphabetical' },
  'inbox.completion': { kind: 'text', value: 'Completion' },
  'inbox.completion.any': { kind: 'text', value: 'Any' },
  'inbox.completion.incomplete': { kind: 'text', value: 'Incomplete' },
  'inbox.completion.complete': { kind: 'text', value: 'Complete' },
  'inbox.createdFrom': { kind: 'text', value: 'Created from' },
  'inbox.createdTo': { kind: 'text', value: 'Created to' },
  'inbox.dueFrom': { kind: 'text', value: 'Due from' },
  'inbox.dueTo': { kind: 'text', value: 'Due to' },
  'inbox.loadFailedTitle': { kind: 'text', value: 'Inbox could not load' },
  'inbox.cards': { kind: 'text', value: 'Inbox cards' },
  'inbox.archivedCards': { kind: 'text', value: 'Archived Inbox cards' },
  'inbox.archiveEmptyTitle': { kind: 'text', value: 'The archive is empty' },
  'inbox.archiveEmptyBody': {
    kind: 'text',
    value: 'Archived Inbox cards can be restored here.',
  },
  'inbox.emptyTitle': { kind: 'text', value: 'The Inbox is empty' },
  'inbox.emptyBody': {
    kind: 'text',
    value: 'Cards you capture before choosing a board will appear here.',
  },
  'inbox.untitledCard': { kind: 'text', value: 'Untitled card' },
  'inbox.untitledInboxCard': { kind: 'text', value: 'Untitled Inbox card' },
  'inbox.restoringCard': { kind: 'text', value: 'Restoring card...' },
  'inbox.archivingCard': { kind: 'text', value: 'Archiving card...' },
  'inbox.restore': { kind: 'text', value: 'Restore' },
  'inbox.archive': { kind: 'text', value: 'Archive' },
  'inbox.dueDate': { kind: 'text', value: 'Due {date}' },
  'inbox.checklistProgress': {
    kind: 'text',
    value: '{checked}/{total} checklist',
  },
  'inbox.attachmentCount': {
    kind: 'plural',
    forms: { one: '1 attachment', other: '{count} attachments' },
  },
  'inbox.unfiled': { kind: 'text', value: 'Unfiled' },
  'inbox.capture.heading': { kind: 'text', value: 'Capture' },
  'inbox.capture.description': {
    kind: 'text',
    value: 'Save links, voice notes and chat messages.',
  },
  'inbox.capture.form': { kind: 'text', value: 'Capture from another app' },
  'inbox.capture.type': { kind: 'text', value: 'Capture type' },
  'inbox.capture.typeLabel': { kind: 'text', value: '{source} capture type' },
  'inbox.capture.source.link': { kind: 'text', value: 'Link' },
  'inbox.capture.source.voice': { kind: 'text', value: 'Voice note' },
  'inbox.capture.source.chat': { kind: 'text', value: 'Chat message' },
  'inbox.capture.spokenTitle': { kind: 'text', value: 'Spoken title' },
  'inbox.capture.title': { kind: 'text', value: 'Title' },
  'inbox.capture.url': { kind: 'text', value: 'URL' },
  'inbox.capture.sourceLink': { kind: 'text', value: 'Source link' },
  'inbox.capture.messageText': { kind: 'text', value: 'Message text' },
  'inbox.capture.notes': { kind: 'text', value: 'Notes' },
  'inbox.capture.saving': { kind: 'text', value: 'Saving capture...' },
  'inbox.capture.save': { kind: 'text', value: 'Save to Inbox' },
  'inbox.capture.nameFirst': {
    kind: 'text',
    value: 'Name the captured item before saving it.',
  },
  'inbox.capture.urlFirst': {
    kind: 'text',
    value: 'Paste a URL before saving a link capture.',
  },
  'inbox.capture.uploadUnavailable': {
    kind: 'text',
    value: 'Image and file capture waits for a real Inbox upload route.',
  },
  'inbox.settings.title': { kind: 'text', value: 'Inbox settings' },
  'inbox.settings.description': {
    kind: 'text',
    value: 'Capture address and background.',
  },
  'inbox.settings.emailAddress': { kind: 'text', value: 'Email address' },
  'inbox.settings.noAddress': {
    kind: 'text',
    value: 'No Inbox address has loaded yet.',
  },
  'inbox.settings.inboxEmail': { kind: 'text', value: 'Inbox email' },
  'inbox.settings.copied': { kind: 'text', value: 'Copied' },
  'inbox.settings.copyAddress': { kind: 'text', value: 'Copy address' },
  'inbox.settings.regeneratingAddress': {
    kind: 'text',
    value: 'Regenerating address...',
  },
  'inbox.settings.regenerateAddress': {
    kind: 'text',
    value: 'Regenerate address',
  },
  'inbox.settings.connectedCapture': {
    kind: 'text',
    value: 'Connected capture',
  },
  'inbox.settings.connectedTools': {
    kind: 'plural',
    forms: {
      one: '1 read-write connected tool can save to this Inbox.',
      other: '{count} read-write connected tools can save to this Inbox.',
    },
  },
  'inbox.settings.connectedToolsEmpty': {
    kind: 'text',
    value: 'Grant a read-write connected tool before external tools can save here.',
  },
  'inbox.settings.manageConnectedTools': {
    kind: 'text',
    value: 'Manage connected tools',
  },
  'inbox.settings.background': { kind: 'text', value: 'Background' },
  'inbox.settings.solidColours': {
    kind: 'text',
    value: 'Inbox solid colours',
  },
  'inbox.settings.useColour': {
    kind: 'text',
    value: 'Use {background} Inbox background',
  },
  'inbox.settings.photoBackgrounds': {
    kind: 'text',
    value: 'Inbox photo backgrounds',
  },
  'inbox.settings.usePhoto': {
    kind: 'text',
    value: 'Use {background} Inbox photo background',
  },
  'inbox.settings.clearBackground': {
    kind: 'text',
    value: 'Clear background',
  },
  'inbox.card.description': {
    kind: 'text',
    value: 'Inbox cards stay private until they are filed onto a board.',
  },
  'inbox.card.closeLabel': { kind: 'text', value: 'Close Inbox card' },
  'inbox.card.close': { kind: 'text', value: 'Close' },
  'inbox.card.fileOntoBoard': { kind: 'text', value: 'File onto board' },
  'inbox.card.state': { kind: 'text', value: 'Card state' },
  'inbox.card.noDueDate': { kind: 'text', value: 'No due date' },
  'inbox.card.checklistItems': {
    kind: 'plural',
    forms: {
      one: '{checked}/{total} checklist item',
      other: '{checked}/{total} checklist items',
    },
  },
  'inbox.card.descriptionHeading': { kind: 'text', value: 'Description' },
  'inbox.card.dueDateLabel': { kind: 'text', value: 'Due date' },
  'inbox.card.invalidDate': {
    kind: 'text',
    value: 'Choose a valid date and time.',
  },
  'inbox.card.savingDueDate': {
    kind: 'text',
    value: 'Saving due date...',
  },
  'inbox.card.saveDueDate': { kind: 'text', value: 'Save due date' },
  'inbox.card.clearDueDate': { kind: 'text', value: 'Clear due date' },
  'inbox.card.checklistTitle': { kind: 'text', value: 'Checklist title' },
  'inbox.card.addingChecklist': {
    kind: 'text',
    value: 'Adding checklist...',
  },
  'inbox.card.checklistTitleFirst': {
    kind: 'text',
    value: 'Enter a checklist title first.',
  },
  'inbox.card.addChecklist': { kind: 'text', value: 'Add checklist' },
  'inbox.card.boardToolsTitle': {
    kind: 'text',
    value: 'Board tools unlock after filing',
  },
  'inbox.card.boardToolsBody': {
    kind: 'text',
    value:
      'Members, labels, custom fields, mirroring, automation and sharing are board-scoped. Filing is one-way; a board card cannot be moved back into the Inbox.',
  },
  'inbox.file.title': { kind: 'text', value: 'File {card}' },
  'inbox.file.description': {
    kind: 'text',
    value: 'Choose the board, list and position for this Inbox card.',
  },
  'inbox.file.cancelLabel': { kind: 'text', value: 'Cancel filing' },
  'inbox.file.cancel': { kind: 'text', value: 'Cancel' },
  'inbox.file.filing': { kind: 'text', value: 'Filing card...' },
  'inbox.file.chooseDestination': {
    kind: 'text',
    value: 'Choose a destination before filing.',
  },
  'inbox.file.submit': { kind: 'text', value: 'File card' },
  'inbox.file.emptyTitle': { kind: 'text', value: 'Nowhere to file this card' },
  'inbox.file.emptyBody': {
    kind: 'text',
    value: 'You do not have an editable board with an open list yet.',
  },
  'inbox.file.searchBoards': {
    kind: 'text',
    value: 'Search destination boards',
  },
  'inbox.file.destinationBoard': {
    kind: 'text',
    value: 'Destination board',
  },
  'inbox.file.noOpenListsTitle': { kind: 'text', value: 'No open lists' },
  'inbox.file.noOpenListsBody': {
    kind: 'text',
    value: 'Create a list on {board} before filing cards there.',
  },
  'inbox.file.destinationList': { kind: 'text', value: 'Destination list' },
  'inbox.file.position': { kind: 'text', value: 'Position' },
  'inbox.file.topOfList': { kind: 'text', value: 'Top of list' },
  'inbox.file.bottomOfList': { kind: 'text', value: 'Bottom of list' },
  'inbox.file.afterCard': { kind: 'text', value: 'After {card}' },
  'inbox.signInTitle': { kind: 'text', value: 'Sign in to open the Inbox' },
  'inbox.signInBody': {
    kind: 'text',
    value: 'Your Inbox is private. Sign in to capture and file personal cards.',
  },
  'inbox.signInAction': { kind: 'text', value: 'Sign in' },
  'inbox.genericError': {
    kind: 'text',
    value: 'Something went wrong. Try again.',
  },
  'inbox.announce.cardAdded': {
    kind: 'text',
    value: '{card} added to the Inbox.',
  },
  'inbox.announce.captureSaved': {
    kind: 'text',
    value: '{card} saved to the Inbox.',
  },
  'inbox.announce.addressRegenerated': {
    kind: 'text',
    value: 'Inbox email address regenerated.',
  },
  'inbox.announce.backgroundSaved': {
    kind: 'text',
    value: 'Inbox background saved.',
  },
  'inbox.announce.cardArchived': {
    kind: 'text',
    value: '{card} archived.',
  },
  'inbox.announce.cardRestored': {
    kind: 'text',
    value: '{card} restored.',
  },
  'inbox.announce.dueDateSaved': {
    kind: 'text',
    value: 'Inbox card due date saved.',
  },
  'inbox.announce.checklistAdded': {
    kind: 'text',
    value: 'Inbox checklist added.',
  },
  'inbox.announce.cardFiled': {
    kind: 'text',
    value: '{card} filed onto a board.',
  },
  'planner.title': { kind: 'text', value: 'Planner' },
  'planner.description': { kind: 'text', value: 'Personal planning' },
  'planner.previous': { kind: 'text', value: 'Previous' },
  'planner.today': { kind: 'text', value: 'Today' },
  'planner.next': { kind: 'text', value: 'Next' },
  'planner.rangeCalendar': { kind: 'text', value: '{range} calendar' },
  'planner.loadFailedTitle': { kind: 'text', value: 'Planner could not load' },
  'planner.genericError': {
    kind: 'text',
    value: 'Something went wrong. Try again.',
  },
  'planner.retry': { kind: 'text', value: 'Try again' },
  'planner.signInTitle': { kind: 'text', value: 'Sign in to open Planner' },
  'planner.signInBody': {
    kind: 'text',
    value: 'Planner is personal to your account.',
  },
  'planner.signInAction': { kind: 'text', value: 'Sign in' },
  'planner.schedulable.title': { kind: 'text', value: 'Schedulable cards' },
  'planner.schedulable.description': {
    kind: 'text',
    value: 'Due cards from selected boards and your Inbox.',
  },
  'planner.schedulable.includeUnassigned': {
    kind: 'text',
    value: 'Include unassigned shared-board cards',
  },
  'planner.schedulable.emptyTitle': { kind: 'text', value: 'No schedulable cards' },
  'planner.schedulable.emptyBody': {
    kind: 'text',
    value: 'Cards with due dates appear here when they match your filters.',
  },
  'planner.schedulable.list': { kind: 'text', value: 'Schedulable cards' },
  'planner.schedulable.cardLabel': {
    kind: 'text',
    value: '{card} schedulable card',
  },
  'planner.source.inbox': { kind: 'text', value: 'Inbox' },
  'planner.untitledCard': { kind: 'text', value: 'Untitled card' },
  'planner.dueDate': { kind: 'text', value: 'Due {date}' },
  'planner.scheduleAt': { kind: 'text', value: 'Schedule {time}' },
  'planner.complete': { kind: 'text', value: 'Complete' },
  'planner.markIncomplete': { kind: 'text', value: 'Mark incomplete' },
  'planner.boardFilters.title': { kind: 'text', value: 'Board filters' },
  'planner.boardFilters.empty': {
    kind: 'text',
    value: 'No board cards in this range.',
  },
  'planner.focus.blockCount': {
    kind: 'plural',
    forms: { one: '1 focus block', other: '{count} focus blocks' },
  },
  'planner.focus.slot': { kind: 'text', value: '{day} {time} focus slot' },
  'planner.focus.blocks': { kind: 'text', value: '{day} {time} focus blocks' },
  'planner.focus.addCardAt': { kind: 'text', value: 'Add {card} at {time}' },
  'planner.focus.addCard': { kind: 'text', value: 'Add' },
  'planner.focus.moveFirstHere': { kind: 'text', value: 'Move first focus block here' },
  'planner.focus.move': { kind: 'text', value: 'Move' },
  'planner.focus.blockLabel': { kind: 'text', value: '{title} focus block' },
  'planner.focus.titleFallback': { kind: 'text', value: 'Focus time' },
  'planner.focus.timeRange': { kind: 'text', value: 'Focus time / {range}' },
  'planner.focus.sync': { kind: 'text', value: 'Calendar sync: {status}' },
  'planner.focus.linkedCards': { kind: 'text', value: '{title} linked cards' },
  'planner.focus.remove': { kind: 'text', value: 'Remove' },
  'planner.focus.extend': { kind: 'text', value: 'Extend 30 min' },
  'planner.focus.linkCard': { kind: 'text', value: 'Link {card}' },
  'planner.focus.delete': { kind: 'text', value: 'Delete block' },
  'planner.focus.unavailableCard': { kind: 'text', value: 'Unavailable card' },
  'planner.focus.state.live': { kind: 'text', value: 'active' },
  'planner.focus.state.completed': { kind: 'text', value: 'complete' },
  'planner.focus.state.archived': { kind: 'text', value: 'archived' },
  'planner.focus.state.deleted': { kind: 'text', value: 'deleted' },
  'planner.focus.state.inaccessible': { kind: 'text', value: 'access removed' },
  'planner.focus.state.orphaned': { kind: 'text', value: 'missing' },
  'planner.focus.sync.not_configured': { kind: 'text', value: 'not configured' },
  'planner.focus.sync.pending': { kind: 'text', value: 'pending' },
  'planner.focus.sync.synced': { kind: 'text', value: 'synced' },
  'planner.focus.sync.failed': { kind: 'text', value: 'failed' },
  'planner.rules.title': { kind: 'text', value: 'Planner rules' },
  'planner.rules.workingDayStarts': { kind: 'text', value: 'Working day starts' },
  'planner.rules.workingDayEnds': { kind: 'text', value: 'Working day ends' },
  'planner.weekday.monday': { kind: 'text', value: 'monday' },
  'planner.weekday.tuesday': { kind: 'text', value: 'tuesday' },
  'planner.weekday.wednesday': { kind: 'text', value: 'wednesday' },
  'planner.weekday.thursday': { kind: 'text', value: 'thursday' },
  'planner.weekday.friday': { kind: 'text', value: 'friday' },
  'planner.weekday.saturday': { kind: 'text', value: 'saturday' },
  'planner.weekday.sunday': { kind: 'text', value: 'sunday' },
  'planner.calendar.title': { kind: 'text', value: 'External calendars' },
  'planner.calendar.description': {
    kind: 'text',
    value:
      'Connected accounts show calendar identities and any events returned by the Planner feed.',
  },
  'planner.calendar.connect': { kind: 'text', value: 'Connect {provider}' },
  'planner.calendar.provider.google': { kind: 'text', value: 'Google' },
  'planner.calendar.provider.outlook': { kind: 'text', value: 'Outlook' },
  /* FE-80.1 automation-authoring — the board Automation panel and its authoring
     form: `board/AutomationEditor.tsx`, `board/BoardAutomationSection.tsx` and
     `automation/AutomationScheduleFields.tsx`. 392 keys — 387 at base plus the
     five `log.*` phrases fan-out 13 added for `FE-70.2` and `FE-71.3` — the
     largest single family in this catalogue, and the reasons it is this large
     are two.

     ⚠ THE COMPOSED AUTOMATION SENTENCE IS A WIRE VALUE AND IS NOT ROUTED.
     `AutomationEditor.submit()` posts the live sentence as `plainText`; the
     server stores it verbatim and `BoardAutomationSection` renders it back to
     every OTHER board member, in the command row and in the administrator
     definition dialog. Route the sentence and a Spanish author's rule is
     stored in Spanish and shown in Spanish to English readers, permanently,
     with nothing red. So `scheduleSentence`, `dueDateSentence`,
     `actionSentence` and `conditionText` keep their English, and the three
     English label tables `RULE_TRIGGER_LABELS`, `ACTION_LABELS` and
     `AUTOMATION_COMMAND_LABELS` survive with exactly one reader each — the
     sentence. The SCREEN positions of those same fifty-nine words (the
     `<option>`s, the action row heading, the tab strips, the activity and admin
     lines) route through the KEY TWINS below: `editor.triggerOption.*`,
     `editor.actionOption.*` and `commandKind.*`. Fifty-nine values therefore
     exist twice, once as English for the wire and once as a catalogue arm for
     the screen, and `automation-authoring.routing.test.ts` asserts the two
     halves agree arm for arm. That duplication is the price of not translating
     the database. Reported, not taken: the server should compose `plainText`
     from the stored structure, or render it per viewer.

     ⚠ COLLISION RULINGS, MEASURED OVER THE CATALOGUE AT THIS SLICE'S BASE.
     `Cancel` had 24 prior authors and gains three more here — the editor
     footer, the delete dialog and the administrator-disable dialog — because a
     shared `Cancel` lets one dialog's translation rename every other dialog's
     escape hatch. `Automation` had one (`boardMenu.automation`, the menu item
     that OPENS this panel) and gains two: the `<section>`'s `aria-label` and
     the `<h3>` inside it. New authors of existing bytes, each a different node
     on a different surface: `Copy`, `Close`, `Delete`, `Edit`, `Remove`,
     `Timezone`, `Disabled` (three), `Admin` (a TAB, not a role), `Activity`,
     `Calendar`, `Due date`, `Any list`, `Destination board`, `Unnamed label`.
     The planner's seven lower-case weekday words are deliberately NOT reused:
     these are `Monday`-cased checkbox labels on a board panel.

     ⚠ `Retry` HAS ZERO PRIOR AUTHORS AND THE PRODUCT'S OTHER WORD FOR THIS
     AFFORDANCE IS `Try again` (nine keys). `automationAuthoring.retry` keeps
     the bytes this component ships, in all four of its error `Notice` actions —
     a routing slice preserves bytes — and the divergence is reported rather
     than settled here. So are `Enable`, `Runs`, `Operations`, `Usage`,
     `Reason`, `Scope`, `Trigger`, `Actions`, `Conditions`, `Move up`,
     `Move down`, `Stored only`, `Frequency`, `Time`, `Unit`, `Minutes`,
     `Hours`, `Days` and `Monday`–`Sunday`, every one of them a first author.

     ⚠ FIVE COUNTED SENTENCES, and every one is called as `t(key, values,
     count)` with the count as the THIRD argument. `interfaceMessage` selects
     `forms.other` whenever `count !== 1`, so a dropped third argument renders
     the plural always: invisible at 993 and wrong at 1. The slice's OTHER
     English plurals (`day`/`days`, `hour`/`hours`, `{n} recipient(s)`) stay as
     inline ternaries because they are fragments of the stored sentence. */
  'automationAuthoring.schedule.legend': { kind: 'text', value: 'Schedule' },
  'automationAuthoring.schedule.frequencyLabel': { kind: 'text', value: 'Frequency' },
  'automationAuthoring.schedule.frequency.daily': { kind: 'text', value: 'Every day' },
  'automationAuthoring.schedule.frequency.weekdays': {
    kind: 'text',
    value: 'Every weekday',
  },
  'automationAuthoring.schedule.frequency.weekly': { kind: 'text', value: 'Every week' },
  'automationAuthoring.schedule.frequency.monthly': {
    kind: 'text',
    value: 'Every month',
  },
  'automationAuthoring.schedule.frequency.yearly': { kind: 'text', value: 'Every year' },
  'automationAuthoring.schedule.weekdaysLabel': {
    kind: 'text',
    value: 'Schedule weekdays',
  },
  'automationAuthoring.schedule.weekday.monday': { kind: 'text', value: 'Monday' },
  'automationAuthoring.schedule.weekday.tuesday': { kind: 'text', value: 'Tuesday' },
  'automationAuthoring.schedule.weekday.wednesday': { kind: 'text', value: 'Wednesday' },
  'automationAuthoring.schedule.weekday.thursday': { kind: 'text', value: 'Thursday' },
  'automationAuthoring.schedule.weekday.friday': { kind: 'text', value: 'Friday' },
  'automationAuthoring.schedule.weekday.saturday': { kind: 'text', value: 'Saturday' },
  'automationAuthoring.schedule.weekday.sunday': { kind: 'text', value: 'Sunday' },
  'automationAuthoring.schedule.dayOfMonthLabel': { kind: 'text', value: 'Day of month' },
  'automationAuthoring.schedule.yearlyDateLabel': { kind: 'text', value: 'Yearly date' },
  'automationAuthoring.schedule.yearlyDatePlaceholder': { kind: 'text', value: 'MM-DD' },
  'automationAuthoring.schedule.timeLabel': { kind: 'text', value: 'Time' },
  'automationAuthoring.schedule.timezoneLabel': { kind: 'text', value: 'Timezone' },
  'automationAuthoring.dueTrigger.legend': { kind: 'text', value: 'Due date trigger' },
  'automationAuthoring.dueTrigger.relationLabel': { kind: 'text', value: 'Runs' },
  'automationAuthoring.dueTrigger.relation.before': {
    kind: 'text',
    value: 'Before the card is due',
  },
  'automationAuthoring.dueTrigger.relation.at': {
    kind: 'text',
    value: 'When the card becomes due',
  },
  'automationAuthoring.dueTrigger.relation.after': {
    kind: 'text',
    value: 'After the card is overdue',
  },
  'automationAuthoring.dueTrigger.amountLabel': { kind: 'text', value: 'Offset amount' },
  'automationAuthoring.dueTrigger.unitLabel': { kind: 'text', value: 'Unit' },
  'automationAuthoring.dueTrigger.unit.minutes': { kind: 'text', value: 'Minutes' },
  'automationAuthoring.dueTrigger.unit.hours': { kind: 'text', value: 'Hours' },
  'automationAuthoring.dueTrigger.unit.days': { kind: 'text', value: 'Days' },
  'automationAuthoring.editor.nameLabel': { kind: 'text', value: 'Automation name' },
  'automationAuthoring.editor.sentenceLabel': {
    kind: 'text',
    value: 'Automation sentence',
  },
  'automationAuthoring.editor.membersHint': {
    kind: 'text',
    value: 'Member-specific blanks load from board membership.',
  },
  'automationAuthoring.editor.loopSafetyTitle': { kind: 'text', value: 'Loop safety' },
  'automationAuthoring.editor.loopSafetyBody': {
    kind: 'text',
    value:
      'Loop safety is applied at run time: this rule does not re-trigger itself, and command chains stop after {limit} runs with a loop-suppressed log entry.',
  },
  'automationAuthoring.editor.trigger.legend': { kind: 'text', value: 'Trigger' },
  'automationAuthoring.editor.trigger.phraseLabel': {
    kind: 'text',
    value: 'Trigger phrase',
  },
  'automationAuthoring.editor.trigger.listLabel': { kind: 'text', value: 'Trigger list' },
  'automationAuthoring.editor.trigger.listPlaceholder': {
    kind: 'text',
    value: 'Choose a list',
  },
  'automationAuthoring.editor.trigger.textLabel': { kind: 'text', value: 'Trigger text' },
  'automationAuthoring.editor.cardScope.legend': { kind: 'text', value: 'Card scope' },
  'automationAuthoring.editor.cardScope.listLabel': {
    kind: 'text',
    value: 'Due-date list restriction',
  },
  'automationAuthoring.editor.cardScope.anyList': { kind: 'text', value: 'Any list' },
  'automationAuthoring.editor.unnamedLabel': { kind: 'text', value: 'Unnamed label' },
  'automationAuthoring.editor.triggerOption.card_added_to_list': {
    kind: 'text',
    value: 'when a card is added to a list',
  },
  'automationAuthoring.editor.triggerOption.card_moved_into_list': {
    kind: 'text',
    value: 'when a card moves into a list',
  },
  'automationAuthoring.editor.triggerOption.card_moved_out_of_list': {
    kind: 'text',
    value: 'when a card moves out of a list',
  },
  'automationAuthoring.editor.triggerOption.card_archived': {
    kind: 'text',
    value: 'when a card is archived',
  },
  'automationAuthoring.editor.triggerOption.card_unarchived': {
    kind: 'text',
    value: 'when a card is unarchived',
  },
  'automationAuthoring.editor.triggerOption.label_added': {
    kind: 'text',
    value: 'when a label is added',
  },
  'automationAuthoring.editor.triggerOption.label_removed': {
    kind: 'text',
    value: 'when a label is removed',
  },
  'automationAuthoring.editor.triggerOption.member_joined_card': {
    kind: 'text',
    value: 'when a member joins a card',
  },
  'automationAuthoring.editor.triggerOption.member_left_card': {
    kind: 'text',
    value: 'when a member leaves a card',
  },
  'automationAuthoring.editor.triggerOption.due_date_added': {
    kind: 'text',
    value: 'when a due date is added',
  },
  'automationAuthoring.editor.triggerOption.due_date_changed': {
    kind: 'text',
    value: 'when a due date changes',
  },
  'automationAuthoring.editor.triggerOption.due_date_removed': {
    kind: 'text',
    value: 'when a due date is removed',
  },
  'automationAuthoring.editor.triggerOption.due_date_marked_complete': {
    kind: 'text',
    value: 'when a due date is marked complete',
  },
  'automationAuthoring.editor.triggerOption.checklist_added': {
    kind: 'text',
    value: 'when a checklist is added',
  },
  'automationAuthoring.editor.triggerOption.checklist_completed': {
    kind: 'text',
    value: 'when a checklist is completed',
  },
  'automationAuthoring.editor.triggerOption.checklist_item_checked': {
    kind: 'text',
    value: 'when a checklist item is checked',
  },
  'automationAuthoring.editor.triggerOption.checklist_item_unchecked': {
    kind: 'text',
    value: 'when a checklist item is unchecked',
  },
  'automationAuthoring.editor.triggerOption.comment_posted': {
    kind: 'text',
    value: 'when a comment is posted',
  },
  'automationAuthoring.editor.triggerOption.comment_matching_text_posted': {
    kind: 'text',
    value: 'when a comment contains text',
  },
  'automationAuthoring.editor.triggerOption.attachment_added': {
    kind: 'text',
    value: 'when an attachment is added',
  },
  'automationAuthoring.editor.triggerOption.card_voted_on': {
    kind: 'text',
    value: 'when a card is voted on',
  },
  'automationAuthoring.editor.triggerOption.custom_field_value_changed': {
    kind: 'text',
    value: 'when a custom field changes',
  },
  'automationAuthoring.editor.triggerOption.card_name_matches': {
    kind: 'text',
    value: 'when a card name matches text',
  },
  'automationAuthoring.editor.triggerOption.list_created': {
    kind: 'text',
    value: 'when a list is created',
  },
  'automationAuthoring.editor.triggerOption.list_renamed': {
    kind: 'text',
    value: 'when a list is renamed',
  },
  'automationAuthoring.editor.triggerOption.list_archived': {
    kind: 'text',
    value: 'when a list is archived',
  },
  'automationAuthoring.editor.condition.legend': { kind: 'text', value: 'Conditions' },
  'automationAuthoring.editor.condition.addLabel': {
    kind: 'text',
    value: 'Add condition',
  },
  'automationAuthoring.editor.condition.addButton': {
    kind: 'text',
    value: 'Add condition',
  },
  'automationAuthoring.editor.condition.empty': { kind: 'text', value: 'No conditions.' },
  'automationAuthoring.editor.condition.remove': {
    kind: 'text',
    value: 'Remove condition',
  },
  'automationAuthoring.editor.condition.labelLabel': {
    kind: 'text',
    value: 'Condition {index} label',
  },
  'automationAuthoring.editor.condition.labelPlaceholder': {
    kind: 'text',
    value: 'Choose a label',
  },
  'automationAuthoring.editor.condition.memberLabel': {
    kind: 'text',
    value: 'Condition {index} member',
  },
  'automationAuthoring.editor.condition.memberPlaceholder': {
    kind: 'text',
    value: 'Choose a member',
  },
  'automationAuthoring.editor.condition.textLabel': {
    kind: 'text',
    value: 'Condition {index} text',
  },
  'automationAuthoring.editor.conditionOption.actor_not_me': {
    kind: 'text',
    value: 'By anyone but me',
  },
  'automationAuthoring.editor.conditionOption.actor_me': { kind: 'text', value: 'By me' },
  'automationAuthoring.editor.conditionOption.actor_specific_member': {
    kind: 'text',
    value: 'By a specific member',
  },
  'automationAuthoring.editor.conditionOption.label': {
    kind: 'text',
    value: 'With label',
  },
  'automationAuthoring.editor.conditionOption.without_label': {
    kind: 'text',
    value: 'Without label',
  },
  'automationAuthoring.editor.conditionOption.assigned_to_me': {
    kind: 'text',
    value: 'Assigned to me',
  },
  'automationAuthoring.editor.conditionOption.assigned_to_member': {
    kind: 'text',
    value: 'Assigned to a member',
  },
  'automationAuthoring.editor.conditionOption.due_date': {
    kind: 'text',
    value: 'With a due date',
  },
  'automationAuthoring.editor.conditionOption.list_name': {
    kind: 'text',
    value: 'List name contains',
  },
  'automationAuthoring.editor.conditionOption.card_text': {
    kind: 'text',
    value: 'Card name contains',
  },
  'automationAuthoring.editor.conditionOption.comment_text': {
    kind: 'text',
    value: 'Comment contains',
  },
  'automationAuthoring.editor.preview.fallback.member': {
    kind: 'text',
    value: 'a member',
  },
  'automationAuthoring.editor.preview.fallback.label': {
    kind: 'text',
    value: 'a label',
  },
  'automationAuthoring.editor.preview.fallback.list': { kind: 'text', value: 'a list' },
  'automationAuthoring.editor.preview.fallback.chosenDays': {
    kind: 'text',
    value: 'chosen days',
  },
  'automationAuthoring.editor.preview.fallback.requestUrl': {
    kind: 'text',
    value: 'a request URL',
  },
  'automationAuthoring.editor.preview.fallback.emailSubject': {
    kind: 'text',
    value: 'with a subject',
  },
  'automationAuthoring.editor.preview.fallback.emailRecipients': {
    kind: 'text',
    value: 'recipients',
  },
  'automationAuthoring.editor.preview.condition.actorMe': {
    kind: 'text',
    value: 'by me',
  },
  'automationAuthoring.editor.preview.condition.actorNotMe': {
    kind: 'text',
    value: 'by anyone but me',
  },
  'automationAuthoring.editor.preview.condition.actorMember': {
    kind: 'text',
    value: 'by {member}',
  },
  'automationAuthoring.editor.preview.condition.withLabel': {
    kind: 'text',
    value: 'with label {label}',
  },
  'automationAuthoring.editor.preview.condition.withoutLabel': {
    kind: 'text',
    value: 'without label {label}',
  },
  'automationAuthoring.editor.preview.condition.assignedToMe': {
    kind: 'text',
    value: 'assigned to me',
  },
  'automationAuthoring.editor.preview.condition.assignedToMember': {
    kind: 'text',
    value: 'assigned to {member}',
  },
  'automationAuthoring.editor.preview.condition.dueDate': {
    kind: 'text',
    value: 'with a due date',
  },
  'automationAuthoring.editor.preview.condition.listName': {
    kind: 'text',
    value: 'in a list whose name contains "{text}"',
  },
  'automationAuthoring.editor.preview.condition.cardText': {
    kind: 'text',
    value: 'with card name containing "{text}"',
  },
  'automationAuthoring.editor.preview.condition.commentText': {
    kind: 'text',
    value: 'with comment containing "{text}"',
  },
  'automationAuthoring.editor.preview.schedule.daily': {
    kind: 'text',
    value: 'every day at {time} {timezone}',
  },
  'automationAuthoring.editor.preview.schedule.weekdays': {
    kind: 'text',
    value: 'weekdays at {time} {timezone}',
  },
  'automationAuthoring.editor.preview.schedule.weekly': {
    kind: 'text',
    value: 'every {days} at {time} {timezone}',
  },
  'automationAuthoring.editor.preview.schedule.monthly': {
    kind: 'text',
    value: 'day {day} each month at {time} {timezone}',
  },
  'automationAuthoring.editor.preview.schedule.yearly': {
    kind: 'text',
    value: 'every {date} at {time} {timezone}',
  },
  'automationAuthoring.editor.preview.duration.days': {
    kind: 'plural',
    forms: { one: '{count} day', other: '{count} days' },
  },
  'automationAuthoring.editor.preview.duration.hours': {
    kind: 'plural',
    forms: { one: '{count} hour', other: '{count} hours' },
  },
  'automationAuthoring.editor.preview.duration.minutes': {
    kind: 'plural',
    forms: { one: '{count} minute', other: '{count} minutes' },
  },
  'automationAuthoring.editor.preview.dueDate.atDue': {
    kind: 'text',
    value: 'when a card becomes due',
  },
  'automationAuthoring.editor.preview.dueDate.before': {
    kind: 'text',
    value: '{amount} before a card is due',
  },
  'automationAuthoring.editor.preview.dueDate.after': {
    kind: 'text',
    value: '{amount} after a card is due',
  },
  'automationAuthoring.editor.preview.action.postComment': {
    kind: 'text',
    value: 'post comment "{text}"',
  },
  'automationAuthoring.editor.preview.action.renameCard': {
    kind: 'text',
    value: 'rename card to "{text}"',
  },
  'automationAuthoring.editor.preview.action.createCard': {
    kind: 'text',
    value: 'create card "{text}"',
  },
  'automationAuthoring.editor.preview.action.createList': {
    kind: 'text',
    value: 'create list "{text}"',
  },
  'automationAuthoring.editor.preview.action.setDueDate': {
    kind: 'plural',
    forms: { one: 'set due date in {count} day', other: 'set due date in {count} days' },
  },
  'automationAuthoring.editor.preview.action.sortList': {
    kind: 'text',
    value: 'sort {list} by due date',
  },
  'automationAuthoring.editor.preview.action.addLabel': {
    kind: 'text',
    value: 'add label {label}',
  },
  'automationAuthoring.editor.preview.action.removeLabel': {
    kind: 'text',
    value: 'remove label {label}',
  },
  'automationAuthoring.editor.preview.action.deleteCard': {
    kind: 'text',
    value: 'delete card',
  },
  'automationAuthoring.editor.preview.action.httpRequest': {
    kind: 'text',
    value: '{method} {url}',
  },
  'automationAuthoring.editor.preview.action.emailRecipients': {
    kind: 'plural',
    forms: { one: '{count} recipient', other: '{count} recipients' },
  },
  'automationAuthoring.editor.preview.action.sendEmail': {
    kind: 'text',
    value: 'send email {subject} to {recipients}',
  },
  'automationAuthoring.editor.preview.trigger.listTarget': {
    kind: 'text',
    value: '{trigger} {list}',
  },
  'automationAuthoring.editor.preview.trigger.matchingText': {
    kind: 'text',
    value: '{trigger} matching "{text}"',
  },
  'automationAuthoring.editor.preview.trigger.dueDateList': {
    kind: 'text',
    value: '{trigger} in {list}',
  },
  'automationAuthoring.editor.preview.trigger.commandRuns': {
    kind: 'text',
    value: 'when this {command} runs',
  },
  'automationAuthoring.editor.preview.join.and': {
    kind: 'text',
    value: '{left} and {right}',
  },
  'automationAuthoring.editor.preview.join.then': {
    kind: 'text',
    value: '{left}, then {right}',
  },
  'automationAuthoring.editor.preview.sentence.noCondition': {
    kind: 'text',
    value: '{trigger}, then {actions}',
  },
  'automationAuthoring.editor.preview.sentence.withCondition': {
    kind: 'text',
    value: '{trigger} {condition}, then {actions}',
  },
  'automationAuthoring.editor.action.legend': { kind: 'text', value: 'Actions' },
  'shellShortcuts.area.board': { kind: 'text', value: 'Board' },
  'shellShortcuts.area.cards': { kind: 'text', value: 'Cards' },
  'shellShortcuts.area.cardDetail': { kind: 'text', value: 'Card detail' },
  'shellShortcuts.area.navigation': { kind: 'text', value: 'Navigation' },
  'shellShortcuts.area.activity': { kind: 'text', value: 'Activity' },
  'shellShortcuts.area.editing': { kind: 'text', value: 'Editing' },
  'shellShortcuts.shortcut.shortcutsOpen': {
    kind: 'text',
    value: 'Open keyboard shortcuts',
  },
  'shellShortcuts.shortcut.navigationBoardSwitcher': {
    kind: 'text',
    value: 'Open board switcher',
  },
  'shellShortcuts.shortcut.navigationSearch': { kind: 'text', value: 'Focus search' },
  'shellShortcuts.shortcut.navigationPlanner': { kind: 'text', value: 'Open Planner' },
  'shellShortcuts.shortcut.boardFilterOpen': { kind: 'text', value: 'Open filter menu' },
  'shellShortcuts.shortcut.boardFilterClear': {
    kind: 'text',
    value: 'Clear all filters',
  },
  'shellShortcuts.shortcut.boardFilterMyCards': {
    kind: 'text',
    value: 'Toggle cards assigned to me',
  },
  'shellShortcuts.shortcut.boardListCollapseToggle': {
    kind: 'text',
    value: 'Collapse or expand a list',
  },
  'shellShortcuts.shortcut.boardHorizontalScroll': {
    kind: 'text',
    value: 'Scroll the board horizontally',
  },
  'shellShortcuts.shortcut.activityBoardMenu': {
    kind: 'text',
    value: 'Open board menu activity',
  },
  'shellShortcuts.shortcut.cardsOpen': { kind: 'text', value: 'Open card' },
  'shellShortcuts.shortcut.cardsAddBelow': {
    kind: 'text',
    value: 'Add card below current card',
  },
  'shellShortcuts.shortcut.cardsEditTitle': { kind: 'text', value: 'Edit card title' },
  'shellShortcuts.shortcut.cardsQuickEdit': { kind: 'text', value: 'Quick-edit card' },
  'shellShortcuts.shortcut.cardsDates': { kind: 'text', value: 'Open dates' },
  'shellShortcuts.shortcut.cardsLabels': { kind: 'text', value: 'Open labels' },
  'shellShortcuts.shortcut.cardsLabelPosition': {
    kind: 'text',
    value: 'Toggle label by position',
  },
  'shellShortcuts.shortcut.cardsMembers': { kind: 'text', value: 'Open members' },
  'shellShortcuts.shortcut.cardsToggleSelf': {
    kind: 'text',
    value: 'Assign or unassign yourself',
  },
  'shellShortcuts.shortcut.cardsWatch': { kind: 'text', value: 'Watch or unwatch card' },
  'shellShortcuts.shortcut.cardsArchive': { kind: 'text', value: 'Archive card' },
  'shellShortcuts.shortcut.cardsSelectNext': {
    kind: 'text',
    value: 'Move selection to next card',
  },
  'shellShortcuts.shortcut.cardsSelectPrevious': {
    kind: 'text',
    value: 'Move selection to previous card',
  },
  'shellShortcuts.shortcut.cardDetailClose': {
    kind: 'text',
    value: 'Close card or cancel edit',
  },
  'shellShortcuts.shortcut.editingUndo': { kind: 'text', value: 'Undo last action' },
  'shellShortcuts.shortcut.editingRedo': {
    kind: 'text',
    value: 'Redo last undone action',
  },
  'shellShortcuts.shortcut.editingRepeat': { kind: 'text', value: 'Repeat last action' },
  'shellShortcuts.shortcut.editingSubmit': {
    kind: 'text',
    value: 'Submit comment or composer',
  },
  'shellShortcuts.overlay.title': { kind: 'text', value: 'Keyboard shortcuts' },
  'shellShortcuts.overlay.close': { kind: 'text', value: 'Close keyboard shortcuts' },
  'shellShortcuts.overlay.preferencesLabel': {
    kind: 'text',
    value: 'Shortcut preferences',
  },
  'shellShortcuts.overlay.singleKeyLabel': {
    kind: 'text',
    value: 'Disable single-key shortcuts',
  },
  'shellShortcuts.overlay.singleKeyDescription': {
    kind: 'text',
    value: 'Modifier shortcuts and this overlay stay available.',
  },
  'shellShortcuts.overlay.reducedMotionLabel': { kind: 'text', value: 'Reduce motion' },
  'shellShortcuts.overlay.reducedMotionDescription': {
    kind: 'text',
    value: 'Use quieter transitions on this device.',
  },
  'shellShortcuts.overlay.statusUnavailable': { kind: 'text', value: 'Unavailable' },
  'shellShortcuts.overlay.statusDisabled': { kind: 'text', value: 'Disabled' },
  'shellShortcuts.overlay.keysPair': { kind: 'text', value: '{first} or {second}' },
  'shellShortcuts.root.skipToContent': { kind: 'text', value: 'Skip to content' },
  'shellShortcuts.root.createWorkspaceTitle': {
    kind: 'text',
    value: 'Create a workspace',
  },
  'shellShortcuts.root.createWorkspaceClose': { kind: 'text', value: 'Close' },
  'shellShortcuts.root.workspaceCreated': {
    kind: 'text',
    value: 'Workspace {name} created.',
  },
  'shellShortcuts.keyCap.cmd': { kind: 'text', value: 'Cmd' },
  'shellShortcuts.keyCap.ctrl': { kind: 'text', value: 'Ctrl' },
  'shellShortcuts.keyCap.down': { kind: 'text', value: 'Down' },
  'shellShortcuts.keyCap.enter': { kind: 'text', value: 'Enter' },
  'shellShortcuts.keyCap.esc': { kind: 'text', value: 'Esc' },
  'shellShortcuts.keyCap.scroll': { kind: 'text', value: 'Scroll' },
  'shellShortcuts.keyCap.shift': { kind: 'text', value: 'Shift' },
  'shellShortcuts.keyCap.space': { kind: 'text', value: 'Space' },
  'shellShortcuts.keyCap.up': { kind: 'text', value: 'Up' },
  'automationAuthoring.editor.action.addLabel': { kind: 'text', value: 'Add action' },
  'automationAuthoring.editor.action.addButton': { kind: 'text', value: 'Add action' },
  'automationAuthoring.editor.action.optionStoredOnly': {
    kind: 'text',
    value: '{action} (stored only)',
  },
  'automationAuthoring.editor.action.storedOnlyChip': {
    kind: 'text',
    value: 'Stored only',
  },
  'automationAuthoring.editor.action.limitMessage': {
    kind: 'text',
    value: 'An automation command can contain at most {limit} actions.',
  },
  'automationAuthoring.editor.action.limitNotice': {
    kind: 'text',
    value:
      'This command has {limit} actions, which is the maximum. Remove an action before adding another.',
  },
  'automationAuthoring.editor.action.moveUp': { kind: 'text', value: 'Move up' },
  'automationAuthoring.editor.action.moveDown': { kind: 'text', value: 'Move down' },
  'automationAuthoring.editor.action.remove': { kind: 'text', value: 'Remove' },
  'automationAuthoring.editor.action.textLabel': { kind: 'text', value: '{action} text' },
  'automationAuthoring.editor.action.daysLabel': { kind: 'text', value: 'Days from now' },
  'automationAuthoring.editor.action.labelLabel': { kind: 'text', value: 'Action label' },
  'automationAuthoring.editor.action.labelPlaceholder': {
    kind: 'text',
    value: 'Choose a label',
  },
  'automationAuthoring.editor.action.listLabel': { kind: 'text', value: 'List to sort' },
  'automationAuthoring.editor.action.listPlaceholder': {
    kind: 'text',
    value: 'Choose a list',
  },
  'automationAuthoring.editor.actionOption.move_card': {
    kind: 'text',
    value: 'move card',
  },
  'automationAuthoring.editor.actionOption.copy_card': {
    kind: 'text',
    value: 'copy card',
  },
  'automationAuthoring.editor.actionOption.mirror_card': {
    kind: 'text',
    value: 'mirror card',
  },
  'automationAuthoring.editor.actionOption.archive_card': {
    kind: 'text',
    value: 'archive card',
  },
  'automationAuthoring.editor.actionOption.unarchive_card': {
    kind: 'text',
    value: 'unarchive card',
  },
  'automationAuthoring.editor.actionOption.delete_card': {
    kind: 'text',
    value: 'delete card',
  },
  'automationAuthoring.editor.actionOption.rename_card': {
    kind: 'text',
    value: 'rename card',
  },
  'automationAuthoring.editor.actionOption.set_due_date': {
    kind: 'text',
    value: 'set due date',
  },
  'automationAuthoring.editor.actionOption.move_due_date': {
    kind: 'text',
    value: 'move due date',
  },
  'automationAuthoring.editor.actionOption.remove_due_date': {
    kind: 'text',
    value: 'remove due date',
  },
  'automationAuthoring.editor.actionOption.mark_due_date_complete': {
    kind: 'text',
    value: 'mark due date complete',
  },
  'automationAuthoring.editor.actionOption.add_label': {
    kind: 'text',
    value: 'add label',
  },
  'automationAuthoring.editor.actionOption.remove_label': {
    kind: 'text',
    value: 'remove label',
  },
  'automationAuthoring.editor.actionOption.add_member': {
    kind: 'text',
    value: 'add member',
  },
  'automationAuthoring.editor.actionOption.remove_member': {
    kind: 'text',
    value: 'remove member',
  },
  'automationAuthoring.editor.actionOption.post_comment': {
    kind: 'text',
    value: 'post comment',
  },
  'automationAuthoring.editor.actionOption.create_card': {
    kind: 'text',
    value: 'create card',
  },
  'automationAuthoring.editor.actionOption.create_list': {
    kind: 'text',
    value: 'create list',
  },
  'automationAuthoring.editor.actionOption.rename_list': {
    kind: 'text',
    value: 'rename list',
  },
  'automationAuthoring.editor.actionOption.sort_list': {
    kind: 'text',
    value: 'sort list',
  },
  'automationAuthoring.editor.actionOption.move_all_cards_in_list': {
    kind: 'text',
    value: 'move all cards in list',
  },
  'automationAuthoring.editor.actionOption.add_checklist': {
    kind: 'text',
    value: 'add checklist',
  },
  'automationAuthoring.editor.actionOption.remove_checklist': {
    kind: 'text',
    value: 'remove checklist',
  },
  'automationAuthoring.editor.actionOption.check_checklist_item': {
    kind: 'text',
    value: 'check checklist item',
  },
  'automationAuthoring.editor.actionOption.uncheck_checklist_item': {
    kind: 'text',
    value: 'uncheck checklist item',
  },
  'automationAuthoring.editor.actionOption.set_custom_field_value': {
    kind: 'text',
    value: 'set custom field value',
  },
  'automationAuthoring.editor.actionOption.send_email': {
    kind: 'text',
    value: 'send email',
  },
  'automationAuthoring.editor.actionOption.http_request': {
    kind: 'text',
    value: 'HTTP request',
  },
  'automationAuthoring.editor.http.notice': {
    kind: 'text',
    value:
      'Secret headers such as Authorization and x-api-key are masked after save. Masked values are placeholders; replace or remove them before saving edits. Later actions can use {httpresponse} or {httpresponse.property}.',
  },
  'automationAuthoring.editor.http.methodLabel': { kind: 'text', value: 'HTTP method' },
  'automationAuthoring.editor.http.urlLabel': { kind: 'text', value: 'Request URL' },
  'automationAuthoring.editor.http.urlPlaceholder': {
    kind: 'text',
    value: 'https://example.com/webhook',
  },
  'automationAuthoring.editor.http.headersLabel': {
    kind: 'text',
    value: 'Request headers',
  },
  'automationAuthoring.editor.http.headersPlaceholder': {
    kind: 'text',
    value: 'Authorization: Bearer secret',
  },
  'automationAuthoring.editor.http.bodyLabel': { kind: 'text', value: 'Request body' },
  'automationAuthoring.editor.http.bodyPlaceholder': {
    kind: 'text',
    value: '{"card":"{cardlink}"}',
  },
  'automationAuthoring.editor.http.headerFormatError': {
    kind: 'text',
    value: 'Write request headers as one Name: value pair per line.',
  },
  'automationAuthoring.editor.http.maskedHeaderError': {
    kind: 'text',
    value: 'Replace or remove the masked {header} header before saving.',
  },
  'automationAuthoring.editor.http.urlRequired': {
    kind: 'text',
    value: 'Add a request URL before saving.',
  },
  'automationAuthoring.editor.email.toLabel': { kind: 'text', value: 'Email recipients' },
  'automationAuthoring.editor.email.toPlaceholder': {
    kind: 'text',
    value: 'alex@example.com, sam@example.com',
  },
  'automationAuthoring.editor.email.toHint': {
    kind: 'text',
    value: 'Use commas or new lines. To and cc count together.',
  },
  'automationAuthoring.editor.email.ccLabel': { kind: 'text', value: 'Cc recipients' },
  'automationAuthoring.editor.email.ccPlaceholder': {
    kind: 'text',
    value: 'ops@example.com',
  },
  'automationAuthoring.editor.email.subjectLabel': {
    kind: 'text',
    value: 'Email subject',
  },
  'automationAuthoring.editor.email.subjectPlaceholder': {
    kind: 'text',
    value: 'Card update: {cardname}',
  },
  'automationAuthoring.editor.email.bodyLabel': { kind: 'text', value: 'Email body' },
  'automationAuthoring.editor.email.bodyPlaceholder': {
    kind: 'text',
    value: 'Card {cardname}: {cardlink}',
  },
  'automationAuthoring.editor.email.recipientRequired': {
    kind: 'text',
    value: 'Add at least one email recipient before saving.',
  },
  'automationAuthoring.editor.email.recipientLimit': {
    kind: 'text',
    value: 'An automation email action can address at most {limit} recipients.',
  },
  'automationAuthoring.editor.email.recipientInvalid': {
    kind: 'text',
    value: 'Enter valid email addresses before saving.',
  },
  'automationAuthoring.editor.email.subjectRequired': {
    kind: 'text',
    value: 'Add an email subject before saving.',
  },
  'automationAuthoring.editor.email.bodyRequired': {
    kind: 'text',
    value: 'Add an email body before saving.',
  },
  'automationAuthoring.editor.validation.nameRequired': {
    kind: 'text',
    value: 'Name this automation before saving.',
  },
  'automationAuthoring.editor.validation.triggerListRequired': {
    kind: 'text',
    value: 'Choose the list for this trigger.',
  },
  'automationAuthoring.editor.validation.triggerTextRequired': {
    kind: 'text',
    value: 'Fill the trigger text blank.',
  },
  'automationAuthoring.editor.validation.weekdayRequired': {
    kind: 'text',
    value: 'Choose at least one weekday for this schedule.',
  },
  'automationAuthoring.editor.validation.dayOfMonthRequired': {
    kind: 'text',
    value: 'Choose the day of month for this schedule.',
  },
  'automationAuthoring.editor.validation.yearlyDateRequired': {
    kind: 'text',
    value: 'Choose the yearly date for this schedule.',
  },
  'automationAuthoring.editor.validation.conditionLabelRequired': {
    kind: 'text',
    value: 'Choose the label for this condition.',
  },
  'automationAuthoring.editor.validation.conditionMemberRequired': {
    kind: 'text',
    value: 'Choose the member for this condition.',
  },
  'automationAuthoring.editor.validation.actingMemberRequired': {
    kind: 'text',
    value: 'Choose an acting member before saving this condition.',
  },
  'automationAuthoring.editor.validation.conditionTextRequired': {
    kind: 'text',
    value: 'Fill the condition text blank.',
  },
  'automationAuthoring.editor.validation.actionBlankRequired': {
    kind: 'text',
    value: 'Fill every required action blank before saving.',
  },
  'automationAuthoring.editor.validation.fallback': {
    kind: 'text',
    value: 'Finish every required blank before saving.',
  },
  'automationAuthoring.editor.variablesLabel': {
    kind: 'text',
    value: 'Variable inserter',
  },
  'automationAuthoring.editor.save': { kind: 'text', value: 'Save automation' },
  'automationAuthoring.editor.cancel': { kind: 'text', value: 'Cancel' },
  'automationAuthoring.commandKind.rule': { kind: 'text', value: 'Rules' },
  'automationAuthoring.commandKind.card_button': { kind: 'text', value: 'Card button' },
  'automationAuthoring.commandKind.board_button': { kind: 'text', value: 'Board button' },
  'automationAuthoring.commandKind.calendar_command': { kind: 'text', value: 'Calendar' },
  'automationAuthoring.commandKind.due_date_command': { kind: 'text', value: 'Due date' },
  'automationAuthoring.commandKindSingular.rule': { kind: 'text', value: 'rule' },
  'automationAuthoring.commandKindSingular.card_button': {
    kind: 'text',
    value: 'card button',
  },
  'automationAuthoring.commandKindSingular.board_button': {
    kind: 'text',
    value: 'board button',
  },
  'automationAuthoring.commandKindSingular.calendar_command': {
    kind: 'text',
    value: 'calendar',
  },
  'automationAuthoring.commandKindSingular.due_date_command': {
    kind: 'text',
    value: 'due date',
  },
  'automationAuthoring.starter.rule': {
    kind: 'text',
    value: 'Start with: when a card is added to Inbox, add a label.',
  },
  'automationAuthoring.starter.card_button': {
    kind: 'text',
    value: 'Start with: move this card forward and set a due date.',
  },
  'automationAuthoring.starter.board_button': {
    kind: 'text',
    value: 'Start with: sort Backlog by due date.',
  },
  'automationAuthoring.starter.calendar_command': {
    kind: 'text',
    value: 'Start with: every Monday, create a weekly review card.',
  },
  'automationAuthoring.starter.due_date_command': {
    kind: 'text',
    value: 'Start with: when a card becomes due, add a reminder comment.',
  },
  'automationAuthoring.section.label': { kind: 'text', value: 'Automation' },
  'automationAuthoring.section.heading': { kind: 'text', value: 'Automation' },
  'automationAuthoring.section.description': {
    kind: 'text',
    value:
      'Authoring is available to board admins. Activity and usage show automation runs across readable boards in this workspace.',
  },
  'automationAuthoring.view.viewsLabel': { kind: 'text', value: 'Automation views' },
  'automationAuthoring.view.commands': { kind: 'text', value: 'Commands' },
  'automationAuthoring.view.activity': { kind: 'text', value: 'Activity' },
  'automationAuthoring.view.usage': { kind: 'text', value: 'Usage' },
  'automationAuthoring.view.admin': { kind: 'text', value: 'Admin' },
  'automationAuthoring.view.kindsLabel': {
    kind: 'text',
    value: 'Automation command types',
  },
  'automationAuthoring.commands.metadataLoading': {
    kind: 'text',
    value: 'Loading lists, labels and members for command blanks...',
  },
  'automationAuthoring.commands.blockedChecking': {
    kind: 'text',
    value: 'Checking automations for this board...',
  },
  'automationAuthoring.commands.blockedNotAdmin': {
    kind: 'text',
    value: 'Only a board admin can manage automations.',
  },
  'automationAuthoring.commands.readOnlyDefault': {
    kind: 'text',
    value: 'Automation editing is available on the web.',
  },
  'automationAuthoring.commands.create': {
    kind: 'text',
    value: 'Create {kind} automation',
  },
  'automationAuthoring.commands.buttonLimitTitle': {
    kind: 'text',
    value: '{limit} active {kind} automations',
  },
  'automationAuthoring.commands.buttonLimitDefaultTitle': {
    kind: 'text',
    value: 'Active button limit',
  },
  'automationAuthoring.commands.buttonLimitCard': {
    kind: 'text',
    value:
      'A member can have at most {limit} active card buttons. Extra buttons are saved disabled and cannot be enabled until another is deactivated.',
  },
  'automationAuthoring.commands.buttonLimitBoard': {
    kind: 'text',
    value:
      'A member can have at most {limit} active board buttons. Extra buttons are saved disabled and cannot be enabled until another is deactivated.',
  },
  'automationAuthoring.commands.checking': {
    kind: 'text',
    value: 'Checking saved automations...',
  },
  'automationAuthoring.commands.emptyTitle': {
    kind: 'text',
    value: 'No {kind} automations yet.',
  },
  'automationAuthoring.commands.rowLabel': { kind: 'text', value: '{name} automation' },
  'automationAuthoring.commands.enabled': { kind: 'text', value: 'Enabled' },
  'automationAuthoring.commands.disabled': { kind: 'text', value: 'Disabled' },
  'automationAuthoring.commands.brokenTitle': { kind: 'text', value: 'Needs attention' },
  'automationAuthoring.commands.brokenBody': {
    kind: 'text',
    value: 'This command has a broken reference.',
  },
  'automationAuthoring.commands.edit': { kind: 'text', value: 'Edit' },
  'automationAuthoring.commands.editLabel': { kind: 'text', value: 'Edit {name}' },
  'automationAuthoring.commands.enable': { kind: 'text', value: 'Enable' },
  'automationAuthoring.commands.disable': { kind: 'text', value: 'Disable' },
  'automationAuthoring.commands.enableLabel': { kind: 'text', value: 'Enable {name}' },
  'automationAuthoring.commands.disableLabel': { kind: 'text', value: 'Disable {name}' },
  'automationAuthoring.commands.brokenDisabledReason': {
    kind: 'text',
    value: 'Fix unresolved automation references before enabling it.',
  },
  'automationAuthoring.commands.copy': { kind: 'text', value: 'Copy' },
  'automationAuthoring.commands.copyLabel': { kind: 'text', value: 'Copy {name}' },
  'automationAuthoring.commands.openLog': { kind: 'text', value: 'Open log' },
  'automationAuthoring.commands.openLogLabel': {
    kind: 'text',
    value: 'Open log for {name}',
  },
  'automationAuthoring.commands.delete': { kind: 'text', value: 'Delete' },
  'automationAuthoring.commands.deleteLabel': { kind: 'text', value: 'Delete {name}' },
  'automationAuthoring.activity.heading': { kind: 'text', value: 'Automation activity' },
  'automationAuthoring.activity.description': {
    kind: 'text',
    value: 'Runs from every readable board in this workspace.',
  },
  'automationAuthoring.activity.boardLabel': { kind: 'text', value: 'Activity board' },
  'automationAuthoring.activity.allBoards': {
    kind: 'text',
    value: 'All readable boards',
  },
  'automationAuthoring.activity.thisBoard': { kind: 'text', value: 'This board' },
  'automationAuthoring.activity.outcomeLabel': { kind: 'text', value: 'Run outcome' },
  'automationAuthoring.activity.outcomeFilter.all': {
    kind: 'text',
    value: 'All outcomes',
  },
  'automationAuthoring.activity.outcomeFilter.succeeded': {
    kind: 'text',
    value: 'Succeeded',
  },
  'automationAuthoring.activity.outcomeFilter.warning': {
    kind: 'text',
    value: 'Warnings',
  },
  'automationAuthoring.activity.outcomeFilter.failed': { kind: 'text', value: 'Failed' },
  'automationAuthoring.activity.loading': {
    kind: 'text',
    value: 'Loading automation activity...',
  },
  'automationAuthoring.activity.loadFailedTitle': {
    kind: 'text',
    value: 'Automation activity could not be loaded',
  },
  'automationAuthoring.activity.emptyTitle': {
    kind: 'text',
    value: 'No automation runs found.',
  },
  'automationAuthoring.activity.emptyBody': {
    kind: 'text',
    value: 'Change the board or outcome filter to inspect another slice.',
  },
  'automationAuthoring.activity.rowLabel': {
    kind: 'text',
    value: '{name} automation activity',
  },
  'automationAuthoring.activity.actor': { kind: 'text', value: 'Actor {actor}' },
  'automationAuthoring.activity.boardRun': { kind: 'text', value: 'Board-level run' },
  'automationAuthoring.activity.cardTarget': { kind: 'text', value: 'Card {card}' },
  'automationAuthoring.activity.userFallback': { kind: 'text', value: 'User {user}' },
  'automationAuthoring.retry': { kind: 'text', value: 'Retry' },
  'automationAuthoring.usage.loading': {
    kind: 'text',
    value: 'Loading automation quota...',
  },
  'automationAuthoring.usage.loadFailedTitle': {
    kind: 'text',
    value: 'Automation quota could not be loaded',
  },
  'automationAuthoring.usage.summaryLabel': {
    kind: 'text',
    value: 'Monthly automation quota',
  },
  'automationAuthoring.usage.metric.runs': { kind: 'text', value: 'Runs' },
  'automationAuthoring.usage.metric.operations': { kind: 'text', value: 'Operations' },
  'automationAuthoring.usage.metric.emails': { kind: 'text', value: 'Automation emails' },
  'automationAuthoring.usage.runsExhaustedTitle': {
    kind: 'text',
    value: 'Monthly run quota exhausted',
  },
  'automationAuthoring.usage.runsExhaustedBody': {
    kind: 'text',
    value: 'Automations stop firing until the workspace quota resets.',
  },
  'automationAuthoring.usage.metricExhaustedTitle': {
    kind: 'text',
    value: '{metric} quota exhausted',
  },
  'automationAuthoring.usage.metricExhaustedBody': {
    kind: 'text',
    value: 'Automations that need that quota stop until the workspace quota resets.',
  },
  'automationAuthoring.usage.runningLowTitle': {
    kind: 'text',
    value: '{metric} quota running low',
  },
  'automationAuthoring.usage.runningLowBody.runs': {
    kind: 'plural',
    forms: {
      one: '{remaining} run remaining this month.',
      other: '{remaining} runs remaining this month.',
    },
  },
  'automationAuthoring.usage.runningLowBody.operations': {
    kind: 'plural',
    forms: {
      one: '{remaining} operation remaining this month.',
      other: '{remaining} operations remaining this month.',
    },
  },
  'automationAuthoring.usage.runningLowBody.emails': {
    kind: 'plural',
    forms: {
      one: '{remaining} automation email remaining this month.',
      other: '{remaining} automation emails remaining this month.',
    },
  },
  'automationAuthoring.usage.quotaTitle': {
    kind: 'text',
    value: 'Monthly automation quota',
  },
  'automationAuthoring.usage.quotaBody': {
    kind: 'plural',
    forms: {
      one: '{remaining} run remaining this month.',
      other: '{remaining} runs remaining this month.',
    },
  },
  'automationAuthoring.usage.quotaBodyFallback': {
    kind: 'text',
    value: 'Usage is available for this workspace.',
  },
  'automationAuthoring.usage.resetText': {
    kind: 'text',
    value: 'Resets at 00:00 GMT on {date}.',
  },
  'automationAuthoring.usage.heading': { kind: 'text', value: 'Automation usage' },
  'automationAuthoring.usage.description': {
    kind: 'text',
    value:
      'Current workspace quota for automation runs, operations and automation emails.',
  },
  'automationAuthoring.usage.exhaustedNoticeBody': {
    kind: 'text',
    value: 'Commands stop firing until the workspace quota resets.',
  },
  'automationAuthoring.usage.exhaustedNoticeBodyWithReset': {
    kind: 'text',
    value: 'Commands stop firing until the workspace quota resets. {reset}',
  },
  'automationAuthoring.usage.metricArticle': {
    kind: 'text',
    value: '{metric} monthly quota',
  },
  'automationAuthoring.usage.metricMeter': {
    kind: 'text',
    value: '{metric} monthly quota used',
  },
  'automationAuthoring.usage.metricConsumed': {
    kind: 'text',
    value: '{metric} consumed',
  },
  'automationAuthoring.usage.metricRemaining': {
    kind: 'text',
    value: '{remaining} remaining',
  },
  'automationAuthoring.usage.metricExhausted': { kind: 'text', value: 'Quota exhausted' },
  'automationAuthoring.usage.metricRunningLow': { kind: 'text', value: 'Running low' },
  'automationAuthoring.usage.period': {
    kind: 'text',
    value: 'Current period {start} to {end}; {reset}',
  },
  'automationAuthoring.entryKind.run': { kind: 'text', value: 'Run' },
  'automationAuthoring.entryKind.quota_blocked': { kind: 'text', value: 'Quota blocked' },
  'automationAuthoring.entryKind.permission_blocked': {
    kind: 'text',
    value: 'Permission blocked',
  },
  'automationAuthoring.entryKind.missing_reference_blocked': {
    kind: 'text',
    value: 'Missing reference',
  },
  'automationAuthoring.entryKind.cap_exceeded': { kind: 'text', value: 'Cap exceeded' },
  'automationAuthoring.entryKind.loop_suppressed': {
    kind: 'text',
    value: 'Loop suppressed',
  },
  'automationAuthoring.entryKind.definition_changed': {
    kind: 'text',
    value: 'Definition changed',
  },
  'automationAuthoring.entryKind.admin_disabled': {
    kind: 'text',
    value: 'Admin disabled',
  },
  'automationAuthoring.outcome.succeeded': { kind: 'text', value: 'succeeded' },
  'automationAuthoring.outcome.warning': { kind: 'text', value: 'warning' },
  'automationAuthoring.outcome.failed': { kind: 'text', value: 'failed' },
  'automationAuthoring.outcome.recorded': { kind: 'text', value: 'Recorded' },
  'automationAuthoring.diagnostic.title': { kind: 'text', value: 'Diagnostic: {reason}' },
  'automationAuthoring.diagnostic.reason.disabled': { kind: 'text', value: 'disabled' },
  'automationAuthoring.diagnostic.reason.quota_exhausted': {
    kind: 'text',
    value: 'quota exhausted',
  },
  'automationAuthoring.diagnostic.reason.condition_excluded': {
    kind: 'text',
    value: 'condition excluded',
  },
  'automationAuthoring.diagnostic.reason.permission_denied': {
    kind: 'text',
    value: 'permission denied',
  },
  'automationAuthoring.diagnostic.reason.missing_reference': {
    kind: 'text',
    value: 'missing reference',
  },
  'automationAuthoring.diagnostic.reason.cap_exceeded': {
    kind: 'text',
    value: 'cap exceeded',
  },
  'automationAuthoring.diagnostic.reason.loop_suppressed': {
    kind: 'text',
    value: 'loop suppressed',
  },
  'automationAuthoring.diagnostic.reason.acting_member_missing': {
    kind: 'text',
    value: 'acting member missing',
  },
  'automationAuthoring.diagnostic.reason.admin_disabled': {
    kind: 'text',
    value: 'admin disabled',
  },
  'automationAuthoring.log.title': { kind: 'text', value: 'Automation log' },
  /*
    FE-70.2 / FE-71.3 — the three phrases `BE-70.8` put on the wire and could
    not put on the screen, declared in ONE block against the
    `automationAuthoring.log.title` anchor.

    ⚠ `cardWithheld` NAMES NOTHING, and that is the whole of `FE-70.2`. The log
    route projects `entries[].card.state === 'unreadable'` for a card that has
    since moved somewhere this reader was never authorised to see; falling back
    to `card.id` or `card.shortLink` would make the log a cross-board existence
    oracle, which CLAUDE.md forbids by the same rule that answers 404 rather
    than 403 outside a read scope. It says a target exists and is not shown,
    and it deliberately does not say WHY — "moved to another board" leaks where
    the card went.

    ⚠ **THE LOWER-CASE `card` IS LOAD-BEARING, NOT HOUSE STYLE.**
    `BoardAutomationSection.test.tsx` pins the withheld arm with
    `expect(text).not.toContain('Card ')` — the assertion that the `cardTarget`
    sentence is NOT what rendered. `String.includes` is case-sensitive, so
    sentence-casing this value to "Card …" turns that sibling assertion red for
    a reason nobody could reconstruct from the diff. If this phrase is ever
    reworded, keep it clear of the literal `Card ` with a capital C.

    ⚠ `actionHttpStatus` renders the DECLARED `actions[].httpStatus` field. It
    is not assembled from `error_text`'s English sentence
    (`HTTP request returned 503.`), which is where the status used to reach a
    person and is exactly the defect `FE-71.3` names. The field is present on
    failures and ABSENT on successes — `automation/execute.ts` discards a
    successful call's status before any row is written — so the call site
    renders absence as absence and never invents a 200.
  */
  'automationAuthoring.log.cardWithheld': {
    kind: 'text',
    value: 'Acted on a card you cannot see',
  },
  'automationAuthoring.log.actionHttpStatus': {
    kind: 'text',
    value: 'HTTP status {status}',
  },
  'automationAuthoring.log.loadOlder': { kind: 'text', value: 'Load older entries' },
  'automationAuthoring.log.loadingOlder': {
    kind: 'text',
    value: 'Loading older entries...',
  },
  'automationAuthoring.log.olderFailed': {
    kind: 'text',
    value: 'Could not load older entries',
  },
  'automationAuthoring.log.titleNamed': { kind: 'text', value: 'Log for {name}' },
  'automationAuthoring.log.description': {
    kind: 'text',
    value: 'Recent command runs, definition changes and diagnostics.',
  },
  'automationAuthoring.log.loading': { kind: 'text', value: 'Loading automation log...' },
  'automationAuthoring.log.retention': {
    kind: 'plural',
    forms: {
      one: 'Showing the last {days} day since',
      other: 'Showing the last {days} days since',
    },
  },
  'automationAuthoring.log.neverRunTitle': {
    kind: 'text',
    value: 'This automation has not run yet.',
  },
  'automationAuthoring.log.neverRunBody': {
    kind: 'text',
    value: 'The log will show each run, failed action and definition change here.',
  },
  'automationAuthoring.log.boardRun': { kind: 'text', value: 'Board-level run' },
  'automationAuthoring.log.cardTarget': { kind: 'text', value: 'Card {card}' },
  'automationAuthoring.log.actorChanged': { kind: 'text', value: 'Changed by {actor}' },
  'automationAuthoring.log.actorDisabled': { kind: 'text', value: 'Disabled by {actor}' },
  'automationAuthoring.log.actor': { kind: 'text', value: 'Actor {actor}' },
  'automationAuthoring.log.userFallback': { kind: 'text', value: 'User {user}' },
  'automationAuthoring.log.capTitle': { kind: 'text', value: 'Per-run cap reached' },
  'automationAuthoring.log.capBody': {
    kind: 'text',
    value:
      'The command processed only the allowed number of cards. The skipped count is recorded in this log entry.',
  },
  'automationAuthoring.log.loopTitle': { kind: 'text', value: 'Loop suppressed' },
  'automationAuthoring.log.loopBody': {
    kind: 'text',
    value:
      'Automation loop prevention stopped this chain before another run. Command chains stop after {limit} runs.',
  },
  'automationAuthoring.log.actionSucceeded': { kind: 'text', value: 'Succeeded' },
  'automationAuthoring.log.actionFailed': { kind: 'text', value: 'Failed' },
  'automationAuthoring.admin.heading': { kind: 'text', value: 'Administrator review' },
  'automationAuthoring.admin.description': {
    kind: 'text',
    value:
      'Shared rules and calendar commands created by other members in this workspace.',
  },
  'automationAuthoring.admin.lockedBody': {
    kind: 'text',
    value: 'Only a workspace admin can review automations.',
  },
  'automationAuthoring.admin.boardLabel': { kind: 'text', value: 'Review board' },
  'automationAuthoring.admin.allBoards': { kind: 'text', value: 'All workspace boards' },
  'automationAuthoring.admin.thisBoard': { kind: 'text', value: 'This board' },
  'automationAuthoring.admin.loading': {
    kind: 'text',
    value: 'Loading administrator review...',
  },
  'automationAuthoring.admin.loadFailedTitle': {
    kind: 'text',
    value: 'Administrator review could not be loaded',
  },
  'automationAuthoring.admin.emptyTitle': {
    kind: 'text',
    value: 'No shared rules or calendar commands found.',
  },
  'automationAuthoring.admin.emptyBody': {
    kind: 'text',
    value: 'Private buttons and your own commands are not listed here.',
  },
  'automationAuthoring.admin.rowLabel': {
    kind: 'text',
    value: '{name} administrator review',
  },
  'automationAuthoring.admin.enabled': { kind: 'text', value: 'Enabled' },
  'automationAuthoring.admin.disabled': { kind: 'text', value: 'Disabled' },
  'automationAuthoring.admin.createdBy': { kind: 'text', value: 'Created by' },
  'automationAuthoring.admin.actsAs': { kind: 'text', value: 'Acts as' },
  'automationAuthoring.admin.scope': { kind: 'text', value: 'Scope' },
  'automationAuthoring.admin.lastRun': { kind: 'text', value: 'Last run' },
  'automationAuthoring.admin.allAdministrableBoards': {
    kind: 'text',
    value: 'All administrable boards',
  },
  'automationAuthoring.admin.noActingMember': { kind: 'text', value: 'No acting member' },
  'automationAuthoring.admin.userFallback': { kind: 'text', value: 'User {user}' },
  'automationAuthoring.admin.viewDefinition': { kind: 'text', value: 'View definition' },
  'automationAuthoring.admin.viewDefinitionLabel': {
    kind: 'text',
    value: 'View definition for {name}',
  },
  'automationAuthoring.admin.openLog': { kind: 'text', value: 'Open log' },
  'automationAuthoring.admin.openLogLabel': {
    kind: 'text',
    value: 'Open log for {name}',
  },
  'automationAuthoring.admin.disableAction': { kind: 'text', value: 'Disable' },
  'automationAuthoring.admin.disableLabel': {
    kind: 'text',
    value: 'Disable {name} as administrator',
  },
  'automationAuthoring.editorDialog.createTitle': {
    kind: 'text',
    value: 'Create {kind} automation',
  },
  'automationAuthoring.editorDialog.fallbackKind': { kind: 'text', value: 'automation' },
  'automationAuthoring.editorDialog.editTitle': { kind: 'text', value: 'Edit {name}' },
  'automationAuthoring.editorDialog.description': {
    kind: 'text',
    value: 'Pick clauses from menus and check the sentence before saving.',
  },
  'automationAuthoring.delete.title': { kind: 'text', value: 'Delete automation' },
  'automationAuthoring.delete.description': {
    kind: 'text',
    value: 'Delete {name}? It will stop running and disappear from this board.',
  },
  'automationAuthoring.delete.confirm': { kind: 'text', value: 'Delete automation' },
  'automationAuthoring.delete.cancel': { kind: 'text', value: 'Cancel' },
  'automationAuthoring.copy.title': { kind: 'text', value: 'Copy automation' },
  'automationAuthoring.copy.titleNamed': { kind: 'text', value: 'Copy {name}' },
  'automationAuthoring.copy.description': {
    kind: 'text',
    value: 'Create a disabled copy in this board or copy it to another board.',
  },
  'automationAuthoring.copy.nameLabel': { kind: 'text', value: 'Copy name' },
  'automationAuthoring.copy.namePlaceholder': { kind: 'text', value: '{name} (copy)' },
  'automationAuthoring.copy.destinationLabel': {
    kind: 'text',
    value: 'Destination board',
  },
  'automationAuthoring.copy.thisBoard': { kind: 'text', value: 'This board' },
  'automationAuthoring.copy.doneTitle': {
    kind: 'text',
    value: '{name} was copied disabled.',
  },
  'automationAuthoring.copy.unresolvedBody': {
    kind: 'text',
    value:
      'Some references could not be matched on the destination board. Fix the highlighted command before enabling it.',
  },
  'automationAuthoring.copy.unchangedBody': {
    kind: 'text',
    value: 'The original automation was not changed.',
  },
  'automationAuthoring.copy.unresolvedFallback': {
    kind: 'text',
    value: 'Unresolved reference',
  },
  'automationAuthoring.copy.submit': { kind: 'text', value: 'Copy automation' },
  'automationAuthoring.copy.close': { kind: 'text', value: 'Close' },
  'automationAuthoring.copy.referenceKind.board': { kind: 'text', value: 'board' },
  'automationAuthoring.copy.referenceKind.list': { kind: 'text', value: 'list' },
  'automationAuthoring.copy.referenceKind.label': { kind: 'text', value: 'label' },
  'automationAuthoring.copy.referenceKind.member': { kind: 'text', value: 'member' },
  'automationAuthoring.copy.referenceKind.card': { kind: 'text', value: 'card' },
  'automationAuthoring.copy.referenceKind.checklist': {
    kind: 'text',
    value: 'checklist',
  },
  'automationAuthoring.copy.referenceKind.checklist_item': {
    kind: 'text',
    value: 'checklist item',
  },
  'automationAuthoring.copy.referenceKind.custom_field': {
    kind: 'text',
    value: 'custom field',
  },
  'automationAuthoring.copy.unresolvedReason.not_found_on_destination_board': {
    kind: 'text',
    value: 'not found on destination board',
  },
  'automationAuthoring.copy.unresolvedReason.ambiguous_on_destination_board': {
    kind: 'text',
    value: 'ambiguous on destination board',
  },
  'automationAuthoring.copy.unresolvedReason.not_copyable_to_destination_board': {
    kind: 'text',
    value: 'not copyable to destination board',
  },
  'automationAuthoring.definition.title': {
    kind: 'text',
    value: 'Automation definition',
  },
  'automationAuthoring.definition.titleNamed': {
    kind: 'text',
    value: 'Definition for {name}',
  },
  'automationAuthoring.definition.description': {
    kind: 'text',
    value: 'The rule definition as saved by its author.',
  },
  'automationAuthoring.definition.loading': {
    kind: 'text',
    value: 'Loading automation definition...',
  },
  'automationAuthoring.definition.loadFailedTitle': {
    kind: 'text',
    value: 'Automation definition could not be loaded',
  },
  'automationAuthoring.definition.kind': { kind: 'text', value: 'Kind:' },
  'automationAuthoring.definition.actions': { kind: 'text', value: 'Actions:' },
  'automationAuthoring.definition.visibility': { kind: 'text', value: 'Visibility:' },
  'automationAuthoring.definition.state': { kind: 'text', value: 'State:' },
  'automationAuthoring.definition.enabled': { kind: 'text', value: 'Enabled' },
  'automationAuthoring.definition.disabled': { kind: 'text', value: 'Disabled' },
  'automationAuthoring.definition.visibilityValue.shared': {
    kind: 'text',
    value: 'shared',
  },
  'automationAuthoring.definition.visibilityValue.private': {
    kind: 'text',
    value: 'private',
  },
  'automationAuthoring.adminDisable.title': {
    kind: 'text',
    value: 'Disable automation as administrator',
  },
  'automationAuthoring.adminDisable.description': {
    kind: 'text',
    value: 'Disable {name}? Its author will see this entry in the automation log.',
  },
  'automationAuthoring.adminDisable.reasonLabel': { kind: 'text', value: 'Reason' },
  'automationAuthoring.adminDisable.reasonPlaceholder': {
    kind: 'text',
    value: 'Optional reason shown in the command log',
  },
  'automationAuthoring.adminDisable.confirm': {
    kind: 'text',
    value: 'Disable automation',
  },
  /* ⚠ `BE-68.11` (fan-out 15) — INSERTED IMMEDIATELY *BEFORE*
     `automationAuthoring.adminDisable.cancel`, NOT AFTER IT, AND THAT IS
     LOAD-BEARING. `components/integrations/integrations-and-feeds.routing.test.ts`
     takes that same key as its `ANCHOR` and asserts that the 185 keys FOLLOWING
     it are the `integrations.*` family, contiguous and complete. THAT NUMERAL
     MOVES WITH THE FAMILY AND THIS LINE HAS GONE STALE TWICE (174 -> 182 -> 185):
     the test is the authority, not this comment — re-measure it at
     `integrations-and-feeds.routing.test.ts:1015-1040` rather than trusting the
     number written here. Four keys
     placed after the anchor push the last four `integrations.snapshot.*` keys
     out of that window and turn two shipped assertions red while saying
     nothing true. Placed here the block still lands inside its own family
     (between `adminDisable.confirm` and `adminDisable.cancel`), which is what
     the anchor rule is for, and the integrations run is undisturbed. */
  'automationAuthoring.editor.trigger.unsupportedTitle': {
    kind: 'text',
    value: 'This trigger cannot run yet',
  },
  'automationAuthoring.editor.trigger.unsupportedSuffix': {
    kind: 'text',
    value: '(cannot run yet)',
  },
  'automationAuthoring.editor.trigger.unsupportedReason.no_activity_event': {
    kind: 'text',
    value:
      'Nothing records a card vote as board activity yet, so this trigger cannot fire. The rule still saves, and starts running when card votes are recorded.',
  },
  'automationAuthoring.editor.trigger.unsupportedReason.no_completion_aggregate': {
    kind: 'text',
    value:
      'Checking the last item on a checklist does not yet report the checklist as complete, so this trigger cannot fire. The rule still saves, and starts running when checklist completion is reported.',
  },
  'automationAuthoring.adminDisable.cancel': { kind: 'text', value: 'Cancel' },
  'integrations.webhook.scope.readOnly': { kind: 'text', value: 'Read only' },
  'integrations.webhook.scope.readWrite': { kind: 'text', value: 'Read and write' },
  'integrations.webhook.appOption': { kind: 'text', value: '{toolName} - {scope}' },
  'integrations.webhook.appOptionRevoked': {
    kind: 'text',
    value: '{toolName} - {scope} - revoked',
  },
  'integrations.webhook.targetFallback': { kind: 'text', value: '{kind} {id}' },
  'integrations.webhook.eventFilter.unknown': {
    kind: 'text',
    value: '{name} is not a recognised activity event.',
  },
  'integrations.webhook.status.paused': { kind: 'text', value: 'Paused' },
  'integrations.webhook.status.failing': {
    kind: 'plural',
    forms: {
      one: 'Failing after 1 failed attempt',
      other: 'Failing after {count} failed attempts',
    },
  },
  'integrations.webhook.status.delivering': { kind: 'text', value: 'Delivering' },
  'integrations.webhook.status.waiting': {
    kind: 'text',
    value: 'Waiting for matching changes',
  },
  /**
   * ⚠ THE RECOVERY, AND IT MUST NOT PROMISE A RESUMPTION. `failing = true` is
   * TERMINAL: `markDelivered` is the only writer of `failing = false` and
   * `deliver()`'s guard returns before reaching it, and `failing` blocks
   * ENQUEUE too. Nothing in the product clears the flag, and a resume route is
   * ruled unowned new work (`implementation-plan.md`, BE-73.7, `TESTED`). So
   * the sentence states the stop and names the only action that works —
   * remove and create again, which the catalogue permits because
   * `change_registrations` carries no unique constraint beyond its primary
   * key. Do not soften it to 'deliveries will resume'; they will not.
   */
  /**
   * ⚠ THE VALUE WAS INVERTED, AND SO WAS THE PIN THAT GUARDED IT. It read
   * "Delivery has stopped for good. Remove this registration and create it
   * again to start receiving changes", and a unit case asserted the words
   * "resume", "retry" and "reactivate" were ABSENT from it — a guard against
   * softening the copy into a promise no route could keep. `POST
   * /me/change-registrations/:id/resume` is that route, so the absence pin now
   * points the wrong way and is a PRESENCE pin instead.
   *
   * ⚠ IT STILL SAYS WHAT RESUMING DOES NOT DO, and that half is not padding.
   * `claimDueDeliveries` claims `status = 'pending'` only, so a delivery that
   * exhausted its five attempts is FINAL: resuming starts the feed again from
   * now and replays nothing. A member who reads "resume" as "catch up" will
   * reconcile nothing and never know there is a gap.
   */
  'integrations.webhook.failingRecovery': {
    kind: 'text',
    value:
      'Delivery stopped after repeated failures. Resume it once the endpoint is healthy — changes from that moment are delivered again, and the ones missed while it was stopped are not sent.',
  },
  'integrations.webhook.reason.checking': {
    kind: 'text',
    value: 'Checking webhook registrations...',
  },
  'integrations.webhook.reason.ownerOnly': {
    kind: 'text',
    value: 'Only the connected-tool owner can manage webhooks.',
  },
  'integrations.webhook.reason.needsToken': {
    kind: 'text',
    value: 'Create a connected tool token before registering a webhook.',
  },
  'integrations.webhook.reason.needsTarget': {
    kind: 'text',
    value: 'Choose a board, list or card before registering a webhook.',
  },
  'integrations.webhook.error.chooseTarget': {
    kind: 'text',
    value: 'Choose what this webhook listens to.',
  },
  'integrations.webhook.error.enterTargetId': {
    kind: 'text',
    value: 'Enter the board, list or card id this webhook listens to.',
  },
  'integrations.webhook.error.urlInvalid': {
    kind: 'text',
    value: 'Callback URL must be a valid URL.',
  },
  'integrations.webhook.error.urlScheme': {
    kind: 'text',
    value: 'Callback URL must use http or https.',
  },
  'integrations.webhook.heading': { kind: 'text', value: 'Webhook registrations' },
  'integrations.webhook.description': {
    kind: 'text',
    value:
      'Register connected tools for board, list or card changes and review delivery status.',
  },
  'integrations.webhook.createHeading': { kind: 'text', value: 'Create webhook' },
  'integrations.webhook.appLabel': { kind: 'text', value: 'Connected tool' },
  'integrations.webhook.targetLabel': { kind: 'text', value: 'Change target' },
  'integrations.webhook.customTargetOption': {
    kind: 'text',
    value: 'Board, list or card id...',
  },
  /**
   * ⚠ THE THREE KEYS FE-2 ADDED, AND THE REASON THE FAMILY COUNT MOVED
   * 182 -> 185. `targetOption.wholeBoard` and `targetOption.list` are the two
   * halves of one decision: inside an `<optgroup>` headed by the board, the
   * board's OWN row cannot just repeat the board title (a list may be called
   * "Roadmap" too), and a list's row does not need to — so the `<select>`
   * reads "Everything on this board / Backlog / Doing" under one heading,
   * while the registration TABLE, which has no heading above it, renders the
   * self-contained `targetOption.list` pair instead.
   */
  'integrations.webhook.targetOption.wholeBoard': {
    kind: 'text',
    value: 'Everything on this board',
  },
  'integrations.webhook.targetOption.list': { kind: 'text', value: '{list} in {board}' },
  /*
   * ⚠ `integrations.webhook.targetListCaveat` STOOD HERE AND IS DELETED, NOT
   * RENAMED. It warned that a list registration never fires when a card
   * LEAVES the list — true until the matcher gained its `previousListId` arm,
   * false the moment that landed, and the source-reading pin that was written
   * to force exactly this deletion is what reported it. A translation of the
   * key survives in no catalogue: the Spanish override in
   * `apps/web/e2e/fe-80-1-integrations-and-feeds.capture.ts` went with it, and
   * a key declared here with no consumer would have kept the family count one
   * above what the surface actually routes.
   */
  'integrations.webhook.targetKindLabel': { kind: 'text', value: 'Target type' },
  'integrations.webhook.targetKind.board': { kind: 'text', value: 'Board' },
  'integrations.webhook.targetKind.list': { kind: 'text', value: 'List' },
  'integrations.webhook.targetKind.card': { kind: 'text', value: 'Card' },
  'integrations.webhook.targetIdLabel': { kind: 'text', value: 'Target id' },
  'integrations.webhook.callbackUrlLabel': { kind: 'text', value: 'Callback URL' },
  'integrations.webhook.callbackUrlPlaceholder': {
    kind: 'text',
    value: 'https://example.test/molt/webhook',
  },
  'integrations.webhook.eventFilterLabel': { kind: 'text', value: 'Event filter' },
  'integrations.webhook.eventFilterHint': {
    kind: 'text',
    value: 'Optional. Separate activity event names with spaces or commas.',
  },
  'integrations.webhook.eventIntent.legend': {
    kind: 'text',
    value: 'What to listen for',
  },
  'integrations.webhook.eventIntent.hint': {
    kind: 'text',
    value:
      'Leave every option unticked to receive everything that happens on the target.',
  },
  'integrations.webhook.eventIntent.advanced': {
    kind: 'text',
    value: 'Advanced: event names',
  },
  'integrations.webhook.eventIntent.movedList': {
    kind: 'text',
    value: 'Moved to a different list',
  },
  'integrations.webhook.eventIntent.completed': {
    kind: 'text',
    value: 'Marked complete or not complete',
  },
  'integrations.webhook.eventIntent.archived': {
    kind: 'text',
    value: 'Archived or restored',
  },
  'integrations.webhook.eventIntent.assignedCard': {
    kind: 'text',
    value: 'Assigned to a card',
  },
  'integrations.webhook.eventIntent.assignedChecklistItem': {
    kind: 'text',
    value: 'Assigned to a checklist item',
  },
  /**
   * ⚠ THE ONE KEY UNDER `eventIntent.` THAT IS NOT AN INTENT LABEL, and it is
   * a WARNING rather than an ERROR. `integrations.webhook.eventFilter.unknown`
   * means "the route will refuse this name"; this one means "the route will
   * store this name and your subscription is narrower than you probably
   * intend". Conflating them would make a warning read as a rejection, and the
   * form still submits with this sentence on screen — deliberately, because
   * `Advanced: event names` is an escape hatch and a caller may want one half.
   *
   * `{intent}` is filled with another catalogue value (the intent label), the
   * `SCOPE_LABEL_KEYS` shape, so the sentence stays whole in one language
   * rather than being assembled from two.
   */
  'integrations.webhook.eventIntent.halfPair': {
    kind: 'text',
    value:
      '{name} is listed without {partner}. Both belong to "{intent}", so this registration misses half of those changes with no error. Tick "{intent}" above to listen for both.',
  },
  'integrations.webhook.createAction': {
    kind: 'text',
    value: 'Create webhook registration',
  },
  'integrations.webhook.loadFailedTitle': {
    kind: 'text',
    value: 'Could not load webhook registrations',
  },
  'integrations.webhook.retry': { kind: 'text', value: 'Retry' },
  'integrations.webhook.emptyTitle': { kind: 'text', value: 'No webhook registrations' },
  'integrations.webhook.emptyBody': {
    kind: 'text',
    value: 'Create a registration when a connected tool needs change notifications.',
  },
  'integrations.webhook.listLabel': { kind: 'text', value: 'Webhook registrations' },
  'integrations.webhook.rowLabel': {
    kind: 'text',
    value: '{label} webhook registration',
  },
  'integrations.webhook.lastDelivery': { kind: 'text', value: 'Last delivery' },
  'integrations.webhook.never': { kind: 'text', value: 'Never' },
  'integrations.webhook.lastResponse': { kind: 'text', value: 'Last response' },
  'integrations.webhook.none': { kind: 'text', value: 'None' },
  'integrations.webhook.nextRetry': { kind: 'text', value: 'Next retry' },
  'integrations.webhook.notScheduled': { kind: 'text', value: 'Not scheduled' },
  'integrations.webhook.events': { kind: 'text', value: 'Events' },
  'integrations.webhook.allActivity': { kind: 'text', value: 'All activity' },
  'integrations.webhook.removeAction': { kind: 'text', value: 'Remove webhook' },
  /**
   * ⚠ ONE KEY, AND IT IS THE ONLY MEMBER-FACING SENTENCE ABOUT RESUMING. The
   * route re-checks CURRENT read access to the subject before clearing
   * anything and answers 404 when that access is gone, 403 on a revoked or
   * expired grant and 409 on a registration that is `active: false`; none of
   * those is a state this button renders in, and the row's own `<Notice>`
   * carries whatever the server said when one happens anyway.
   */
  'integrations.webhook.resumeAction': { kind: 'text', value: 'Resume deliveries' },
  /**
   * ⚠ THE SIGNING-SECRET FAMILY. Seven of these eight exist because the value
   * is issued ONCE — by the create 201 and by the rotate 200 — and can never
   * be read back: there is no "show it again" route and there will not be one,
   * because a GET that returned it would make every cached response and every
   * history entry a copy of a signing key.
   *
   * ⚠ `unsigned` IS ABOUT A REAL POPULATION, NOT A HYPOTHETICAL. Signing
   * arrived after registrations existed and migration `0062`'s column is
   * nullable and NOT backfilled, so every registration created before it
   * delivers unsigned for ever until somebody rotates. `hasSigningSecret:
   * false` is how the list says so, and rotating is the only cure.
   */
  'integrations.webhook.secret.heading': { kind: 'text', value: 'Signing secret' },
  'integrations.webhook.secret.shownOnce': {
    kind: 'text',
    value:
      'Copy this now. It is shown once and cannot be read again — losing it means rotating a new one and updating your endpoint.',
  },
  'integrations.webhook.secret.copy': { kind: 'text', value: 'Copy signing secret' },
  'integrations.webhook.secret.copied': {
    kind: 'text',
    value: 'Signing secret copied.',
  },
  'integrations.webhook.secret.copyFailed': {
    kind: 'text',
    value: 'Could not copy the signing secret. Select it and copy it by hand.',
  },
  'integrations.webhook.secret.clipboardUnavailable': {
    kind: 'text',
    value: 'This browser will not let the page copy for you.',
  },
  'integrations.webhook.secret.dismiss': { kind: 'text', value: 'I have copied it' },
  'integrations.webhook.secret.unsigned': {
    kind: 'text',
    value:
      'Deliveries for this registration carry no signature, so your endpoint cannot tell them from anything else that knows the URL. Rotate a signing secret onto it to fix that.',
  },
  'integrations.webhook.secret.rotateAction': {
    kind: 'text',
    value: 'Rotate signing secret',
  },
  'integrations.webhook.secret.rotateCaveat': {
    kind: 'text',
    value:
      'Rotating replaces the secret immediately and there is no grace period. Anything still verifying with the old value rejects every delivery until you copy the new one across.',
  },
  'integrations.webhook.secret.rotateConfirm': {
    kind: 'text',
    value: 'Replace the secret',
  },
  'integrations.webhook.secret.rotateCancel': {
    kind: 'text',
    value: 'Keep the current secret',
  },
  /**
   * ⚠ THE EDITOR'S LEGEND NAMES ITS SUBJECT, AND THAT IS AN ACCESSIBILITY
   * DECISION RATHER THAN A COSMETIC ONE. A `<fieldset>`'s `<legend>` is its
   * accessible name; the editor can be open while the create form is on
   * screen, so two groups both called "What to listen for" would leave a
   * screen-reader user with no way to tell which registration the second one
   * belongs to. `{label}` is the same standalone-unambiguous target name the
   * row heading renders.
   *
   * ⚠ `enqueueCaveat` SAYS THE ONE THING THE ROUTE CANNOT DO. The filter is
   * evaluated at ENQUEUE, so narrowing it does not un-queue deliveries the old
   * filter already matched — retries included — and widening it is not
   * retroactive: events that happened while they were excluded were never
   * queued and no route will backfill them.
   */
  'integrations.webhook.editEvents.action': { kind: 'text', value: 'Change events' },
  'integrations.webhook.editEvents.legend': {
    kind: 'text',
    value: 'Events for {label}',
  },
  'integrations.webhook.editEvents.enqueueCaveat': {
    kind: 'text',
    value:
      'A change here applies to what happens next. Deliveries already queued under the old selection are still attempted, retries included, and events excluded before now are not sent afterwards.',
  },
  'integrations.webhook.editEvents.save': { kind: 'text', value: 'Save events' },
  'integrations.webhook.editEvents.cancel': {
    kind: 'text',
    value: 'Keep the current events',
  },
  'integrations.firstParty.scope.readOnly': { kind: 'text', value: 'Read only' },
  'integrations.firstParty.scope.readWrite': { kind: 'text', value: 'Read and write' },
  'integrations.firstParty.state.notConfigured': {
    kind: 'text',
    value: 'Not configured',
  },
  'integrations.firstParty.state.connected': { kind: 'text', value: 'Connected' },
  'integrations.firstParty.state.revoked': { kind: 'text', value: 'Reconnect required' },
  'integrations.firstParty.state.noAccess': { kind: 'text', value: 'No access' },
  'integrations.firstParty.state.unavailable': { kind: 'text', value: 'Unavailable' },
  'integrations.firstParty.stateBody.connectedNotEnabled': {
    kind: 'text',
    value:
      'Account connected. Enable this integration on the board before it renders there.',
  },
  'integrations.firstParty.stateBody.connected': {
    kind: 'text',
    value: 'Ready to show live external state where this board uses it.',
  },
  'integrations.firstParty.stateBody.revoked': {
    kind: 'text',
    value:
      'The external account link was revoked. Reconnect before showing provider data.',
  },
  'integrations.firstParty.stateBody.noAccess': {
    kind: 'text',
    value:
      'The viewer cannot read the linked external item, so its title and status stay hidden.',
  },
  'integrations.firstParty.stateBody.unavailable': {
    kind: 'text',
    value: 'This integration is listed but is not available for this board yet.',
  },
  'integrations.firstParty.stateBody.notConfigured': {
    kind: 'text',
    value:
      'Connect an external account before this integration can add board or card data.',
  },
  'integrations.firstParty.boardState.enabled': {
    kind: 'text',
    value: 'Enabled on this board',
  },
  'integrations.firstParty.boardState.notEnabled': {
    kind: 'text',
    value: 'Not enabled on this board',
  },
  'integrations.firstParty.reason.editorOnly': {
    kind: 'text',
    value: 'Only board members with edit access can configure integrations.',
  },
  'integrations.firstParty.heading': { kind: 'text', value: 'First-party integrations' },
  'integrations.firstParty.description': {
    kind: 'text',
    value:
      'Connect expected tools and review whether their board surfaces can show live data.',
  },
  'integrations.firstParty.loadFailedTitle': {
    kind: 'text',
    value: 'Could not load integrations',
  },
  'integrations.firstParty.retry': { kind: 'text', value: 'Retry' },
  'integrations.firstParty.emptyTitle': {
    kind: 'text',
    value: 'No integrations available',
  },
  'integrations.firstParty.emptyBody': {
    kind: 'text',
    value: 'The integration catalogue did not return any entries.',
  },
  'integrations.firstParty.listLabel': {
    kind: 'text',
    value: 'First-party integrations',
  },
  'integrations.firstParty.rowLabel': { kind: 'text', value: '{name} integration' },
  'integrations.firstParty.boardStatusLabel': { kind: 'text', value: 'Board status' },
  'integrations.firstParty.accountScoped': { kind: 'text', value: 'Account scoped' },
  'integrations.firstParty.accountLabel': { kind: 'text', value: 'Account' },
  'integrations.firstParty.noAccount': { kind: 'text', value: 'No account connected' },
  'integrations.firstParty.grantedLabel': { kind: 'text', value: 'Granted' },
  'integrations.firstParty.lastActiveLabel': { kind: 'text', value: 'Last active' },
  'integrations.firstParty.never': { kind: 'text', value: 'Never' },
  'integrations.firstParty.connectAction': { kind: 'text', value: 'Connect {name}' },
  'integrations.firstParty.reconnectAction': { kind: 'text', value: 'Reconnect {name}' },
  'integrations.firstParty.configureAction': { kind: 'text', value: 'Configure {name}' },
  'integrations.firstParty.enableOnBoard': { kind: 'text', value: 'Enable on board' },
  'integrations.firstParty.disableOnBoard': { kind: 'text', value: 'Disable on board' },
  'integrations.firstParty.disconnectAction': {
    kind: 'text',
    value: 'Disconnect {name}',
  },
  'integrations.calendarFeed.reason.checking': {
    kind: 'text',
    value: 'Checking this calendar feed...',
  },
  'integrations.calendarFeed.reason.adminOnly': {
    kind: 'text',
    value: 'Only a board admin can enable calendar feeds.',
  },
  'integrations.calendarFeed.reason.noUrl': {
    kind: 'text',
    value: 'Generate a calendar feed URL first.',
  },
  'integrations.calendarFeed.reason.clipboardUnavailable': {
    kind: 'text',
    value: 'Clipboard access is not available.',
  },
  'integrations.calendarFeed.announce.copied': {
    kind: 'text',
    value: 'Calendar feed URL copied.',
  },
  'integrations.calendarFeed.announce.copyFailed': {
    kind: 'text',
    value: 'Calendar feed URL could not be copied.',
  },
  'integrations.calendarFeed.heading': { kind: 'text', value: 'Calendar feed' },
  'integrations.calendarFeed.description': {
    kind: 'text',
    value:
      'Anyone holding this URL can read card names and due dates without signing in. External calendars refresh on their own schedule.',
  },
  'integrations.calendarFeed.checkingNotice': {
    kind: 'text',
    value: 'Checking your calendar feed...',
  },
  'integrations.calendarFeed.loadFailed': {
    kind: 'text',
    value: 'Calendar feed could not be loaded.',
  },
  'integrations.calendarFeed.disabledNotice': {
    kind: 'text',
    value: 'Feeds are disabled. This URL stays empty until an admin enables feeds.',
  },
  'integrations.calendarFeed.urlLabel': { kind: 'text', value: 'Calendar feed URL' },
  'integrations.calendarFeed.toggleLabel': {
    kind: 'text',
    value: 'Calendar feeds enabled',
  },
  'integrations.calendarFeed.toggleOn': { kind: 'text', value: 'Feeds enabled' },
  'integrations.calendarFeed.toggleOff': { kind: 'text', value: 'Feeds disabled' },
  'integrations.calendarFeed.resetAction': {
    kind: 'text',
    value: 'Reset calendar feed URL',
  },
  'integrations.calendarFeed.generateAction': {
    kind: 'text',
    value: 'Generate calendar feed URL',
  },
  'integrations.calendarFeed.copyAction': { kind: 'text', value: 'Copy calendar URL' },
  'integrations.calendarFeed.resetDialog.title': {
    kind: 'text',
    value: 'Reset calendar feed URL',
  },
  'integrations.calendarFeed.resetDialog.description': {
    kind: 'text',
    value:
      'The current URL will stop returning card data. Update any external calendar subscriptions after resetting it.',
  },
  'integrations.calendarFeed.resetDialog.cancel': { kind: 'text', value: 'Cancel' },
  'integrations.calendarFeed.resetDialog.confirm': { kind: 'text', value: 'Reset URL' },
  'integrations.calendarFeed.resetDialog.body': {
    kind: 'text',
    value: 'Reset only if this URL was shared somewhere it should not have been.',
  },
  'integrations.emailToBoard.position.top': { kind: 'text', value: 'Top of the list' },
  'integrations.emailToBoard.position.bottom': {
    kind: 'text',
    value: 'Bottom of the list',
  },
  'integrations.emailToBoard.chooseList': { kind: 'text', value: 'Choose a list' },
  'integrations.emailToBoard.reason.checking': {
    kind: 'text',
    value: 'Checking this email-to-board address...',
  },
  'integrations.emailToBoard.reason.editorOnly': {
    kind: 'text',
    value: 'Only board members with edit access can configure email.',
  },
  'integrations.emailToBoard.reason.needsList': {
    kind: 'text',
    value: 'Create a list before choosing where emailed cards land.',
  },
  'integrations.emailToBoard.reason.chooseDestination': {
    kind: 'text',
    value: 'Choose a destination list.',
  },
  'integrations.emailToBoard.heading': { kind: 'text', value: 'Email to board' },
  'integrations.emailToBoard.description': {
    kind: 'text',
    value:
      'Anyone holding this address can create cards as you. Attachments count toward the 10 MB message limit.',
  },
  'integrations.emailToBoard.checkingNotice': {
    kind: 'text',
    value: 'Checking your email-to-board address...',
  },
  'integrations.emailToBoard.lockedFallback': {
    kind: 'text',
    value: 'Only board members with edit access can use email-to-board.',
  },
  'integrations.emailToBoard.addressLabel': {
    kind: 'text',
    value: 'Email-to-board address',
  },
  'integrations.emailToBoard.listTriggerLabel': {
    kind: 'text',
    value: '{title} — change email destination list',
  },
  'integrations.emailToBoard.listMenuHeading': {
    kind: 'text',
    value: 'Email destination list',
  },
  'integrations.emailToBoard.positionTriggerLabel': {
    kind: 'text',
    value: '{position} — change email card position',
  },
  'integrations.emailToBoard.positionMenuHeading': {
    kind: 'text',
    value: 'Email card position',
  },
  'integrations.emailToBoard.saveAction': {
    kind: 'text',
    value: 'Save email destination',
  },
  'integrations.emailToBoard.resetAction': { kind: 'text', value: 'Reset email address' },
  'integrations.snapshot.reason.adminOnly': {
    kind: 'text',
    value: 'Only a workspace admin can manage board snapshots.',
  },
  'integrations.snapshot.checking': {
    kind: 'text',
    value: 'Checking board snapshots...',
  },
  'integrations.snapshot.loadFailed': {
    kind: 'text',
    value: 'Board snapshots could not be loaded.',
  },
  'integrations.snapshot.retention.invalid': {
    kind: 'text',
    value: 'Retention must be a positive whole number.',
  },
  'integrations.snapshot.retention.optionDefault': {
    kind: 'text',
    value: 'Default {days} days',
  },
  'integrations.snapshot.retention.optionCustom': { kind: 'text', value: 'Custom days' },
  'integrations.snapshot.restoreTitleDefault': {
    kind: 'text',
    value: '{title} snapshot copy',
  },
  'integrations.snapshot.heading': { kind: 'text', value: 'Snapshots' },
  'integrations.snapshot.description': {
    kind: 'text',
    value:
      'Daily board snapshots are internal recovery points. Restores create a new board and never overwrite this one.',
  },
  'integrations.snapshot.emptyDisabledTitle': { kind: 'text', value: 'No snapshots yet' },
  'integrations.snapshot.emptyDisabledBody': {
    kind: 'text',
    value:
      'Enable daily snapshots to start retaining internal recovery points for this board.',
  },
  'integrations.snapshot.emptyEnabledTitle': {
    kind: 'text',
    value: 'Waiting for the first daily snapshot',
  },
  'integrations.snapshot.emptyEnabledBody': {
    kind: 'text',
    value:
      'Daily snapshots are enabled. The first retained snapshot appears after the next scheduled capture.',
  },
  'integrations.snapshot.restored.title': { kind: 'text', value: 'Snapshot restored' },
  'integrations.snapshot.restored.openLink': { kind: 'text', value: 'Open {title}' },
  'integrations.snapshot.restored.lists': {
    kind: 'plural',
    forms: { one: '1 list', other: '{count} lists' },
  },
  'integrations.snapshot.restored.cards': {
    kind: 'plural',
    forms: { one: '1 card', other: '{count} cards' },
  },
  'integrations.snapshot.restored.summary': {
    kind: 'text',
    value: 'with {lists} and {cards} restored.',
  },
  'integrations.snapshot.restored.dismiss': {
    kind: 'text',
    value: 'Dismiss restore result',
  },
  'integrations.snapshot.toggleLabel': {
    kind: 'text',
    value: 'Daily board snapshots enabled',
  },
  'integrations.snapshot.toggleOn': { kind: 'text', value: 'Snapshots enabled' },
  'integrations.snapshot.toggleOff': { kind: 'text', value: 'Snapshots disabled' },
  'integrations.snapshot.attachmentsToggleLabel': {
    kind: 'text',
    value: 'Future attachment capture enabled',
  },
  'integrations.snapshot.attachmentsOn': { kind: 'text', value: 'Capture attachments' },
  'integrations.snapshot.attachmentsOff': { kind: 'text', value: 'Skip attachments' },
  'integrations.snapshot.attachmentsNote': {
    kind: 'text',
    value: 'Attachment capture affects future snapshots only.',
  },
  'integrations.snapshot.retention.mode.default': { kind: 'text', value: 'Default' },
  'integrations.snapshot.retention.mode.custom': { kind: 'text', value: 'Custom' },
  'integrations.snapshot.retention.menuLabel': {
    kind: 'text',
    value: '{mode} retention, effective {days} days',
  },
  'integrations.snapshot.retention.trigger': {
    kind: 'plural',
    forms: { one: 'Retention: 1 day', other: 'Retention: {count} days' },
  },
  'integrations.snapshot.retention.menuHeading': {
    kind: 'text',
    value: 'Snapshot retention',
  },
  'integrations.snapshot.retention.daysLabel': { kind: 'text', value: 'Retention days' },
  'integrations.snapshot.retention.daysHint': {
    kind: 'text',
    value: 'Existing snapshots keep their current expiry dates.',
  },
  'integrations.snapshot.retention.saveAction': { kind: 'text', value: 'Save retention' },
  'integrations.snapshot.retention.note': {
    kind: 'text',
    value: 'Retention changes are not retroactive for snapshots already retained.',
  },
  'integrations.snapshot.listLabel': { kind: 'text', value: 'Retained snapshots' },
  'integrations.snapshot.restoreInProgress': {
    kind: 'text',
    value: 'Another snapshot restore is already in progress.',
  },
  'integrations.snapshot.takenLabel': { kind: 'text', value: 'Taken' },
  'integrations.snapshot.expiresLabel': { kind: 'text', value: 'Expires' },
  'integrations.snapshot.restoreRowLabel': {
    kind: 'text',
    value: 'Restore snapshot taken {date}',
  },
  'integrations.snapshot.restoreAction': { kind: 'text', value: 'Restore' },
  'integrations.snapshot.restoreDialog.title': {
    kind: 'text',
    value: 'Restore snapshot',
  },
  'integrations.snapshot.restoreDialog.description': {
    kind: 'text',
    value: 'A snapshot restore creates a new board from this retained recovery point.',
  },
  'integrations.snapshot.restoreDialog.cancel': { kind: 'text', value: 'Cancel' },
  'integrations.snapshot.restoreDialog.confirm': {
    kind: 'text',
    value: 'Restore to new board',
  },
  'integrations.snapshot.restoreDialog.titleLabel': {
    kind: 'text',
    value: 'Restored board title',
  },
  'integrations.snapshot.restoreDialog.warning': {
    kind: 'text',
    value:
      'The original board will not be overwritten. Comments, activity, checklist state, members, votes and Power-Up data are not replayed by the B79 backend payload.',
  },
  'planner.calendar.limitReached': {
    kind: 'text',
    value: 'The Planner account limit is reached.',
  },
  'planner.calendar.emptyAccounts': {
    kind: 'text',
    value: 'No calendar accounts connected.',
  },
  'planner.calendar.accountList': {
    kind: 'text',
    value: 'Connected calendar accounts',
  },
  'planner.calendar.providerCalendar': { kind: 'text', value: '{provider} calendar' },
  'planner.calendar.disconnect': { kind: 'text', value: 'Disconnect' },
  'planner.calendar.readOnly': { kind: 'text', value: 'read-only' },
  'planner.calendar.fallback': { kind: 'text', value: 'Calendar' },
  'planner.calendar.busyEvent': { kind: 'text', value: 'Busy event' },
  'planner.calendar.externalEventLabel': {
    kind: 'text',
    value: '{event} external event',
  },
  'planner.calendar.eventDetails': {
    kind: 'text',
    value: '{provider} / {calendar} / {range}',
  },
  'planner.calendar.readOnlyEvent': {
    kind: 'text',
    value: 'Read-only external event',
  },
  'planner.smartSchedule.title': { kind: 'text', value: 'Smart schedule' },
  'planner.smartSchedule.description': {
    kind: 'text',
    value: 'Preview blocks inside {start}-{end}.',
  },
  'planner.smartSchedule.empty': {
    kind: 'text',
    value: 'No open working-hour slots were found.',
  },
  'planner.smartSchedule.previews': {
    kind: 'text',
    value: 'Smart schedule previews',
  },
  'planner.smartSchedule.preview': { kind: 'text', value: 'Preview {range}' },
  'planner.smartSchedule.reasonFallback': {
    kind: 'text',
    value: 'Suggested from your open time.',
  },
  'planner.smartSchedule.accept': { kind: 'text', value: 'Accept preview' },
  'planner.smartSchedule.reject': { kind: 'text', value: 'Reject preview' },
  'planner.announce.boardFiltersSaved': {
    kind: 'text',
    value: 'Planner board filters saved.',
  },
  'planner.announce.cardFilterSaved': {
    kind: 'text',
    value: 'Planner card filter saved.',
  },
  'planner.announce.workingHoursSaved': {
    kind: 'text',
    value: 'Planner working hours saved.',
  },
  'planner.announce.workingDaysSaved': {
    kind: 'text',
    value: 'Planner working days saved.',
  },
  'planner.announce.calendarConnected': {
    kind: 'text',
    value: '{provider} calendar connected.',
  },
  'planner.announce.calendarDisplaySaved': {
    kind: 'text',
    value: 'Calendar display saved.',
  },
  'planner.announce.calendarDisconnected': {
    kind: 'text',
    value: 'Calendar account disconnected.',
  },
  'planner.announce.focusScheduled': {
    kind: 'text',
    value: 'Focus time scheduled.',
  },
  'planner.announce.focusMoved': { kind: 'text', value: 'Focus time moved.' },
  'planner.announce.focusResized': { kind: 'text', value: 'Focus time resized.' },
  'planner.announce.cardLinked': {
    kind: 'text',
    value: 'Card linked to focus time.',
  },
  'planner.announce.cardRemoved': {
    kind: 'text',
    value: 'Card removed from focus time.',
  },
  'planner.announce.focusDeleted': { kind: 'text', value: 'Focus time deleted.' },
  'planner.announce.cardCompleted': { kind: 'text', value: 'Card completed.' },
  'planner.announce.cardReopened': { kind: 'text', value: 'Card reopened.' },
  'planner.announce.suggestionsReady': {
    kind: 'text',
    value: 'Smart schedule previews ready.',
  },
  'planner.announce.suggestionAccepted': {
    kind: 'text',
    value: 'Smart schedule preview accepted.',
  },
  'planner.announce.suggestionRejected': {
    kind: 'text',
    value: 'Smart schedule preview rejected.',
  },
  'appearance.heading': { kind: 'text', value: 'Appearance' },
  'appearance.light': { kind: 'text', value: 'Light' },
  'appearance.dark': { kind: 'text', value: 'Dark' },
  'appearance.matchSystem': { kind: 'text', value: 'Match system' },
  'appearance.colourBlindOn': {
    kind: 'text',
    value: 'Colour-blind label mode on',
  },
  'appearance.colourBlindOff': {
    kind: 'text',
    value: 'Colour-blind label mode off',
  },
  'accessibility.heading': { kind: 'text', value: 'Accessibility' },
  'accessibility.keyboardShortcuts': {
    kind: 'text',
    value: 'Keyboard shortcuts',
  },
  'accessibility.singleKeyOn': {
    kind: 'text',
    value: 'Single-key shortcuts on',
  },
  'accessibility.singleKeyOff': {
    kind: 'text',
    value: 'Single-key shortcuts off',
  },
  'accessibility.reducedMotionOn': {
    kind: 'text',
    value: 'Reduced motion on',
  },
  'accessibility.reducedMotionOff': {
    kind: 'text',
    value: 'Reduced motion off',
  },
  'profile.displayNameSaved': { kind: 'text', value: 'Display name saved.' },
  'profile.bioSaved': { kind: 'text', value: 'Bio saved.' },
  'profile.avatarSaved': { kind: 'text', value: 'Avatar saved.' },
  // FE-44.2 -- the avatar file picker on `/profile`. `profile.avatarSaved`
  // directly above is REUSED as its success announcement rather than given a
  // twin: one event, one sentence, whether the URL was typed or uploaded.
  //
  // ⚠ There is no size key here. `{size}` is fed
  // `formatAttachmentSize(t, AVATAR_UPLOAD_MAX_BYTES)`, which renders `10 MB`
  // from the constant the SERVER signs -- so raising the cap in
  // `packages/shared/src/user.ts` cannot leave this copy claiming the old one.
  //
  // ⚠ `bytesFailed` and `refused` deliberately do NOT reuse
  // `primitives.attachment.upload.bytesFailed` / `.refused`. Those say
  // "attachment", and `/profile` has no attachments -- a shared key would
  // render the wrong noun on a surface that has none. `{status}` is an HTTP
  // status code, an opaque numeral that agrees with nothing.
  'profile.avatarUpload.heading': { kind: 'text', value: 'Upload an avatar' },
  'profile.avatarUpload.field': { kind: 'text', value: 'Avatar image file' },
  'profile.avatarUpload.hint': {
    kind: 'text',
    value:
      'JPEG, PNG, WebP or GIF, up to {size}. Uploading replaces the avatar link above.',
  },
  'profile.avatarUpload.submit': { kind: 'text', value: 'Upload avatar' },
  'profile.avatarUpload.rejectedType': {
    kind: 'text',
    value: 'Choose a JPEG, PNG, WebP or GIF image.',
  },
  'profile.avatarUpload.rejectedSize': {
    kind: 'text',
    value: 'That image is larger than {size}. Choose a smaller one.',
  },
  'profile.avatarUpload.bytesFailed': {
    kind: 'text',
    value: 'Could not upload the image.',
  },
  'profile.avatarUpload.refused': {
    kind: 'text',
    value: 'The upload was refused with HTTP {status}.',
  },
  'profile.accountDeactivated': { kind: 'text', value: 'Account deactivated.' },
  'profile.accountDeleted': { kind: 'text', value: 'Account deleted.' },
  'profile.interfaceLanguageSaved': {
    kind: 'text',
    value: 'Interface language saved.',
  },
  'profile.timezoneSaved': { kind: 'text', value: 'Timezone saved.' },
  'profile.connectedToolConnected': {
    kind: 'text',
    value: '{toolName} connected.',
  },
  'profile.connectedToolRevoked': {
    kind: 'text',
    value: '{toolName} revoked.',
  },
  'profile.connectedToolRevealed': {
    kind: 'text',
    value: '{toolName} token revealed.',
  },
  'profile.connectedToolRegenerated': {
    kind: 'text',
    value: '{toolName} token regenerated.',
  },
  'profile.webhookCreated': {
    kind: 'text',
    value: 'Webhook registration created.',
  },
  'profile.webhookRemoved': {
    kind: 'text',
    value: 'Webhook registration removed.',
  },
  /*
   * ⚠ THREE MORE ANNOUNCEMENTS, BESIDE THE TWO THAT SHIPPED, AND THE REASON IS
   * THE LIVE REGION RATHER THAN SYMMETRY. Creating and removing move a row in
   * or out of a list a sighted member watches change; resuming flips a chip,
   * saving a filter closes an editor and rewrites one `<dd>`, and rotating
   * replaces a value inside a panel that was already on screen. None of the
   * three is announced by anything else, and the last two are exactly the
   * shape a screen-reader user would otherwise have to go looking for.
   *
   * ⚠ NOT `integrations.webhook.*`. These are `profile.*` because the
   * announcer is `profile.tsx`'s, the component has no live region of its own,
   * and the two that already exist for this surface are declared here.
   */
  'profile.webhookSecretRotated': {
    kind: 'text',
    value: 'New signing secret issued. Copy it before leaving this page.',
  },
  'profile.webhookResumed': {
    kind: 'text',
    value: 'Deliveries resumed.',
  },
  'profile.webhookEventsSaved': {
    kind: 'text',
    value: 'Events updated for this registration.',
  },
  // FE-80.1 -- `lib/locale-formatting.ts`'s `languageLabel` and `timezoneLabel`.
  // `profile.locale.*` and NOT `primitives.*`, deliberately, and the rule is
  // reach rather than the module a function happens to live in.
  // `primitives.attachment.*` was earned because that leaf surfaces on three
  // sections; `primitives.cardDate.*` because `dueStatusLabel` alone renders on
  // the card front, the card back and Home. These two have exactly ONE
  // consumer, `LocalePreferencesSection`, and a language picker cannot exist on
  // a second surface -- so a `primitives.*` namespace would claim a reach they
  // do not have. `lib/attachments.ts` is the precedent for one module
  // contributing to two namespaces in one slice.
  //
  // The values are each language's name in the INTERFACE language, not its
  // endonym: `LANGUAGE_LABELS` has always read `German` rather than `Deutsch`,
  // and routing a string is not the place to change what it says. A locale that
  // wants endonyms writes them here.
  //
  // `.enUS` / `.enGB` / `.ptBR` drop the hyphen the `LocaleLanguage` union
  // carries: the key grammar is dotted and a hyphen inside a segment appears
  // nowhere else in this catalogue. The mapping lives in ONE place --
  // `languageKey` in the leaf -- so it cannot drift between the leaf and here.
  'profile.locale.language.en': { kind: 'text', value: 'English' },
  'profile.locale.language.enUS': { kind: 'text', value: 'English (US)' },
  'profile.locale.language.enGB': { kind: 'text', value: 'English (UK)' },
  'profile.locale.language.fr': { kind: 'text', value: 'French' },
  'profile.locale.language.de': { kind: 'text', value: 'German' },
  'profile.locale.language.es': { kind: 'text', value: 'Spanish' },
  'profile.locale.language.ptBR': { kind: 'text', value: 'Portuguese (Brazil)' },
  'profile.locale.language.ja': { kind: 'text', value: 'Japanese' },
  // Only the null branch of `timezoneLabel`. Its other branch returns the raw
  // IANA identifier (`Asia/Tokyo`), which is an identifier and not copy, and
  // stays literal in every locale.
  'profile.locale.deviceTimezone': { kind: 'text', value: 'Use device timezone' },
  'route.notFound.title': { kind: 'text', value: 'Not found' },
  'route.notFound.body': {
    kind: 'text',
    value: 'There is nothing at this address — or it is not yours to see.',
  },
  'route.notFound.signIn': { kind: 'text', value: 'Sign in' },
  'route.notFound.backToBoards': { kind: 'text', value: 'Back to your boards' },
  'route.error.title': { kind: 'text', value: 'Something went wrong' },
  'route.error.retry': { kind: 'text', value: 'Try again' },
  'route.error.genericBody': {
    kind: 'text',
    value: 'Something on this page broke. Reloading might fix it.',
  },
  'board.loading': { kind: 'text', value: 'Loading the board' },
  'board.error.title': { kind: 'text', value: "The board couldn't load" },
  'board.error.retry': { kind: 'text', value: 'Try again' },
  /* `BoardEmpty`, whose third string is `boardFace.list.add` above — the
     button is shared vocabulary, the explanation around it is this file's own.
     Both stay in the `board.*` family that `BoardStates.tsx`'s other seven call
     sites already use. */
  'board.empty.title': { kind: 'text', value: '{title} has no lists yet' },
  'board.empty.body': {
    kind: 'text',
    value: 'Lists are how work gets organised on a board.',
  },
  /* FE-80.1 board states — `ListEmpty` and `BoardClosedNotice`, the two
     components `BoardStates.tsx` still authored English in after the
     list-composer slice took `BoardEmpty`.

     ⚠ `ListEmpty` is `BoardEmpty`'s shape one level down, so it takes
     `BoardEmpty`'s ruling verbatim: the BUTTON is shared vocabulary and reaches
     for `boardFace.list.addCard` / `boardFace.list.addCardTo` (declared above,
     and already routed by `ListColumn.tsx:642,645` for the SAME affordance on a
     non-empty list — `C3-BRIEF.md` §6.5, "wire it to the same callback, do not
     add a third"); the explanation around it is this file's own and stays in
     `board.*`. A `board.listEmpty.action` beside these two would let the empty
     list and the full list disagree in every language but this one.

     ⚠ `{list}` here, `{title}` in `board.empty.title` — and NOT to be
     "normalised" later. `{list}` is the catalogue-wide name for a LIST title
     (`boardFace.list.cardStack`, `boardFace.list.expand`,
     `cardBack.dialog.description`); `{title}` is the BOARD's. One family
     spelling both subjects `{title}` is how a translator is told the wrong
     noun. `ListEmpty` passes `{ list: listTitle }` — the prop is `listTitle`,
     the slot is `list`, and a mismatch makes `interfaceMessage` THROW into the
     render, which the error boundary swallows whole.

     ⚠ `board.closed.title` is a SECOND author of `This board is closed`;
     `cardBack.comments.permission.closed.title` carries the same bytes. The
     namespace decides, not the bytes (the settled ruling `board.card.untitled`
     records against `myCards.untitledCard`): `cardBack.*` is the card back's
     route namespace and a board-face component must not reach into it — and
     the two are not the same sentence, one being a whole board's read-only
     banner and the other the reason a comment box is disabled, their bodies
     already diverging completely.

     ⚠ `board.closed.body` MUST KEEP ITS SLOT FIRST. `FR-EXP-012.spec.ts:739`
     and `J3.spec.ts:137` both locate the notice with
     `getByRole('status').filter({ hasText: '<board> is closed' })`, and
     `hasText` reads TEXT CONTENT, not the `aria-label` — so those locators are
     satisfied by this BODY carrying `<title> is closed` as a prefix substring.
     Moving the name into the middle of the sentence is green in every unit test
     and red in two e2e specs.

     ⚠ `board.closed.reopen` is ONE key for TWO call sites. `ReopenAction`'s
     disabled and enabled branches are written out separately (under
     `exactOptionalPropertyTypes` a conditional spread is a hole in `tsc`), and a
     second key for the disabled one is the second-author defect one level down.
     `BoardMenuPanel.tsx:263` and `FR-EXP-012.spec.ts:713` both record that
     `Reopen board` is named exactly once product-wide BY DESIGN, so that
     `getByRole('button', { name: 'Reopen board' })` is unambiguous. */
  'board.listEmpty.title': { kind: 'text', value: '{list} has no cards yet' },
  'board.listEmpty.body': {
    kind: 'text',
    value: 'Cards are the work items in this list.',
  },
  'board.closed.title': { kind: 'text', value: 'This board is closed' },
  'board.closed.body': { kind: 'text', value: '{title} is closed and read-only.' },
  'board.closed.label': { kind: 'text', value: '{title} is closed' },
  'board.closed.reopen': { kind: 'text', value: 'Reopen board' },
  'board.signIn.title': { kind: 'text', value: 'Sign in to see this board' },
  'board.signIn.body': {
    kind: 'text',
    value:
      'Boards are shown to the people they belong to. Signing in brings you straight back to this address.',
  },
  'board.signIn.action': { kind: 'text', value: 'Sign in' },
  /* `BoardCapacityNotice.tsx` — machine counters from B47 rendered in the
     board menu. The `{board}` slot is authored board data and the numerals are
     preformatted by the existing component path; this slice externalises only
     the chrome around them. */
  'boardCapacity.titleCompact': { kind: 'text', value: 'Board capacity' },
  'boardCapacity.title': { kind: 'text', value: '{board} capacity' },
  'boardCapacity.kind.openCards': { kind: 'text', value: 'open cards' },
  'boardCapacity.kind.totalCards': { kind: 'text', value: 'total cards' },
  'boardCapacity.kind.openLists': { kind: 'text', value: 'open lists' },
  'boardCapacity.kind.totalLists': { kind: 'text', value: 'total lists' },
  'boardCapacity.kind.attachmentsPerCard': {
    kind: 'text',
    value: 'attachments on one card',
  },
  'boardCapacity.kind.checklistsPerCard': {
    kind: 'text',
    value: 'checklists on one card',
  },
  'boardCapacity.kind.checklistsPerBoard': {
    kind: 'text',
    value: 'checklists on this board',
  },
  'boardCapacity.kind.labelsPerBoard': {
    kind: 'text',
    value: 'labels on this board',
  },
  'boardCapacity.kind.membersPerBoard': {
    kind: 'text',
    value: 'members on this board',
  },
  'boardCapacity.warning': {
    kind: 'text',
    value:
      '{current} of {limit} {kind} are in use. The warning threshold is {warningAt}.',
  },
  'boardCapacity.atLimit': {
    kind: 'text',
    value:
      '{current} of {limit} {kind} are in use. Archive or delete items before adding more.',
  },
  'boardMenu.title': { kind: 'text', value: 'Board menu' },
  'boardMenu.landmark': { kind: 'text', value: 'Board menu: {board}' },
  'boardMenu.back': { kind: 'text', value: 'Back to board menu' },
  'boardMenu.hide': { kind: 'text', value: 'Hide board menu' },
  'boardMenu.activity': { kind: 'text', value: 'Activity' },
  'boardMenu.archived': { kind: 'text', value: 'Archived items' },
  'boardMenu.members': { kind: 'text', value: 'Members' },
  'boardMenu.settings': { kind: 'text', value: 'Settings' },
  'boardMenu.background': { kind: 'text', value: 'Change background' },
  'boardMenu.customFields': { kind: 'text', value: 'Custom fields' },
  'boardMenu.integrations': { kind: 'text', value: 'First-party integrations' },
  'boardMenu.integrationsShort': { kind: 'text', value: 'Integrations' },
  'boardMenu.powerUps': { kind: 'text', value: 'Power-Ups' },
  'boardMenu.share': { kind: 'text', value: 'Share board' },
  'boardMenu.automation': { kind: 'text', value: 'Automation' },
  'boardMenu.snapshots': { kind: 'text', value: 'Snapshots' },
  'boardMenu.externalAccess': { kind: 'text', value: 'External access' },
  'boardMenu.inviteLink': { kind: 'text', value: 'Invite link' },
  'boardMenu.keyboardShortcuts': { kind: 'text', value: 'Keyboard shortcuts' },
  'boardMenu.keyboardShortcutsVisible': {
    kind: 'text',
    value: 'Keyboard shortcuts (?)',
  },
  'boardMenu.watch.label': { kind: 'text', value: 'board' },
  'boardMenu.watch.description': {
    kind: 'text',
    value:
      'Notifications for comments, card moves, archives, due-date changes and new cards on this board.',
  },
  'boardMenu.silence.label': { kind: 'text', value: 'Silence board' },
  'boardMenu.silence.description': {
    kind: 'text',
    value: 'Suppress watch notifications from this board. Mentions still arrive.',
  },
  'boardMenu.silence.onLabel': { kind: 'text', value: 'Silence board on' },
  'boardMenu.silence.offLabel': { kind: 'text', value: 'Silence board off' },
  'boardMenu.silence.saveFailed': {
    kind: 'text',
    value: 'Board silence could not be saved.',
  },
  'boardMenu.silence.enabled': { kind: 'text', value: 'Board silenced.' },
  'boardMenu.silence.disabled': { kind: 'text', value: 'Board unsilenced.' },
  'boardMenu.collapseAllLists': { kind: 'text', value: 'Collapse all lists' },
  'boardMenu.expandAllLists': { kind: 'text', value: 'Expand all lists' },
  'boardMenu.copyBoard': { kind: 'text', value: 'Copy board' },
  'boardMenu.makeTemplate': { kind: 'text', value: 'Make template' },
  'boardMenu.removeTemplateFlag': { kind: 'text', value: 'Remove template flag' },
  'boardMenu.announce.templates.marked': {
    kind: 'text',
    value: '{board} is now a template.',
  },
  'boardMenu.announce.templates.unmarked': {
    kind: 'text',
    value: '{board} is no longer a template.',
  },
  'boardMenu.announce.templates.descriptionSaved': {
    kind: 'text',
    value: 'Template description saved.',
  },
  'boardMenu.dangerZone': { kind: 'text', value: 'Danger zone' },
  'boardMenu.closeBoard': { kind: 'text', value: 'Close board' },
  'boardMenu.deleteBoard': {
    kind: 'text',
    value: 'Delete board permanently',
  },
  'boardMenu.leaveBoard': { kind: 'text', value: 'Leave board' },
  'boardMenu.cancel': { kind: 'text', value: 'Cancel' },
  'boardMenu.deleted': { kind: 'text', value: 'Deleted the board {board}.' },
  'boardMenu.left': { kind: 'text', value: 'You left {board}.' },
  'boardMenu.backgroundChanged': {
    kind: 'text',
    value: 'Board background changed.',
  },
  'boardMenu.customBackgroundUploaded': {
    kind: 'text',
    value: 'Custom board background uploaded.',
  },
  'boardMenu.announce.visibilityChanged': {
    kind: 'text',
    value: 'Board visibility is now {visibility}. {consequence}',
  },
  'boardMenu.announce.memberManagement.admins': {
    kind: 'text',
    value: 'Member management is now board admins only.',
  },
  'boardMenu.announce.memberManagement.allMembers': {
    kind: 'text',
    value: 'Member management is now available to board members.',
  },
  'boardMenu.memberFallback': { kind: 'text', value: 'That member' },
  'boardMenu.announce.memberAdded': {
    kind: 'text',
    value: '{member} was added to this board.',
  },
  'boardMenu.announce.memberAlreadyAdded': {
    kind: 'text',
    value: '{member} is already on this board.',
  },
  'boardMenu.announce.memberRemoved': {
    kind: 'text',
    value: '{member} was removed from this board.',
  },
  'boardMenu.announce.memberRole.admin': {
    kind: 'text',
    value: '{member} is now an admin.',
  },
  'boardMenu.announce.memberRole.normal': {
    kind: 'text',
    value: '{member} is now a member.',
  },
  'boardMenu.announce.memberRole.observer': {
    kind: 'text',
    value: '{member} is now an observer.',
  },
  'boardMenu.announce.commentingChanged': {
    kind: 'text',
    value: 'Commenting permission changed.',
  },
  'boardMenu.announce.selfJoin.enabled': {
    kind: 'text',
    value: 'Workspace members may join and edit this board.',
  },
  'boardMenu.announce.selfJoin.disabled': {
    kind: 'text',
    value: 'Workspace members can read this board without self-joining.',
  },
  'boardMenu.announce.voting.enabled': { kind: 'text', value: 'Voting enabled.' },
  'boardMenu.announce.voting.disabled': { kind: 'text', value: 'Voting disabled.' },
  'boardMenu.announce.votingPermissionChanged': {
    kind: 'text',
    value: 'Voting permission changed.',
  },
  'boardMenu.announce.votingTallies.hidden': {
    kind: 'text',
    value: 'Vote tallies hidden.',
  },
  'boardMenu.announce.votingTallies.visible': {
    kind: 'text',
    value: 'Vote tallies visible.',
  },
  'boardMenu.announce.cardNumbers.shown': {
    kind: 'text',
    value: 'Card references shown on card fronts.',
  },
  'boardMenu.announce.cardNumbers.hidden': {
    kind: 'text',
    value: 'Card references hidden from card fronts.',
  },
  'boardMenu.announce.cardKey.changed': {
    kind: 'text',
    value: 'Board key changed to {key}.',
  },
  'boardMenu.announce.cardAging.off': {
    kind: 'text',
    value: 'Card aging disabled.',
  },
  'boardMenu.announce.cardAging.regular': {
    kind: 'text',
    value: 'Card aging changed to regular.',
  },
  'boardMenu.announce.cardAging.pirate': {
    kind: 'text',
    value: 'Card aging changed to pirate.',
  },
  'boardMenu.announce.invite.generated': {
    kind: 'text',
    value: 'Invite link generated.',
  },
  'boardMenu.announce.invite.observerGenerated': {
    kind: 'text',
    value: 'Observer invite link generated.',
  },
  'boardMenu.announce.invite.revoked': {
    kind: 'text',
    value: 'Invite link revoked.',
  },
  'boardMenu.announce.emailToBoard.reset': {
    kind: 'text',
    value: 'Email-to-board address reset.',
  },
  'boardMenu.announce.emailToBoard.saved': {
    kind: 'text',
    value: 'Email-to-board destination saved.',
  },
  'boardMenu.announce.calendarFeed.reset': {
    kind: 'text',
    value: 'Calendar feed URL reset.',
  },
  'boardMenu.announce.calendarFeed.enabled': {
    kind: 'text',
    value: 'Calendar feeds enabled.',
  },
  'boardMenu.announce.calendarFeed.disabled': {
    kind: 'text',
    value: 'Calendar feeds disabled.',
  },
  'boardMenu.announce.snapshots.enabled': {
    kind: 'text',
    value: 'Daily board snapshots enabled.',
  },
  'boardMenu.announce.snapshots.disabled': {
    kind: 'text',
    value: 'Daily board snapshots disabled.',
  },
  'boardMenu.announce.snapshots.retentionChanged': {
    kind: 'plural',
    forms: {
      one: 'Snapshot retention is now 1 day.',
      other: 'Snapshot retention is now {count} days.',
    },
  },
  'boardMenu.announce.snapshots.attachmentsIncluded': {
    kind: 'text',
    value: 'Future snapshots will include attachment metadata.',
  },
  'boardMenu.announce.snapshots.attachmentsSkipped': {
    kind: 'text',
    value: 'Future snapshots will skip attachment metadata.',
  },
  'boardMenu.announce.snapshots.restored': {
    kind: 'text',
    value: 'Snapshot restored to new board {board}.',
  },
  'boardMenu.announce.powerUps.enabled': {
    kind: 'text',
    value: '{powerUp} Power-Up enabled on this board.',
  },
  'boardMenu.announce.powerUps.headerShown': {
    kind: 'text',
    value: '{powerUp} Power-Up header button shown.',
  },
  'boardMenu.announce.powerUps.headerHidden': {
    kind: 'text',
    value: '{powerUp} Power-Up header button hidden.',
  },
  'boardMenu.announce.powerUps.settingsSaved': {
    kind: 'text',
    value: '{powerUp} Power-Up settings saved.',
  },
  'boardMenu.announce.powerUps.authorised': {
    kind: 'text',
    value: '{powerUp} Power-Up authorised for your account.',
  },
  'boardMenu.announce.powerUps.disconnected': {
    kind: 'text',
    value: '{powerUp} Power-Up disconnected from your account.',
  },
  'boardMenu.announce.powerUps.disabled': {
    kind: 'text',
    value: '{powerUp} Power-Up disabled on this board.',
  },
  'boardMenu.announce.powerUps.personalSettingsRemoved': {
    kind: 'text',
    value: '{powerUp} personal Power-Up settings removed.',
  },
  'boardMenu.reason.closedReadonly': {
    kind: 'text',
    value: 'Closed boards are read-only.',
  },
  'boardMenu.reason.editorArchive': {
    kind: 'text',
    value: 'Only board members with edit access can restore or delete archived items.',
  },
  'boardMenu.reason.checkingBoardAccess': {
    kind: 'text',
    value: 'Checking what you can do on this board…',
  },
  'boardMenu.reason.adminPermissionSettings': {
    kind: 'text',
    value: 'Only a board admin can change board permission settings.',
  },
  'boardMenu.reason.editorBackground': {
    kind: 'text',
    value: 'Only a board member who can edit this board can change the background.',
  },
  'boardMenu.reason.editorCustomFields': {
    kind: 'text',
    value: 'Only a board member who can edit this board can manage custom fields.',
  },
  'boardMenu.reason.signInAutomation': {
    kind: 'text',
    value: 'Sign in to view automation activity.',
  },
  'boardMenu.reason.checkingWorkspaceRole': {
    kind: 'text',
    value: 'Checking your workspace role...',
  },
  'boardMenu.reason.workspaceAdminAutomationReview': {
    kind: 'text',
    value: "Only a workspace admin can review other members' automations.",
  },
  'boardMenu.reason.workspaceAdminSnapshots': {
    kind: 'text',
    value: 'Only a workspace admin can manage board snapshots.',
  },
  'boardMenu.reason.adminAutomations': {
    kind: 'text',
    value: 'Only a board admin can manage automations.',
  },
  'boardMenu.reason.adminPowerUps': {
    kind: 'text',
    value: 'Only a board admin can manage Power-Ups.',
  },
  'boardMenu.reason.editorEmailToBoard': {
    kind: 'text',
    value: 'Only a board member who can edit this board can use email-to-board.',
  },
  'boardMenu.reason.memberCalendarFeed': {
    kind: 'text',
    value: 'Only board members can use the board calendar feed.',
  },
  'boardMenu.reason.checkingBoardMembership': {
    kind: 'text',
    value: 'Checking your board membership…',
  },
  'boardMenu.reason.adminCalendarFeeds': {
    kind: 'text',
    value: 'Only a board admin can enable calendar feeds.',
  },
  'boardMenu.reason.adminCopyBoard': {
    kind: 'text',
    value: 'Only a board admin can copy this board.',
  },
  'boardMenu.reason.memberLeaveBoard': {
    kind: 'text',
    value: 'Only board members can leave this board.',
  },
  'boardMenu.reason.adminTemplate': {
    kind: 'text',
    value: 'Only a board admin can manage this board template.',
  },
  'boardMenu.reason.guestTemplate': {
    kind: 'text',
    value: 'Guests cannot turn a board into a template.',
  },
  'boardMenu.reason.checkingTemplateSettings': {
    kind: 'text',
    value: "Checking this board's template settings…",
  },
  'boardMenu.reason.signInCollapseLists': {
    kind: 'text',
    value: 'Sign in to save collapsed lists for this board.',
  },
  'boardMenu.reason.noListsToCollapse': {
    kind: 'text',
    value: 'This board has no lists to collapse.',
  },
  'boardMenu.reason.signInExpandLists': {
    kind: 'text',
    value: 'Sign in to save expanded lists for this board.',
  },
  'boardMenu.reason.noListsToExpand': {
    kind: 'text',
    value: 'This board has no lists to expand.',
  },
  'boardMenu.mobile.workspaceAdmin.title': {
    kind: 'text',
    value: 'Workspace administration is available on the web',
  },
  'boardMenu.mobile.workspaceAdmin.accessBody': {
    kind: 'text',
    value:
      'Workspace administration settings are available on web. Open this exact board on web to manage workspace-level access.',
  },
  'boardMenu.mobile.workspaceAdmin.policyBody': {
    kind: 'text',
    value:
      'Workspace administration settings are available on web. Open this exact board on web to manage workspace-level policy.',
  },
  'boardMenu.mobile.customFields.title': {
    kind: 'text',
    value: 'Custom field setup is available on the web',
  },
  'boardMenu.mobile.customFields.bodyShort': {
    kind: 'text',
    value: 'Custom field setup and editing are available on the web.',
  },
  'boardMenu.mobile.automation.title': {
    kind: 'text',
    value: 'Automation editing is available on the web',
  },
  'boardMenu.mobile.automation.body': {
    kind: 'text',
    value:
      'Existing rules stay readable on mobile. Create and edit automation rules on web.',
  },
  'boardMenu.mobile.automation.bodyShort': {
    kind: 'text',
    value: 'Automation editing is available on the web.',
  },
  'boardMenu.mobile.powerUps.title': {
    kind: 'text',
    value: 'Power-Up configuration is available on the web',
  },
  'boardMenu.mobile.powerUps.bodyShort': {
    kind: 'text',
    value: 'Power-Up setup and configuration are available on web.',
  },
  'boardMenu.powerUp.enableOnBoard': {
    kind: 'text',
    value: 'Enable {powerUp} Power-Up on this board',
  },
  'boardMenu.integration.connect': { kind: 'text', value: 'Connect' },
  'boardMenu.integration.reconnect': { kind: 'text', value: 'Reconnect' },
  'boardMenu.integration.disconnect': { kind: 'text', value: 'Disconnect' },
  'boardMenu.integration.configure': { kind: 'text', value: 'Configure' },
  'boardMenu.integration.thisIntegration': {
    kind: 'text',
    value: 'this integration',
  },
  'boardMenu.integration.genericIntegration': {
    kind: 'text',
    value: 'This integration',
  },
  'boardMenu.integration.unavailableAction': {
    kind: 'text',
    value: '{action} for {integration} is not exposed by the current frontend contract.',
  },
  'boardMenu.integration.noPowerUpRow': {
    kind: 'text',
    value: '{integration} has no Power-Up catalogue row to enable yet.',
  },
  'boardMenu.integration.enabled': {
    kind: 'text',
    value: '{integration} integration enabled on this board.',
  },
  'boardMenu.integration.notEnabled': {
    kind: 'text',
    value: '{integration} is not enabled on this board.',
  },
  'boardMenu.integration.disabled': {
    kind: 'text',
    value: '{integration} integration disabled on this board.',
  },
  'boardMenu.integration.disableTitle': {
    kind: 'text',
    value: 'Disable integration?',
  },
  'boardMenu.integration.disableNamedTitle': {
    kind: 'text',
    value: 'Disable {integration} integration?',
  },
  'boardMenu.integration.disableDescription': {
    kind: 'text',
    value:
      "Disabling removes this integration's board and card surfaces for every member of this board.",
  },
  'boardMenu.integration.disable': {
    kind: 'text',
    value: 'Disable integration',
  },
  'boardMenu.integration.disableNamed': {
    kind: 'text',
    value: 'Disable {integration} integration',
  },
  'boardMenu.integration.keepDataDescription': {
    kind: 'text',
    value:
      'Keep all integration data stored on this board. Native card fields, attachments and comments stay in place either way.',
  },
  'boardMenu.customFields.created': {
    kind: 'text',
    value: 'Custom field {field} created.',
  },
  'boardMenu.customFields.renamed': {
    kind: 'text',
    value: 'Custom field renamed.',
  },
  'boardMenu.customFields.hidden': {
    kind: 'text',
    value: 'Custom field hidden.',
  },
  'boardMenu.customFields.shown': {
    kind: 'text',
    value: 'Custom field shown.',
  },
  'boardMenu.customFields.showOnFront': {
    kind: 'text',
    value: 'Custom field will show on card fronts.',
  },
  'boardMenu.customFields.hideOnFront': {
    kind: 'text',
    value: 'Custom field removed from card fronts.',
  },
  'boardMenu.customFields.deleted': {
    kind: 'text',
    value: 'Custom field deleted.',
  },
  'boardMenu.customFields.reordered': {
    kind: 'text',
    value: 'Custom field reordered.',
  },
  'boardMenu.customFields.optionAdded': {
    kind: 'text',
    value: 'Custom field option added.',
  },
  'boardMenu.customFields.optionRenamed': {
    kind: 'text',
    value: 'Custom field option renamed.',
  },
  'boardMenu.customFields.optionDeleted': {
    kind: 'text',
    value: 'Custom field option deleted.',
  },
  'boardMenu.customFields.optionReordered': {
    kind: 'text',
    value: 'Custom field option reordered.',
  },
  'boardMenu.leave.title': { kind: 'text', value: 'Leave board?' },
  'boardMenu.leave.description': {
    kind: 'text',
    value:
      'Leaving removes your board membership and unassigns you from every card on this board. Items you authored stay in place.',
  },
  /* FE-80.1 board-menu announcements — the eight live-region sentences the
     automation and copy-board paths still composed in English with a backtick
     template, inside a menu whose chrome was routed long ago. They were the
     last `announce(` sites in the file that did not go through `t`.

     ⚠ THE `t('` ANCHOR WAS BLIND TO ALL EIGHT. They are arguments to
     `announce()`, not to `t()`, so every count guard in this workstream read
     them as absent and every routing pin was green over them. That is why
     `board-menu-panels.routing.test.ts` asserts these nine BY NAME and
     additionally forbids `announce(\`` from returning to the file at all: the
     by-name half cannot see a sentence recomposed beside a routed call, and
     the source-level half cannot see a key that is declared and never asked
     for.

     ⚠ EVERY INTERPOLATED VALUE IS USER DATA AND STAYS A SLOT.
     `command.name`, `lifecycle.name` and `result.board.title` are board
     content. `{name} deleted.` LEADS with its slot because the English does,
     and that is the shape a language with a different word order needs — the
     one thing `` `${name} deleted.` `` can never give it.

     ⚠ SEVEN VERBS, SEVEN KEYS. `created` / `updated` / `enabled` / `disabled`
     / `copied` / `adminDisabled` are six English participles with no
     guaranteed shared inflection anywhere else, and `deleted` is a seventh
     sentence again — it names the thing where the other six name its kind.

     ⚠ AND THE `Automation` FALLBACK IS NOT DECLARED HERE. It reuses
     `boardMenu.automation` above. `board-dashboard.routing.test.ts:511`
     asserts `Automation` has exactly five authors, in a pin file this slice
     may not edit; a tenth key carrying those bytes would turn it red on
     `main` after the merge. The word is the same noun on the same panel — the
     menu row that opens this section — so the reuse is right on the merits as
     well as by the pin. `Power-Up` has no author and no pin, so it gets its
     own key, in the shape `boardMenu.memberFallback` already set. */
  'boardMenu.announce.automation.created': {
    kind: 'text',
    value: 'Automation {name} created.',
  },
  'boardMenu.announce.automation.updated': {
    kind: 'text',
    value: 'Automation {name} updated.',
  },
  'boardMenu.announce.automation.enabled': {
    kind: 'text',
    value: 'Automation {name} enabled.',
  },
  'boardMenu.announce.automation.disabled': {
    kind: 'text',
    value: 'Automation {name} disabled.',
  },
  'boardMenu.announce.automation.deleted': { kind: 'text', value: '{name} deleted.' },
  'boardMenu.announce.automation.copied': {
    kind: 'text',
    value: 'Automation {name} copied.',
  },
  'boardMenu.announce.automation.adminDisabled': {
    kind: 'text',
    value: 'Automation {name} disabled by administrator.',
  },
  'boardMenu.announce.boardCopied': { kind: 'text', value: 'Board {title} copied.' },
  'boardMenu.powerUpFallback': { kind: 'text', value: 'Power-Up' },
  /* FE-80.1 board-menu panels — the three panels `BoardMenuPanel.tsx` composes
     (:139, :150, :153) and the last dense English left inside an already-routed
     menu. `BoardSettingsSection.tsx` (533 lines), `ArchivedItemsPanel.tsx`
     (453) and `BoardMemberList.tsx` (376) held 1,362 lines and ZERO `t(` call
     sites between them, so every one of these panels spoke English while the
     `boardMenu.*` chrome around it spoke the catalogue.

     ⚠ EVERY `— change X` NAME IS A WHOLE SLOTTED VALUE, NEVER A FRAME PLUS A
     LOWERCASED FRAGMENT. The source built all seven as
     `${triggerLabel} — change ${heading.toLowerCase()}`.
     `profile.connectedTools.scopeTrigger` already settled the join half — *a
     join is not translatable; word order and the separator are both
     language-specific* — and `.toLowerCase()` stacks a second trap on top of
     it: German capitalises nouns and Turkish lowercases `I` to a dotless `ı`,
     so the lowercase form has to be the translator's choice, written into the
     value. The English is byte-identical to what the join rendered;
     `FR-PERM-025:105`, `FR-PERM-027:531` and `FR-CARD-033:78,84` bind it.

     ⚠ `boardSettings.checking` is ONE key, and the discriminator is *would a
     locale need to tell these apart*, NOT *can they co-render*. `Checking…`
     renders on four setting rows at once; the co-render test would split it
     four ways, which is wrong — it is one word for one state on parallel
     controls. (The board-table slice's two `Cancel` keys were right for the
     other reason: a composer's cancel and a rename form's are plausibly
     different words in some language. These four are not.)

     ⚠ `Off` IS DECLARED TWICE, deliberately. `boardSettings.off` is a boolean
     switch's state; `boardSettings.cardAging.option.off` is a mode's name.
     They co-render — the self-join switch reads `Off` while the card-aging
     menu is open — and they are not the same word in every language.

     ⚠ SO ARE `Disabled` / `Members` / `Members and observers` /
     `Workspace members`, once under `commenting.*` and once under
     `votingPermission.*`. `boardTable.heading`'s ruling, four times over: they
     are members of two INDEPENDENT API enums (`CommentingPermission`,
     `VotingPermission`), each sits beside an already-different description
     (`…can comment.` against `…can vote.`), and `FR-CARD-033:75` /
     `FR-PERM-026:69` each bind `/^Members and observers/` inside a DIFFERENT
     menu. A locale that must inflect the option for its verb has to be able to.

     ⚠ ONLY THE RECORD VALUES ARE ROUTED. Every record KEY (`admins`,
     `all_members`, `disabled`, `members`, `members_and_observers`,
     `workspace_members`, `any_user`, `off`, `regular`, `pirate`) and every
     member of `COMMENTING_OPTIONS` / `VOTING_OPTIONS` / `CARD_AGING_OPTIONS`
     is an API enum member the section's `onValueChange` casts straight back
     (`value as CommentingPermission`) — literal in the request, routed only
     where it is DISPLAYED.

     ⚠ `SettingMenu` used to pass `id: heading`, so a display sentence became
     part of a DOM id (`Menu.tsx:194`, `` `${baseId}-${section.id}-heading` ``)
     and a React key. Inert today — `aria-labelledby` is wired only when
     `section.heading` is set and `SettingMenu` never sets it — but routing
     `heading` without splitting that prop would have translated a DOM id. The
     component now takes a `sectionId` ASCII slug beside an `accessibleName`,
     the shape `SettingSwitch` already had.

     REUSED, NOT DECLARED: `boardMenu.reason.adminPermissionSettings`.
     `BoardMenuPanel.tsx:732` already resolves that exact sentence into the
     `permissionDisabledReason` prop this file falls back from, so declaring it
     again is the `boardTable.viewLabel` defect — the composer-supplied branch
     speaks the translation, the `??` branch speaks English, nothing red. */
  'boardSettings.heading.visibility': { kind: 'text', value: 'Visibility' },
  'boardSettings.heading.memberPermission': {
    kind: 'text',
    value: 'Who can add and remove members',
  },
  'boardSettings.heading.commenting': {
    kind: 'text',
    value: 'Commenting permission',
  },
  'boardSettings.heading.voting': { kind: 'text', value: 'Voting' },
  'boardSettings.heading.votingPermission': { kind: 'text', value: 'Who can vote' },
  'boardSettings.heading.votingTallies': {
    kind: 'text',
    value: 'Vote tally visibility',
  },
  'boardSettings.heading.cardAging': { kind: 'text', value: 'Card aging' },
  'boardSettings.heading.selfJoin': {
    kind: 'text',
    value: 'Workspace members may join and edit',
  },
  /* The seven whole trigger names. ⚠ `trigger.cardAging` says `card aging
     mode` where `heading.cardAging` says `Card aging` — the section's `<h3>`
     and the menu's own heading prop were two different strings in the source
     and both are bound by tests, so the asymmetry is preserved rather than
     tidied. */
  'boardSettings.trigger.memberPermission': {
    kind: 'text',
    value: '{label} — change who can add and remove members',
  },
  'boardSettings.trigger.commenting': {
    kind: 'text',
    value: '{label} — change commenting permission',
  },
  'boardSettings.trigger.voting': {
    kind: 'text',
    value: '{label} — change voting',
  },
  'boardSettings.trigger.votingPermission': {
    kind: 'text',
    value: '{label} — change who can vote',
  },
  'boardSettings.trigger.votingTallies': {
    kind: 'text',
    value: '{label} — change vote tally visibility',
  },
  'boardSettings.trigger.cardAging': {
    kind: 'text',
    value: '{label} — change card aging mode',
  },
  'boardSettings.trigger.selfJoin': {
    kind: 'text',
    value: '{label} — change whether workspace members may join and edit',
  },
  'boardSettings.checking': { kind: 'text', value: 'Checking…' },
  'boardSettings.heading.cardNumbers': {
    kind: 'text',
    value: 'Card references on card fronts',
  },
  'boardSettings.trigger.cardNumbers': {
    kind: 'text',
    value: '{label} — change whether card references show on card fronts',
  },
  'boardSettings.cardNumbers.note': {
    kind: 'text',
    value: 'Shows each card’s reference in small text before its title.',
  },
  'boardSettings.heading.cardKey': { kind: 'text', value: 'Card reference key' },
  'boardSettings.cardKey.label': { kind: 'text', value: 'Board key' },
  'boardSettings.cardKey.save': { kind: 'text', value: 'Save key' },
  // PR-list approvals (owner-directed, no requirement).
  'boardSettings.heading.requiredApprovals': {
    kind: 'text',
    value: 'Approvals required',
  },
  'boardSettings.requiredApprovals.label': {
    kind: 'text',
    value: 'Approvals required',
  },
  'boardSettings.requiredApprovals.save': { kind: 'text', value: 'Save' },
  'boardSettings.requiredApprovals.invalid': {
    kind: 'text',
    value: 'Enter a whole number from 1 to 20.',
  },
  'boardSettings.requiredApprovals.note': {
    kind: 'text',
    value:
      'How many approvals a card in the PR list needs before it counts as approved. Rejections are shown but do not block it.',
  },
  'boardMenu.announce.requiredApprovals.changed': {
    kind: 'text',
    value: 'Approvals required set to {count}',
  },
  // Dangerous comments (owner-directed, no requirement).
  'boardSettings.heading.dangerousComments': {
    kind: 'text',
    value: 'Dangerous comments',
  },
  'boardSettings.dangerousComments.label': {
    kind: 'text',
    value: 'Number of dangerous comments',
  },
  'boardSettings.dangerousComments.save': { kind: 'text', value: 'Save limit' },
  'boardSettings.dangerousComments.invalid': {
    kind: 'text',
    value: 'Enter a whole number from 1 to 10000, or leave empty.',
  },
  'boardSettings.dangerousComments.note': {
    kind: 'text',
    value:
      'Cards with at least this many comments are highlighted. Leave empty to turn off.',
  },
  'boardMenu.announce.dangerousComments.changed': {
    kind: 'text',
    value: 'Dangerous comments set to {count}',
  },
  'boardMenu.announce.dangerousComments.cleared': {
    kind: 'text',
    value: 'Dangerous comments turned off',
  },
  'board.card.badge.dangerousComments': { kind: 'text', value: 'Many comments' },
  'boardSettings.cardKey.note': {
    kind: 'text',
    value:
      'Every card reference on this board starts with this key. References copied before a change keep working.',
  },
  'boardSettings.cardKey.taken': {
    kind: 'text',
    value: 'Another board already uses this key. Choose a different one.',
  },
  'boardSettings.cardKey.invalid': {
    kind: 'text',
    value: 'Use 2 to 10 letters or digits, starting with a letter.',
  },
  'boardSettings.on': { kind: 'text', value: 'On' },
  'boardSettings.off': { kind: 'text', value: 'Off' },
  'boardSettings.tallies.hidden': { kind: 'text', value: 'Counts hidden' },
  'boardSettings.tallies.visible': { kind: 'text', value: 'Counts visible' },
  'boardSettings.reason.checkingSetting': {
    kind: 'text',
    value: 'Checking this board setting…',
  },
  /* ⚠ ONE key at TWO source sites, and one of the two is unreachable. The
     `selfJoinReason` ternary's `selfJoinOffered === false` arm feeds
     `selfJoinDisabledReasonProps`, which is spread only onto the Button that
     renders when `selfJoinOffered` is TRUE; the `<p>` is the one a reader
     actually sees. Both are routed to this key rather than one being deleted,
     so the dead arm cannot drift into a second language, and so the count
     guard can still see it. */
  'boardSettings.selfJoin.privateBoard': {
    kind: 'text',
    value: 'Workspace members cannot self-join a private board.',
  },
  'boardSettings.memberPermission.option.admins': {
    kind: 'text',
    value: 'Board admins only',
  },
  'boardSettings.memberPermission.option.allMembers': {
    kind: 'text',
    value: 'Any board member',
  },
  'boardSettings.memberPermission.description.admins': {
    kind: 'text',
    value: 'Only board admins may add or remove board members.',
  },
  'boardSettings.memberPermission.description.allMembers': {
    kind: 'text',
    value: 'Board admins and normal members may add or remove board members.',
  },
  'boardSettings.commenting.option.disabled': { kind: 'text', value: 'Disabled' },
  'boardSettings.commenting.option.members': { kind: 'text', value: 'Members' },
  'boardSettings.commenting.option.membersAndObservers': {
    kind: 'text',
    value: 'Members and observers',
  },
  'boardSettings.commenting.option.workspaceMembers': {
    kind: 'text',
    value: 'Workspace members',
  },
  'boardSettings.commenting.option.anyUser': {
    kind: 'text',
    value: 'Any signed-in user',
  },
  'boardSettings.commenting.description.disabled': {
    kind: 'text',
    value: 'Nobody can post new comments, including admins.',
  },
  'boardSettings.commenting.description.members': {
    kind: 'text',
    value: 'Board admins and normal members can comment.',
  },
  'boardSettings.commenting.description.membersAndObservers': {
    kind: 'text',
    value: 'Board admins, normal members and observers can comment.',
  },
  'boardSettings.commenting.description.workspaceMembers': {
    kind: 'text',
    value: 'Anyone in the workspace can comment.',
  },
  'boardSettings.commenting.description.anyUser': {
    kind: 'text',
    value: 'Anyone signed in can comment on this public board.',
  },
  'boardSettings.votingPermission.option.disabled': {
    kind: 'text',
    value: 'Disabled',
  },
  'boardSettings.votingPermission.option.members': { kind: 'text', value: 'Members' },
  'boardSettings.votingPermission.option.membersAndObservers': {
    kind: 'text',
    value: 'Members and observers',
  },
  'boardSettings.votingPermission.option.workspaceMembers': {
    kind: 'text',
    value: 'Workspace members',
  },
  'boardSettings.votingPermission.description.disabled': {
    kind: 'text',
    value: 'Nobody can vote, including admins.',
  },
  'boardSettings.votingPermission.description.members': {
    kind: 'text',
    value: 'Board admins and normal members can vote.',
  },
  'boardSettings.votingPermission.description.membersAndObservers': {
    kind: 'text',
    value: 'Board admins, normal members and observers can vote.',
  },
  'boardSettings.votingPermission.description.workspaceMembers': {
    kind: 'text',
    value: 'Anyone in the workspace can vote.',
  },
  'boardSettings.cardAging.option.off': { kind: 'text', value: 'Off' },
  'boardSettings.cardAging.option.regular': { kind: 'text', value: 'Regular' },
  'boardSettings.cardAging.option.pirate': { kind: 'text', value: 'Pirate' },
  'boardSettings.cardAging.description.off': {
    kind: 'text',
    value: 'Card fronts render normally.',
  },
  'boardSettings.cardAging.description.regular': {
    kind: 'text',
    value: 'Stale cards fade in place while staying readable.',
  },
  'boardSettings.cardAging.description.pirate': {
    kind: 'text',
    value: 'Stale cards use a torn-map treatment.',
  },
  'boardSettings.voting.note': {
    kind: 'text',
    value: 'Turning voting off hides vote controls and counts on cards.',
  },
  'boardSettings.votingTallies.note': {
    kind: 'text',
    value: 'Hidden tallies hide both counts and voter lists from everyone.',
  },
  'boardSettings.cardAging.note': {
    kind: 'text',
    value: 'Aging changes only how stale card fronts look; it never moves cards.',
  },
  /* ⚠ A CURLY apostrophe, verbatim from the source: `members’ edit access`.
     `FR-PERM-025` reads it back. */
  'boardSettings.selfJoin.note': {
    kind: 'text',
    value: 'Turning this off keeps existing board members’ edit access.',
  },
  /* FE-80.1 board-menu panels — `board/ArchivedItemsPanel.tsx` (453 lines,
     ZERO `t(` call sites before this slice), the archive drawer inside the
     already-routed board menu.

     ⚠ TWO REUSES, DECLARED NOWHERE HERE. `boardMenu.reason.editorArchive` is
     what `BoardMenuPanel.tsx:715` already resolves into the `disabledReason`
     prop this file's `??` falls back from, and `cardDisplayTitle(t, …)`
     (`lib/card-title.ts`) already owns `Untitled card` for every card surface.
     Re-declaring either would be the `boardTable.viewLabel` defect: composed,
     the panel speaks the translation; uncomposed, the fallback speaks English,
     and nothing goes red.

     ⚠ BUT `archivedItems.untitledList` IS DECLARED, and the refusal is the
     same rule read the other way. `myCards.untitledList` carries identical
     bytes and `myCards.*` is a ROUTE namespace this component must not reach
     into — precisely the ground `card-title.ts` records for refusing
     `myCards.untitledCard`. There is no `listDisplayTitle` leaf to reuse
     instead: `Untitled list` has five other unrouted authors (`HomePage:239`,
     `ArchiveDeleteConfirm:23`, `ListCopyDialog:54,266`, `BoardFace:3695`)
     across four files this slice does not own, so building the shared leaf
     with one caller would claim a node it cannot see. Returned as a residual.

     ⚠ THE FOUR `announce.*` SENTENCES ARE SECOND AUTHORS ON PURPOSE.
     `cardBack.announce.restored` and `cardBack.announce.deleted` carry
     byte-identical English. The merge close-out settled this exact shape for
     `Card {title} created from template.` — two surfaces each announcing the
     same event through the shared live region, each owning its own sentence —
     and reaching into `cardBack.*` from an archive panel is the route-namespace
     objection above again.

     ⚠ `sendToBoard` / `delete` / `restoreLabel` ARE ONE KEY AT TWO CALL SITES
     each: the cards branch and the lists branch of `type`, which are mutually
     exclusive and can never co-render. `deleteCardLabel` and `deleteListLabel`
     are TWO keys only because the English itself differs.
     `cardBack.archived.sendToBoard` / `.delete` stay separate — a card back's
     own un-archive button is a different node on a different surface.
     ⚠ `ArchiveRestoreDestinationDialog.tsx:63` is a THIRD author of `Send to
     board` and is NOT this slice's file. It is a real two-key question rather
     than a duplicate: `FR-EXP-010:651` clicks the ROW's button and `:657,660`
     then assert the DIALOG's, so the two are on screen at once. Returned.

     ⚠ `archivedItems.archivedOn` IS A WHOLE SLOTTED SENTENCE, not `Archived `
     plus a date — the source built it with a template literal, and a
     translator has to be able to move the date inside the sentence. The
     formatter behind `{date}` is still `toLocaleDateString(undefined, …)`,
     i.e. the BROWSER's locale rather than the user's; that is
     `lib/locale-formatting.ts`'s residual, unchanged here, and routing the
     sentence gives the later fix exactly one place to land. */
  'archivedItems.typeGroup': { kind: 'text', value: 'Archived item type' },
  'archivedItems.cards': { kind: 'text', value: 'Archived cards' },
  'archivedItems.lists': { kind: 'text', value: 'Archived lists' },
  'archivedItems.search': { kind: 'text', value: 'Search archived items' },
  'archivedItems.error.title': {
    kind: 'text',
    value: 'Archived items could not be loaded',
  },
  'archivedItems.error.body': { kind: 'text', value: 'Try opening the archive again.' },
  'archivedItems.retry': { kind: 'text', value: 'Try again' },
  'archivedItems.loading': { kind: 'text', value: 'Loading archived items…' },
  'archivedItems.empty.title': { kind: 'text', value: 'Nothing archived yet' },
  'archivedItems.empty.body': {
    kind: 'text',
    value: 'Cards and lists you archive from this board will appear here.',
  },
  'archivedItems.noMatch.title': { kind: 'text', value: 'No archived items match' },
  'archivedItems.noMatch.body': {
    kind: 'text',
    value: 'Clear the search to return to every archived item.',
  },
  'archivedItems.loadMore': { kind: 'text', value: 'Load more archived items' },
  'archivedItems.loadMoreBusy': { kind: 'text', value: 'Loading…' },
  'archivedItems.untitledList': { kind: 'text', value: 'Untitled list' },
  'archivedItems.archivedWithList': { kind: 'text', value: 'Archived with its list' },
  'archivedItems.archivedNoDate': { kind: 'text', value: 'Archived' },
  'archivedItems.archivedOn': { kind: 'text', value: 'Archived {date}' },
  'archivedItems.listArchived': {
    kind: 'text',
    value: 'The original list is archived.',
  },
  /* ⚠ NO `archivedItems.listCardCount`. The archived list row's card count
     REUSES `boardFace.list.cardCount` (`:1307`), which the list-header slice
     declared as ONE sentence for two surfaces — `ListHeader`'s expanded header
     and `ListColumn`'s collapsed rail — and pinned as the only declaration of
     those forms (`list-header.routing.test.ts:169`).

     This slice declared a second one first, and that pin went red at the full
     suite: the node is "how many cards this list holds", `boardFace.list.*`
     already names it, and the two are on screen AT ONCE — the archive panel
     opens over the board face, so a locale translating them independently
     would show one list counted two ways in the same frame. That is the
     `boardTable.viewLabel` defect with both authors visible together.

     The `myCards.untitledList` refusal one comment down does NOT apply here:
     `myCards.*` is a route namespace, while `boardFace.list.*` is the shared
     list leaf this panel's rows already are. The same reach is already made
     for `boardMenu.reason.editorArchive` and, through `cardDisplayTitle`, for
     `board.card.untitled`. */
  'archivedItems.restoreLabel': { kind: 'text', value: 'Send to board: {title}' },
  'archivedItems.sendToBoard': { kind: 'text', value: 'Send to board' },
  'archivedItems.delete': { kind: 'text', value: 'Delete' },
  'archivedItems.deleteCardLabel': {
    kind: 'text',
    value: 'Delete archived card: {title}',
  },
  'archivedItems.deleteListLabel': {
    kind: 'text',
    value: 'Delete archived list: {title}',
  },
  'archivedItems.reason.archiveFirst': {
    kind: 'text',
    value: 'Archive this card before deleting it permanently.',
  },
  'archivedItems.announce.cardRestored': {
    kind: 'text',
    value: 'Card {title} was sent to the board.',
  },
  'archivedItems.announce.listRestored': {
    kind: 'text',
    value: 'List {title} was sent to the board.',
  },
  'archivedItems.announce.cardDeleted': {
    kind: 'text',
    value: 'Deleted the archived card {title}.',
  },
  'archivedItems.announce.listDeleted': {
    kind: 'text',
    value: 'Deleted the archived list {title}.',
  },
  /* FE-80.1 board-menu panels — `board/BoardMemberList.tsx` (376 lines, ZERO
     `t(` call sites before this slice), the board menu's members section.

     ⚠ A ROLE NAME THAT IS ALSO AN API VALUE STAYS LITERAL IN THE REQUEST AND
     IS ROUTED ONLY WHERE IT IS DISPLAYED. `ASSIGNABLE_BOARD_ROLES`, every
     record KEY (`admin` / `normal` / `observer`), the `MenuItem.id` and the
     section's `value` are all the same `BoardRole` the handler casts straight
     back (`value as BoardRole`) into `PATCH /boards/:id/members/:userId`.
     Only the three DISPLAYED names and their three descriptions are routed.
     `normal` displays as `Member`, which is why the API spelling and the
     display spelling must not be allowed to converge.

     ⚠ `boardMembers.roleTrigger` IS A WHOLE SLOTTED SENTENCE, not a frame plus
     a role fragment. The source built it as
     `` `${roleLabel} — change ${name}'s role` ``: two slots, a separator and a
     possessive, none of which survives a language change intact.
     `profile.connectedTools.scopeTrigger` is the settled precedent, and
     `D4-BRIEF.md` §7 fixes these exact bytes. `FR-PERM-029:854` and
     `FR-PERM-032:826,845` read `boardMembers.remove` the same way.

     ⚠ `boardMembers.role.option.*` IS ONE KEY PER ROLE ACROSS THREE CALL
     SITES — the read-only span on a row with no control, the menu trigger's
     own label, and the radio item inside the menu. One role, three renderings
     of it; a second key for any of them would let a translation rename the
     control against the label beside it.

     ⚠ `{name}` IS USER DATA AND ARRIVES ALREADY RESOLVED. It comes from
     `profileName(member)` (`Avatar.tsx:155–172`), which is DELIBERATELY
     translator-free across ~45 callers and feeds search haystacks — its own
     `Member` / `Deleted account` fallbacks are the shared-primitives slice's
     residual, not this one's. What is routed here is the sentence around the
     name, never the name.

     ⚠ `boardMembers.you` is `(you)` WITH ITS PARENTHESES INSIDE THE VALUE.
     `FR-PERM-015:998,1008` reads it back whole, and a locale that marks the
     viewer differently — a different bracket, no bracket, a different word
     order against the name beside it — has to own the punctuation.

     ⚠ `guest` and `guestLabel` ARE TWO KEYS FOR ONE BADGE, and that is WCAG
     2.5.3 rather than duplication: `Guest` is the visible text and
     `Single-board guest` is the `aria-label` that replaces it. They are
     deliberately NOT prefix-related, because the visible word is already the
     head noun of the longer one and `FR-PERM-023:655,659` binds `Guest`
     `exact: true` — the accessible name has to stay a superset in meaning
     without the spec's exact match drifting.

     ⚠ The row's `rowError` is NOT ROUTED and must never be: it is the server's
     refusal, verbatim, keyed by user id (`FR-PERM-032`'s "explained where it
     happens"). The same goes for `addError`. */
  'boardMembers.role.option.admin': { kind: 'text', value: 'Admin' },
  'boardMembers.role.option.normal': { kind: 'text', value: 'Member' },
  'boardMembers.role.option.observer': { kind: 'text', value: 'Observer' },
  'boardMembers.role.description.admin': {
    kind: 'text',
    value: 'Can change board settings, roles and content.',
  },
  'boardMembers.role.description.normal': {
    kind: 'text',
    value: 'Can create, edit, move, archive and comment.',
  },
  'boardMembers.role.description.observer': {
    kind: 'text',
    value: 'Can read the board and comment only when commenting includes observers.',
  },
  'boardMembers.you': { kind: 'text', value: '(you)' },
  'boardMembers.guest': { kind: 'text', value: 'Guest' },
  'boardMembers.guestLabel': { kind: 'text', value: 'Single-board guest' },
  'boardMembers.remove': { kind: 'text', value: 'Remove {name} from this board' },
  'boardMembers.roleTrigger': {
    kind: 'text',
    value: "{role} — change {name}'s role",
  },
  'boardMembers.addLabel': { kind: 'text', value: 'Email or username' },
  'boardMembers.add': { kind: 'text', value: 'Add' },
  'boardFace.skipLists': { kind: 'text', value: 'Skip to lists' },
  'boardFace.skipViewContent': { kind: 'text', value: 'Skip to view content' },
  'boardFace.focus.enter': { kind: 'text', value: 'Focus' },
  'boardFace.focus.exit': { kind: 'text', value: 'Exit focus' },
  'boardFace.focus.enterLabel': { kind: 'text', value: 'Enter focus mode' },
  'boardFace.focus.exitLabel': { kind: 'text', value: 'Exit focus mode' },
  'boardFace.view.board': { kind: 'text', value: 'Board' },
  'boardFace.view.table': { kind: 'text', value: 'Table' },
  'boardFace.view.calendar': { kind: 'text', value: 'Calendar' },
  'boardFace.view.timeline': { kind: 'text', value: 'Timeline' },
  'boardFace.view.dashboard': { kind: 'text', value: 'Dashboard' },
  'boardFace.view.map': { kind: 'text', value: 'Map' },
  'boardFace.pinned.region': { kind: 'text', value: 'Pinned board views' },
  'boardFace.pinned.shortcut': {
    kind: 'text',
    value: '{view} pinned view shortcut',
  },
  'boardFace.pinned.moveLeft': {
    kind: 'text',
    value: 'Move {view} pinned view left',
  },
  'boardFace.pinned.moveRight': {
    kind: 'text',
    value: 'Move {view} pinned view right',
  },
  'boardFace.pinned.reordered': {
    kind: 'text',
    value: 'Pinned views reordered: {views}.',
  },
  'boardFace.powerUps.openCalendar': {
    kind: 'text',
    value: 'Open Calendar Power-Up',
  },
  'boardFace.powerUps.calendar': { kind: 'text', value: 'Calendar Power-Up' },
  'boardFace.labels.show': { kind: 'text', value: 'Show label names' },
  'boardFace.labels.hide': { kind: 'text', value: 'Hide label names' },
  'boardFace.undo': { kind: 'text', value: 'Undo last action' },
  'boardFace.redo': { kind: 'text', value: 'Redo last undone action' },
  'boardFace.watching': { kind: 'text', value: 'Watching {board}' },
  'boardFace.menu': { kind: 'text', value: 'Board menu' },
  'boardFace.filters.activeRegion': {
    kind: 'text',
    value: 'Active board filters',
  },
  'boardFace.filters.trigger.emptyLabel': {
    kind: 'text',
    value: 'Filter, 0 active',
  },
  'boardFace.filters.trigger.activeLabel': {
    kind: 'plural',
    forms: { one: 'Filtered, 1 active', other: 'Filtered, {count} active' },
  },
  'boardFace.filters.panel.title': { kind: 'text', value: 'Filter board' },
  'boardFace.filters.panel.close': {
    kind: 'text',
    value: 'Close filter panel',
  },
  'boardFace.filters.filter': { kind: 'text', value: 'Filter' },
  'boardFace.filters.filtered': { kind: 'text', value: 'Filtered' },
  'boardFace.filters.activeCount': {
    kind: 'plural',
    forms: { one: '1 active', other: '{count} active' },
  },
  'boardFace.filters.clearAllLabel': {
    kind: 'text',
    value: 'Clear all filters',
  },
  'boardFace.filters.clearAll': { kind: 'text', value: 'Clear all' },
  'boardFace.filters.panel.clear': { kind: 'text', value: 'Clear filter' },
  'boardFace.filters.panel.region': {
    kind: 'text',
    value: 'Filters for {board}',
  },
  'boardFace.filters.keyword.heading': { kind: 'text', value: 'Keyword' },
  'boardFace.filters.keyword.label': {
    kind: 'text',
    value: 'Filter by keyword',
  },
  'boardFace.filters.members.heading': { kind: 'text', value: 'Members' },
  'boardFace.filters.members.searchLabel': {
    kind: 'text',
    value: 'Filter members',
  },
  'boardFace.filters.members.none': { kind: 'text', value: 'No members' },
  'boardFace.filters.members.signInRequired': {
    kind: 'text',
    value: 'Sign in to filter by member.',
  },
  'boardFace.filters.members.assignedFallback': {
    kind: 'text',
    value: 'Assigned to 1 person',
  },
  'boardFace.filters.members.memberFallback': {
    kind: 'text',
    value: 'Member',
  },
  'boardFace.filters.labels.heading': { kind: 'text', value: 'Labels' },
  'boardFace.filters.labels.searchLabel': {
    kind: 'text',
    value: 'Filter labels',
  },
  'boardFace.filters.labels.none': { kind: 'text', value: 'No labels' },
  'boardFace.filters.labels.deleted': {
    kind: 'text',
    value: 'Deleted label',
  },
  'boardFace.filters.labels.unnamedColour': {
    kind: 'text',
    value: '{colour} label',
  },
  'boardFace.filters.activity.heading': { kind: 'text', value: 'Activity' },
  'boardFace.filters.activity.anyAge': {
    kind: 'text',
    value: 'Any activity age',
  },
  /*
   * `FR-EXP-033` AC2 — the one-time first-run tip, and the confirmation that
   * spends it. Three keys, declared together because they are one moment:
   * the tip asks for a drag, the confirmation acknowledges it, and the
   * `announce` line is the same acknowledgement for a screen reader.
   *
   * ⚠ Focus mode, this chunk's other half, declares NOTHING here —
   * `boardFace.focus.on` / `boardFace.focus.off` already exist and are reused
   * unchanged. Declaring a second author of the same two words is exactly the
   * defect the `FE-80.1` slices were run to remove.
   */
  'boardFace.welcomeTip.title': {
    kind: 'text',
    value: 'Try dragging a card',
  },
  'boardFace.welcomeTip.body': {
    kind: 'text',
    value:
      'Pick up the first card and drop it in the next list. That is how work moves across a board.',
  },
  'boardFace.welcomeTip.done': {
    kind: 'text',
    value: 'Nicely done — that is how work moves across a board.',
  },
  'boardFace.filters.activity.active_last_week': {
    kind: 'text',
    value: 'Active in the last week',
  },
  'boardFace.filters.activity.active_last_2_weeks': {
    kind: 'text',
    value: 'Active in the last two weeks',
  },
  'boardFace.filters.activity.active_last_4_weeks': {
    kind: 'text',
    value: 'Active in the last four weeks',
  },
  'boardFace.filters.activity.no_activity_last_4_weeks': {
    kind: 'text',
    value: 'Without recent activity',
  },
  'boardFace.filters.due.heading': { kind: 'text', value: 'Due date' },
  'boardFace.filters.due.no_due_date': {
    kind: 'text',
    value: 'No due date',
  },
  'boardFace.filters.due.overdue': { kind: 'text', value: 'Overdue' },
  'boardFace.filters.due.due_next_day': {
    kind: 'text',
    value: 'Due in the next day',
  },
  'boardFace.filters.due.due_next_week': {
    kind: 'text',
    value: 'Due in the next week',
  },
  'boardFace.filters.due.due_next_month': {
    kind: 'text',
    value: 'Due in the next month',
  },
  'boardFace.filters.completion.heading': {
    kind: 'text',
    value: 'Completion',
  },
  'boardFace.filters.completion.marked_complete': {
    kind: 'text',
    value: 'Marked as complete',
  },
  'boardFace.filters.completion.not_marked_complete': {
    kind: 'text',
    value: 'Not marked as complete',
  },
  'boardFace.filters.facet.member': { kind: 'text', value: 'Member' },
  'boardFace.filters.facet.label': { kind: 'text', value: 'Label' },
  'boardFace.filters.facet.due': { kind: 'text', value: 'Due' },
  'boardFace.filters.facet.completion': {
    kind: 'text',
    value: 'Completion',
  },
  'boardFace.filters.facet.activity': { kind: 'text', value: 'Activity' },
  'boardFace.filters.facet.keyword': { kind: 'text', value: 'Keyword' },
  'boardFace.filters.chip.member': { kind: 'text', value: 'Member: {value}' },
  'boardFace.filters.chip.label': { kind: 'text', value: 'Label: {value}' },
  'boardFace.filters.chip.due': { kind: 'text', value: 'Due: {value}' },
  'boardFace.filters.chip.completion': {
    kind: 'text',
    value: 'Completion: {value}',
  },
  'boardFace.filters.chip.activity': {
    kind: 'text',
    value: 'Activity: {value}',
  },
  'boardFace.filters.chip.keyword': {
    kind: 'text',
    value: 'Keyword: {value}',
  },
  'boardFace.filters.remove.value': {
    kind: 'text',
    value: 'Remove {value} filter',
  },
  'boardFace.filters.remove.keyword': {
    kind: 'text',
    value: 'Remove keyword filter',
  },
  // `FR-SEARCH-031` — the per-custom-field facets. The FIELD's name is member
  // data and is interpolated, never a key of its own: a board may define a
  // field called anything, so there is no catalogue entry to translate.
  'boardFace.filters.chip.customField': {
    kind: 'text',
    value: '{field}: {value}',
  },
  'boardFace.filters.remove.customField': {
    kind: 'text',
    value: 'Remove {field} filter {value}',
  },
  'boardFace.filters.customField.ticked': { kind: 'text', value: 'Ticked' },
  'boardFace.filters.customField.unticked': {
    kind: 'text',
    value: 'Not ticked',
  },
  'boardFace.filters.customField.deletedOption': {
    kind: 'text',
    value: 'Deleted option',
  },
  'boardFace.filters.customField.unknownField': {
    kind: 'text',
    value: 'Custom field',
  },
  'boardFace.filters.customField.containsLabel': {
    kind: 'text',
    value: '{field} contains',
  },
  'boardFace.filters.customField.equalsLabel': {
    kind: 'text',
    value: '{field} equals',
  },
  'boardFace.selection.region': { kind: 'text', value: 'Selected cards' },
  'boardFace.selection.count': {
    kind: 'plural',
    forms: { one: '1 card selected', other: '{count} cards selected' },
  },
  'boardFace.selection.limit': {
    kind: 'plural',
    forms: {
      one: 'You can select up to 1 card at once.',
      other: 'You can select up to {count} cards at once.',
    },
  },
  'boardFace.selection.optimistic': {
    kind: 'text',
    value: 'This card is still saving and cannot be selected yet.',
  },
  'boardFace.selection.crossListRange': {
    kind: 'text',
    value:
      'Range selection stays within one list. Use Command or Control to add cards from another list.',
  },
  'boardFace.selection.archive': { kind: 'text', value: 'Archive' },
  'boardFace.selection.move': { kind: 'text', value: 'Move' },
  'boardFace.selection.copy': { kind: 'text', value: 'Copy' },
  'boardFace.selection.merge': { kind: 'text', value: 'Merge' },
  'boardFace.selection.clear': { kind: 'text', value: 'Clear' },
  'boardFace.selection.mergeDisabled': {
    kind: 'text',
    value: 'Select exactly two cards to merge.',
  },
  'boardFace.mobileLimitation.label': {
    kind: 'text',
    value: '{view} view limitation',
  },
  'boardFace.mobileLimitation.title': {
    kind: 'text',
    value: '{view} is available on the web',
  },
  'boardFace.mobileLimitation.body': {
    kind: 'text',
    value:
      '{view} is not supported on mobile. Open this exact board on web to use the full view.',
  },
  'boardFace.live.open': { kind: 'text', value: 'Connected to live updates.' },
  'boardFace.live.reconnecting': {
    kind: 'text',
    value: 'Reconnecting to live updates...',
  },
  'boardFace.live.refused': {
    kind: 'text',
    value: 'Live updates stopped. Reload to check access.',
  },
  'boardFace.lists': { kind: 'text', value: 'Lists' },
  'boardFace.list.cardStack': { kind: 'text', value: 'Cards in {list}' },
  /* ⚠ `plural`, and the `one` form is load-bearing — do NOT flatten this back
     to a `text` entry with a `{count} cards` template.

     The history, moved here from `ListHeader.tsx`'s `cardCountLabel` when the
     list-header slice deleted that function as a second author of this
     sentence. `C1-BRIEF.md` §7.3 fixed the accessible name as `<n> cards` and
     no pluralisation rule existed anywhere in the product, so the first build
     announced **"1 cards"** on every list holding exactly one. `Avatar.tsx`'s
     overflow label is the precedent for the spelling.

     ⚠ A call site must pass the count TWICE — `t(key, { count: n }, n)`.
     `interfaceMessage` selects `forms.other` whenever the third argument is
     absent or `!== 1`, so a two-argument call renders `1 cards` again with
     nothing red: the values object alone does not reach the arity selector. */
  'boardFace.list.cardCount': {
    kind: 'plural',
    forms: { one: '1 card', other: '{count} cards' },
  },
  /* FE-80.1 list composer — `board/ListComposer.tsx`, which had ZERO `t(` call
     sites and is on every board, and the one line of `board/BoardStates.tsx`
     that renders the SAME trigger copy.

     ⚠ `boardFace.list.add` is declared ONCE and routed from two files on
     purpose. `BoardEmpty`'s inert fallback button and the composer's real
     trigger are documented as the same affordance (`BoardStates.tsx` §
     `BoardEmptyProps.action`: "keeps getting the plain `Add a list` button C1
     shipped"), and six e2e specs bind to that one accessible name across both
     — `FR-VIEW-001`, `FR-BOARD-001`, `FR-TMPL-007`, `FR-PERM-009`/`010`,
     `FR-EXP-012`/`034`. Declaring a second key beside it is the second-author
     defect the undo slice spent a mutation proving: the two would translate
     independently and the empty board would disagree with the full one in
     every language but this one. */
  'boardFace.list.add': { kind: 'text', value: 'Add a list' },
  'boardFace.list.composer.name': { kind: 'text', value: 'List name' },
  'boardFace.list.composer.submit': { kind: 'text', value: 'Add list' },
  'boardFace.list.composer.cancel': { kind: 'text', value: 'Cancel' },
  'boardFace.list.addCard': { kind: 'text', value: 'Add a card' },
  'boardFace.list.addCardTo': {
    kind: 'text',
    value: 'Add a card to {list}',
  },
  'boardFace.list.addCardToTop': {
    kind: 'text',
    value: 'Add a card to the top of {list}',
  },
  'boardFace.list.gapInsertTitle': {
    kind: 'text',
    value: 'Double-click to add a list here',
  },
  /* FE-80.1 card composer — `board/CardComposer.tsx`, which had ZERO `t(` call
     sites while being the control every card on every board is created
     through. Named `boardFace.card.composer.*` to mirror
     `boardFace.list.composer.*` three keys up: the two composers are the same
     shape for the two subjects, and a reader who finds one finds the other.

     ⚠ `Card title` is declared a SEVENTH time product-wide and `Cancel` a
     TWENTIETH — both counted with `grep -c "value: 'Card title' }"` rather than
     carried over from the last return — and both are the settled
     duplicate-value ruling rather than an oversight. `inbox.cardTitle`,
     `boardFace.calendarCreate.titleLabel`, `quickEdit.titleLabel`,
     `cardBack.title.label`, `boardMap.cardTitle` and `boardMap.renameCardTitle`
     already declare `Card title` for six other surfaces, and
     `FR-BOARD-020.spec.ts` records that the composer's field and quick edit's
     field carry the same accessible name BY DESIGN ("both are a card's title"),
     scoping every query for either. Sharing one key across those namespaces
     would let one surface's translation silently rename a field on five
     others. Each family declares its own. */
  'boardFace.card.composer.name': { kind: 'text', value: 'Card title' },
  'boardFace.card.composer.submit': { kind: 'text', value: 'Add card' },
  'boardFace.card.composer.cancel': { kind: 'text', value: 'Cancel' },
  /* ⚠ NOT byte-identical to `cardBack.actions.createFromTemplate`, which reads
     `Create card from template` — a different sentence for the card back's own
     menu item, and the only place the two could be confused is a grep. */
  'boardFace.card.composer.fromTemplate': {
    kind: 'text',
    value: 'Create from template',
  },
  /* FE-80.1 list header — `board/ListHeader.tsx`, which had ZERO `t(` call
     sites while sitting at the top of every list on every board.

     ⚠ `boardFace.list.expand` is REUSED, not re-declared, and that is the
     shared-key half of this slice. `ListColumn.tsx:665` already routes it for
     the collapsed rail's real expand button; `ListHeader.tsx` was authoring the
     same sentence a second time as
     `` `${collapsed ? 'Expand' : 'Collapse'} ${list.title}` ``.

     Two whole sentences selected by a ternary, NOT a verb interpolated into a
     shared template. `Expand`/`Collapse` as fragments assembled around a name
     is precisely the shape `lib/undo.ts` was emptied of: a translator cannot
     move the name, cannot inflect the verb for the noun, and cannot see the
     two halves at once.

     ⚠ AND `boardFace.list.collapse` IS THE ONLY ONE OF THE PAIR THAT PRODUCTION
     EVER RENDERS. `ListColumn.tsx:382` passes `collapsed={false}` hard-coded —
     collapsing swaps the whole column for `ListColumn`'s own rail (its
     `if (collapsed)` early return at :646), which renders its own expand
     button. So `ListHeader`'s `Expand` half is unreachable today, the same
     inert-but-public shape as `BoardEmpty`'s fallback button one slice earlier.
     Routed anyway: an unreachable-today branch that renders English tomorrow is
     the same defect, discovered later. */
  'boardFace.list.expand': { kind: 'text', value: 'Expand {list}' },
  'boardFace.list.collapse': { kind: 'text', value: 'Collapse {list}' },
  'boardFace.list.reorder': { kind: 'text', value: 'Reorder {list}' },
  /* The `InlineEdit` label, and it is not free-standing copy: `InlineEdit`'s
     idle button appends `<span class="sr-only">, edit {label}</span>`, so this
     value is read as the tail of a sentence. It is also the string whose
     name-from-content folding forced the explicit `aria-label` on
     `ListHeader`'s `<h2>` — a column once announced itself as
     "Doing, edit Rename list". Changing this value changes that sentence. */
  'boardFace.list.rename': { kind: 'text', value: 'Rename list' },
  /* ⚠ NOT `boardFace.watching` (`Watching {board}`) two hundred lines up. Same
     English shape, different subject and a differently named slot — the same
     discriminator `boardFace.list.moved` and `boardFace.card.added` already
     follow. A translator whose language inflects the participle for the noun
     needs the two apart. */
  'boardFace.list.watching': { kind: 'text', value: 'Watching {list}' },
  'boardFace.list.actions': { kind: 'text', value: 'List actions for {list}' },
  /* The watch toggle's two states. Two keys rather than one with a slot: they
     are different sentences, and only one is ever on the item at a time. */
  'boardFace.list.watch': { kind: 'text', value: 'Watch this list' },
  'boardFace.list.unwatch': { kind: 'text', value: 'Unwatch this list' },
  /* PR-list designation. Two item labels (only one is on the menu at a time)
     rather than one with a slot, on the watch pair's footing. */
  'boardFace.list.setPrList': { kind: 'text', value: 'Set as PR list' },
  'boardFace.list.removePrList': { kind: 'text', value: 'Remove PR list' },
  'boardFace.list.prListIcon': { kind: 'text', value: '{list} is the PR list' },
  'boardFace.list.prListSet': { kind: 'text', value: '{title} is now the PR list' },
  'boardFace.list.prListRemoved': {
    kind: 'text',
    value: '{title} is no longer the PR list',
  },
  'boardFace.list.prListFailed': {
    kind: 'text',
    value: 'Could not change the PR list',
  },
  /* `FR-BOARD-009` AC3's keyboard path into the composer the list-composer
     slice routed one slice earlier — the two belong to one vocabulary. */
  'boardFace.list.insertAfter': { kind: 'text', value: 'Add a list to the right' },
  'boardFace.list.move': { kind: 'text', value: 'Move this list' },
  'boardFace.list.copy': { kind: 'text', value: 'Copy this list' },
  'boardFace.list.operations': { kind: 'text', value: 'Sort and bulk actions' },
  'boardFace.list.archive': { kind: 'text', value: 'Archive this list' },
  'boardFace.list.powerUpActions': {
    kind: 'text',
    value: 'Power-Up actions for {list}',
  },
  'boardFace.list.colours': { kind: 'text', value: 'List colours for {list}' },
  'boardFace.list.noFilterMatches': {
    kind: 'text',
    value: 'No cards match this filter.',
  },

  /* FE-80.1 board face — `board/BoardFace.tsx`, the largest single file in
     `apps/web` and the last unrouted one on the board frame.

     ⚠ THE DYNAMIC KEYS ARE THE TRAP HERE. Three families are built by
     interpolation rather than written as literals — `boardFace.view.{kind}`
     (`boardViewMessageKey`), `boardFace.live.{status}`, and nothing else,
     BY DESIGN. A source-reading pin anchored on `t('…')` cannot see a key it
     never spells, so its per-file count guard silently undercounts and an
     undeclared member of the family is invisible. `board-face.routing.test.ts`
     closes that with an ENUMERATION assert over the two unions instead, and the
     five view blocks below deliberately take a `{view}` SLOT rather than
     becoming a third interpolated family. */

  // The five view blocks (Table, Calendar, Timeline, Dashboard, Map) are
  // structurally identical, so they share three keys with a `{view}` slot
  // filled from `boardFace.view.*` rather than spelling twenty. That is also
  // what fixes the board-frame slice's finding 5: `Loading Map...` was the
  // first English string a person saw when opening the Map view.
  'boardFace.viewRegion': { kind: 'text', value: '{view} view' },
  'boardFace.viewError': { kind: 'text', value: '{view} could not load' },
  'boardFace.viewLoading': { kind: 'text', value: 'Loading {view}...' },
  // ONE key at seven sites — the six view blocks and the Power-Up dialog. They
  // are the same control saying the same thing about the same kind of failure,
  // and the settled discriminator (two keys when both can be on screen at once)
  // does not apply: only one of the seven is ever mounted.
  'boardFace.retry': { kind: 'text', value: 'Try again' },

  'boardFace.reopenRefused': {
    kind: 'text',
    value: 'Only a board admin can reopen this board.',
  },

  // ⚠ The ONE deliberate copy change in this slice, and it is one character.
  // `BoardFace` spelled this with three full stops where its two other authors
  // — `lib/boards.ts`'s `CHECKING_REASON` and the already-routed
  // `boardMenu.reason.checkingBoardAccess` — spell it with U+2026. Two keys
  // whose only difference is a typo enshrines the typo for every translator, so
  // the ellipsis is unified here rather than preserved. The value is a
  // deliberate duplicate of `boardMenu.reason.checkingBoardAccess`: that key is
  // the board MENU's, and a namespace this file must not reach into.
  'boardFace.template.checkingAccess': {
    kind: 'text',
    value: 'Checking what you can do on this board…',
  },
  'boardFace.template.closedReason': {
    kind: 'text',
    value: 'Reopen this board before changing template settings.',
  },
  'boardFace.template.adminReason': {
    kind: 'text',
    value: 'Only a board admin can manage this template.',
  },
  // Three full stops KEPT here, unlike the key above: this sentence has exactly
  // one author, so there is no second spelling to unify with and changing it
  // would be a copy decision rather than a routing one.
  'boardFace.template.checkingStatus': {
    kind: 'text',
    value: 'Checking template status...',
  },
  // ⚠ The `{visibility}` and `{category}` slots are filled from
  // `BoardVisibilityControl`'s `VISIBILITY_LABELS` and
  // `TemplateVisibilityControls`' `TEMPLATE_CATEGORY_LABELS` — the two tables
  // that already own those nouns — rather than from a fourth English author in
  // this file. `BoardFace` had been building them with
  // `value.replace('_', ' ')`, which produced `project management` where the
  // control that had just been used said `Project management`. Those tables are
  // still unrouted; they are the next vocabulary slice, named in the returns.
  'boardFace.template.visibilitySet': {
    kind: 'text',
    value: 'Template visibility is now {visibility}.',
  },
  'boardFace.template.categoryCleared': {
    kind: 'text',
    value: 'Template gallery category cleared.',
  },
  'boardFace.template.categorySet': {
    kind: 'text',
    value: 'Template gallery category set to {category}.',
  },

  'boardFace.calendarCreate.title': {
    kind: 'text',
    value: 'Create card on Calendar',
  },
  'boardFace.calendarCreate.description': {
    kind: 'text',
    value: 'The new card will be due on {date}.',
  },
  'boardFace.calendarCreate.cancel': { kind: 'text', value: 'Cancel' },
  'boardFace.calendarCreate.submit': { kind: 'text', value: 'Create card' },
  'boardFace.calendarCreate.titleLabel': { kind: 'text', value: 'Card title' },
  'boardFace.calendarCreate.listLabel': { kind: 'text', value: 'List' },
  'boardFace.calendarCreate.titleRequired': {
    kind: 'text',
    value: 'Name the card before creating it.',
  },
  'boardFace.calendarCreate.listRequired': {
    kind: 'text',
    value: 'Choose a list for the new card.',
  },
  'boardFace.calendarCreate.failed': {
    kind: 'text',
    value: 'Could not create the calendar card.',
  },

  'boardFace.paste.capacityLimit': {
    kind: 'text',
    value:
      'This board is at the open cards limit of {limit}. Archive cards before adding more.',
  },
  'boardFace.paste.failed': {
    kind: 'text',
    value: 'Could not create cards from that paste.',
  },

  // ⚠ A CONDITIONAL PAIR whose arms carry different slots, which is the shape
  // that crashes rather than reading wrong: `interfaceMessage` THROWS on a slot
  // it is given no value for, so `syncFailedWithReason`'s `{reason}` cannot be
  // folded into `syncFailed`. Both arms are reached by fixtures.
  'boardFace.offline.synced': { kind: 'text', value: 'Offline changes synced.' },
  'boardFace.offline.syncedWithRejections': {
    kind: 'text',
    value: 'Some offline changes could not sync. The saved text is shown on the board.',
  },
  'boardFace.offline.syncFailed': {
    kind: 'text',
    value: 'Offline changes could not sync.',
  },
  'boardFace.offline.syncFailedWithReason': {
    kind: 'text',
    value: 'Offline changes could not sync. {reason}',
  },
  'boardFace.offline.queuedMove': {
    kind: 'text',
    value: 'Card {title} moved. It will sync when this device is back online.',
  },
  'boardFace.offline.rejectedTitle': {
    kind: 'text',
    value: 'Could not save {title}',
  },
  'boardFace.offline.rejectedBody': {
    kind: 'text',
    value: 'The card is no longer available. Your unsent description was:',
  },
  // TWO `Dismiss` keys in one file, and this is the discriminator earning its
  // keep rather than an oversight: the undo toast and a rejected-description
  // notice are both header children and can be on screen at the same time.
  'boardFace.offline.dismiss': { kind: 'text', value: 'Dismiss' },
  'boardFace.undo.dismiss': { kind: 'text', value: 'Dismiss' },

  'boardFace.focus.on': { kind: 'text', value: 'Focus mode on.' },
  'boardFace.focus.off': { kind: 'text', value: 'Focus mode off.' },
  'boardFace.lists.allCollapsed': { kind: 'text', value: 'All lists collapsed.' },
  'boardFace.lists.allExpanded': { kind: 'text', value: 'All lists expanded.' },

  // Replaces an inline `n === 1 ? 'card' : 'cards'` ternary at two call sites.
  'boardFace.filter.showing': {
    kind: 'plural',
    forms: {
      one: 'Board filter showing {count} card.',
      other: 'Board filter showing {count} cards.',
    },
  },
  'boardFace.filter.cleared': { kind: 'text', value: 'Board filter cleared.' },

  // The fallback `dragAnnouncements`' resolver returns for an id that matches
  // neither a list nor a card. Lower-case and article-bearing because it is
  // spliced into `dnd.pickedUp` and its five siblings.
  'boardFace.drag.item': { kind: 'text', value: 'an item' },

  // ⚠ `text`, not `plural`, and the singular's absence is structural rather
  // than an oversight: the caller announces the ONE move's own sentence when
  // exactly one card moved, so this key is only ever reached with a count
  // greater than one. A `plural` declaration would carry an `one` form that no
  // call site can reach, which is the shape the kind audit exists to catch.
  'boardFace.remote.cardsMoved': {
    kind: 'text',
    value: '{count} cards moved on this board.',
  },
  'boardFace.remote.cardMoved': {
    kind: 'text',
    value: 'Card {title} moved to {list}, position {position}.',
  },

  'boardFace.card.moved': { kind: 'text', value: 'Card {title} moved.' },
  'boardFace.card.movedToList': {
    kind: 'text',
    value: 'Card {title} moved to {list}.',
  },
  'boardFace.card.movedInList': {
    kind: 'text',
    value: 'Card {title} moved in {list}.',
  },
  // The `{list}` slot's own fallback, for a drop whose destination list cannot
  // be resolved from the local cache. A separate key rather than a second arm,
  // because the sentence is unchanged and only the noun is missing.
  'boardFace.card.destinationFallback': { kind: 'text', value: 'the list' },
  'boardFace.card.archived': { kind: 'text', value: 'Card {title} archived.' },
  'boardFace.card.markedComplete': { kind: 'text', value: 'Card marked complete.' },
  'boardFace.card.markedIncomplete': {
    kind: 'text',
    value: 'Card marked incomplete.',
  },
  'boardFace.card.renamed': { kind: 'text', value: 'Card renamed to {title}.' },
  'boardFace.card.copied': { kind: 'text', value: 'Card {title} copied.' },
  'boardFace.card.added': { kind: 'text', value: 'Card {title} added.' },
  /* FE-80.1 card composer — the spoken OUTCOME of the composer's
     `Create from template` button, which `ListColumn.tsx:327` authored as
     `` announce(`Card ${result.card.title} created from template.`) ``.
     Declared HERE beside `.added` rather than beside the button's own label, on
     the precedent `boardFace.list.added` set one slice earlier: an
     announcement's siblings are the other announcements about the same subject,
     not the control that triggers it.

     ⚠ BYTE-IDENTICAL TO `cardBack.announce.createdFromTemplate`, and that is a
     deliberate refusal to share rather than a duplicate left behind — the same
     ruling `board.card.untitled` records against `myCards.untitledCard`.
     `cardBack.*` is the card back's route namespace and a board-face component
     must not reach into it; the two are different surfaces with different
     owners, reached by different gestures. The routing pin asserts the two
     EQUAL, so a later slice that diverges them does it deliberately rather than
     discovering it in a screenshot. */
  'boardFace.card.createdFromTemplate': {
    kind: 'text',
    value: 'Card {title} created from template.',
  },
  'boardFace.card.merged': {
    kind: 'text',
    value: 'Cards {target} and {source} merged into {result}.',
  },
  'boardFace.list.moved': { kind: 'text', value: 'List {title} moved.' },
  'boardFace.list.copied': { kind: 'text', value: 'List {title} copied.' },
  /* ⚠ Declared HERE rather than beside the composer's other four keys, because
     this is where its siblings are: `boardFace.card.added` two lines up is the
     same sentence about the other subject, and `.moved`/`.copied` are the same
     subject in the other two verbs. `ListComposer.tsx` built it as
     `` announce(`List ${trimmedTitle} added.`) `` — an interpolated sentence of
     exactly the shape `lib/undo.ts` was emptied of, and the reason a slot
     (`{title}`) is what a translator receives rather than two fragments to
     reassemble around a name they cannot move. */
  'boardFace.list.added': { kind: 'text', value: 'List {title} added.' },
  /* FE-80.1 list header — the three announcements `ListHeader.tsx` built as
     interpolated templates, declared HERE with their siblings above rather than
     beside the header's menu labels, on the precedent the list-composer slice
     set for `.added`. */
  'boardFace.list.archived': { kind: 'text', value: 'List {title} archived.' },
  /* ⚠ THE UNDO LABEL IS *NOT* DECLARED HERE. It is `undo.source.listArchived`,
     beside `undo.source.listMoved` in the family every other undo label already
     uses — see the comment there for why the two are separate keys at all. */
  'boardFace.list.colourCleared': {
    kind: 'text',
    value: 'List colour cleared for {title}.',
  },
  /* ⚠ A PARTIAL ROUTE, NAMED SO IT IS NOT MISTAKEN FOR A FINISHED ONE. The
     sentence is routed; `{colour}` is filled by `listColourLabel()`, whose
     vocabulary lives in `lib/list-colours.ts` and belongs to the label/colour/
     template slice that still needs a human to ratify `palette.ts`. Until then
     this renders a translated sentence around an English colour name. */
  'boardFace.list.recoloured': {
    kind: 'text',
    value: 'List {title} changed to {colour}.',
  },
  'boardFace.board.renamed': { kind: 'text', value: 'Board renamed to {title}.' },
  'boardFace.board.createdFromTemplate': {
    kind: 'text',
    value: 'Board {title} created from template.',
  },

  'boardFace.table.cardMoved': { kind: 'text', value: 'Card moved from Table view.' },
  'boardFace.table.cardCreated': {
    kind: 'text',
    value: 'Card {title} created from Table view.',
  },
  'boardFace.dueDate.removed': { kind: 'text', value: 'Due date removed.' },
  'boardFace.dueDate.updated': { kind: 'text', value: 'Due date updated.' },

  'boardFace.calendar.cardCreated': {
    kind: 'text',
    value: 'Card {title} created on the calendar.',
  },
  'boardFace.calendar.rescheduled': { kind: 'text', value: 'Card {title} rescheduled.' },
  'boardFace.calendar.rangeUpdated': {
    kind: 'text',
    value: 'Card {title} date range updated.',
  },
  'boardFace.calendar.rangeRefused': {
    kind: 'text',
    value: 'Calendar range refused because the due date would be before the start date.',
  },

  'boardFace.timeline.movedToLane': {
    kind: 'text',
    value: 'Card {title} moved to {lane}.',
  },
  'boardFace.timeline.moved': {
    kind: 'text',
    value: 'Card {title} moved on Timeline.',
  },
  'boardFace.timeline.rangeUpdated': {
    kind: 'text',
    value: 'Card {title} Timeline range updated.',
  },
  'boardFace.timeline.rangeRefused': {
    kind: 'text',
    value: 'Timeline range refused because the due date would be before the start date.',
  },
  'boardFace.timeline.scheduled': { kind: 'text', value: 'Card scheduled on Timeline.' },

  // The second conditional pair whose arms differ by a slot. Same crash shape
  // as `boardFace.offline.syncFailed*`, same two-fixture rule.
  'boardFace.dashboard.updateFailed': {
    kind: 'text',
    value: 'Dashboard tile update failed.',
  },
  'boardFace.dashboard.updateFailedWithReason': {
    kind: 'text',
    value: 'Dashboard tile update failed. {reason}',
  },
  'boardFace.dashboard.tileAdded': { kind: 'text', value: 'Dashboard tile added.' },
  'boardFace.dashboard.tileUpdated': { kind: 'text', value: 'Dashboard tile updated.' },
  'boardFace.dashboard.tileRemoved': { kind: 'text', value: 'Dashboard tile removed.' },

  'boardFace.map.cardCreated': {
    kind: 'text',
    value: 'Card {title} created from Map view.',
  },

  'boardFace.automation.stripLabel': {
    kind: 'text',
    value: 'Board automation buttons',
  },
  'boardFace.automation.disabledReason': {
    kind: 'text',
    value: 'Only a member who can edit this board can run board buttons.',
  },

  'boardFace.powerUps.calendarOpened': {
    kind: 'text',
    value: 'Calendar Power-Up opened.',
  },
  'boardFace.powerUps.notInteractive': {
    kind: 'text',
    value: '{powerUp}: {label} is not interactive yet.',
  },
  'boardFace.powerUps.headerAction': { kind: 'text', value: '{powerUp}: {label}' },
  'boardFace.powerUps.calendarDescription': {
    kind: 'text',
    value: 'A read-only month grid pinned to this board.',
  },
  'boardFace.powerUps.closeCalendar': {
    kind: 'text',
    value: 'Close Calendar Power-Up',
  },
  'boardFace.powerUps.calendarLoadError': {
    kind: 'text',
    value: 'Calendar Power-Up could not load',
  },
  'boardFace.powerUps.calendarLoading': {
    kind: 'text',
    value: 'Loading Calendar Power-Up...',
  },

  /* FE-80.1 board face — `board/QuickEdit.tsx`, which renders inside
     `BoardFace` and was taken in the same slice for that reason.

     ⚠ `quickEdit.archived` is byte-identical to `boardFace.card.archived` and
     they are still TWO keys, on the standing refusal to share across files that
     twelve slices have applied. Naming it here rather than leaving it to be
     rediscovered: the duplicate count is a measurement in the returns, not a
     defect. */
  'quickEdit.group': { kind: 'text', value: 'Quick edit: {title}' },
  'quickEdit.titleLabel': { kind: 'text', value: 'Card title' },
  'quickEdit.open': { kind: 'text', value: 'Open card' },
  'quickEdit.move': { kind: 'text', value: 'Move' },
  'quickEdit.copy': { kind: 'text', value: 'Copy' },
  'quickEdit.archive': { kind: 'text', value: 'Archive card' },
  'quickEdit.archived': { kind: 'text', value: 'Card {title} archived.' },

  /* FE-80.1 board face — `lib/dnd.ts`, the drag vocabulary every `DndContext`
     in the product inherits.

     ⚠ TWO authors reached it unrouted (`BoardFace` and
     `boards/BoardShortcutSection`), and making `t` a required first parameter
     of both exports is what forced the second one to be found and routed in the
     same commit rather than left English behind a green suite.

     ⚠ `dnd.instructions` was a template literal carrying newlines and source
     indentation. It is a single line here: the whitespace was an artefact of
     how the constant was written, every consumer passes it to
     `screenReaderInstructions` where runs of whitespace collapse, and a
     translator handed a value with leading indentation reproduces it. */
  'dnd.instructions': {
    kind: 'text',
    value:
      'To pick up an item, press space. While dragging, use the arrow keys to move it. ' +
      'Press space or enter again to drop it in its new position, or press escape to cancel.',
  },
  'dnd.pickedUp': { kind: 'text', value: 'Picked up {item}.' },
  'dnd.over': { kind: 'text', value: '{item} is over {target}.' },
  'dnd.notOver': { kind: 'text', value: '{item} is no longer over a drop target.' },
  'dnd.dropped': { kind: 'text', value: 'Dropped {item} onto {target}.' },
  'dnd.droppedOutside': {
    kind: 'text',
    value: 'Dropped {item}. It was returned to its original position.',
  },
  'dnd.cancelled': {
    kind: 'text',
    value: 'Cancelled. {item} was returned to its original position.',
  },
  // `boards/BoardShortcutSection.tsx`'s resolver fallback — the second author
  // `lib/dnd.ts`'s required `t` parameter dragged into the light. Its own
  // namespace rather than `boardFace.*`: the starred-boards grid is not the
  // board face, and the two fallbacks read differently on purpose (`an item`
  // covers a card OR a list; this one is always a board).
  'boardShortcuts.dragItem': { kind: 'text', value: 'board' },

  'card.notAvailable.title': { kind: 'text', value: "This card isn't available" },
  'card.notAvailable.body': {
    kind: 'text',
    value: 'You no longer have access, or this card is no longer available.',
  },
  'card.notAvailable.backToBoards': {
    kind: 'text',
    value: 'Back to your boards',
  },
  'publicProfile.title': { kind: 'text', value: 'Public profile' },
  'publicProfile.loading': { kind: 'text', value: 'Loading public profile' },
  'publicProfile.loadFailedTitle': {
    kind: 'text',
    value: 'Profile could not be loaded',
  },
  'publicProfile.tryAgainLater': { kind: 'text', value: 'Try again later.' },
  'publicProfile.visibleBoards': { kind: 'text', value: 'Visible boards' },
  'publicProfile.noSharedBoards': {
    kind: 'text',
    value: 'No shared boards to show.',
  },
  // The public-profile visible-board link fallback. Real board titles remain
  // user-authored data and are not translated.
  'publicProfile.untitledBoard': { kind: 'text', value: 'Untitled board' },
  'publicProfile.recentActivity': { kind: 'text', value: 'Recent activity' },
  'publicProfile.noRecentActivity': {
    kind: 'text',
    value: 'No recent shared activity to show.',
  },
  'publicProfile.signInTitle': {
    kind: 'text',
    value: 'Sign in to see this profile',
  },
  'publicProfile.signInBody': {
    kind: 'text',
    value:
      'Public profile pages are available to signed-in members who share work with this person.',
  },
  'publicProfile.signInAction': { kind: 'text', value: 'Sign in' },
  'boards.title': { kind: 'text', value: 'Boards' },
  'boards.filterLabel': { kind: 'text', value: 'Filter boards by name' },
  'boards.filterCleared': {
    kind: 'text',
    value: 'Filter cleared. Showing all boards.',
  },
  'boards.filterMatches': {
    kind: 'plural',
    forms: {
      one: '1 board matches "{query}".',
      other: '{count} boards match "{query}".',
    },
  },
  'boards.filterNoMatches': {
    kind: 'text',
    value: 'No boards match "{query}".',
  },
  'boards.loadFailedTitle': {
    kind: 'text',
    value: 'We could not load your boards',
  },
  'boards.retry': { kind: 'text', value: 'Try again' },
  'boards.noClosedTitle': { kind: 'text', value: 'No closed boards.' },
  'boards.noClosedBody': {
    kind: 'text',
    value:
      'Boards you close are kept here, with everything on them, until you delete them for good.',
  },
  'boards.clearFilter': { kind: 'text', value: 'Clear filter' },
  'boards.starredTitle': { kind: 'text', value: 'Starred' },
  'boards.starredList': { kind: 'text', value: 'Starred boards' },
  'boards.recentTitle': { kind: 'text', value: 'Recently viewed' },
  'boards.recentList': { kind: 'text', value: 'Recently viewed boards' },
  'boards.firstRunTitle': { kind: 'text', value: 'You have no boards yet' },
  'boards.firstRunBody': {
    kind: 'text',
    value:
      'Boards live in a workspace. Create one and the boards you can see will appear here.',
  },
  'boards.createWorkspace': { kind: 'text', value: 'Create a workspace' },
  'boards.createFirstWorkspace': {
    kind: 'text',
    value: 'Create your first workspace',
  },
  'boards.groupEmpty': {
    kind: 'text',
    value: 'No boards in this workspace yet.',
  },
  'boards.untitledWorkspace': { kind: 'text', value: 'Untitled workspace' },
  'boards.createBoard': { kind: 'text', value: 'Create a board' },
  'boards.templateGallery': { kind: 'text', value: 'Template gallery' },
  'boards.createDialog.close': { kind: 'text', value: 'Close' },
  'boards.createBoardInWorkspace': {
    kind: 'text',
    value: 'Create a board in {workspace}',
  },
  'boards.untitledBoard': { kind: 'text', value: 'Untitled board' },
  'boards.templateBadge': { kind: 'text', value: 'Template' },
  'boards.createForm.titleEmpty': {
    kind: 'text',
    value: 'Title cannot be empty.',
  },
  'boards.createForm.titleLabel': { kind: 'text', value: 'Board title' },
  'boards.createForm.workspaceLabel': { kind: 'text', value: 'Workspace' },
  'boards.createForm.backgroundLabel': { kind: 'text', value: 'Background' },
  'boards.createForm.selectBackground': {
    kind: 'text',
    value: 'Select {background} background colour',
  },
  'boards.createForm.busyLabel': { kind: 'text', value: 'Creating board…' },
  /*
   * `BE-09.4` / `FR-BOARD-001` — the creation-time visibility control.
   *
   * ⚠ Two NEW keys rather than a reuse of `boardTemplates.boardVisibility.*`.
   * The four LEVEL labels and the four CONSEQUENCE sentences ARE reused, from
   * that anchor, so the same level cannot be spelled two ways on two surfaces
   * — the `boardMap.viewLabel` defect. But the trigger's accessible name
   * could not be: `boardTemplates.boardVisibility.trigger` reads *"{label} —
   * **change** who can see **this board**"*, and at creation there is no board
   * yet and nothing to change. The verb and the subject are both wrong, and
   * WCAG 2.5.3 makes this string the control's whole accessible name.
   *
   * The value "Visibility" has other authors in this catalogue, deliberately
   * and harmlessly: the pins over it are family-scoped (`boardTemplates.*`)
   * or assert only a lower bound, and a group label on a creation form is a
   * different surface from a settings-panel heading, which is exactly the
   * case a shared key makes untranslatable independently.
   */
  'boards.createForm.visibilityLabel': { kind: 'text', value: 'Visibility' },
  'boards.createForm.visibilityTrigger': {
    kind: 'text',
    value: '{label} — choose who can see this board',
  },
  /*
   * `FR-PERM-033` AC1 — *"then public is unavailable **and the reason names
   * Enterprise policy**"*.
   *
   * ⚠ **THE STRING "Enterprise policy" IS THE ASSERTION**, not decoration:
   * `FR-PERM-033.spec.ts`'s AC1 case matches `/Enterprise policy/` on the
   * refused row's own text content, which is also the row's accessible name.
   * Retranslating this key is fine; dropping the two words from the English
   * value turns a criterion into a colour.
   *
   * ⚠ **ONE key, not one per level.** The level it refuses is already the row
   * it sits inside, so naming it again here ("Enterprise policy forbids public
   * boards") would be the same fact twice — and would need four keys that can
   * drift apart.
   */
  'boards.createForm.visibilityRefusedEnterprise': {
    kind: 'text',
    value: 'Unavailable — Enterprise policy',
  },
  'boards.stateNav': { kind: 'text', value: 'Board state' },
  'boards.openBoards': { kind: 'text', value: 'Open boards' },
  'boards.closedBoards': { kind: 'text', value: 'Closed boards' },
  'boards.starBoard': { kind: 'text', value: 'Star {board}' },
  'boards.unstarBoard': { kind: 'text', value: 'Unstar {board}' },
  'search.label': { kind: 'text', value: 'Search' },
  'search.boardContext': { kind: 'text', value: 'Board' },
  'search.cardsGroup': { kind: 'text', value: 'Cards' },
  'search.boardsGroup': { kind: 'text', value: 'Boards' },
  /* FE-33.1 / `FR-SEARCH-017` — the search results page's fourth side toggle.
     Declared HERE, at this chunk's assigned anchor, rather than beside the
     `search.results.include*` pair it renders next to: twelve branches are
     inserting into this file at once and the map is flat, so position is
     cosmetic and a collision is not.
     ⚠ Not a reuse of `search.results.has.description` ("Has description").
     That is `FR-SEARCH-023`'s `has:` facet — *does the card have a
     description* — which is the opposite question from *is the description
     searched*, and sharing the string would mislabel both controls. */
  'search.results.matchDescriptions': {
    kind: 'text',
    value: 'Match card descriptions',
  },
  'search.results.matchDescriptionsHelp': {
    kind: 'text',
    value: 'Descriptions are searched by default.',
  },
  'search.starredBoardsGroup': { kind: 'text', value: 'Starred boards' },
  'search.recentBoardsGroup': { kind: 'text', value: 'Recently viewed boards' },
  'search.loadingBoardShortcuts': {
    kind: 'text',
    value: 'Loading board shortcuts',
  },
  'search.boardShortcutsFailedTitle': {
    kind: 'text',
    value: 'Board shortcuts could not be loaded',
  },
  'search.tryAgainMoment': { kind: 'text', value: 'Try again in a moment.' },
  'search.noShortcuts': {
    kind: 'text',
    value: 'No starred or recent boards yet.',
  },
  'search.keepTyping': {
    kind: 'text',
    value: 'Keep typing - {count} characters minimum',
  },
  'search.searching': { kind: 'text', value: 'Searching' },
  'search.loadFailedTitle': {
    kind: 'text',
    value: 'Search could not be loaded',
  },
  'search.noMatches': { kind: 'text', value: 'No matches for {query}' },
  'search.advanced': { kind: 'text', value: 'Advanced search' },
  'search.close': { kind: 'text', value: 'Close search' },
  'search.results.title': { kind: 'text', value: 'Search results' },
  'search.results.queryLabel': { kind: 'text', value: 'Search query' },
  'search.results.filters': { kind: 'text', value: 'Search filters' },
  'search.results.filter.board': {
    kind: 'text',
    value: 'Restrict to board',
  },
  'search.results.filter.boardPlaceholder': {
    kind: 'text',
    value: 'Any board',
  },
  'search.results.filter.list': { kind: 'text', value: 'Restrict to list' },
  'search.results.filter.listPlaceholder': {
    kind: 'text',
    value: 'Any list',
  },
  'search.results.filter.member': {
    kind: 'text',
    value: 'Restrict to member',
  },
  'search.results.filter.memberPlaceholder': {
    kind: 'text',
    value: '@me or a handle',
  },
  'search.results.filter.label': {
    kind: 'text',
    value: 'Restrict to label',
  },
  'search.results.filter.labelPlaceholder': {
    kind: 'text',
    value: 'Any label',
  },
  'search.results.starredOnly': {
    kind: 'text',
    value: 'Only starred boards',
  },
  'search.results.starredOnlyHelp': {
    kind: 'text',
    value: 'Writes the starred operator into the search text.',
  },
  'search.results.cardAttribute': { kind: 'text', value: 'Card attribute' },
  'search.results.has.any': { kind: 'text', value: 'Any content' },
  'search.results.has.attachments': { kind: 'text', value: 'Has attachments' },
  'search.results.has.description': { kind: 'text', value: 'Has description' },
  'search.results.has.cover': { kind: 'text', value: 'Has cover' },
  'search.results.has.members': { kind: 'text', value: 'Has members' },
  'search.results.has.stickers': { kind: 'text', value: 'Has stickers' },
  'search.results.dueDate': { kind: 'text', value: 'Due date' },
  'search.results.due.any': { kind: 'text', value: 'Any due date' },
  'search.results.due.day': { kind: 'text', value: 'Due today' },
  'search.results.due.week': { kind: 'text', value: 'Due this week' },
  'search.results.due.month': { kind: 'text', value: 'Due this month' },
  'search.results.due.overdue': { kind: 'text', value: 'Overdue' },
  'search.results.due.complete': { kind: 'text', value: 'Due complete' },
  'search.results.due.incomplete': { kind: 'text', value: 'Due incomplete' },
  'search.results.createdWithin': { kind: 'text', value: 'Created within' },
  'search.results.editedWithin': { kind: 'text', value: 'Updated within' },
  'search.results.time.any': { kind: 'text', value: 'Any time' },
  'search.results.time.day': { kind: 'text', value: 'Past 24 hours' },
  'search.results.time.week': { kind: 'text', value: 'Past 7 days' },
  'search.results.time.month': { kind: 'text', value: 'Past 30 days' },
  'search.results.time.year': { kind: 'text', value: 'Past year' },
  'search.results.cardStatus': { kind: 'text', value: 'Card status' },
  'search.results.status.any': { kind: 'text', value: 'Any status' },
  'search.results.status.open': { kind: 'text', value: 'Open cards' },
  'search.results.status.archived': { kind: 'text', value: 'Archived cards' },
  'search.results.sort': { kind: 'text', value: 'Sort results' },
  'search.results.sort.created': { kind: 'text', value: 'Newest' },
  'search.results.sort.updated': { kind: 'text', value: 'Updated' },
  'search.results.sort.due': { kind: 'text', value: 'Due date' },
  'search.results.includeArchived': {
    kind: 'text',
    value: 'Include archived cards',
  },
  'search.results.includeArchivedHelp': {
    kind: 'text',
    value: 'Archived cards and archived-list cards are hidden by default.',
  },
  'search.results.includeClosedBoards': {
    kind: 'text',
    value: 'Include closed boards',
  },
  'search.results.includeClosedBoardsHelp': {
    kind: 'text',
    value: 'Closed boards stay separate from archived cards.',
  },
  'search.results.saved.title': { kind: 'text', value: 'Saved searches' },
  'search.results.saved.description': {
    kind: 'text',
    value: 'Personal shortcuts for this results page.',
  },
  'search.results.saved.nameLabel': {
    kind: 'text',
    value: 'Saved search name',
  },
  'search.results.saved.placeholder': {
    kind: 'text',
    value: 'Example: Renewal risks',
  },
  'search.results.saved.busy': {
    kind: 'text',
    value: 'Saving saved search',
  },
  'search.results.saved.disabledBlank': {
    kind: 'text',
    value: 'Enter a search query before saving it.',
  },
  'search.results.saved.save': { kind: 'text', value: 'Save this search' },
  'search.results.saved.actionFailed': {
    kind: 'text',
    value: 'Saved search action failed',
  },
  'search.results.saved.loadFailed': {
    kind: 'text',
    value: 'Saved searches could not be loaded',
  },
  'search.results.saved.loading': {
    kind: 'text',
    value: 'Loading saved searches',
  },
  'search.results.saved.empty': {
    kind: 'text',
    value: 'No saved searches yet.',
  },
  'search.results.saved.untitled': {
    kind: 'text',
    value: 'Untitled saved search',
  },
  'search.results.saved.run': {
    kind: 'text',
    value: 'Run saved search {name}',
  },
  'search.results.saved.running': {
    kind: 'text',
    value: 'Running saved search {name}',
  },
  'search.results.saved.rename': {
    kind: 'text',
    value: 'Rename saved search {name}',
  },
  'search.results.saved.remove': {
    kind: 'text',
    value: 'Remove saved search {name}',
  },
  'search.results.saved.savingName': {
    kind: 'text',
    value: 'Saving saved search name',
  },
  'search.results.saved.saveName': { kind: 'text', value: 'Save name' },
  'search.results.saved.cancel': { kind: 'text', value: 'Cancel' },
  'search.results.adjacent.title': {
    kind: 'text',
    value: 'Adjacent products',
  },
  'search.results.adjacent.molt': { kind: 'text', value: 'Molt' },
  'search.results.adjacent.loadFailed': {
    kind: 'text',
    value: 'Adjacent product results could not be loaded',
  },
  'search.results.adjacent.loading': {
    kind: 'text',
    value: 'Loading adjacent product results',
  },
  'search.results.adjacent.panel': {
    kind: 'text',
    value: '{provider} results',
  },
  'search.results.adjacent.resultLink': {
    kind: 'text',
    value: '{title}, {provider}',
  },
  'search.results.adjacent.providerQuery': {
    kind: 'text',
    value: 'Provider query: {query}',
  },
  'search.results.adjacent.emptyQuery': {
    kind: 'text',
    value: 'empty query',
  },
  'search.results.adjacent.notLinked': {
    kind: 'text',
    value: '{provider} is not linked to this account.',
  },
  'search.results.adjacent.noAccess': {
    kind: 'text',
    value: 'No {provider} content is available.',
  },
  'search.results.adjacent.noMatches': {
    kind: 'text',
    value: 'No {provider} matches for this provider query.',
  },
  'search.results.results': { kind: 'text', value: 'Results' },
  'search.results.visibleCount': {
    kind: 'plural',
    forms: { one: '1 visible result', other: '{count} visible results' },
  },
  'search.results.enterQueryTitle': {
    kind: 'text',
    value: 'Enter a search query.',
  },
  'search.results.enterQueryBody': {
    kind: 'text',
    value: 'Search results open here once the query has at least one character.',
  },
  'search.results.loadFailed': {
    kind: 'text',
    value: 'Search results could not be loaded',
  },
  'search.results.loading': { kind: 'text', value: 'Loading search results' },
  'search.results.noVisibleMatches': {
    kind: 'text',
    value: 'No visible matches.',
  },
  'search.results.noVisibleMatchesBody': {
    kind: 'text',
    value: 'Try a different query or adjust the filters.',
  },
  'search.results.untitledCard': { kind: 'text', value: 'Untitled card' },
  'search.results.archived': { kind: 'text', value: 'Archived' },
  'search.results.closedBoard': { kind: 'text', value: 'Closed board' },
  'search.results.updated': { kind: 'text', value: 'Updated {date}' },
  'search.results.cardArchivedSuffix': { kind: 'text', value: ', archived' },
  'search.results.cardClosedBoardSuffix': {
    kind: 'text',
    value: ', closed board',
  },
  'search.operators.help': { kind: 'text', value: 'Search operator help' },
  'search.operators.active': { kind: 'text', value: 'Active search operators' },
  'search.operators.activeTitle': { kind: 'text', value: 'Active filters' },
  'search.operators.guidance': {
    kind: 'text',
    value: 'Search operator guidance',
  },
  'search.operators.check': { kind: 'text', value: 'Check operators' },
  'search.operators.suggestions': { kind: 'text', value: 'Operator suggestions' },
  'search.operators.suggestionsTitle': { kind: 'text', value: 'Suggestions' },
  'search.operators.fallback': {
    kind: 'text',
    value: 'Use operators like label:, due:week or @me to narrow search.',
  },
  'search.operators.summary.board.active': {
    kind: 'text',
    value: 'board {value}',
  },
  'search.operators.summary.board.negated': {
    kind: 'text',
    value: 'not board {value}',
  },
  'search.operators.summary.list.active': { kind: 'text', value: 'list {value}' },
  'search.operators.summary.list.negated': {
    kind: 'text',
    value: 'not list {value}',
  },
  'search.operators.summary.member.active': {
    kind: 'text',
    value: 'member {value}',
  },
  'search.operators.summary.member.negated': {
    kind: 'text',
    value: 'not member {value}',
  },
  'search.operators.summary.label.active': {
    kind: 'text',
    value: 'label {value}',
  },
  'search.operators.summary.label.negated': {
    kind: 'text',
    value: 'not label {value}',
  },
  'search.operators.summary.has.attachments': {
    kind: 'text',
    value: 'has attachments',
  },
  'search.operators.summary.has.description': {
    kind: 'text',
    value: 'has description',
  },
  'search.operators.summary.has.cover': { kind: 'text', value: 'has cover' },
  'search.operators.summary.has.members': { kind: 'text', value: 'has members' },
  'search.operators.summary.has.stickers': {
    kind: 'text',
    value: 'has stickers',
  },
  'search.operators.summary.has.negated.attachments': {
    kind: 'text',
    value: 'no attachments',
  },
  'search.operators.summary.has.negated.description': {
    kind: 'text',
    value: 'no description',
  },
  'search.operators.summary.has.negated.cover': {
    kind: 'text',
    value: 'no cover',
  },
  'search.operators.summary.has.negated.members': {
    kind: 'text',
    value: 'no members',
  },
  'search.operators.summary.has.negated.stickers': {
    kind: 'text',
    value: 'no stickers',
  },
  'search.operators.summary.due.day': { kind: 'text', value: 'due today' },
  'search.operators.summary.due.week': { kind: 'text', value: 'due this week' },
  'search.operators.summary.due.month': {
    kind: 'text',
    value: 'due this month',
  },
  'search.operators.summary.due.days': {
    kind: 'plural',
    forms: {
      one: 'due in 1 day',
      other: 'due in {count} days',
    },
  },
  'search.operators.summary.due.overdue': { kind: 'text', value: 'due overdue' },
  'search.operators.summary.due.complete': {
    kind: 'text',
    value: 'due complete',
  },
  'search.operators.summary.due.incomplete': {
    kind: 'text',
    value: 'due incomplete',
  },
  'search.operators.summary.due.negated.day': {
    kind: 'text',
    value: 'not due today',
  },
  'search.operators.summary.due.negated.week': {
    kind: 'text',
    value: 'not due this week',
  },
  'search.operators.summary.due.negated.month': {
    kind: 'text',
    value: 'not due this month',
  },
  'search.operators.summary.due.negated.days': {
    kind: 'plural',
    forms: {
      one: 'not due in 1 day',
      other: 'not due in {count} days',
    },
  },
  'search.operators.summary.due.negated.overdue': {
    kind: 'text',
    value: 'not overdue',
  },
  'search.operators.summary.due.negated.complete': {
    kind: 'text',
    value: 'not due complete',
  },
  'search.operators.summary.due.negated.incomplete': {
    kind: 'text',
    value: 'not due incomplete',
  },
  'search.operators.summary.created.day': {
    kind: 'text',
    value: 'created today',
  },
  'search.operators.summary.created.week': {
    kind: 'text',
    value: 'created this week',
  },
  'search.operators.summary.created.month': {
    kind: 'text',
    value: 'created this month',
  },
  'search.operators.summary.created.days': {
    kind: 'plural',
    forms: {
      one: 'created in the past 1 day',
      other: 'created in the past {count} days',
    },
  },
  'search.operators.summary.created.negated.day': {
    kind: 'text',
    value: 'not created today',
  },
  'search.operators.summary.created.negated.week': {
    kind: 'text',
    value: 'not created this week',
  },
  'search.operators.summary.created.negated.month': {
    kind: 'text',
    value: 'not created this month',
  },
  'search.operators.summary.created.negated.days': {
    kind: 'plural',
    forms: {
      one: 'not created in the past 1 day',
      other: 'not created in the past {count} days',
    },
  },
  'search.operators.summary.edited.day': { kind: 'text', value: 'edited today' },
  'search.operators.summary.edited.week': {
    kind: 'text',
    value: 'edited this week',
  },
  'search.operators.summary.edited.month': {
    kind: 'text',
    value: 'edited this month',
  },
  'search.operators.summary.edited.days': {
    kind: 'plural',
    forms: {
      one: 'edited in the past 1 day',
      other: 'edited in the past {count} days',
    },
  },
  'search.operators.summary.edited.negated.day': {
    kind: 'text',
    value: 'not edited today',
  },
  'search.operators.summary.edited.negated.week': {
    kind: 'text',
    value: 'not edited this week',
  },
  'search.operators.summary.edited.negated.month': {
    kind: 'text',
    value: 'not edited this month',
  },
  'search.operators.summary.edited.negated.days': {
    kind: 'plural',
    forms: {
      one: 'not edited in the past 1 day',
      other: 'not edited in the past {count} days',
    },
  },
  'search.operators.summary.is.starred': {
    kind: 'text',
    value: 'starred boards',
  },
  'search.operators.summary.is.open': { kind: 'text', value: 'open cards' },
  'search.operators.summary.is.archived': {
    kind: 'text',
    value: 'archived cards',
  },
  'search.operators.summary.is.negated.starred': {
    kind: 'text',
    value: 'not starred boards',
  },
  'search.operators.summary.is.negated.open': {
    kind: 'text',
    value: 'not open cards',
  },
  'search.operators.summary.is.negated.archived': {
    kind: 'text',
    value: 'not archived cards',
  },
  'search.operators.summary.sort.created': {
    kind: 'text',
    value: 'sort created',
  },
  'search.operators.summary.sort.edited': { kind: 'text', value: 'sort edited' },
  'search.operators.summary.sort.due': { kind: 'text', value: 'sort due' },
  'search.operators.summary.sort.reversed.created': {
    kind: 'text',
    value: 'sort reverse created',
  },
  'search.operators.summary.sort.reversed.edited': {
    kind: 'text',
    value: 'sort reverse edited',
  },
  'search.operators.summary.sort.reversed.due': {
    kind: 'text',
    value: 'sort reverse due',
  },
  'search.operators.warning.unrecognised': {
    kind: 'text',
    value: '{token} is not a recognised operator, so it will be searched as text.',
  },
  'search.operators.warning.unsupportedValue': {
    kind: 'text',
    value:
      '{token} is not a supported {prefixToken} value, so it will be searched as text.',
  },
  'search.operators.description.prefix-board': {
    kind: 'text',
    value: 'Restrict results to a board name or id.',
  },
  'search.operators.description.prefix-list': {
    kind: 'text',
    value: 'Restrict results to cards in lists with this name.',
  },
  'search.operators.description.prefix-member': {
    kind: 'text',
    value: 'Restrict results to cards assigned to a username.',
  },
  'search.operators.description.prefix-label': {
    kind: 'text',
    value: 'Restrict results to a label name or colour.',
  },
  'search.operators.description.prefix-has': {
    kind: 'text',
    value: 'Find cards that have a specific attribute.',
  },
  'search.operators.description.prefix-due': {
    kind: 'text',
    value: 'Restrict results by due date or due completion.',
  },
  'search.operators.description.prefix-created': {
    kind: 'text',
    value: 'Restrict results by when cards were created.',
  },
  'search.operators.description.prefix-edited': {
    kind: 'text',
    value: 'Restrict results by recent card edits.',
  },
  'search.operators.description.prefix-is': {
    kind: 'text',
    value: 'Restrict results by status or starred boards.',
  },
  'search.operators.description.prefix-sort': {
    kind: 'text',
    value: 'Choose created, edited or due-date ordering.',
  },
  'search.operators.description.label-name': {
    kind: 'text',
    value: 'Filter by a multi-word label name.',
  },
  'search.operators.description.label-colour': {
    kind: 'text',
    value: 'Filter by a label colour.',
  },
  'search.operators.description.label-hash': {
    kind: 'text',
    value: 'Use the label shorthand.',
  },
  'search.operators.description.board-name': {
    kind: 'text',
    value: 'Use quotes when a board name has spaces.',
  },
  'search.operators.description.list-done': {
    kind: 'text',
    value: 'Find cards in lists named Done.',
  },
  'search.operators.description.member-username': {
    kind: 'text',
    value: 'Find cards assigned to a username.',
  },
  'search.operators.description.member-me': {
    kind: 'text',
    value: 'Find cards assigned to you.',
  },
  'search.operators.description.has.attachments': {
    kind: 'text',
    value: 'Find cards with attachments.',
  },
  'search.operators.description.has.description': {
    kind: 'text',
    value: 'Find cards with descriptions.',
  },
  'search.operators.description.has.cover': {
    kind: 'text',
    value: 'Find cards with covers.',
  },
  'search.operators.description.has.members': {
    kind: 'text',
    value: 'Find cards with members.',
  },
  'search.operators.description.has.stickers': {
    kind: 'text',
    value: 'Find cards with stickers.',
  },
  'search.operators.description.due': {
    kind: 'text',
    value: 'Filter by due date.',
  },
  'search.operators.description.created': {
    kind: 'text',
    value: 'Filter by created date.',
  },
  'search.operators.description.edited': {
    kind: 'text',
    value: 'Filter by recent edits.',
  },
  'search.operators.description.edited-year': {
    kind: 'text',
    value: 'Filter by edits in the past year.',
  },
  'search.operators.description.status': {
    kind: 'text',
    value: 'Filter by search status.',
  },
  'search.operators.description.sort': {
    kind: 'text',
    value: 'Change result ordering.',
  },
  'notifications.title': { kind: 'text', value: 'Notifications' },
  'notifications.close': { kind: 'text', value: 'Close notifications' },
  'notifications.bellLabel': {
    kind: 'text',
    value: 'Notifications, {count} unread',
  },
  'notifications.groupedSuffix': {
    kind: 'text',
    value: '{base} and {count} others',
  },
  'notifications.fallbackActor': { kind: 'text', value: 'Someone' },
  'notifications.fallbackMember': { kind: 'text', value: 'Member' },
  'notifications.fallbackWorkspace': { kind: 'text', value: 'this workspace' },
  'notifications.fallbackCard': { kind: 'text', value: 'this card' },
  'notifications.unreadOnly': { kind: 'text', value: 'Unread only' },
  'notifications.settings': { kind: 'text', value: 'Notification settings' },
  'notifications.unreadCountLoading': {
    kind: 'text',
    value: 'Unread count loading',
  },
  'notifications.unreadCount': {
    kind: 'plural',
    forms: { one: '1 unread', other: '{count} unread' },
  },
  'notifications.noUnread': { kind: 'text', value: 'No unread notifications' },
  'notifications.markAllRead': { kind: 'text', value: 'Mark all as read' },
  'notifications.history': { kind: 'text', value: 'Notification history' },
  'notifications.loading': { kind: 'text', value: 'Loading notifications' },
  'notifications.loadFailedTitle': {
    kind: 'text',
    value: 'Notifications could not be loaded',
  },
  'notifications.tryOpenAgain': {
    kind: 'text',
    value: 'Try opening the drawer again.',
  },
  'notifications.allCaughtUp': { kind: 'text', value: 'All caught up' },
  'notifications.noUnreadLast90': {
    kind: 'text',
    value: 'There are no unread notifications in the last 90 days.',
  },
  'notifications.noneYet': { kind: 'text', value: 'No notifications yet' },
  'notifications.last90Appear': {
    kind: 'text',
    value: 'Notifications from the last 90 days appear here.',
  },
  'notifications.list': { kind: 'text', value: 'Notifications' },
  'notifications.endLast90': {
    kind: 'text',
    value: "That's everything from the last 90 days.",
  },
  'notifications.loadingOlder': {
    kind: 'text',
    value: 'Loading older notifications...',
  },
  'notifications.moreHistory': {
    kind: 'text',
    value: 'More history available.',
  },
  'notifications.markRead': { kind: 'text', value: 'Mark as read' },
  'notifications.markUnread': { kind: 'text', value: 'Mark as unread' },
  'notifications.sourceUnavailable': {
    kind: 'text',
    value: 'This source is no longer available.',
  },
  'notifications.showFullExcerpt': {
    kind: 'text',
    value: 'Show full excerpt',
  },
  'notifications.replyLabel': { kind: 'text', value: 'Reply to notification' },
  'notifications.replyButton': { kind: 'text', value: 'Reply' },
  'notifications.replyEmpty': { kind: 'text', value: 'A reply cannot be empty.' },
  'notifications.replyFailed': {
    kind: 'text',
    value: 'Could not post this reply.',
  },
  'notifications.preferences.loading': {
    kind: 'text',
    value: 'Loading notification settings',
  },
  'notifications.preferences.loadFailedTitle': {
    kind: 'text',
    value: 'Notification settings could not be loaded',
  },
  'notifications.preferences.emailFrequency': {
    kind: 'text',
    value: 'Email frequency',
  },
  'notifications.preferences.frequency.instantly': {
    kind: 'text',
    value: 'Instantly',
  },
  'notifications.preferences.frequency.periodically': {
    kind: 'text',
    value: 'Periodically',
  },
  'notifications.preferences.frequency.never': { kind: 'text', value: 'Never' },
  'notifications.preferences.emailTypes': { kind: 'text', value: 'Email types' },
  'notifications.preferences.mobilePushTypes': {
    kind: 'text',
    value: 'Mobile push types',
  },
  'notifications.preferences.mobileBlocked': {
    kind: 'text',
    value: 'One of your mobile installs is blocked by OS notification permission.',
  },
  'notifications.preferences.mobileInstalls': {
    kind: 'text',
    value: 'Mobile installs',
  },
  'notifications.preferences.mobileInstallList': {
    kind: 'text',
    value: 'Mobile push installs',
  },
  'notifications.preferences.mobileInstallDescription': {
    kind: 'text',
    value: '{platform} / {permission}',
  },
  'notifications.preferences.platform.ios': { kind: 'text', value: 'iOS' },
  'notifications.preferences.platform.android': { kind: 'text', value: 'Android' },
  'notifications.preferences.platform.web': { kind: 'text', value: 'Web' },
  'notifications.preferences.permission.granted': { kind: 'text', value: 'granted' },
  'notifications.preferences.permission.denied': { kind: 'text', value: 'denied' },
  'notifications.preferences.permission.revoked': { kind: 'text', value: 'revoked' },
  'notifications.preferences.removeInstall': { kind: 'text', value: 'Remove' },
  'notifications.preferences.desktop': {
    kind: 'text',
    value: 'Desktop notifications',
  },
  'notifications.preferences.desktopBlocked': {
    kind: 'text',
    value:
      'Your browser is blocking notifications. Change this site in browser settings to enable them.',
  },
  'notifications.preferences.desktopUnsupported': {
    kind: 'text',
    value: 'This browser does not support desktop notifications.',
  },
  'notifications.preferences.desktopOpenOnly': {
    kind: 'text',
    value: 'Works only while Molt is open in this browser.',
  },
  'notifications.preferences.webPush': {
    kind: 'text',
    value: 'Push notifications on this device',
  },
  'notifications.preferences.webPushToggle': {
    kind: 'text',
    value: 'Push notifications on this device {state}',
  },
  'notifications.preferences.webPushClosedApp': {
    kind: 'text',
    value: 'Arrives even when Molt is closed. Uses your mobile push choices above.',
  },
  'notifications.preferences.webPushBlocked': {
    kind: 'text',
    value:
      'Your browser is blocking notifications for this site. Allow them in the site settings to turn push on.',
  },
  'notifications.preferences.webPushDismissed': {
    kind: 'text',
    value: 'Push stays off until you allow notifications when your browser asks.',
  },
  'notifications.preferences.webPushFailed': {
    kind: 'text',
    value: 'Push notifications could not be changed on this device. Try again.',
  },
  'notifications.preferences.emailToggle': {
    kind: 'text',
    value: 'Email {category} {state}',
  },
  'notifications.preferences.mobileToggle': {
    kind: 'text',
    value: 'Mobile push {category} {state}',
  },
  'notifications.preferences.desktopToggle': {
    kind: 'text',
    value: 'Desktop notifications {state}',
  },
  'notifications.preferences.on': { kind: 'text', value: 'on' },
  'notifications.preferences.off': { kind: 'text', value: 'off' },
  'notifications.preferences.category.comments': { kind: 'text', value: 'Comments' },
  /* FE-80.1 board templates — the public gallery route, the create-from-template
     dialog, the template board banner, the description editor and the TWO
     visibility vocabularies (a board's four-value audience, a template's
     three-value audience plus its eleven gallery categories).

     ⚠ THE CROSS-SLICE EDGE. A module constant cannot call `t()`, so
     `BoardVisibilityControl.tsx`'s `VISIBILITY_LABEL_KEYS` /
     `VISIBILITY_CONSEQUENCE_KEYS` and `TemplateVisibilityControls.tsx`'s
     `TEMPLATE_CATEGORY_LABEL_KEYS` hold KEYS from this block, and every consumer
     resolves at render. The consumers are `TemplateVisibilityControls.tsx`,
     `TemplateGallery.tsx`, `BoardMenuPanel.tsx` and `BoardFace.tsx` — FIVE files
     on the path, not two endpoints. Change an arm here and all five change.

     Duplicate-value rulings, settled in the slice brief, all SEPARATE:
     - `Private` / `Workspace` / `Enterprise` / `Public` — a BOARD's four-value
       audience (`.boardVisibility.*`) and a create-from-template destination's
       three-value audience (`.create.visibility.*`) are two vocabularies that can
       be on screen in one session, exactly as a workspace's and a board's are.
     - `Marketing` / `Project management` / `Sales` — a board-template gallery
       taxonomy, unrelated to `powerUps.category.*`'s Power-Up taxonomy.
     - `Untitled template` / `Template card` — the `cardTemplateCreate.*` comment
       further down already ruled these stay separate "under the namespace rule —
       do not reach for these". Honoured, four and two authors respectively.
     - `Cancel`, `Done`, `Make public` — per dialog, never shared.
     - `Used 1 time` / `Used {count} times` and `No template description yet.` —
       a gallery card's line, a board banner's, and the editor's empty state.
     - `Template gallery` — `.gallery.region` is the grid's landmark name and
       `.route.heading` the page's `<h1>`; both render on `/templates` at once.
     - `Board {title} created from template.` — second author beside
       `boardFace.board.createdFromTemplate`; that one is announced by the board
       face's own dialog, this one by the gallery route.

     THREE plurals and only three (`.create.copiedCount`, `.gallery.copyCount`,
     `.banner.copyCount`) — each agrees with a count that genuinely varies.
     `.gallery.searchTooShort` is `text` DESPITE holding a number: `{min}` is
     `SEARCH_QUERY_MIN_LENGTH`, a shared constant, and the sentence agrees with the
     cap rather than with a count. */
  'boardTemplates.boardVisibility.private': { kind: 'text', value: 'Private' },
  'boardTemplates.boardVisibility.workspace': { kind: 'text', value: 'Workspace' },
  'boardTemplates.boardVisibility.enterprise': { kind: 'text', value: 'Enterprise' },

  /* ── `enterpriseAdmin.` — fan-out 10, Enterprise Policy Route ──────────────
   * `FE-67.2`/`FE-67.3`, `FR-PERM-034`. Inserted at this chunk's assigned
   * anchor (`boardTemplates.boardVisibility.enterprise`) and nowhere else.
   *
   * ⚠ THESE THREE ARE ROUTED FROM `routes/w.$workspaceId.members.tsx`, NOT
   * from the Enterprise route. A later `enterpriseAdmin.*` family pin that
   * scans only `routes/enterprises.$enterpriseId.policy.tsx` will report all
   * three as orphans; it has to scan the workspace members route as well.
   *
   * ⚠ NOT declared under `workspaceMembers.` even though the workspace members
   * route is what asks for them. `workspace-members.routing.test.ts` pins that
   * family at exactly 46 keys and that file is not this chunk's to edit — a
   * forty-seventh key there fails a pin nobody owns here. The prefix is the
   * only thing the pin's `startsWith('workspaceMembers.')` filter cannot see.
   */
  'enterpriseAdmin.workspaceLink.heading': {
    kind: 'text',
    value: 'Enterprise administration',
  },
  'enterpriseAdmin.workspaceLink.body': {
    kind: 'text',
    value:
      'This workspace belongs to an Enterprise you administer. Default board visibility, guest invites and managed members are set there, not here.',
  },
  'enterpriseAdmin.workspaceLink.action': {
    kind: 'text',
    value: 'Open Enterprise policy',
  },

  /* ── `enterpriseAdmin.` continued — the mounted route itself ───────────────
   * Routed from `routes/enterprises.$enterpriseId.policy.tsx`, the surface
   * `FE-67.2` was short of. Same block, same anchor, one hunk.
   *
   * ⚠ THIRTY-SIX DECLARED AGAINST TWENTY REUSED, and the split follows one
   * rule: REUSE A KEY WHEN ITS ENGLISH IS LEVEL-NEUTRAL, DECLARE ONE WHEN THE
   * ENGLISH NAMES A LEVEL. `Nobody`, `Anyone`, `Workspace admins only`,
   * `Create {visibility} boards`, `Board visibility policy`, `Admin`, `Member`
   * and the four board-audience nouns are reused verbatim from
   * `workspaceSettings.*`, `workspaceMembers.*` and `boardTemplates.*`. The
   * two board-policy rule words are NOT: at Enterprise level `any_member`
   * means any *Enterprise* member and `admins_only` means *Enterprise*
   * admins, and reusing the workspace words would attribute the rule to the
   * wrong level — the inversion `FR-PERM-033`'s edge case exists to forbid.
   *
   * ⚠ `guestInvite.rule.sameWorkspaceMembers` is the one guest-invite word
   * with no existing key, and its absence is a CATALOGUE FACT rather than an
   * oversight: `ck_enterprise_settings_guest_invite_rule` admits four values
   * while `ck_workspaces_guest_invite_rule` cannot represent
   * `same_workspace_members`, so no workspace surface has ever had to say it.
   *
   * ⚠ `route.title` duplicates the English of `workspaceLink.heading` above
   * and is deliberately a SEPARATE key. That one names a link on the
   * workspace members page; this one is the page's own `<h1>`. Sharing the key
   * would let a later edit to the link relabel the page.
   *
   * ⚠ No plural in this family: nothing on the page holds a count sentence,
   * so every entry below is `kind: 'text'`.
   */
  'enterpriseAdmin.route.title': {
    kind: 'text',
    value: 'Enterprise administration',
  },
  'enterpriseAdmin.route.description': {
    kind: 'text',
    value:
      'Policy for every workspace and board in this Enterprise, and the managed accounts it governs.',
  },
  'enterpriseAdmin.route.signInTitle': {
    kind: 'text',
    value: 'Sign in to administer this Enterprise',
  },
  'enterpriseAdmin.route.signInBody': {
    kind: 'text',
    value: 'Enterprise policy is visible only to Enterprise admins.',
  },
  'enterpriseAdmin.policy.heading': {
    kind: 'text',
    value: 'Enterprise policy',
  },
  'enterpriseAdmin.policy.description': {
    kind: 'text',
    value:
      'These settings apply to every workspace in the Enterprise. A workspace admin cannot override them.',
  },
  'enterpriseAdmin.policy.loading': {
    kind: 'text',
    value: 'Checking Enterprise policy...',
  },
  'enterpriseAdmin.defaultVisibility.label': {
    kind: 'text',
    value: 'Default board visibility',
  },
  'enterpriseAdmin.defaultVisibility.control': {
    kind: 'text',
    value: '{visibility} - default board visibility',
  },
  'enterpriseAdmin.defaultVisibility.saved': {
    kind: 'text',
    value: 'Default board visibility set to {visibility}.',
  },
  'enterpriseAdmin.guestInvite.description': {
    kind: 'text',
    value: 'Who may invite guests to boards anywhere in this Enterprise.',
  },
  'enterpriseAdmin.guestInvite.rule.sameWorkspaceMembers': {
    kind: 'text',
    value: 'Members of the same workspace',
  },
  'enterpriseAdmin.boardPolicy.description': {
    kind: 'text',
    value:
      'Choose who can create boards at each visibility across the Enterprise, and whether a workspace admin may set its own rule.',
  },
  'enterpriseAdmin.boardPolicy.rule.anyMember': {
    kind: 'text',
    value: 'Any Enterprise member',
  },
  'enterpriseAdmin.boardPolicy.rule.adminsOnly': {
    kind: 'text',
    value: 'Enterprise admins only',
  },
  'enterpriseAdmin.boardPolicy.ruleDescription.anyMember': {
    kind: 'text',
    value: 'Any Enterprise member can use this visibility.',
  },
  'enterpriseAdmin.boardPolicy.ruleDescription.adminsOnly': {
    kind: 'text',
    value: 'Only Enterprise admins can use this visibility.',
  },
  'enterpriseAdmin.boardPolicy.workspaceControl.label': {
    kind: 'text',
    value: 'Workspace admin control',
  },
  'enterpriseAdmin.boardPolicy.workspaceControl.allowed': {
    kind: 'text',
    value: 'Workspace admins may set their own rule',
  },
  'enterpriseAdmin.boardPolicy.workspaceControl.blocked': {
    kind: 'text',
    value: 'Enterprise rule only',
  },
  'enterpriseAdmin.boardPolicy.workspaceControl.control': {
    kind: 'text',
    value: '{state} - workspace admin control of {visibility} boards',
  },
  'enterpriseAdmin.boardPolicy.saved': {
    kind: 'text',
    value: '{visibility} board policy updated.',
  },
  'enterpriseAdmin.members.heading': {
    kind: 'text',
    value: 'Managed members',
  },
  'enterpriseAdmin.members.description': {
    kind: 'text',
    value:
      "Accounts on this Enterprise's verified domains. Deactivating one revokes access and leaves everything they wrote in place, still attributed to them.",
  },
  'enterpriseAdmin.members.loading': {
    kind: 'text',
    value: 'Checking Enterprise members...',
  },
  'enterpriseAdmin.members.empty': {
    kind: 'text',
    value: 'No Enterprise members.',
  },
  'enterpriseAdmin.members.listLabel': {
    kind: 'text',
    value: 'Enterprise members',
  },
  'enterpriseAdmin.members.managed': {
    kind: 'text',
    value: 'Managed',
  },
  'enterpriseAdmin.members.deactivated': {
    kind: 'text',
    value: 'Deactivated',
  },
  'enterpriseAdmin.members.deactivate': {
    kind: 'text',
    value: 'Deactivate',
  },
  'enterpriseAdmin.members.reactivate': {
    kind: 'text',
    value: 'Reactivate',
  },
  'enterpriseAdmin.members.deactivateControl': {
    kind: 'text',
    value: 'Deactivate {name}',
  },
  'enterpriseAdmin.members.reactivateControl': {
    kind: 'text',
    value: 'Reactivate {name}',
  },
  'enterpriseAdmin.members.unmanagedReason': {
    kind: 'text',
    value: 'Only an account on a verified Enterprise domain can be deactivated.',
  },
  'enterpriseAdmin.members.deactivatedAnnounce': {
    kind: 'text',
    value: '{name} was deactivated.',
  },
  'enterpriseAdmin.members.reactivatedAnnounce': {
    kind: 'text',
    value: '{name} was reactivated.',
  },
  'boardTemplates.boardVisibility.public': { kind: 'text', value: 'Public' },
  'boardTemplates.boardVisibility.consequence.private': {
    kind: 'text',
    value: "Only people on this board's member list can see it.",
  },
  'boardTemplates.boardVisibility.consequence.workspace': {
    kind: 'text',
    value:
      'Everyone in the workspace can see this board. Guests see only the boards they were added to.',
  },
  'boardTemplates.boardVisibility.consequence.enterprise': {
    kind: 'text',
    value: 'Anyone in the Enterprise can see this board.',
  },
  'boardTemplates.boardVisibility.consequence.public': {
    kind: 'text',
    value: 'Anyone on the internet can see this board, signed in or not.',
  },
  'boardTemplates.boardVisibility.trigger': {
    kind: 'text',
    value: '{label} — change who can see this board',
  },
  'boardTemplates.boardVisibility.lockedReason': {
    kind: 'text',
    value: 'Only a board admin can change who can see this board.',
  },
  'boardTemplates.boardVisibility.confirmTitle': {
    kind: 'text',
    value: 'Make this board public?',
  },
  'boardTemplates.boardVisibility.confirmBody': {
    kind: 'text',
    value:
      'Anyone holding the link can read this board without signing in — its lists, its cards, its comments, and its attachments and card covers. Editing stays restricted to board members. The board is not advertised anywhere, and search engines are not invited in.',
  },
  'boardTemplates.boardVisibility.confirm': { kind: 'text', value: 'Make public' },
  'boardTemplates.boardVisibility.cancel': { kind: 'text', value: 'Cancel' },
  'boardTemplates.category.business': { kind: 'text', value: 'Business' },
  'boardTemplates.category.design': { kind: 'text', value: 'Design' },
  'boardTemplates.category.education': { kind: 'text', value: 'Education' },
  'boardTemplates.category.engineering': { kind: 'text', value: 'Engineering' },
  'boardTemplates.category.marketing': { kind: 'text', value: 'Marketing' },
  'boardTemplates.category.personal': { kind: 'text', value: 'Personal' },
  'boardTemplates.category.productManagement': {
    kind: 'text',
    value: 'Product management',
  },
  'boardTemplates.category.projectManagement': {
    kind: 'text',
    value: 'Project management',
  },
  'boardTemplates.category.remoteWork': { kind: 'text', value: 'Remote work' },
  'boardTemplates.category.sales': { kind: 'text', value: 'Sales' },
  'boardTemplates.category.support': { kind: 'text', value: 'Support' },
  'boardTemplates.templateVisibility.copy.private': {
    kind: 'text',
    value: 'Only board members can open or copy this template.',
  },
  'boardTemplates.templateVisibility.copy.workspace': {
    kind: 'text',
    value:
      'Workspace members can find this template from the workspace boards page and copy it.',
  },
  'boardTemplates.templateVisibility.copy.public': {
    kind: 'text',
    value:
      'Anyone on the internet can view this template and it can appear in the gallery.',
  },
  'boardTemplates.templateVisibility.region': {
    kind: 'text',
    value: 'Template visibility',
  },
  'boardTemplates.templateVisibility.heading': {
    kind: 'text',
    value: 'Template visibility',
  },
  'boardTemplates.templateVisibility.trigger': {
    kind: 'text',
    value: '{label} - change who can see and copy this template',
  },
  'boardTemplates.templateVisibility.categoryTrigger': {
    kind: 'text',
    value: '{label} - change template gallery category',
  },
  'boardTemplates.templateVisibility.noCategory': {
    kind: 'text',
    value: 'No gallery category',
  },
  'boardTemplates.templateVisibility.noCategoryDescription': {
    kind: 'text',
    value: 'Keep this template out of category browsing.',
  },
  'boardTemplates.templateVisibility.categoryDescription': {
    kind: 'text',
    value: 'Show this public template under {category}.',
  },
  'boardTemplates.templateVisibility.manageReason': {
    kind: 'text',
    value: 'Only a board admin can manage this template.',
  },
  'boardTemplates.templateVisibility.confirmTitle': {
    kind: 'text',
    value: 'Make this template public?',
  },
  'boardTemplates.templateVisibility.confirmBody': {
    kind: 'text',
    value:
      'Anyone with the link can view this template without signing in. Public templates can also appear in the gallery. Copies already made from this template stay ordinary boards if the template is later narrowed again.',
  },
  'boardTemplates.templateVisibility.confirmClose': {
    kind: 'text',
    value: 'Cancel public template confirmation',
  },
  'boardTemplates.templateVisibility.cancel': { kind: 'text', value: 'Cancel' },
  'boardTemplates.templateVisibility.confirm': { kind: 'text', value: 'Make public' },
  'boardTemplates.create.untitledTemplate': { kind: 'text', value: 'Untitled template' },
  'boardTemplates.create.title': { kind: 'text', value: 'Create board from {template}' },
  'boardTemplates.create.description': {
    kind: 'text',
    value: 'Choose the destination workspace, visibility and template contents.',
  },
  'boardTemplates.create.close': { kind: 'text', value: 'Cancel create from template' },
  'boardTemplates.create.done': { kind: 'text', value: 'Done' },
  'boardTemplates.create.cancel': { kind: 'text', value: 'Cancel' },
  'boardTemplates.create.busy': { kind: 'text', value: 'Creating board...' },
  'boardTemplates.create.submit': { kind: 'text', value: 'Create board' },
  'boardTemplates.create.titleLabel': { kind: 'text', value: 'Board title' },
  'boardTemplates.create.titleRequired': { kind: 'text', value: 'Enter a board title.' },
  'boardTemplates.create.loadingReason': {
    kind: 'text',
    value: 'Destination workspaces are still loading.',
  },
  'boardTemplates.create.noWorkspacesReason': {
    kind: 'text',
    value: 'Join or create a workspace before creating from this template.',
  },
  'boardTemplates.create.resultTitle': {
    kind: 'text',
    value: 'Board created from template',
  },
  'boardTemplates.create.resultBody': { kind: 'text', value: 'The new board is ready.' },
  'boardTemplates.create.copiedCount': {
    kind: 'plural',
    forms: { one: 'Copied 1 card.', other: 'Copied {count} cards.' },
  },
  'boardTemplates.create.openCreated': { kind: 'text', value: 'Open created board' },
  'boardTemplates.create.workspaceLabel': {
    kind: 'text',
    value: 'Destination workspace',
  },
  'boardTemplates.create.untitledWorkspace': {
    kind: 'text',
    value: 'Untitled workspace',
  },
  'boardTemplates.create.noWorkspacesTitle': {
    kind: 'text',
    value: 'No destination workspaces',
  },
  'boardTemplates.create.visibilityLegend': { kind: 'text', value: 'Visibility' },
  'boardTemplates.create.keepCards': { kind: 'text', value: 'Keep cards' },
  'boardTemplates.create.keepCardsDescription': {
    kind: 'text',
    value: "Include the template board's example cards.",
  },
  'boardTemplates.create.visibility.private': { kind: 'text', value: 'Private' },
  'boardTemplates.create.visibility.workspace': { kind: 'text', value: 'Workspace' },
  'boardTemplates.create.visibility.public': { kind: 'text', value: 'Public' },
  'boardTemplates.create.visibility.privateDescription': {
    kind: 'text',
    value: 'Only people added to the new board can see it.',
  },
  'boardTemplates.create.visibility.workspaceDescription': {
    kind: 'text',
    value: 'Every member of the destination workspace can see the new board.',
  },
  'boardTemplates.create.visibility.publicDescription': {
    kind: 'text',
    value: 'Anyone with the link can read the new board.',
  },
  'boardTemplates.gallery.region': { kind: 'text', value: 'Template gallery' },
  'boardTemplates.gallery.searchLabel': {
    kind: 'text',
    value: 'Search public templates',
  },
  'boardTemplates.gallery.search': { kind: 'text', value: 'Search' },
  'boardTemplates.gallery.searchTooShort': {
    kind: 'text',
    value: 'Enter at least {min} characters to search.',
  },
  'boardTemplates.gallery.categories': { kind: 'text', value: 'Template categories' },
  'boardTemplates.gallery.allCategories': { kind: 'text', value: 'All templates' },
  'boardTemplates.gallery.errorTitle': {
    kind: 'text',
    value: 'Could not load templates',
  },
  'boardTemplates.gallery.emptyTitle': {
    kind: 'text',
    value: 'No public templates found',
  },
  'boardTemplates.gallery.emptyBody': {
    kind: 'text',
    value: 'Try another category or a more specific search.',
  },
  'boardTemplates.gallery.untitledTemplate': { kind: 'text', value: 'Untitled template' },
  'boardTemplates.gallery.copyCount': {
    kind: 'plural',
    forms: { one: 'Used 1 time', other: 'Used {count} times' },
  },
  'boardTemplates.gallery.noDescription': {
    kind: 'text',
    value: 'No template description yet.',
  },
  'boardTemplates.gallery.uncategorised': { kind: 'text', value: 'Uncategorised' },
  'boardTemplates.gallery.unknownCreator': { kind: 'text', value: 'Unknown creator' },
  'boardTemplates.gallery.preview': { kind: 'text', value: 'Preview' },
  'boardTemplates.gallery.create': { kind: 'text', value: 'Create board' },
  'boardTemplates.description.heading': { kind: 'text', value: 'Template description' },
  'boardTemplates.description.fieldLabel': {
    kind: 'text',
    value: 'Template description',
  },
  'boardTemplates.description.creatorUnavailable': {
    kind: 'text',
    value: 'Creator unavailable',
  },
  'boardTemplates.description.createdBy': { kind: 'text', value: 'Created by {name}' },
  'boardTemplates.description.clear': {
    kind: 'text',
    value: 'Clear template description',
  },
  'boardTemplates.description.edit': { kind: 'text', value: 'Edit template description' },
  'boardTemplates.description.errorTitle': {
    kind: 'text',
    value: 'Template description was not saved',
  },
  'boardTemplates.description.busy': {
    kind: 'text',
    value: 'Saving template description...',
  },
  'boardTemplates.description.save': { kind: 'text', value: 'Save template description' },
  'boardTemplates.description.cancel': { kind: 'text', value: 'Cancel description edit' },
  'boardTemplates.description.empty': {
    kind: 'text',
    value: 'No template description yet.',
  },
  'boardTemplates.cardVisibility.untitledTemplate': {
    kind: 'text',
    value: 'Untitled template',
  },
  'boardTemplates.cardVisibility.show': { kind: 'text', value: 'Show in list' },
  'boardTemplates.cardVisibility.hide': { kind: 'text', value: 'Hide from list' },
  'boardTemplates.cardVisibility.showName': {
    kind: 'text',
    value: 'Show {title} in its list',
  },
  'boardTemplates.cardVisibility.hideName': {
    kind: 'text',
    value: 'Hide {title} from its list',
  },
  'boardTemplates.cardVisibility.reason': {
    kind: 'text',
    value: 'Only a board member who can edit this card can hide templates.',
  },
  'boardTemplates.cardVisibility.hiddenLabel': {
    kind: 'text',
    value: 'Hidden template card',
  },
  'boardTemplates.cardVisibility.label': { kind: 'text', value: 'Template card' },
  'boardTemplates.banner.region': { kind: 'text', value: 'Template board' },
  'boardTemplates.banner.badge': { kind: 'text', value: 'Template' },
  'boardTemplates.banner.createdBy': { kind: 'text', value: 'Created by {name}' },
  'boardTemplates.banner.copyCount': {
    kind: 'plural',
    forms: { one: 'Used 1 time', other: 'Used {count} times' },
  },
  'boardTemplates.banner.noDescription': {
    kind: 'text',
    value: 'No template description yet. You can still create a board from it.',
  },
  'boardTemplates.banner.create': { kind: 'text', value: 'Create board from template' },
  'boardTemplates.route.heading': { kind: 'text', value: 'Template gallery' },
  'boardTemplates.route.intro': {
    kind: 'text',
    value:
      'Browse public templates, preview the source board and create an ordinary board from one.',
  },
  'boardTemplates.route.createdAnnounce': {
    kind: 'text',
    value: 'Board {title} created from template.',
  },
  /*
   * FE-80.1 board-template hook announcements — `lib/board-templates.ts`.
   *
   * These six failure sentences are authored by the mutation hooks, not by the
   * board-menu section or the template gallery. Visible board-template chrome
   * already uses `boardTemplates.*`; the hook layer gets its own family on the
   * `cardTemplateHooks.*` precedent so route, menu and gallery callers do not
   * speak through one another's surface namespace. Board and template titles
   * remain literal data.
   */
  'boardTemplateHooks.markFailed': {
    kind: 'text',
    value: 'Could not mark this board as a template.',
  },
  'boardTemplateHooks.unmarkFailed': {
    kind: 'text',
    value: "Could not remove this board's template flag.",
  },
  'boardTemplateHooks.descriptionFailed': {
    kind: 'text',
    value: 'Could not update the template description.',
  },
  'boardTemplateHooks.visibilityFailed': {
    kind: 'text',
    value: 'Could not update the template visibility.',
  },
  'boardTemplateHooks.categoryFailed': {
    kind: 'text',
    value: 'Could not update the template category.',
  },
  'boardTemplateHooks.createFailed': {
    kind: 'text',
    value: 'Could not create a board from the template {title}.',
  },
  'notifications.preferences.categoryName.comments': {
    kind: 'text',
    value: 'comments',
  },
  'notifications.preferences.category.due_dates': {
    kind: 'text',
    value: 'Due dates',
  },
  'notifications.preferences.categoryName.due_dates': {
    kind: 'text',
    value: 'due dates',
  },
  'notifications.preferences.category.removed_from_card': {
    kind: 'text',
    value: 'Removed from card',
  },
  'notifications.preferences.categoryName.removed_from_card': {
    kind: 'text',
    value: 'removed from card',
  },
  'notifications.preferences.category.attachments_added': {
    kind: 'text',
    value: 'Attachments added',
  },
  'notifications.preferences.categoryName.attachments_added': {
    kind: 'text',
    value: 'attachments added',
  },
  'notifications.preferences.category.cards_created': {
    kind: 'text',
    value: 'Cards created',
  },
  'notifications.preferences.categoryName.cards_created': {
    kind: 'text',
    value: 'cards created',
  },
  'notifications.preferences.category.cards_moved': {
    kind: 'text',
    value: 'Cards moved',
  },
  'notifications.preferences.categoryName.cards_moved': {
    kind: 'text',
    value: 'cards moved',
  },
  'notifications.preferences.category.cards_archived': {
    kind: 'text',
    value: 'Cards archived',
  },
  'notifications.preferences.categoryName.cards_archived': {
    kind: 'text',
    value: 'cards archived',
  },
  'notifications.preferences.mobileCategory.added_to_card': {
    kind: 'text',
    value: 'Added to card',
  },
  'notifications.preferences.mobileCategoryName.added_to_card': {
    kind: 'text',
    value: 'added to card',
  },
  'notifications.preferences.mobileCategory.removed_from_card': {
    kind: 'text',
    value: 'Removed from card',
  },
  'notifications.preferences.mobileCategoryName.removed_from_card': {
    kind: 'text',
    value: 'removed from card',
  },
  'notifications.preferences.mobileCategory.comment_on_card': {
    kind: 'text',
    value: 'Comment on card',
  },
  'notifications.preferences.mobileCategoryName.comment_on_card': {
    kind: 'text',
    value: 'comment on card',
  },
  'notifications.preferences.mobileCategory.attachment_added': {
    kind: 'text',
    value: 'Attachment added',
  },
  'notifications.preferences.mobileCategoryName.attachment_added': {
    kind: 'text',
    value: 'attachment added',
  },
  'notifications.preferences.mobileCategory.card_changed': {
    kind: 'text',
    value: 'Card changed',
  },
  'notifications.preferences.mobileCategoryName.card_changed': {
    kind: 'text',
    value: 'card changed',
  },
  'notifications.preferences.mobileCategory.card_created': {
    kind: 'text',
    value: 'Card created',
  },
  'notifications.preferences.mobileCategoryName.card_created': {
    kind: 'text',
    value: 'card created',
  },
  'notifications.preferences.mobileCategory.added_to_board': {
    kind: 'text',
    value: 'Added to board',
  },
  'notifications.preferences.mobileCategoryName.added_to_board': {
    kind: 'text',
    value: 'added to board',
  },
  'notifications.preferences.mobileCategory.removed_from_board': {
    kind: 'text',
    value: 'Removed from board',
  },
  'notifications.preferences.mobileCategoryName.removed_from_board': {
    kind: 'text',
    value: 'removed from board',
  },
  'notifications.preferences.mobileCategory.made_board_admin': {
    kind: 'text',
    value: 'Made board admin',
  },
  'notifications.preferences.mobileCategoryName.made_board_admin': {
    kind: 'text',
    value: 'made board admin',
  },
  'notifications.preferences.mobileCategory.board_closed': {
    kind: 'text',
    value: 'Board closed',
  },
  'notifications.preferences.mobileCategoryName.board_closed': {
    kind: 'text',
    value: 'board closed',
  },
  'notifications.preferences.mobileCategory.added_to_workspace': {
    kind: 'text',
    value: 'Added to workspace',
  },
  'notifications.preferences.mobileCategoryName.added_to_workspace': {
    kind: 'text',
    value: 'added to workspace',
  },
  'notifications.preferences.mobileCategory.removed_from_workspace': {
    kind: 'text',
    value: 'Removed from workspace',
  },
  'notifications.preferences.mobileCategoryName.removed_from_workspace': {
    kind: 'text',
    value: 'removed from workspace',
  },
  'notifications.preferences.mobileCategory.made_workspace_admin': {
    kind: 'text',
    value: 'Made workspace admin',
  },
  'notifications.preferences.mobileCategoryName.made_workspace_admin': {
    kind: 'text',
    value: 'made workspace admin',
  },
  'notifications.preferences.mobileCategory.mentioned': {
    kind: 'text',
    value: 'Mentioned',
  },
  'notifications.preferences.mobileCategoryName.mentioned': {
    kind: 'text',
    value: 'mentioned',
  },
  'notifications.preferences.mobileCategory.due_date_approaching': {
    kind: 'text',
    value: 'Due date approaching',
  },
  'notifications.preferences.mobileCategoryName.due_date_approaching': {
    kind: 'text',
    value: 'due date approaching',
  },
  'cardBack.untitledCard': { kind: 'text', value: 'Untitled card' },
  'cardBack.titleFallback': { kind: 'text', value: 'Card' },
  'cardBack.templateBadge': { kind: 'text', value: 'Template' },
  'cardBack.close': { kind: 'text', value: 'Close card' },
  'cardBack.title.label': { kind: 'text', value: 'Card title' },
  'cardBack.dialog.description': { kind: 'text', value: 'In list {list}' },
  // docs/notes/card-keys.md — the card's `KEY-NNNN` reference beside the
  // title. The reference itself is DATA and reaches these sentences only
  // through `{reference}`; it is never a catalogue value.
  'cardBack.reference.copy': { kind: 'text', value: 'Copy' },
  'cardBack.reference.copied': { kind: 'text', value: 'Copied' },
  'cardBack.reference.copyLabel': { kind: 'text', value: 'Copy reference {reference}' },
  'cardBack.reference.copiedLabel': {
    kind: 'text',
    value: 'Reference {reference} copied',
  },
  'cardBack.reference.announceCopied': {
    kind: 'text',
    value: 'Reference {reference} copied to the clipboard.',
  },
  'cardBack.reference.copyFailed': {
    kind: 'text',
    value: 'Could not copy. Select the reference to copy it by hand.',
  },
  'cardBack.reason.archivedReadOnly': {
    kind: 'text',
    value: 'Archived cards are read-only until restored.',
  },
  'cardBack.reason.evergreenEditorsOnly': {
    kind: 'text',
    value: 'Only card editors can mark a card Evergreen.',
  },
  'cardBack.reason.convertLinkCardFirst': {
    kind: 'text',
    value: 'Convert this link card before changing ordinary card fields.',
  },
  'cardBack.reason.boardClosed': {
    kind: 'text',
    value: 'Closed boards cannot change card settings.',
  },
  'cardBack.reason.editorHideTemplate': {
    kind: 'text',
    value: 'Only a member who can edit this card can hide template cards.',
  },
  'cardBack.reason.automationEditorsOnly': {
    kind: 'text',
    value: 'Only a member who can edit this card can run card buttons.',
  },
  'cardBack.reason.emailBoardClosed': {
    kind: 'text',
    value: 'Closed boards are read-only.',
  },
  'cardBack.reason.emailCommenter': {
    kind: 'text',
    value: 'Only someone who can comment on this card can use email-to-card.',
  },
  'cardBack.announce.descriptionSaved': {
    kind: 'text',
    value: 'Card description saved.',
  },
  'cardBack.announce.queuedDescriptionSaved': {
    kind: 'text',
    value: 'Queued card description saved.',
  },
  'cardBack.announce.markedComplete': { kind: 'text', value: 'Card marked complete.' },
  'cardBack.announce.markedIncomplete': {
    kind: 'text',
    value: 'Card marked incomplete.',
  },
  'cardBack.announce.markedEvergreen': {
    kind: 'text',
    value: 'Card marked Evergreen.',
  },
  'cardBack.announce.agingRestored': { kind: 'text', value: 'Card aging restored.' },
  'cardBack.announce.archived': { kind: 'text', value: 'Card {title} archived.' },
  /* FE-80.1 workspace views — the four workspace-scoped view surfaces: the
     Workspace Table, the Workspace Calendar, the Workspace views index and a
     saved Workspace view. 154 keys, inserted as ONE contiguous block while
     three sibling slices edit this file at anchors far from this one.

     ⚠ THREE COLLISION RULINGS, taken knowingly rather than defaulted into.

     1. `Table`, `Calendar` and `Private` gain a fourth/third/third author here.
        A `WorkspaceViewKind` `<option>` is not a view-switcher radio
        (`boardFace.view.table`), a page heading (`boardTable.heading`) or a
        rich-text format button. And workspace-view visibility is a THIRD
        two-value vocabulary — `private | workspace_visible` — beside the
        workspace's own and a board's four-value set; `workspace-members`
        argued those two stay separate and the same argument admits a third.
        This is the `boardTable.heading` ruling, not the `boardMap.viewLabel`
        defect.

     2. `boardTable.*` and `boardCalendar.*` hold byte-identical values for
        eleven keys below (`Rename {title}`, `Change list for {title}`, the six
        column names, …). They stay SEPARATE: `boardTable.*` names the table
        view of ONE board, `workspaceViews.table.*` a table over up to twenty
        boards in a workspace, carrying a `Board` column the board-level table
        cannot have.

     3. `Members` and `Labels` each get TWO authors inside this slice — a filter
        facet name (`table.facet.*` / `calendar.legend.*`) and a table column
        name (`table.column.*`). A translator must be able to move a facet's
        name without renaming a column; that is the split
        `boardFace.filters.members.heading` vs `boardTable.column.members`
        already ships.

     ⚠ REUSED, NOT DECLARED. `WorkspaceCalendarPage.tsx` and
     `WorkspaceViewsPage.tsx` thread `boardFilterLabelFormatter(t)` into
     `memberFilterLabel` / `labelFilterLabel` / `dueBucketFilterLabel`, so the
     twelve `boardFace.filters.*` arms answer for them and no due-bucket key is
     declared for either page. The calendar's two sentinel `FacetOption`s reuse
     `boardFace.filters.members.none` and `boardFace.filters.labels.none` for
     the same reason: the sentinel row and the deleted/unnamed fallback row two
     rows below it are the same class of placeholder in one `<fieldset>`, and a
     second declaration would let them disagree in every language but English.

     ⚠ TWO BYTE DIVERGENCES PRESERVED, on `workspaceSwitcher.activity`'s ruling
     that a routing slice which normalises copy turns files it does not own red.
     (a) The due vocabulary is spelled two ways: the index and calendar pages
     render `Due in the next week` through `boardFace.filters.due.*`, while
     `WorkspaceTablePage.tsx` hardcodes the shorter `Due next week`. Nothing
     binds the short form and FR-VIEW-030 binds the long one, so
     `workspaceViews.table.due.*` below carries the SHORT spelling verbatim.
     A third spelling this slice cannot reach lives in a response body at
     `apps/server/src/dashboard/read.ts:96-98` — reported, not patched.
     (b) The 404 sentence carries an EM DASH on the table and calendar routes
     and a HYPHEN on the views and saved-view routes.
     `w.$workspaceId.calendar.test.tsx` asserts the em dash and FR-VIEW-031 the
     hyphen, so both are preserved. Four keys, two spellings.

     ⚠ FIVE SITES COULD NOT CALL `t()` WHERE THEY SAT, all resolved on the
     `ALL_WORKSPACES_KEY` precedent — the constant holds the KEY and the render
     resolves it: the two byte-identical `visibilityLabel()` helpers, the three
     view-kind ternaries, `WEEKDAY_LABELS` (now `WEEKDAY_KEYS`), the inline Due
     facet array, and `dueDateLabel()`, which takes the resolved `No due date`
     string as a parameter rather than returning a key — returning a key is the
     `lib/comments.ts` finding that poisoned a search haystack.

     ⚠ SEVEN OF THE 154 ARE PLURAL and the other 147 are text.
     `table.selectedCount` is TEXT with hard evidence: `{count} of {limit}
     boards selected` agrees with the CAP, not the selection, so at count 1 it
     must still read `1 of 20 boards selected`. A plural key would render
     `1 of 20 board selected` and turn `WorkspaceTablePage.test.tsx` and
     FR-VIEW-016 red. The same reasoning makes `index.boardLimit`,
     `calendar.boardsDescription` and `calendar.boardLimit` text.
     `calendar.moreEntries` is text because `+{count} more` carries no noun.
     `calendar.excluded`'s singular arm reads `1 card without due dates is not
     shown.` — a plural noun inside a singular sentence, asserted verbatim by
     `WorkspaceCalendarPage.test.tsx`, preserved rather than corrected. */
  'workspaceViews.kind.table': { kind: 'text', value: 'Table' },
  'workspaceViews.kind.calendar': { kind: 'text', value: 'Calendar' },
  'workspaceViews.visibility.private': { kind: 'text', value: 'Private' },
  'workspaceViews.visibility.workspaceVisible': {
    kind: 'text',
    value: 'Workspace visible',
  },
  /* ⚠ `Workspace views` has TWO authors inside this slice and none outside it:
     `index.title` is a page `<h1>` (and its route frame's `sr-only <h1>`, which
     never coexist), `saved.backToViews` is a back link on another page. */
  'workspaceViews.index.title': { kind: 'text', value: 'Workspace views' },
  'workspaceViews.index.savedHeading': { kind: 'text', value: 'Saved views' },
  /* Three ASCII dots, not `…` — the bytes the component shipped. */
  'workspaceViews.index.loadingViews': {
    kind: 'text',
    value: 'Loading Workspace views...',
  },
  'workspaceViews.index.noSavedViews': {
    kind: 'text',
    value: 'No saved Workspace views',
  },
  'workspaceViews.index.viewMeta': {
    kind: 'plural',
    forms: {
      one: '{kind} / {visibility} / {count} board',
      other: '{kind} / {visibility} / {count} boards',
    },
  },
  /* ⚠ THREE AUTHORS FOR `Create Workspace view`, INSIDE ONE FORM, deliberately.
     FR-VIEW-030 binds the form's accessible name and the submit button's name
     SEPARATELY, and a translator must be free to make the button an imperative
     while the heading stays a noun phrase. */
  'workspaceViews.index.createFormLabel': {
    kind: 'text',
    value: 'Create Workspace view',
  },
  'workspaceViews.index.createHeading': {
    kind: 'text',
    value: 'Create Workspace view',
  },
  'workspaceViews.index.createSubmit': {
    kind: 'text',
    value: 'Create Workspace view',
  },
  'workspaceViews.index.createDescription': {
    kind: 'text',
    value: 'Save a Table or Calendar configuration for this workspace.',
  },
  'workspaceViews.index.titleLabel': { kind: 'text', value: 'View title' },
  'workspaceViews.index.kindLabel': { kind: 'text', value: 'View type' },
  'workspaceViews.index.visibilityLabel': { kind: 'text', value: 'Visibility' },
  'workspaceViews.index.boardsLegend': { kind: 'text', value: 'Boards' },
  'workspaceViews.index.loadingBoards': { kind: 'text', value: 'Loading boards...' },
  'workspaceViews.index.noBoards': {
    kind: 'text',
    value: 'Create a board before saving a Workspace view.',
  },
  /* Text, not plural: the noun agrees with the fixed cap. */
  'workspaceViews.index.boardLimit': {
    kind: 'text',
    value: 'Saved Workspace views are capped at {limit} boards.',
  },
  'workspaceViews.index.keywordLabel': { kind: 'text', value: 'Filter by keyword' },
  'workspaceViews.index.dueLegend': { kind: 'text', value: 'Due date' },
  'workspaceViews.index.refresh': { kind: 'text', value: 'Refresh' },
  'workspaceViews.index.signInTitle': {
    kind: 'text',
    value: 'Sign in to see Workspace views',
  },
  'workspaceViews.index.signInBody': {
    kind: 'text',
    value: 'Saved Workspace views are visible only to signed-in workspace members.',
  },
  'workspaceViews.index.signInAction': { kind: 'text', value: 'Sign in' },
  'workspaceViews.index.accountErrorTitle': {
    kind: 'text',
    value: 'We could not load your account',
  },
  'workspaceViews.index.genericError': {
    kind: 'text',
    value: 'Something went wrong. Try again.',
  },
  /* ⚠ HYPHEN. The table and calendar routes write the same sentence with an EM
     DASH; both spellings are asserted by tests this slice does not own. */
  'workspaceViews.index.notFound': {
    kind: 'text',
    value: 'No such workspace - or it is not yours to see.',
  },
  'workspaceViews.index.loadFailedTitle': {
    kind: 'text',
    value: 'We could not load Workspace views',
  },
  'workspaceViews.index.workspaceFallback': { kind: 'text', value: 'Workspace' },
  'workspaceViews.table.title': { kind: 'text', value: 'Workspace Table' },
  /* ⚠ TEXT, NOT PLURAL — see the block comment above. */
  'workspaceViews.table.selectedCount': {
    kind: 'text',
    value: '{count} of {limit} boards selected',
  },
  'workspaceViews.table.boardsHeading': { kind: 'text', value: 'Boards' },
  'workspaceViews.table.noBoards': {
    kind: 'text',
    value: 'Create a board before using the workspace table.',
  },
  'workspaceViews.table.filtersHeading': {
    kind: 'text',
    value: 'Workspace Table filters',
  },
  'workspaceViews.table.keywordLabel': { kind: 'text', value: 'Filter by keyword' },
  'workspaceViews.table.facet.members': { kind: 'text', value: 'Members' },
  'workspaceViews.table.facet.labels': { kind: 'text', value: 'Labels' },
  'workspaceViews.table.facet.lists': { kind: 'text', value: 'Lists' },
  'workspaceViews.table.facet.due': { kind: 'text', value: 'Due' },
  'workspaceViews.table.noMembersOption': { kind: 'text', value: 'No members' },
  'workspaceViews.table.noLabelsOption': { kind: 'text', value: 'No labels' },
  /* A fallback rendered AS a name, which is why it does not reuse
     `labelPicker.newNamePlaceholder` — that placeholder invites one. */
  'workspaceViews.table.unnamedLabel': { kind: 'text', value: 'Unnamed label' },
  /* ⚠ THE SHORT DUE SPELLING, verbatim. The index and calendar pages render the
     LONG form (`Due in the next week`) through `boardFace.filters.due.*`. */
  'workspaceViews.table.due.no_due_date': { kind: 'text', value: 'No due date' },
  'workspaceViews.table.due.overdue': { kind: 'text', value: 'Overdue' },
  'workspaceViews.table.due.due_next_day': { kind: 'text', value: 'Due next day' },
  'workspaceViews.table.due.due_next_week': { kind: 'text', value: 'Due next week' },
  'workspaceViews.table.due.due_next_month': { kind: 'text', value: 'Due next month' },
  /* A COLON. The calendar's own list option uses a slash; they differ on disk
     and are not normalised here. */
  'workspaceViews.table.listOption': { kind: 'text', value: '{board}: {list}' },
  'workspaceViews.table.loadFailedTitle': {
    kind: 'text',
    value: 'We could not load the workspace table',
  },
  'workspaceViews.table.retry': { kind: 'text', value: 'Try again' },
  'workspaceViews.table.loading': {
    kind: 'text',
    value: 'Loading Workspace Table...',
  },
  'workspaceViews.table.selectBoard': {
    kind: 'text',
    value: 'Select at least one board.',
  },
  'workspaceViews.table.rowsLabel': { kind: 'text', value: 'Workspace Table rows' },
  'workspaceViews.table.column.board': { kind: 'text', value: 'Board' },
  'workspaceViews.table.column.card': { kind: 'text', value: 'Card' },
  'workspaceViews.table.column.list': { kind: 'text', value: 'List' },
  'workspaceViews.table.column.labels': { kind: 'text', value: 'Labels' },
  'workspaceViews.table.column.members': { kind: 'text', value: 'Members' },
  'workspaceViews.table.column.dueDate': { kind: 'text', value: 'Due date' },
  /* ONE key for the rename `IconButton`'s name AND the rename `TextField`'s
     label — one affordance, and FR-VIEW-016 binds identical bytes through
     both. */
  'workspaceViews.table.renameCard': { kind: 'text', value: 'Rename {title}' },
  'workspaceViews.table.save': { kind: 'text', value: 'Save' },
  'workspaceViews.table.cancel': { kind: 'text', value: 'Cancel' },
  'workspaceViews.table.changeList': {
    kind: 'text',
    value: 'Change list for {title}',
  },
  'workspaceViews.table.editLabels': {
    kind: 'text',
    value: 'Edit labels for {title}',
  },
  'workspaceViews.table.editMembers': {
    kind: 'text',
    value: 'Edit members for {title}',
  },
  'workspaceViews.table.setDueDate': {
    kind: 'text',
    value: 'Set due date for {title}',
  },
  'workspaceViews.table.noLabelsCell': { kind: 'text', value: 'No labels' },
  'workspaceViews.table.noMembersCell': { kind: 'text', value: 'No members' },
  'workspaceViews.table.noDueDate': { kind: 'text', value: 'No due date' },
  'workspaceViews.table.facetLoading': { kind: 'text', value: 'Loading...' },
  'workspaceViews.table.renameFailed': {
    kind: 'text',
    value: 'Could not rename the card. Its previous title stands.',
  },
  'workspaceViews.table.moveFailed': {
    kind: 'text',
    value: 'Could not move the card. It was returned to its position.',
  },
  'workspaceViews.table.signInTitle': {
    kind: 'text',
    value: 'Sign in to see this workspace table',
  },
  'workspaceViews.table.signInBody': {
    kind: 'text',
    value: 'Workspace tables are visible only to signed-in workspace members.',
  },
  'workspaceViews.table.signInAction': { kind: 'text', value: 'Sign in' },
  'workspaceViews.table.accountErrorTitle': {
    kind: 'text',
    value: 'We could not load your account',
  },
  'workspaceViews.table.genericError': {
    kind: 'text',
    value: 'Something went wrong. Try again.',
  },
  /* ⚠ EM DASH here and on the calendar route; the views and saved-view routes
     write the same sentence with a HYPHEN. Both are asserted elsewhere. */
  'workspaceViews.table.notFound': {
    kind: 'text',
    value: 'No such workspace — or it is not yours to see.',
  },
  'workspaceViews.table.workspaceFallback': { kind: 'text', value: 'Workspace' },
  'workspaceViews.calendar.title': { kind: 'text', value: 'Workspace Calendar' },
  'workspaceViews.calendar.previousMonth': { kind: 'text', value: 'Previous' },
  'workspaceViews.calendar.nextMonth': { kind: 'text', value: 'Next' },
  'workspaceViews.calendar.boardsHeading': { kind: 'text', value: 'Boards' },
  'workspaceViews.calendar.boardsDescription': {
    kind: 'text',
    value: 'Select up to {limit} boards for this calendar.',
  },
  'workspaceViews.calendar.noBoardsTitle': {
    kind: 'text',
    value: 'No boards in this workspace',
  },
  'workspaceViews.calendar.noBoardsBody': {
    kind: 'text',
    value: 'Create a board before using the workspace calendar.',
  },
  'workspaceViews.calendar.boardClosed': { kind: 'text', value: 'Closed' },
  'workspaceViews.calendar.boardLimit': {
    kind: 'text',
    value:
      'The Workspace Calendar is capped at {limit} boards. Clear a selected board before adding another.',
  },
  'workspaceViews.calendar.hiddenBoards': {
    kind: 'plural',
    forms: {
      one: '{count} more board can be added after you clear room in this view.',
      other: '{count} more boards can be added after you clear room in this view.',
    },
  },
  'workspaceViews.calendar.unavailableBoards': {
    kind: 'plural',
    forms: {
      one: '{count} selected board is not available in this picker. It stays selected for the request, but inaccessible boards return no entries.',
      other:
        '{count} selected boards are not available in this picker. They stay selected for the request, but inaccessible boards return no entries.',
    },
  },
  'workspaceViews.calendar.loadFailedTitle': {
    kind: 'text',
    value: 'We could not load the workspace calendar',
  },
  'workspaceViews.calendar.retry': { kind: 'text', value: 'Try again' },
  'workspaceViews.calendar.noSelectionTitle': {
    kind: 'text',
    value: 'Select a board to show the calendar',
  },
  'workspaceViews.calendar.noSelectionBody': {
    kind: 'text',
    value: 'Workspace Calendar needs at least one board selected.',
  },
  /* ⚠ `1 card without due dates is not shown.` — a PLURAL noun inside the
     SINGULAR arm. Those are the shipped bytes and a sibling test asserts them;
     preserved rather than corrected. */
  'workspaceViews.calendar.excluded': {
    kind: 'plural',
    forms: {
      one: '{count} card without due dates is not shown.',
      other: '{count} cards without due dates are not shown.',
    },
  },
  /* `{month}` arrives from `lib/workspace-calendar.ts`, which hardcodes
     `Intl.DateTimeFormat('en', …)` — so this accessible name is half-translated
     in every other language. Reported, and outside this slice's file set. */
  'workspaceViews.calendar.gridLabel': { kind: 'text', value: '{month} calendar' },
  'workspaceViews.calendar.filtersHeading': { kind: 'text', value: 'Filters' },
  'workspaceViews.calendar.noFilters': {
    kind: 'text',
    value: 'Showing selected boards without filters.',
  },
  'workspaceViews.calendar.activeFilters': {
    kind: 'plural',
    forms: { one: '{count} active filter.', other: '{count} active filters.' },
  },
  'workspaceViews.calendar.clearFilters': { kind: 'text', value: 'Clear filters' },
  'workspaceViews.calendar.keywordLabel': { kind: 'text', value: 'Filter by keyword' },
  'workspaceViews.calendar.legend.members': { kind: 'text', value: 'Members' },
  'workspaceViews.calendar.legend.labels': { kind: 'text', value: 'Labels' },
  'workspaceViews.calendar.legend.lists': { kind: 'text', value: 'Lists' },
  'workspaceViews.calendar.legend.due': { kind: 'text', value: 'Due date' },
  /* A SLASH. The table's own list option uses a colon. */
  'workspaceViews.calendar.listOption': { kind: 'text', value: '{board} / {list}' },
  /* The seven grid column headers, held in `WEEKDAY_KEYS` because a
     module-level array cannot call `t()`. The React key is the CATALOGUE KEY,
     never the resolved text, so the key does not change per language. */
  'workspaceViews.calendar.weekday.sun': { kind: 'text', value: 'Sun' },
  'workspaceViews.calendar.weekday.mon': { kind: 'text', value: 'Mon' },
  'workspaceViews.calendar.weekday.tue': { kind: 'text', value: 'Tue' },
  'workspaceViews.calendar.weekday.wed': { kind: 'text', value: 'Wed' },
  'workspaceViews.calendar.weekday.thu': { kind: 'text', value: 'Thu' },
  'workspaceViews.calendar.weekday.fri': { kind: 'text', value: 'Fri' },
  'workspaceViews.calendar.weekday.sat': { kind: 'text', value: 'Sat' },
  'workspaceViews.calendar.dayEntryCount': {
    kind: 'plural',
    forms: { one: '{count} entry', other: '{count} entries' },
  },
  'workspaceViews.calendar.dayEntries': { kind: 'text', value: '{date} entries' },
  'workspaceViews.calendar.checklistItemParent': {
    kind: 'text',
    value: 'Checklist item / {card}',
  },
  'workspaceViews.calendar.untitledCard': { kind: 'text', value: 'Untitled card' },
  /* Text, not plural: `+{count} more` carries no noun and does not branch in
     English. `boardTimeline.drawerOverflow` is plural because it carries
     `card` / `cards`; this one does not. */
  'workspaceViews.calendar.moreEntries': { kind: 'text', value: '+{count} more' },
  'workspaceViews.calendar.signInTitle': {
    kind: 'text',
    value: 'Sign in to see this workspace calendar',
  },
  'workspaceViews.calendar.signInBody': {
    kind: 'text',
    value: 'Workspace calendars are visible only to signed-in workspace members.',
  },
  'workspaceViews.calendar.signInAction': { kind: 'text', value: 'Sign in' },
  'workspaceViews.calendar.accountErrorTitle': {
    kind: 'text',
    value: 'We could not load your account',
  },
  'workspaceViews.calendar.genericError': {
    kind: 'text',
    value: 'Something went wrong. Try again.',
  },
  /* ⚠ EM DASH, as on the table route. */
  'workspaceViews.calendar.notFound': {
    kind: 'text',
    value: 'No such workspace — or it is not yours to see.',
  },
  'workspaceViews.calendar.workspaceFallback': { kind: 'text', value: 'Workspace' },
  'workspaceViews.saved.backToViews': { kind: 'text', value: 'Workspace views' },
  'workspaceViews.saved.eyebrow.table': {
    kind: 'text',
    value: 'Saved Workspace Table',
  },
  'workspaceViews.saved.eyebrow.calendar': {
    kind: 'text',
    value: 'Saved Workspace Calendar',
  },
  'workspaceViews.saved.renameFormLabel': {
    kind: 'text',
    value: 'Rename Workspace view',
  },
  'workspaceViews.saved.titleLabel': { kind: 'text', value: 'View title' },
  'workspaceViews.saved.renameSave': { kind: 'text', value: 'Save' },
  'workspaceViews.saved.renameCancel': { kind: 'text', value: 'Cancel' },
  'workspaceViews.saved.meta': {
    kind: 'plural',
    forms: {
      one: '{visibility} / {count} board',
      other: '{visibility} / {count} boards',
    },
  },
  'workspaceViews.saved.visibilityLabel': { kind: 'text', value: 'Visibility' },
  'workspaceViews.saved.actionsMenu': {
    kind: 'text',
    value: '{title}, saved view actions',
  },
  'workspaceViews.saved.rename': { kind: 'text', value: 'Rename' },
  'workspaceViews.saved.delete': { kind: 'text', value: 'Delete' },
  'workspaceViews.saved.readOnlyNotice': {
    kind: 'text',
    value:
      'Only the creator can rename, change visibility or delete this Workspace view.',
  },
  'workspaceViews.saved.deleteTitle': { kind: 'text', value: 'Delete {title}?' },
  'workspaceViews.saved.deleteDescription': {
    kind: 'text',
    value:
      'This deletes only the saved Workspace view. Boards and cards are not deleted.',
  },
  'workspaceViews.saved.deleteCancel': { kind: 'text', value: 'Cancel' },
  'workspaceViews.saved.deleteConfirm': { kind: 'text', value: 'Delete view' },
  'workspaceViews.saved.deleteBody': {
    kind: 'text',
    value:
      'The boards selected by this view and every card on those boards remain intact.',
  },
  'workspaceViews.saved.title': { kind: 'text', value: 'Workspace view' },
  'workspaceViews.saved.signInTitle': {
    kind: 'text',
    value: 'Sign in to see this Workspace view',
  },
  'workspaceViews.saved.signInBody': {
    kind: 'text',
    value: 'Saved Workspace views are visible only to signed-in workspace members.',
  },
  'workspaceViews.saved.signInAction': { kind: 'text', value: 'Sign in' },
  'workspaceViews.saved.accountErrorTitle': {
    kind: 'text',
    value: 'We could not load your account',
  },
  'workspaceViews.saved.genericError': {
    kind: 'text',
    value: 'Something went wrong. Try again.',
  },
  /* ⚠ HYPHEN, as on the views route. */
  'workspaceViews.saved.notFound': {
    kind: 'text',
    value: 'No such Workspace view - or it is not yours to see.',
  },
  'workspaceViews.saved.loadFailedTitle': {
    kind: 'text',
    value: 'We could not load this Workspace view',
  },
  'workspaceViews.saved.workspaceFallback': { kind: 'text', value: 'Workspace' },
  'cardBack.announce.moved': { kind: 'text', value: 'Card {title} moved.' },
  'cardBack.announce.copied': { kind: 'text', value: 'Card {title} copied.' },
  'cardBack.announce.merged': {
    kind: 'text',
    value: 'Cards {title} and {source} merged into {result}.',
  },
  'cardBack.announce.markedTemplate': {
    kind: 'text',
    value: '{title} marked as a template.',
  },
  'cardBack.announce.unmarkedTemplate': {
    kind: 'text',
    value: '{title} is a regular card again.',
  },
  'cardBack.announce.templateHiddenFromList': {
    kind: 'text',
    value: '{title} hidden from its list.',
  },
  'cardBack.announce.templateShownInList': {
    kind: 'text',
    value: '{title} shown in its list.',
  },
  'cardBack.announce.createdFromTemplate': {
    kind: 'text',
    value: 'Card {title} created from template.',
  },
  'cardBack.announce.restored': {
    kind: 'text',
    value: 'Card {title} was sent to the board.',
  },
  'cardBack.announce.deleted': {
    kind: 'text',
    value: 'Deleted the archived card {title}.',
  },
  'cardBack.announce.cardRenamed': {
    kind: 'text',
    value: 'Card renamed to {title}.',
  },
  'cardBack.announce.emailReset': {
    kind: 'text',
    value: 'Card email address reset.',
  },
  'cardBack.completion.markComplete': { kind: 'text', value: 'Mark card complete' },
  'cardBack.completion.markIncomplete': {
    kind: 'text',
    value: 'Mark card incomplete',
  },
  'cardBack.aging.lastUpdated': { kind: 'text', value: 'Last updated {date}' },
  'cardBack.aging.evergreenOn': { kind: 'text', value: 'Evergreen on' },
  'cardBack.aging.evergreenOff': { kind: 'text', value: 'Evergreen off' },
  'cardBack.aging.evergreenOnAriaLabel': {
    kind: 'text',
    value: 'Evergreen on, turn off for this card',
  },
  'cardBack.aging.evergreenOffAriaLabel': {
    kind: 'text',
    value: 'Evergreen off, turn on for this card',
  },
  'cardBack.error.title': { kind: 'text', value: 'This card could not be opened' },
  'cardBack.error.body': {
    kind: 'text',
    value: 'The card is unavailable, or it is not yours to see.',
  },
  'cardBack.error.retry': { kind: 'text', value: 'Try again' },
  'cardBack.live.open.title': { kind: 'text', value: 'Connected' },
  'cardBack.live.open.body': {
    kind: 'text',
    value: 'Live card updates are current.',
  },
  'cardBack.live.reconnecting.title': { kind: 'text', value: 'Reconnecting' },
  'cardBack.live.reconnecting.body': {
    kind: 'text',
    value:
      'This card may be out of date. You can keep editing; changes are marked until they send.',
  },
  'cardBack.live.refused.title': { kind: 'text', value: 'Live updates stopped' },
  'cardBack.live.refused.body': {
    kind: 'text',
    value: 'This card can no longer receive live updates. Try refreshing the card.',
  },
  'cardBack.description.heading': { kind: 'text', value: 'Description' },
  'cardBack.description.add': { kind: 'text', value: 'Add description' },
  'cardBack.description.edit': { kind: 'text', value: 'Edit' },
  'cardBack.description.limitReached': {
    kind: 'text',
    value: 'The description is at the 16,384 character limit.',
  },
  'cardBack.description.empty.title': { kind: 'text', value: 'No description' },
  'cardBack.description.empty.body': {
    kind: 'text',
    value: 'Add context, links or notes for this card.',
  },
  'cardBack.description.conflict.title': {
    kind: 'text',
    value: 'Description changed while you were editing',
  },
  'cardBack.description.conflict.body': {
    kind: 'text',
    value:
      'Your draft is still here. Saving keeps your text and records the overridden version in activity.',
  },
  'cardBack.description.queued.title': {
    kind: 'text',
    value: 'Pending change on {card}',
  },
  'cardBack.description.queued.body': {
    kind: 'text',
    value: 'The description change has not reached the server yet.',
  },
  'cardBack.description.queued.retry': { kind: 'text', value: 'Retry now' },
  'cardBack.description.queued.retryPaused': {
    kind: 'text',
    value: 'Automatic retry is paused. Try again manually, or wait until {time}.',
  },
  'cardBack.description.queued.saveRetryHint': {
    kind: 'text',
    value: 'Saving will retry the pending description change.',
  },
  'cardBack.description.deleted.title': {
    kind: 'text',
    value: 'Could not save {card}',
  },
  'cardBack.description.deleted.body': {
    kind: 'text',
    value: 'The card is no longer available. Your unsent description was: ',
  },
  'cardBack.description.discard.title': {
    kind: 'text',
    value: 'Discard description draft?',
  },
  'cardBack.description.discard.description': {
    kind: 'text',
    value: 'Your unsaved description text will be removed from this browser.',
  },
  'cardBack.description.discard.body': {
    kind: 'text',
    value: 'This keeps the saved card description unchanged.',
  },
  'cardBack.description.discard.keep': { kind: 'text', value: 'Keep editing' },
  'cardBack.description.discard.confirm': { kind: 'text', value: 'Discard draft' },

  /* FE-80.1 RICH DESCRIPTION EDITOR — 19 keys for 18 authored literals, and
     the count is the reverse asymmetry to the label picker's.

     The picker had 28 keys covering 31 sites because three VALUES were authored
     twice. Here one literal reaches TWO SINKS: each of the twelve `TOOLBAR`
     entries is read by `Tooltip content=` (a description, wired through
     `aria-describedby`) AND by `IconButton label=` (the accessible name). So 18
     literals occupy 30 sinks, and the 19th key is `…editor.saving`, which
     replaces no literal at all — see below.

     ⚠ THE `format.` SEGMENT IS LOAD-BEARING, not tidiness. Without it the
     heading button's key would be `cardBack.description.editor.heading`, which
     is ONE dropped segment away from `cardBack.description.heading` — a key
     that exists, resolves, and carries a DIFFERENT value (`Description`). The
     segment makes that slip a two-segment edit rather than a one-word one.

     ⚠ The slip WAS run as a mutation, and the correction is worth keeping: it
     is caught, by the leak scan and by the toolbar discriminator, but NOT for
     the reason first written here. It survives the `declares every key it
     routes` sweep exactly as predicted — that sweep looks for undeclared KEY
     STRINGS in the residue and a wrong-but-declared key produces none. What
     kills it is that under a doctored catalogue the wrong key resolves to real
     English (`Description`) where a sentinel was expected. So the guard that
     catches it is the one whose stated subject is something else, and the
     prediction that "no guard can see it" was measured false. Reasoning about
     which guard covers what is not a substitute for running the mutation.

     ⚠ TWELVE OF THE EIGHTEEN ARE BARE ENGLISH NOUNS AND VERBS, which makes this
     the worst substring surface FE-80.1 has taken. Each was checked against the
     live catalogue ONE VALUE AT A TIME, before being written here:
       - `Bold`, `Italic`, `Strikethrough`, `Inline code`, `Numbered list`,
         `Quote` are each BYTE-IDENTICAL to a
         `cardBack.comments.composer.formatting.*` value — the comment
         composer's own formatting toolbar. Refused as shared keys; the argument
         is below.
       - `Link` is EXACT at three keys and a proper prefix of eleven more
         (`Link copied.`, `Link card`, `Link {card}`…).
       - `Save` is exact at three and a prefix of thirteen (`Save label`,
         `Save due date`…); `Cancel` is exact at TWELVE.
       - `Table` is exact at `boardFace.view.table`; `Quote` is a prefix of
         `Quote this comment in a reply`.
     None is a duplicate in the sense that matters — each names a different
     control on a different surface — but no substring match can tell a prefix
     from a whole, so in `WebChromeProbe` these are pinned by the `/` separators
     around them and by nothing else. That is truer here than in any prior
     slice.

     ⚠ WHY THESE ARE NOT THE COMPOSER'S KEYS. `CommentComposer` already routes a
     formatting toolbar through `cardBack.comments.composer.formatting.*`, and
     six values are byte-identical. Sharing was refused on the grep: NO key in
     this catalogue is consumed by two different component files. The
     `cardBack.location.heading` precedent — the one place a key is deliberately
     shared — shares between two sinks in ONE file, one header row apart, and
     was explicitly argued that way. Routing a second component through
     `…comments.composer.formatting.*` would make that the first cross-file
     share in the catalogue, and would name the description toolbar's copy after
     the comment composer: a later copy change to one surface would silently
     move the other. The composer slice is itself the precedent — an analogous
     second toolbar that took its own namespace rather than sharing.

     ⚠ AND `…editor.fieldLabel` IS `Description`, THE SAME WORD AS
     `cardBack.description.heading`, ON SCREEN AT THE SAME TIME. This is the
     closest this chunk has come to a shared key: `CardBack.tsx` renders the
     `<h2>` and the editor's `TextField` together whenever the editor is open,
     same word, same meaning. It is still two keys, on the same cross-file grep
     — the `<h2>` is authored in `CardBack.tsx`, the field label in
     `RichDescriptionEditor.tsx` — and on role: one is a region's accessible
     name (`aria-labelledby="card-description-heading"`), the other a control's.
     A translator who diverges them does so visibly, in one section, rather than
     latently. Recorded as the closest call rather than an obvious one.

     ⚠ `…editor.saving` REPLACES NO LITERAL. `RichDescriptionEditor` passes
     `busy` to its Save `Button` and passes no `busyLabel`, so the primitive's
     UNROUTED default — `Working…`, `Button.tsx:154` — is what this surface
     announces into its sr-only `role="status"` today. That default is fe-plan
     residual 9, unowned for eleven slices, and it is NOT taken here: supplying
     a value at this call site fills a slot the file never filled, which leaves
     `Button.tsx` untouched and the residual intact for whoever funds the
     primitive. The value is discriminated by its noun on the
     `cardBack.stickers.loadingLibrary` rule — a bare `Working…` names nothing a
     translator or a screen-reader user can place — and spells its ellipsis as
     THREE ASCII PERIODS, this catalogue's own idiom (`cardBack.dates.saving` is
     `Saving...`), deliberately NOT the U+2026 that `Button`'s default carries.

     Three values are EXTERNALLY PINNED as browser-visible accessible names and
     must stay byte-identical: `Description formatting toolbar` and `Description
     preview` (`FR-CARD-003.spec.ts:641`, `:646`, `:659` and
     `FR-EXP-031.spec.ts:607`), and `Description` (the textbox name every unit
     render locates by). */
  'cardBack.description.editor.toolbar': {
    kind: 'text',
    value: 'Description formatting toolbar',
  },
  'cardBack.description.editor.format.heading': { kind: 'text', value: 'Heading' },
  'cardBack.description.editor.format.bold': { kind: 'text', value: 'Bold' },
  'cardBack.description.editor.format.italic': { kind: 'text', value: 'Italic' },
  'cardBack.description.editor.format.strikethrough': {
    kind: 'text',
    value: 'Strikethrough',
  },
  'cardBack.description.editor.format.inlineCode': {
    kind: 'text',
    value: 'Inline code',
  },
  'cardBack.description.editor.format.link': { kind: 'text', value: 'Link' },
  // ⚠ `Bullet list` here, `Bulleted list` at
  // `cardBack.comments.composer.formatting.bulletedList` — two spellings of one
  // concept, already divergent in English before this slice existed. Preserved
  // byte-for-byte because harmonising them is a copy change to a shipped
  // surface, not a routing one; reported in the chunk's return.
  'cardBack.description.editor.format.bulletList': {
    kind: 'text',
    value: 'Bullet list',
  },
  'cardBack.description.editor.format.numberedList': {
    kind: 'text',
    value: 'Numbered list',
  },
  'cardBack.description.editor.format.quote': { kind: 'text', value: 'Quote' },
  'cardBack.description.editor.format.codeBlock': { kind: 'text', value: 'Code block' },
  'cardBack.description.editor.format.image': { kind: 'text', value: 'Image' },
  'cardBack.description.editor.format.table': { kind: 'text', value: 'Table' },
  'cardBack.description.editor.fieldLabel': { kind: 'text', value: 'Description' },
  'cardBack.description.editor.preview': {
    kind: 'text',
    value: 'Description preview',
  },
  'cardBack.description.editor.previewEmpty': {
    kind: 'text',
    value: 'Nothing to preview yet.',
  },
  'cardBack.description.editor.save': { kind: 'text', value: 'Save' },
  'cardBack.description.editor.saving': {
    kind: 'text',
    value: 'Saving description...',
  },
  'cardBack.description.editor.cancel': { kind: 'text', value: 'Cancel' },
  /* FE-80.1 card cover section — component-owned chrome only. The cover preview
     visual labels still come from `lib/card-cover.ts` because that helper is
     shared with card tiles and needs its own residual. */
  'cardBack.cover.heading': { kind: 'text', value: 'Cover' },
  'cardBack.cover.trigger': { kind: 'text', value: 'Cover' },
  'cardBack.cover.triggerLabel': {
    kind: 'text',
    value: 'Cover, edit cover for {title}',
  },
  'cardBack.cover.popoverTitle': { kind: 'text', value: 'Card cover' },
  'cardBack.cover.closePopover': {
    kind: 'text',
    value: 'Close cover popover',
  },
  'cardBack.cover.remove': { kind: 'text', value: 'Remove cover' },
  'cardBack.cover.removing': { kind: 'text', value: 'Removing...' },
  'cardBack.cover.apply': { kind: 'text', value: 'Apply cover' },
  'cardBack.cover.saving': { kind: 'text', value: 'Saving...' },
  'cardBack.cover.disabled.uploadFirst': {
    kind: 'text',
    value: 'Finish uploading the image first.',
  },
  'cardBack.cover.disabled.photoFirst': {
    kind: 'text',
    value: 'Choose a photo first.',
  },
  'cardBack.cover.disabled.attachmentFirst': {
    kind: 'text',
    value: 'Choose an attachment first.',
  },
  'cardBack.cover.source.label': { kind: 'text', value: 'Cover source' },
  'cardBack.cover.source.colour': { kind: 'text', value: 'Colour' },
  'cardBack.cover.source.photo': { kind: 'text', value: 'Photo' },
  'cardBack.cover.source.attachment': { kind: 'text', value: 'Attachment' },
  'cardBack.cover.coloursLabel': { kind: 'text', value: 'Cover colours' },
  'cardBack.cover.photosLabel': { kind: 'text', value: 'Photo covers' },
  'cardBack.cover.attachmentsLabel': { kind: 'text', value: 'Attachment covers' },
  'cardBack.cover.upload.fileLabel': {
    kind: 'text',
    value: 'Upload cover image',
  },
  'cardBack.cover.upload.submit': {
    kind: 'text',
    value: 'Upload cover image',
  },
  'cardBack.cover.upload.busy': {
    kind: 'text',
    value: 'Uploading cover image',
  },
  'cardBack.cover.upload.unsupportedType': {
    kind: 'text',
    value: 'Cover uploads must be image files.',
  },
  'cardBack.cover.upload.tooLarge': {
    kind: 'text',
    value: 'Files must be 250 MB or smaller.',
  },
  'cardBack.cover.upload.noneSelected': {
    kind: 'text',
    value: 'No image selected',
  },
  'cardBack.cover.upload.selection': {
    kind: 'text',
    value: '{name} · {size}',
  },
  'cardBack.cover.upload.progress': {
    kind: 'text',
    value: 'Cover image upload progress',
  },
  'cardBack.cover.upload.complete': {
    kind: 'text',
    value: '{name} selected as cover.',
  },
  'cardBack.cover.error.uploadFailed': {
    kind: 'text',
    value: 'Could not upload cover image.',
  },
  'cardBack.cover.attachments.emptyTitle': {
    kind: 'text',
    value: 'No file attachments',
  },
  'cardBack.cover.attachments.emptyBody': {
    kind: 'text',
    value: 'Uploaded cover images and other file attachments will appear here.',
  },
  'cardBack.cover.size.label': { kind: 'text', value: 'Cover size' },
  'cardBack.cover.size.normal': { kind: 'text', value: 'Normal' },
  'cardBack.cover.size.full': { kind: 'text', value: 'Full' },
  'cardBack.cover.textTreatment.label': {
    kind: 'text',
    value: 'Text treatment',
  },
  'cardBack.cover.textTreatment.dark': { kind: 'text', value: 'Dark text' },
  'cardBack.cover.textTreatment.light': { kind: 'text', value: 'Light text' },
  'cardBack.cover.noCover': {
    kind: 'text',
    value: 'No cover on this card.',
  },
  'cardBack.cover.announce.saved': {
    kind: 'text',
    value: 'Card cover saved.',
  },
  'cardBack.cover.announce.removed': {
    kind: 'text',
    value: 'Card cover removed.',
  },
  // FE-80.1 card-cover-tail -- `lib/card-cover.ts`'s `coverVisual()`, the three
  // fallback labels the card-cover-section slice deliberately left outside its
  // run (see the comment at `interface-language.test.tsx:2277`) because the
  // helper feeds BOTH the card back (`CardCoverSection.tsx:483/:490`) and the
  // board front (`CardTile.tsx:1444/:1644`). The translator is THREADED to both
  // callers rather than the helper duplicated; a fix at one caller alone leaves
  // the other rendering English with nothing red.
  //
  // ⚠ THE `cardBack` PREFIX IS DELIBERATE DESPITE THE BOARD-FRONT CONSUMER.
  // These sit with the `cardBack.cover.*` family they belong to; a `board.*`
  // home would be another slice's ground.
  //
  // ⚠ `{colour}` IS FILLED BY `labelColourDisplayName(t, colour)`, NOT BY
  // `labelColourName()`. `palette.ts:724` says so in as many words -- the
  // latter is the source-English projection and only a caller with no
  // translator may use it. `board-frame.routing.test.ts:194-205` records
  // `BoardMapView` making exactly this migration for the same sentence, and
  // `boardMap.pin.appearance.colourCover` below carries the same value for the
  // other surface. Filling the slot from the projection would print an English
  // colour word inside a translated sentence -- the half-translated defect
  // `board-backgrounds.ts:200-212` was written to kill.
  //
  // `visual.photo` is a FALLBACK: a known photo resolves through
  // `boardBackgroundPresetLabel(t, photo)` and only an unknown `photoRef`
  // reaches this value.
  'cardBack.cover.visual.colour': { kind: 'text', value: '{colour} cover' },
  'cardBack.cover.visual.photo': { kind: 'text', value: 'Photo cover' },
  'cardBack.cover.visual.attachment': { kind: 'text', value: 'Attachment cover' },

  'cardBack.fieldOverride.title.title': {
    kind: 'text',
    value: 'An unseen title change was overridden',
  },
  'cardBack.fieldOverride.title.description': {
    kind: 'text',
    value: 'An unseen description change was overridden',
  },
  'cardBack.fieldOverride.emptyBody': {
    kind: 'text',
    value: 'The overridden field was empty. The full record is in the activity stream.',
  },
  'cardBack.archivedList.title': {
    kind: 'text',
    value: 'This card is in an archived list',
  },
  'cardBack.archivedList.body': {
    kind: 'text',
    value:
      'The list "{list}" is archived. The card is still reachable from its permalink.',
  },
  'cardBack.archived.title': { kind: 'text', value: 'This card is archived' },
  'cardBack.archived.body': {
    kind: 'text',
    value: 'It is hidden from the board face until it is sent back to the board.',
  },
  'cardBack.archived.mirrorBody': {
    kind: 'text',
    value:
      'The source card is archived. Remove the mirror from this board, or open the source board to delete the source card.',
  },
  'cardBack.archived.restoringBusy': { kind: 'text', value: 'Restoring...' },
  'cardBack.archived.sendToBoard': { kind: 'text', value: 'Send to board' },
  'cardBack.archived.delete': { kind: 'text', value: 'Delete' },
  'cardBack.watch.description': {
    kind: 'text',
    value:
      'Notifications for new comments, due-date changes, card moves and archives will include this card.',
  },
  'cardBack.actions.share': { kind: 'text', value: 'Share card' },
  'cardBack.mobile.share.title': {
    kind: 'text',
    value: 'Card print and export are available on the web',
  },
  'cardBack.mobile.share.body': {
    kind: 'text',
    value: 'Open this exact card on web to print it or export its JSON.',
  },
  'cardBack.actions.convertLink': { kind: 'text', value: 'Convert to card' },
  'cardBack.actions.createFromTemplate': {
    kind: 'text',
    value: 'Create card from template',
  },
  'cardBack.actions.unmarkTemplate': { kind: 'text', value: 'Remove from template' },
  'cardBack.actions.makeTemplate': { kind: 'text', value: 'Make template' },
  'cardBack.actions.mirror': { kind: 'text', value: 'Mirror' },
  'cardBack.actions.move': { kind: 'text', value: 'Move' },
  'cardBack.actions.copy': { kind: 'text', value: 'Copy' },
  'cardBack.actions.copyAriaLabel': { kind: 'text', value: 'Copy card' },
  'cardBack.actions.merge': { kind: 'text', value: 'Merge' },
  'cardBack.actions.archive': { kind: 'text', value: 'Archive card' },
  'cardBack.actions.archivingBusy': { kind: 'text', value: 'Archiving...' },
  /* The card back's header "⋯" trigger (docs/design/stage2/card-back.md D1):
   * Share, Move, Copy, Mirror, Merge, the template toggle and Archive live
   * behind it. The name of the trigger, not of any one action. */
  'cardBack.actions.menu': { kind: 'text', value: 'Card actions' },
  /* Heading of the row of compact triggers for sections that are still empty
   * (cover, location, stickers, related cards) — D1's "Add to card" row. */
  'cardBack.addToCard.heading': { kind: 'text', value: 'Add to card' },
  'cardBack.automation.stripLabel': {
    kind: 'text',
    value: 'Card automation buttons',
  },
  'cardBack.linkCard.title': { kind: 'text', value: 'Link card' },
  'cardBack.linkCard.body': {
    kind: 'text',
    value:
      'This card is a preview. Convert it to an ordinary card before editing descriptions, comments, dates, members, checklists or other card fields.',
  },
  'cardBack.powerUp.badgesLabel': { kind: 'text', value: 'Power-Up card badges' },
  'cardBack.powerUp.buttonsLabel': { kind: 'text', value: 'Power-Up card buttons' },
  'cardBack.powerUp.connect': { kind: 'text', value: 'Connect {powerUp}' },
  'cardBack.powerUp.reconnect': { kind: 'text', value: 'Reconnect {powerUp}' },
  'cardBack.powerUp.notInteractive': {
    kind: 'text',
    value: '{powerUp}: {label} is not interactive in this frontend yet.',
  },
  'cardBack.powerUp.notInteractiveAnnouncement': {
    kind: 'text',
    value: '{powerUp}: {label} is not interactive yet.',
  },
  'cardBack.labels.heading': { kind: 'text', value: 'Labels' },
  'cardBack.labels.empty': { kind: 'text', value: 'No labels on this card.' },
  'cardBack.labels.remove': {
    kind: 'text',
    value: 'Remove {label} from this card',
  },
  'cardBack.labels.filter': { kind: 'text', value: 'Filter' },
  'cardBack.labels.filterBy': { kind: 'text', value: 'Filter board by {label}' },
  'cardBack.members.heading': { kind: 'text', value: 'Members' },
  'cardBack.members.empty': {
    kind: 'text',
    value: 'No one is assigned to this card.',
  },
  'cardBack.members.pickerTitle': { kind: 'text', value: 'Card members' },
  'cardBack.members.edit': { kind: 'text', value: 'Edit card members' },
  'cardBack.members.closePicker': { kind: 'text', value: 'Close members picker' },
  'cardBack.members.search': { kind: 'text', value: 'Search board members' },
  'cardBack.members.join': { kind: 'text', value: 'Join card' },
  'cardBack.members.leave': { kind: 'text', value: 'Leave card' },
  'cardBack.members.noMatches': { kind: 'text', value: 'No board member matches' },
  'cardBack.comments.readOnly.title': {
    kind: 'text',
    value: 'This card is archived',
  },
  'cardBack.comments.readOnly.body': {
    kind: 'text',
    value: 'Archived cards are read-only until restored.',
  },
  'cardBack.comments.heading': { kind: 'text', value: 'Comments and activity' },
  'cardBack.comments.hideDetails': { kind: 'text', value: 'Hide details' },
  'cardBack.comments.showDetails': { kind: 'text', value: 'Show details' },
  'cardBack.activityEntry.articleLabel': { kind: 'text', value: '{actor} activity' },
  'cardBack.activityEntry.newBadge': { kind: 'text', value: 'New' },
  'cardBack.activityEntry.copyTooltip': {
    kind: 'text',
    value: 'Copy a link to this activity entry',
  },
  'cardBack.activityEntry.copyLabel': {
    kind: 'text',
    value: 'Copy link to activity entry',
  },
  'cardBack.activityEntry.boardPrefix': { kind: 'text', value: 'Board:' },
  'cardBack.activityEntry.collapsedChanges': {
    kind: 'plural',
    forms: {
      one: '1 similar change collapsed',
      other: '{count} similar changes collapsed',
    },
  },
  'cardBack.activityEntry.override.title': {
    kind: 'text',
    value: 'Overridden title',
  },
  'cardBack.activityEntry.override.description': {
    kind: 'text',
    value: 'Overridden description',
  },
  'cardBack.activityEntry.override.empty': {
    kind: 'text',
    value: 'The field was empty.',
  },
  'cardBack.comments.watchOffer.title': {
    kind: 'text',
    value: 'Follow replies on this card?',
  },
  'cardBack.comments.watchOffer.body': {
    kind: 'text',
    value: 'Watch this card so new comments and card changes reach your notifications.',
  },
  'cardBack.comments.watchOffer.busyLabel': { kind: 'text', value: 'Watching card' },
  'cardBack.comments.watchOffer.accept': { kind: 'text', value: 'Watch card' },
  'cardBack.comments.watchOffer.dismiss': { kind: 'text', value: 'Not now' },
  'cardBack.comments.deletedNotice.title': {
    kind: 'text',
    value: 'This comment is no longer available.',
  },
  'cardBack.comments.deletedNotice.body': {
    kind: 'text',
    value: 'The card is still available.',
  },
  'cardBack.comments.empty.commentsTitle': { kind: 'text', value: 'No comments yet' },
  'cardBack.comments.empty.activityTitle': { kind: 'text', value: 'No activity yet' },
  'cardBack.comments.empty.commentsBody': {
    kind: 'text',
    value: 'The composer stays available so the first reply is one step away.',
  },
  'cardBack.comments.empty.activityBody': {
    kind: 'text',
    value: 'Changes and comments will appear here.',
  },
  'cardBack.comments.loadingActivity': { kind: 'text', value: 'Loading activity...' },
  'cardBack.comments.loadingComments': { kind: 'text', value: 'Loading comments...' },
  'cardBack.comments.loadMoreActivity': { kind: 'text', value: 'Load more activity' },
  'cardBack.comments.loadMoreComments': { kind: 'text', value: 'Load more comments' },
  'cardBack.comments.announce.attachmentsFailed': {
    kind: 'plural',
    forms: {
      one: 'Comment posted, but 1 attachment could not be added.',
      other: 'Comment posted, but {count} attachments could not be added.',
    },
  },
  'cardBack.comments.announce.commentLinkCopied': {
    kind: 'text',
    value: 'Comment link copied.',
  },
  'cardBack.comments.announce.commentLinkCopyFailed': {
    kind: 'text',
    value: 'Could not copy comment link.',
  },
  'cardBack.comments.announce.activityLinkCopied': {
    kind: 'text',
    value: 'Activity link copied.',
  },
  'cardBack.comments.announce.activityLinkCopyFailed': {
    kind: 'text',
    value: 'Could not copy activity link.',
  },
  'cardBack.comments.announce.reactionRemoved': {
    kind: 'text',
    value: 'Reaction removed.',
  },
  'cardBack.comments.announce.reactionAdded': { kind: 'text', value: 'Reaction added.' },
  'cardBack.comments.announce.attachmentAdded': {
    kind: 'text',
    value: '{name} added to comment.',
  },
  'cardBack.comments.announce.attachmentsAdded': {
    kind: 'plural',
    forms: {
      one: '1 attachment added to comment.',
      other: '{count} attachments added to comment.',
    },
  },
  'cardBack.comments.announce.attachmentRemoved': {
    kind: 'text',
    value: '{name} removed from comment.',
  },
  'cardBack.comments.announce.mentionCandidateAdded': {
    kind: 'text',
    value: '{name} can now be mentioned on this board.',
  },
  'cardBack.comments.error.activityTitle': {
    kind: 'text',
    value: 'Activity could not be loaded',
  },
  'cardBack.comments.error.commentsTitle': {
    kind: 'text',
    value: 'Comments could not be loaded',
  },
  'cardBack.comments.error.body': {
    kind: 'text',
    value: 'Try again from the card back.',
  },
  'cardBack.comments.error.empty': {
    kind: 'text',
    value: 'A comment cannot be empty.',
  },
  'cardBack.comments.error.tooLong': {
    kind: 'text',
    value: 'A comment can be at most 16,384 characters.',
  },
  'cardBack.comments.permission.closed.title': {
    kind: 'text',
    value: 'This board is closed',
  },
  'cardBack.comments.permission.closed.body': {
    kind: 'text',
    value: 'Reopen the board to add comments. Existing comments stay readable.',
  },
  'cardBack.comments.permission.signIn.title': {
    kind: 'text',
    value: 'Sign in to comment',
  },
  'cardBack.comments.permission.signIn.body': {
    kind: 'text',
    value: 'Anyone signed in can comment on this board.',
  },
  'cardBack.comments.permission.disabled.title': {
    kind: 'text',
    value: 'Commenting is turned off on this board',
  },
  'cardBack.comments.permission.disabled.body': {
    kind: 'text',
    value: 'Existing comments stay readable.',
  },
  'cardBack.comments.permission.members.title': {
    kind: 'text',
    value: 'Only board members can comment on this board',
  },
  'cardBack.comments.permission.membersAndObservers.title': {
    kind: 'text',
    value: 'Only members and observers can comment on this board',
  },
  'cardBack.comments.permission.workspaceMembers.title': {
    kind: 'text',
    value: 'Only members of this workspace can comment',
  },
  'cardBack.comments.permission.readOnlyBody': {
    kind: 'text',
    value: 'You can read this card and its comments.',
  },
  'cardBack.comments.permission.anyUser.title': {
    kind: 'text',
    value: 'Only signed-in users can comment on this board',
  },
  'cardBack.comments.permission.anyUser.body': {
    kind: 'text',
    value: 'Sign in to add a comment.',
  },
  'cardBack.comments.mention.cardMember': { kind: 'text', value: 'Card member' },
  'cardBack.comments.mention.boardMember': { kind: 'text', value: 'Board member' },
  'cardBack.comments.mention.workspaceMember': {
    kind: 'text',
    value: 'Workspace member - add to board before mentioning',
  },
  'cardBack.comments.mention.boardNotify': {
    kind: 'plural',
    forms: {
      one: '1 board member may be notified',
      other: '{count} board members may be notified',
    },
  },
  'cardBack.comments.mention.cardNotify': {
    kind: 'plural',
    forms: {
      one: '1 card member may be notified',
      other: '{count} card members may be notified',
    },
  },
  'cardBack.comments.mention.cardNotifyNone': {
    kind: 'text',
    value: 'No card members will be notified',
  },
  'cardBack.comments.composer.bodyLabel': { kind: 'text', value: 'Write a comment' },
  'cardBack.comments.composer.submit': { kind: 'text', value: 'Comment' },
  'cardBack.comments.composer.formatting.toolbar': {
    kind: 'text',
    value: 'Comment formatting',
  },
  'cardBack.comments.composer.formatting.bold': { kind: 'text', value: 'Bold' },
  'cardBack.comments.composer.formatting.italic': { kind: 'text', value: 'Italic' },
  'cardBack.comments.composer.formatting.strikethrough': {
    kind: 'text',
    value: 'Strikethrough',
  },
  'cardBack.comments.composer.formatting.inlineCode': {
    kind: 'text',
    value: 'Inline code',
  },
  'cardBack.comments.composer.formatting.bulletedList': {
    kind: 'text',
    value: 'Bulleted list',
  },
  'cardBack.comments.composer.formatting.numberedList': {
    kind: 'text',
    value: 'Numbered list',
  },
  'cardBack.comments.composer.formatting.quote': { kind: 'text', value: 'Quote' },
  'cardBack.comments.composer.formatting.divider': { kind: 'text', value: 'Divider' },
  'cardBack.comments.composer.mention.comboboxLabel': {
    kind: 'text',
    value: 'Mention a board member',
  },
  'cardBack.comments.composer.mention.groupsHeading': { kind: 'text', value: 'Groups' },
  'cardBack.comments.composer.mention.cardMembersHeading': {
    kind: 'text',
    value: 'Card members',
  },
  'cardBack.comments.composer.mention.boardMembersHeading': {
    kind: 'text',
    value: 'Board members',
  },
  'cardBack.comments.composer.mention.workspaceMembersHeading': {
    kind: 'text',
    value: 'Workspace members',
  },
  'cardBack.comments.composer.mention.typeTwoCharacters': {
    kind: 'text',
    value: 'Type two characters',
  },
  'cardBack.comments.composer.mention.noMatches': {
    kind: 'text',
    value: 'No board member matches',
  },
  'cardBack.comments.composer.mention.notOnBoardTitle': {
    kind: 'text',
    value: '{name} is not on this board',
  },
  'cardBack.comments.composer.mention.notOnBoardBody': {
    kind: 'text',
    value: 'Add them to the board before posting so the mention reaches them.',
  },
  'cardBack.comments.composer.mention.addingMember': {
    kind: 'text',
    value: 'Adding member',
  },
  'cardBack.comments.composer.mention.addToBoard': {
    kind: 'text',
    value: 'Add to board',
  },
  'cardBack.comments.composer.attach': { kind: 'text', value: 'Attach to comment' },
  'cardBack.comments.composer.cardLink.label': {
    kind: 'text',
    value: 'Link a card to comment',
  },
  'cardBack.comments.composer.cardLink.placeholder': {
    kind: 'text',
    value: 'Link a card',
  },
  'cardBack.comments.composer.cardLink.group': { kind: 'text', value: 'Cards' },
  'cardBack.comments.composer.cardLink.noMatches': {
    kind: 'text',
    value: 'No card matches',
  },
  'cardBack.comments.composer.boardLink.label': {
    kind: 'text',
    value: 'Link a board to comment',
  },
  'cardBack.comments.composer.boardLink.placeholder': {
    kind: 'text',
    value: 'Link a board',
  },
  'cardBack.comments.composer.boardLink.group': { kind: 'text', value: 'Boards' },
  'cardBack.comments.composer.boardLink.noMatches': {
    kind: 'text',
    value: 'No board matches',
  },
  'cardBack.comments.composer.boardLink.optionKind': { kind: 'text', value: 'Board' },
  'cardBack.comments.composer.pendingAttachments': {
    kind: 'text',
    value: 'Pending comment attachments',
  },
  'cardBack.comments.composer.removeAttachment': {
    kind: 'text',
    value: 'Remove {name} from comment',
  },
  'cardBack.comments.composer.groupMention.confirmTitle': {
    kind: 'text',
    value: 'Notify the board?',
  },
  'cardBack.comments.composer.groupMention.confirmBody': {
    kind: 'plural',
    forms: {
      one: '1 person may be notified by @{group}.',
      other: '{count} people may be notified by @{group}.',
    },
  },
  'cardBack.comments.composer.groupMention.close': {
    kind: 'text',
    value: 'Close group mention confirmation',
  },
  'cardBack.comments.composer.groupMention.cancel': { kind: 'text', value: 'Cancel' },
  'cardBack.comments.composer.groupMention.post': { kind: 'text', value: 'Post comment' },
  'cardBack.comments.composer.groupMention.guidance': {
    kind: 'text',
    value: 'Use this only when everyone on the board needs the update.',
  },
  'cardBack.comments.item.label': { kind: 'text', value: '{name} comment' },
  // `comments.number`, per card (`docs/notes/comment-numbers.md`). A template
  // rather than a hard-coded `#` so a locale can change the prefix.
  'cardBack.comments.item.number': { kind: 'text', value: '#{number}' },
  'cardBack.comments.item.authorPresence': {
    kind: 'text',
    value: 'former board member',
  },
  'cardBack.comments.item.menu.edit': { kind: 'text', value: 'Edit' },
  'cardBack.comments.item.menu.delete': { kind: 'text', value: 'Delete' },
  'cardBack.comments.item.edited': { kind: 'text', value: '(edited)' },
  'cardBack.comments.item.newBadge': { kind: 'text', value: 'New' },
  'cardBack.comments.item.copyLinkTooltip': {
    kind: 'text',
    value: 'Copy a link to this comment',
  },
  'cardBack.comments.item.copyLink': { kind: 'text', value: 'Copy link to comment' },
  'cardBack.comments.item.replyTooltip': {
    kind: 'text',
    value: 'Quote this comment in a reply',
  },
  'cardBack.comments.item.reply': { kind: 'text', value: 'Reply to {name}' },
  'cardBack.comments.item.editorLabel': { kind: 'text', value: 'Edit comment' },
  'cardBack.comments.item.save': { kind: 'text', value: 'Save' },
  'cardBack.comments.item.editCancel': { kind: 'text', value: 'Cancel' },
  'cardBack.comments.item.showMore': { kind: 'text', value: 'Show more' },
  'cardBack.comments.item.actionsOwn': {
    kind: 'text',
    value: 'Actions on your comment',
  },
  'cardBack.comments.item.actionsOther': {
    kind: 'text',
    value: "Actions on {name}'s comment",
  },
  'cardBack.comments.item.delete.title': { kind: 'text', value: 'Delete comment?' },
  'cardBack.comments.item.delete.description': {
    kind: 'text',
    value: 'This cannot be undone.',
  },
  'cardBack.comments.item.delete.close': {
    kind: 'text',
    value: 'Close delete comment confirmation',
  },
  'cardBack.comments.item.delete.cancel': { kind: 'text', value: 'Cancel' },
  'cardBack.comments.item.delete.confirm': { kind: 'text', value: 'Delete comment' },
  'cardBack.comments.item.delete.body': {
    kind: 'text',
    value: 'The comment will be removed from the card for everyone.',
  },
  'cardBack.comments.reactions.region': { kind: 'text', value: 'Comment reactions' },
  'cardBack.comments.reactions.count': {
    kind: 'plural',
    forms: { one: '1 reaction', other: '{count} reactions' },
  },
  'cardBack.comments.reactions.removeEmoji': {
    kind: 'text',
    value: 'Remove {emoji} reaction',
  },
  'cardBack.comments.reactions.addEmoji': { kind: 'text', value: 'Add {emoji} reaction' },
  'cardBack.comments.reactions.countLabel': {
    kind: 'plural',
    forms: { one: '{emoji} 1 reaction', other: '{emoji} {count} reactions' },
  },
  'cardBack.comments.reactions.add': { kind: 'text', value: 'Add reaction' },
  'cardBack.comments.reactions.closePicker': {
    kind: 'text',
    value: 'Close reaction picker',
  },
  'cardBack.comments.reactions.search': { kind: 'text', value: 'Search emoji' },
  'cardBack.comments.reactions.recentHeading': { kind: 'text', value: 'Recently used' },
  'cardBack.comments.reactions.noMatches': { kind: 'text', value: 'No emoji matches' },
  'cardBack.comments.reactions.allHeading': { kind: 'text', value: 'Emoji' },
  'cardBack.comments.reactions.matchingHeading': {
    kind: 'text',
    value: 'Matching emoji',
  },
  'cardBack.comments.reactions.searchAlias.thumbsUp': {
    kind: 'text',
    value: 'thumbs up',
  },
  'cardBack.comments.reactions.searchAlias.heart': { kind: 'text', value: 'heart' },
  'cardBack.comments.reactions.searchAlias.laugh': { kind: 'text', value: 'laugh' },
  'cardBack.comments.reactions.searchAlias.celebrate': {
    kind: 'text',
    value: 'celebrate tada party',
  },
  'cardBack.comments.reactions.searchAlias.surprised': {
    kind: 'text',
    value: 'surprised',
  },
  'cardBack.comments.reactions.searchAlias.sad': { kind: 'text', value: 'sad' },
  'cardBack.comments.reactions.searchAlias.thanks': {
    kind: 'text',
    value: 'thanks pray',
  },
  'cardBack.comments.reactions.searchAlias.eyes': { kind: 'text', value: 'eyes' },
  'cardBack.comments.reactions.searchAlias.done': {
    kind: 'text',
    value: 'done check',
  },
  'cardBack.comments.reactions.searchAlias.fire': { kind: 'text', value: 'fire' },
  'cardBack.comments.attachments.listLabel': {
    kind: 'text',
    value: 'Comment attachments',
  },
  'cardBack.comments.attachments.fileRemoved': {
    kind: 'text',
    value: 'Attachment removed',
  },
  'cardBack.comments.attachments.cardUnavailable': {
    kind: 'text',
    value: 'Linked card unavailable',
  },
  'cardBack.comments.attachments.cardNoAccess': {
    kind: 'text',
    value: "A card on a board you don't have access to",
  },
  'cardBack.comments.attachments.cardMetaArchived': {
    kind: 'text',
    value: '{board} / {list} / archived',
  },
  'cardBack.comments.attachments.boardUnavailable': {
    kind: 'text',
    value: 'Linked board unavailable',
  },
  'cardBack.comments.attachments.boardNoAccess': {
    kind: 'text',
    value: "A board you don't have access to",
  },
  'cardBack.comments.attachments.boardMeta': { kind: 'text', value: 'Board' },
  'cardBack.comments.typing': {
    kind: 'plural',
    forms: {
      one: '{names} is typing a comment',
      other: '{names} are typing comments',
    },
  },
  // FE-80.1 — shared primitives. Rendered by RelativeTime, Avatar/activity,
  // MarkdownBody and lib/attachments, each of which appears on several surfaces
  // at once, so the namespace names the module rather than a surface.
  'primitives.relativeTime.justNow': { kind: 'text', value: 'just now' },
  'primitives.relativeTime.unknown': { kind: 'text', value: 'Unknown time' },
  'primitives.relativeTime.accessibleName': {
    kind: 'text',
    value: '{relative}, {absolute}',
  },
  // Fixed abbreviations glued to a numeral, never inflected words: the
  // inflected path is `Intl.RelativeTimeFormat`, which does its own CLDR
  // plural selection. Deliberately `kind: 'text'`, not `kind: 'plural'`.
  'primitives.relativeTime.short.year': { kind: 'text', value: '{count}y' },
  'primitives.relativeTime.short.month': { kind: 'text', value: '{count}mo' },
  'primitives.relativeTime.short.week': { kind: 'text', value: '{count}w' },
  'primitives.relativeTime.short.day': { kind: 'text', value: '{count}d' },
  'primitives.relativeTime.short.hour': { kind: 'text', value: '{count}h' },
  'primitives.relativeTime.short.minute': { kind: 'text', value: '{count}m' },
  // One key for all four call sites of the deleted-account name: Avatar's null
  // branch, CommentItem, HomePage's highlight row and lib/activity.
  /**
   * ⚠ FE-80.1, found by the LABEL PICKER slice's leak scan rather than by a
   * source audit. `TextField.tsx` authored `` `Clear ${label}` `` as the
   * default accessible name of its clear control, and it had survived ten
   * slices because none of them passed `clearable` — the picker's search box is
   * the first routed surface that renders the control at all. Byte-identical
   * to the literal it replaces.
   */
  'primitives.textField.clear': { kind: 'text', value: 'Clear {label}' },
  /*
   * FE-80.1 list-colour leaf — `lib/list-colours.ts`. These are product
   * vocabulary labels for the fixed shared enum; the enum values and CSS custom
   * property names remain opaque data.
   */
  'primitives.listColour.none': { kind: 'text', value: 'No colour' },
  'primitives.listColour.subtleGreen': { kind: 'text', value: 'Green' },
  'primitives.listColour.subtleYellow': { kind: 'text', value: 'Yellow' },
  'primitives.listColour.subtleOrange': { kind: 'text', value: 'Orange' },
  'primitives.listColour.subtleRed': { kind: 'text', value: 'Red' },
  'primitives.listColour.subtlePurple': { kind: 'text', value: 'Purple' },
  'primitives.listColour.subtleBlue': { kind: 'text', value: 'Blue' },
  'primitives.listColour.subtleSky': { kind: 'text', value: 'Sky' },
  'primitives.listColour.subtleLime': { kind: 'text', value: 'Lime' },
  'primitives.listColour.subtlePink': { kind: 'text', value: 'Pink' },
  'primitives.listColour.subtleGrey': { kind: 'text', value: 'Grey' },
  // FE-80.1 list hook failures — `lib/lists.ts`. These are hook-authored
  // failure announcements; list titles and server/API error messages stay
  // literal data.
  'primitives.listHooks.createFailed': {
    kind: 'text',
    value: 'Could not add the list {title}.',
  },
  'primitives.listHooks.renameFailed': {
    kind: 'text',
    value: 'Could not rename the list. Its previous name stands.',
  },
  'primitives.listHooks.recolourFailed': {
    kind: 'text',
    value: 'Could not change the list colour. Its previous colour stands.',
  },
  'primitives.listHooks.moveFailed': {
    kind: 'text',
    value: 'Could not move the list. It was returned to its position.',
  },
  'primitives.listHooks.moveToBoardFailed': {
    kind: 'text',
    value: 'Could not move the list {title}. It is still on this board.',
  },
  'primitives.listHooks.archiveFailed': {
    kind: 'text',
    value: 'Could not archive the list. It was restored to the board.',
  },
  'primitives.listHooks.restoreFailed': {
    kind: 'text',
    value: 'Could not restore the list.',
  },
  'primitives.listHooks.deleteFailed': {
    kind: 'text',
    value: 'Could not delete the list.',
  },
  // FE-80.1 card-share hook failures — `lib/card-share.ts`. These are
  // hook-authored live-region announcements; card titles remain data slots and
  // server/API error messages stay literal data.
  'primitives.cardShare.mergeFailed': {
    kind: 'text',
    value: 'Could not merge {target} and {source}. Both cards are still on the board.',
  },
  // FE-80.1 card hook failures — `lib/cards.ts`. These are hook-authored
  // failure announcements; card titles remain slots and server/API error
  // messages stay literal data.
  'primitives.cardHooks.createFailed': {
    kind: 'text',
    value: 'Could not add the card {title}.',
  },
  'primitives.cardHooks.renameFailed': {
    kind: 'text',
    value: 'Could not rename the card. Its previous title stands.',
  },
  'primitives.cardHooks.moveFailed': {
    kind: 'text',
    value: 'Could not move the card. It was returned to its position.',
  },
  'primitives.cardHooks.moveToBoardFailed': {
    kind: 'text',
    value: 'Could not move the card {title}. It is still on this board.',
  },
  'primitives.cardHooks.archiveFailed': {
    kind: 'text',
    value: 'Could not archive the card. It is still on the board.',
  },
  'primitives.cardHooks.restoreFailed': {
    kind: 'text',
    value: 'Could not restore the card.',
  },
  'primitives.cardHooks.deleteFailed': {
    kind: 'text',
    value: 'Could not delete the card.',
  },
  'primitives.cardHooks.descriptionFailed': {
    kind: 'text',
    value: 'Could not save the card description.',
  },
  'primitives.cardHooks.startDateFailed': {
    kind: 'text',
    value: 'Could not update the start date.',
  },
  'primitives.cardHooks.dueDateFailed': {
    kind: 'text',
    value: 'Could not update the due date.',
  },
  'primitives.cardHooks.dueReminderFailed': {
    kind: 'text',
    value: 'Could not update the due date reminder.',
  },
  'primitives.cardHooks.completionFailed': {
    kind: 'text',
    value: 'Could not update card completion.',
  },
  'primitives.cardHooks.coverUpdateFailed': {
    kind: 'text',
    value: 'Could not update the card cover.',
  },
  'primitives.cardHooks.coverRemoveFailed': {
    kind: 'text',
    value: 'Could not remove the card cover.',
  },
  'primitives.cardHooks.membersFailed': {
    kind: 'text',
    value: 'Could not update the card members.',
  },
  /*
   * FE-80.1 board hook reasons/failures — `lib/boards.ts`. Board titles are
   * user-authored data and stay slots; server/API error messages remain literal
   * where the member-management row intentionally speaks the server refusal.
   */
  'primitives.boardHooks.checkingAccess': {
    kind: 'text',
    value: 'Checking what you can do on this board…',
  },
  'primitives.boardHooks.closeDisabled': {
    kind: 'text',
    value: 'Only a board admin can close this board.',
  },
  'primitives.boardHooks.deleteDisabled': {
    kind: 'text',
    value: 'Only a board admin can delete this board.',
  },
  'primitives.boardHooks.visibilityDisabled': {
    kind: 'text',
    value: 'Only a board admin can change who can see this board.',
  },
  'primitives.boardHooks.manageMembersDisabled': {
    kind: 'text',
    value: 'Only a board admin can add or remove members on this board.',
  },
  'primitives.boardHooks.createFailed': {
    kind: 'text',
    value: 'Could not create the board {title}.',
  },
  'primitives.boardHooks.renameFailed': {
    kind: 'text',
    value: 'Could not rename the board. Its previous name stands.',
  },
  'primitives.boardHooks.closeFailed': {
    kind: 'text',
    value: 'Could not close the board.',
  },
  'primitives.boardHooks.reopenFailed': {
    kind: 'text',
    value: 'Could not reopen the board.',
  },
  'primitives.boardHooks.deleteFailed': {
    kind: 'text',
    value: 'Could not delete the board.',
  },
  /*
   * FE-80.1 custom-field hook failures — `lib/custom-fields.ts`. These are
   * fixed mutation failure announcements; field names, option labels, stored
   * values and server/API messages remain literal data at their own sinks.
   */
  'primitives.customFieldHooks.createDefinitionFailed': {
    kind: 'text',
    value: 'Could not create the custom field.',
  },
  'primitives.customFieldHooks.renameDefinitionFailed': {
    kind: 'text',
    value: 'Could not rename the custom field.',
  },
  'primitives.customFieldHooks.updateVisibilityFailed': {
    kind: 'text',
    value: 'Could not update custom field visibility.',
  },
  'primitives.customFieldHooks.updateFrontBadgeFailed': {
    kind: 'text',
    value: 'Could not update the custom field front badge.',
  },
  'primitives.customFieldHooks.reorderDefinitionFailed': {
    kind: 'text',
    value: 'Could not reorder the custom field.',
  },
  'primitives.customFieldHooks.deleteDefinitionFailed': {
    kind: 'text',
    value: 'Could not delete the custom field.',
  },
  'primitives.customFieldHooks.addOptionFailed': {
    kind: 'text',
    value: 'Could not add the custom field option.',
  },
  'primitives.customFieldHooks.renameOptionFailed': {
    kind: 'text',
    value: 'Could not rename the custom field option.',
  },
  'primitives.customFieldHooks.reorderOptionFailed': {
    kind: 'text',
    value: 'Could not reorder the custom field option.',
  },
  'primitives.customFieldHooks.deleteOptionFailed': {
    kind: 'text',
    value: 'Could not delete the custom field option.',
  },
  'primitives.customFieldHooks.updateValueFailed': {
    kind: 'text',
    value: 'Could not update the custom field value.',
  },
  'primitives.customFieldHooks.clearValueFailed': {
    kind: 'text',
    value: 'Could not clear the custom field value.',
  },
  'primitives.avatar.deletedAccount': { kind: 'text', value: 'Deleted account' },
  'primitives.avatar.anonymousMember': { kind: 'text', value: 'Member' },
  'primitives.avatar.tooltip': { kind: 'text', value: '{name} · @{username}' },
  'primitives.avatarStack.overflow.button': {
    kind: 'plural',
    forms: {
      one: 'Show {count} more member',
      other: 'Show {count} more members',
    },
  },
  'primitives.avatarStack.overflow.static': {
    kind: 'plural',
    forms: {
      one: 'and {count} more member',
      other: 'and {count} more members',
    },
  },
  // FE-80.1 — WatchToggle and lib/watch. Target labels are caller data; the
  // primitive owns only the watch action chrome and target-type announcements.
  'primitives.watchToggle.watch': { kind: 'text', value: 'Watch {label}' },
  'primitives.watchToggle.unwatch': { kind: 'text', value: 'Unwatch {label}' },
  'primitives.watchToggle.disabledReason': {
    kind: 'text',
    value: 'Sign in to watch.',
  },
  'primitives.watchToggle.retryCompact': {
    kind: 'text',
    value: 'Retry loading watch state for {label}',
  },
  'primitives.watchToggle.loadFailed.title': {
    kind: 'text',
    value: 'Could not load watch state',
  },
  'primitives.watchToggle.loadFailed.body': {
    kind: 'text',
    value: 'Try again from this surface.',
  },
  'primitives.watchToggle.retry': { kind: 'text', value: 'Retry' },
  'primitives.watchToggle.busy': { kind: 'text', value: 'Updating watch state' },
  'primitives.watchToggle.announce.watching.card': {
    kind: 'text',
    value: 'Watching this card.',
  },
  'primitives.watchToggle.announce.watching.list': {
    kind: 'text',
    value: 'Watching this list.',
  },
  'primitives.watchToggle.announce.watching.board': {
    kind: 'text',
    value: 'Watching this board.',
  },
  'primitives.watchToggle.announce.stopped.card': {
    kind: 'text',
    value: 'Stopped watching this card.',
  },
  'primitives.watchToggle.announce.stopped.list': {
    kind: 'text',
    value: 'Stopped watching this list.',
  },
  'primitives.watchToggle.announce.stopped.board': {
    kind: 'text',
    value: 'Stopped watching this board.',
  },
  'primitives.watchToggle.announce.updateFailed.card': {
    kind: 'text',
    value: 'Could not update the card watch state.',
  },
  'primitives.watchToggle.announce.updateFailed.list': {
    kind: 'text',
    value: 'Could not update the list watch state.',
  },
  'primitives.watchToggle.announce.updateFailed.board': {
    kind: 'text',
    value: 'Could not update the board watch state.',
  },
  // FE-80.1 -- `lib/card-bulk-actions.ts`. The hook owns live-region
  // announcements for successful bulk archive/move/copy and non-Error fallback
  // failures. Destination list titles are user-authored data and remain a
  // `{list}` slot; no-list and with-list variants are separate plural messages
  // so translators never inherit an English optional suffix. Server-authored
  // `Error.message` values remain literal contract data at the hook call sites.
  'primitives.cardBulkActions.archiveSuccess': {
    kind: 'plural',
    forms: {
      one: '1 selected card archived.',
      other: '{count} selected cards archived.',
    },
  },
  'primitives.cardBulkActions.moveSuccess': {
    kind: 'plural',
    forms: { one: '1 selected card moved.', other: '{count} selected cards moved.' },
  },
  'primitives.cardBulkActions.moveSuccessTo': {
    kind: 'plural',
    forms: {
      one: '1 selected card moved to {list}.',
      other: '{count} selected cards moved to {list}.',
    },
  },
  'primitives.cardBulkActions.copySuccess': {
    kind: 'plural',
    forms: { one: '1 selected card copied.', other: '{count} selected cards copied.' },
  },
  'primitives.cardBulkActions.copySuccessTo': {
    kind: 'plural',
    forms: {
      one: '1 selected card copied to {list}.',
      other: '{count} selected cards copied to {list}.',
    },
  },
  'primitives.cardBulkActions.archiveFailed': {
    kind: 'text',
    value: 'Bulk archive failed.',
  },
  'primitives.cardBulkActions.moveFailed': {
    kind: 'text',
    value: 'Bulk move failed.',
  },
  'primitives.cardBulkActions.copyFailed': {
    kind: 'text',
    value: 'Bulk copy failed.',
  },
  'primitives.markdown.mention': { kind: 'text', value: 'Mention {name}' },
  'primitives.markdown.mentionSelf': {
    kind: 'text',
    value: 'Mention {name}, you were mentioned',
  },
  /* FE-80.1 markdown format fallbacks — `lib/markdown-editing.ts`.

     These values are inserted into a member-editable Markdown source field only
     when a formatting control is activated with NO selected text. They are
     product chrome because the product authors the placeholder text; selected
     text remains user content and is never translated. `primitives.*` follows
     reach: the same helper is used by the card description editor and the
     comment composer.

     `tableColumnA`, `tableColumnB` and `tableValue` deliberately route only the
     human words inside the Markdown table sample. Pipes, dashes, brackets,
     backticks, numbering and example URLs are syntax, not language chrome. */
  'primitives.markdown.formatFallback.heading': {
    kind: 'text',
    value: 'Heading',
  },
  'primitives.markdown.formatFallback.listItem': {
    kind: 'text',
    value: 'List item',
  },
  'primitives.markdown.formatFallback.quote': { kind: 'text', value: 'Quote' },
  'primitives.markdown.formatFallback.code': { kind: 'text', value: 'code' },
  'primitives.markdown.formatFallback.linkText': {
    kind: 'text',
    value: 'link text',
  },
  'primitives.markdown.formatFallback.imageDescription': {
    kind: 'text',
    value: 'Image description',
  },
  'primitives.markdown.formatFallback.tableColumnA': {
    kind: 'text',
    value: 'Column A',
  },
  'primitives.markdown.formatFallback.tableColumnB': {
    kind: 'text',
    value: 'Column B',
  },
  'primitives.markdown.formatFallback.tableValue': {
    kind: 'text',
    value: 'Value',
  },
  'primitives.markdown.formatFallback.boldText': {
    kind: 'text',
    value: 'bold text',
  },
  'primitives.markdown.formatFallback.italicText': {
    kind: 'text',
    value: 'italic text',
  },
  'primitives.markdown.formatFallback.struckText': {
    kind: 'text',
    value: 'struck text',
  },
  // `{size}` carries the pre-formatted string `lib/attachments.ts` already
  // produces ('512', '2.0', '1023'), not a number: the arithmetic and its
  // `toFixed(1)` stay in that module, and B/KB/MB/GB are invariant SI symbols
  // that agree with no count.
  'primitives.attachment.size.unknown': { kind: 'text', value: 'Unknown size' },
  'primitives.attachment.size.bytes': { kind: 'text', value: '{size} B' },
  'primitives.attachment.size.kilobytes': { kind: 'text', value: '{size} KB' },
  'primitives.attachment.size.megabytes': { kind: 'text', value: '{size} MB' },
  'primitives.attachment.size.gigabytes': { kind: 'text', value: '{size} GB' },
  // FE-80.1 -- `putAttachmentBytes`. These two are what a user actually reads
  // when an upload fails: the thrown `ApiError.message` is rendered as visible
  // copy by whichever caller caught it. `primitives.*` rather than
  // `cardBack.attachments.*` because the leaf surfaces on three of them -- card
  // attachments, comment attachments and the card cover -- so a
  // `cardBack.attachments.*` key would render inside the comment stream.
  // `{status}` is an HTTP status code, an opaque numeral that agrees with
  // nothing.
  'primitives.attachment.upload.bytesFailed': {
    kind: 'text',
    value: 'Could not upload the attachment bytes.',
  },
  'primitives.attachment.upload.refused': {
    kind: 'text',
    value: 'The attachment upload was refused with HTTP {status}.',
  },
  // FE-80.1 -- `lib/api.ts`, the shared request leaf. These are the three
  // client-authored `ApiError.message` fallbacks a member can read when the
  // network fails, a proxy answers instead of the API, or a response no longer
  // matches the schema the caller asked for. Server-authored API error bodies
  // are deliberately not retranslated here; those messages remain literal
  // contract data.
  'primitives.apiError.network': {
    kind: 'text',
    value: 'Could not reach the server',
  },
  'primitives.apiError.unexpectedResponse': {
    kind: 'text',
    value: 'Unexpected {status} response',
  },
  'primitives.apiError.unexpectedShape': {
    kind: 'text',
    value: 'The server sent an unexpected shape for {path}',
  },
  // FE-80.1 -- `lib/permalinks.ts`, a shared leaf. The current board and card
  // copy-link consumers catch this malformed-response diagnostic and announce
  // their surface-owned failure messages, but the leaf must not carry private
  // English for a future caller that renders the thrown message.
  'primitives.permalink.missingTargetPath': {
    kind: 'text',
    value: 'Permalink response is missing its target path.',
  },
  // FE-80.1 -- `lib/board-backgrounds.ts`, a shared preset leaf rendered by
  // board settings, board creation and Inbox settings. Preset ids, photo refs,
  // CSS vars, assets and attribution strings stay source data; labels rendered
  // into product chrome route through `boardBackgroundPresetLabel()` and
  // `boardBackgroundLabel()`.
  'primitives.boardBackground.default': {
    kind: 'text',
    value: 'Default background',
  },
  'primitives.boardBackground.customColour': {
    kind: 'text',
    value: 'Custom colour',
  },
  'primitives.boardBackground.stockPhoto': { kind: 'text', value: 'Stock photo' },
  'primitives.boardBackground.customUpload': { kind: 'text', value: 'Custom upload' },
  'primitives.boardBackground.swatch.paper': { kind: 'text', value: 'Paper' },
  'primitives.boardBackground.swatch.sage': { kind: 'text', value: 'Sage' },
  'primitives.boardBackground.swatch.sky': { kind: 'text', value: 'Sky' },
  'primitives.boardBackground.swatch.clay': { kind: 'text', value: 'Clay' },
  'primitives.boardBackground.swatch.berry': { kind: 'text', value: 'Berry' },
  'primitives.boardBackground.swatch.pine': { kind: 'text', value: 'Pine' },
  'primitives.boardBackground.swatch.indigo': { kind: 'text', value: 'Indigo' },
  'primitives.boardBackground.swatch.charcoal': { kind: 'text', value: 'Charcoal' },
  'primitives.boardBackground.photo.forestDusk': {
    kind: 'text',
    value: 'Forest dusk',
  },
  'primitives.boardBackground.photo.coastDaylight': {
    kind: 'text',
    value: 'Coast daylight',
  },
  'primitives.boardBackground.photo.paperLight': {
    kind: 'text',
    value: 'Paper light',
  },
  // FE-80.1 -- the three photo ATTRIBUTION sentences, added beside the three
  // photo NAMES they belong to. The comment eleven lines above still holds and
  // is not contradicted: attribution *stays* source data, because
  // `photoBackgroundPut()` ships it as the B42 `photoAttribution` into
  // `boards.background_photo_attribution`, `coverPhotoOptions()` re-exports it
  // as a card cover's `photoAttribution`, and `FR-EXP-022` pins those bytes on
  // the export. Translating the STORED value would make a Spanish board write
  // Spanish into the database.
  //
  // What was unrouted is the DISPLAY: `BoardBackgroundPicker` printed the wire
  // datum straight at a human, under a name that was already routed. So the
  // split is the same one `name`/`labelKey` already makes -- `attribution` is
  // the datum, `attributionKey` is what a reader sees.
  //
  // ⚠ These are Molt's own descriptions of three bundled generated assets, NOT
  // third-party credit. A real credit (`Photo by Someone / Unsplash`) arrives
  // from a photo library, is not ours to translate, and never reaches this
  // table.
  'primitives.boardBackground.photo.forestDusk.attribution': {
    kind: 'text',
    value: 'Generated stock photo: forest hills at dusk',
  },
  'primitives.boardBackground.photo.coastDaylight.attribution': {
    kind: 'text',
    value: 'Generated stock photo: bright coast',
  },
  'primitives.boardBackground.photo.paperLight.attribution': {
    kind: 'text',
    value: 'Generated stock photo: warm paper texture',
  },
  // FE-80.1 -- `lib/board-calendar.ts`, a shared date helper consumed by the
  // board Calendar view and the board Timeline leaf. These are diagnostics,
  // not formatted dates: `{key}` is the malformed opaque input, while invalid
  // timestamp values stay out of the sentence to avoid rendering untrusted
  // server/user data inside fallback chrome. Direct board-calendar mutation
  // paths pass the active translator; non-rendering geometry callers resolve
  // through the source-English fallback rather than carrying private literals.
  'primitives.boardCalendar.invalidDateKey': {
    kind: 'text',
    value: 'Invalid calendar date key: {key}',
  },
  'primitives.boardCalendar.invalidTimestamp': {
    kind: 'text',
    value: 'Cannot update an invalid timestamp.',
  },
  // FE-80.1 -- `lib/card-dates.ts`, a shared leaf. `dueStatusLabel` alone
  // renders on the card front (`CardTile`'s sr-only description and
  // `CardBadges`' aria-label), the card back (`CardDatesSection`,
  // `CardChecklistSection`) and Home, so the namespace names the module rather
  // than a surface -- the `primitives.relativeTime.*` rule above.
  //
  // These six name a card's due STATE. They are deliberately NOT the
  // byte-identical `boardFace.filters.due.no_due_date` / `.overdue`,
  // `myCards.noDueDate`, `inbox.card.noDueDate` or `search.results.due.overdue`,
  // which name a filter OPTION fed by `lib/board-filter.ts`. Same word in
  // English, and a locale must stay free to make them differ.
  'primitives.cardDate.status.none': { kind: 'text', value: 'No due date' },
  'primitives.cardDate.status.neutral': { kind: 'text', value: 'Due later' },
  'primitives.cardDate.status.dueSoon': { kind: 'text', value: 'Due soon' },
  'primitives.cardDate.status.overdue': { kind: 'text', value: 'Overdue' },
  'primitives.cardDate.status.longOverdue': { kind: 'text', value: 'Long overdue' },
  'primitives.cardDate.status.complete': { kind: 'text', value: 'Complete' },
  // Nine literal keys, one per `DueReminderOffsetMinutes`, and deliberately NOT
  // one `kind: 'plural'` message fed a count. A plural selects a form for one
  // noun; these are four different nouns -- minutes, hour, hours, day, days --
  // so no count could pick between them. It would also re-derive the
  // `formatCloudProvider` defect the attachments return recorded: deriving a
  // label from arithmetic takes a per-value editorial choice away from the
  // translator in every locale at once.
  'primitives.cardDate.reminder.none': { kind: 'text', value: 'None' },
  'primitives.cardDate.reminder.atTime': {
    kind: 'text',
    value: 'At time of due date',
  },
  'primitives.cardDate.reminder.minutes5': { kind: 'text', value: '5 minutes before' },
  'primitives.cardDate.reminder.minutes10': { kind: 'text', value: '10 minutes before' },
  'primitives.cardDate.reminder.minutes15': { kind: 'text', value: '15 minutes before' },
  'primitives.cardDate.reminder.hours1': { kind: 'text', value: '1 hour before' },
  'primitives.cardDate.reminder.hours2': { kind: 'text', value: '2 hours before' },
  'primitives.cardDate.reminder.days1': { kind: 'text', value: '1 day before' },
  'primitives.cardDate.reminder.days2': { kind: 'text', value: '2 days before' },
  // `formatCardDate`'s unparseable-timestamp branch. Not reusing
  // `primitives.relativeTime.unknown` ('Unknown time'): a different noun for a
  // different failure, and collapsing them would change rendered output.
  // ✅ Taken. `lib/locale-formatting.ts`'s `formatLocaleCardDate` and
  // `formatLocaleDateTimeLabel` held two byte-identical English copies of this
  // string; both now route to THIS key rather than declaring their own, exactly
  // as the forward reference that stood here instructed. Three call sites, one
  // key, and no `primitives.localeFormatting.unknownDate` beside it that no
  // caller could tell apart.
  'primitives.cardDate.unknown': { kind: 'text', value: 'Unknown date' },
  // FE-80.1 -- `lib/locale-formatting.ts`'s `dateTimeLocalInZoneToIso`, thrown
  // from three sites: once directly, twice inside the private
  // `parseDateTimeLocal`. All three read the module's single
  // `INVALID_DATE_TIME_KEY` constant, so they cannot drift into three
  // spellings, and each resolves it lazily AT the throw -- a successful
  // conversion never touches the catalogue, which its unit test pins by passing
  // a translator that throws when reached.
  //
  // ⚠ Routed because a catch site RENDERS it: `CardDatesSection`'s `saveDates`
  // does `setError(cause instanceof Error ? cause.message : ...)`, so a user who
  // types a nonexistent local time -- 02:30 on a spring-forward date -- reads
  // this sentence. That INVERTS `lib/card-dates.ts:152`, whose byte-identical
  // throw stays English because both of ITS catch sites discard the message.
  // The transferable rule is not "route thrown messages" or "don't"; it is READ
  // THE CATCH SITE. Two sibling leaves applied it and got opposite answers.
  //
  // ⚠ Byte-identical to `inbox.card.invalidDate`, the Inbox panel's own
  // already-routed copy of the same sentence. Two keys on purpose: one is a
  // leaf's thrown message reaching the card back, the other a panel's own copy,
  // and a locale must stay free to phrase them differently. That duplicate
  // value is why the probe asserts both keys inside ONE `/`-separated run -- a
  // swap between them passes every single-value assertion.
  //
  // `primitives.*` rather than `cardBack.dates.*`: the throw reaches
  // `CardDatesSection` today and is latent in the module's other importers, and
  // a key a leaf owns must not sit inside a surface namespace another slice
  // owns.
  'primitives.localeFormatting.invalidDateTime': {
    kind: 'text',
    value: 'Choose a valid date and time.',
  },
  // FE-80.1 -- the card-back checklist section. Rendered by
  // `CardChecklistSection.tsx` and announced by `lib/checklists.ts`. Checklist
  // titles, item text, board-member names, list titles and card titles stay
  // literal: they are user data, and every message that shows one interpolates
  // it as a slot so a translator can move it within the sentence.
  'cardBack.checklists.heading': { kind: 'text', value: 'Checklists' },
  // FE-80.1 -- the last unrouted children of the board face: the Dashboard view
  // and its wire->label leaf, the mirror-card dialog, the link-card chrome drawn
  // on a card front, the mobile-parity notice, the offline board notice, and the
  // signed-out card embed route. Seven files, eight sub-families, all with a
  // trailing dot so a prefix cannot swallow a sibling: `facet.`, `chartType.`,
  // `view.`, `mirror.`, `link.`, `mobile.`, `offline.`, `embed.`.
  //
  // Named for the SLICE rather than for one component, which departs from the
  // precedent argued a few thousand lines up (a dialog with callers in two route
  // namespaces takes a component-named family: `labelPicker.*`, `copyMove.*`).
  // Taken by mandate and sub-familied so the reason behind that precedent still
  // holds: nothing here reaches into `boardFace.*` or `cardBack.*`, and no key
  // here is reached from two route namespaces. Written down so a later slice
  // does not "correct" it and orphan 107 keys.
  //
  // Board, list, card, member and label titles, the offline board address and
  // the cached-copy timestamp are user data: every message that shows one
  // interpolates it as a slot, so a translator can move it inside the sentence.
  // `Untitled card` is deliberately NOT declared here -- `MirrorCardDialog`
  // reuses `cardDisplayTitle`, and `board.card.untitled` already has eight
  // authors.
  //
  // The eight plurals below each agree with the count they interpolate
  // (tile/tiles, card/cards, change is/changes are), never with a cap, so none
  // is the "1 of 20 boards selected" shape that has to stay `text`.
  // `offline.pendingBodyNone` stays `text` on purpose: `pendingSyncBody`
  // returns it at count 0 before the plural is reached, and it names no count.
  //
  // The four `facet.*` labels flow into `view.tooltip`, `view.barLabel`,
  // `view.segmentLabel` and `view.bucketLabel` through the `{title}` slot for
  // the `due_date` facet, where the bucket title is English from the server
  // (`apps/server/src/dashboard/read.ts:96-98`). Reported as a contract bug, not
  // patched here; no served catalogue can translate those three sentences.
  'boardDashboard.facet.list': { kind: 'text', value: 'Cards per list' },
  'boardDashboard.facet.member': { kind: 'text', value: 'Cards per member' },
  'boardDashboard.facet.label': { kind: 'text', value: 'Cards per label' },
  'boardDashboard.facet.dueDate': { kind: 'text', value: 'Cards per due date' },
  'boardDashboard.chartType.bar': { kind: 'text', value: 'Bar' },
  'boardDashboard.chartType.pie': { kind: 'text', value: 'Pie' },
  'boardDashboard.view.region': { kind: 'text', value: 'Dashboard view' },
  'boardDashboard.view.heading': { kind: 'text', value: 'Dashboard' },
  'boardDashboard.view.tileCount': {
    kind: 'plural',
    forms: { one: '1 tile on {board}', other: '{count} tiles on {board}' },
  },
  'boardDashboard.view.addTile': { kind: 'text', value: 'Add tile' },
  'boardDashboard.view.lockedClosed': {
    kind: 'text',
    value: 'Closed boards cannot change dashboard tiles.',
  },
  'boardDashboard.view.lockedRole': {
    kind: 'text',
    value: 'Only board admins can change dashboard tiles.',
  },
  'boardDashboard.view.tileLockedClosed': {
    kind: 'text',
    value: 'Closed boards cannot change this tile.',
  },
  'boardDashboard.view.tileLockedRole': {
    kind: 'text',
    value: 'Only board admins can change this tile.',
  },
  'boardDashboard.view.addFormTitle': { kind: 'text', value: 'Add dashboard tile' },
  'boardDashboard.view.addFormSubmit': { kind: 'text', value: 'Create tile' },
  'boardDashboard.view.emptyTitle': { kind: 'text', value: 'No dashboard tiles' },
  'boardDashboard.view.emptyBody': {
    kind: 'text',
    value: 'Add a tile to chart this board by list, member, label or due date.',
  },
  'boardDashboard.view.tileList': { kind: 'text', value: 'Dashboard tiles' },
  'boardDashboard.view.facetLabel': { kind: 'text', value: 'Facet' },
  'boardDashboard.view.chartTypeLabel': { kind: 'text', value: 'Chart type' },
  'boardDashboard.view.cancel': { kind: 'text', value: 'Cancel' },
  'boardDashboard.view.chartCaption': { kind: 'text', value: '{type} chart' },
  'boardDashboard.view.tileActions': {
    kind: 'text',
    value: 'Dashboard tile actions for {tile}',
  },
  'boardDashboard.view.editTile': { kind: 'text', value: 'Edit tile' },
  'boardDashboard.view.removeTile': { kind: 'text', value: 'Remove tile' },
  'boardDashboard.view.editFormTitle': { kind: 'text', value: 'Edit {tile} tile' },
  'boardDashboard.view.editFormSubmit': { kind: 'text', value: 'Save tile' },
  'boardDashboard.view.chartUnavailable': {
    kind: 'text',
    value: 'This tile could not load its chart data.',
  },
  'boardDashboard.view.noLabels': {
    kind: 'text',
    value: 'No labels are applied to the cards currently shown on this board.',
  },
  'boardDashboard.view.exceedMember': {
    kind: 'plural',
    forms: {
      one: 'Cards with several members count once per member, so this tile total can exceed 1 card.',
      other:
        'Cards with several members count once per member, so this tile total can exceed {count} cards.',
    },
  },
  'boardDashboard.view.exceedLabel': {
    kind: 'plural',
    forms: {
      one: 'Cards with several labels count once per label, so this tile total can exceed 1 card.',
      other:
        'Cards with several labels count once per label, so this tile total can exceed {count} cards.',
    },
  },
  'boardDashboard.view.tooltip': {
    kind: 'plural',
    forms: { one: '{title}: 1 card', other: '{title}: {count} cards' },
  },
  'boardDashboard.view.bucketCounts': { kind: 'text', value: '{facet} counts' },
  'boardDashboard.view.barChart': { kind: 'text', value: '{facet} bar chart' },
  'boardDashboard.view.pieChart': { kind: 'text', value: '{facet} pie chart' },
  'boardDashboard.view.barLabel': {
    kind: 'plural',
    forms: { one: '{title} bar, 1 card', other: '{title} bar, {count} cards' },
  },
  'boardDashboard.view.segmentLabel': {
    kind: 'plural',
    forms: { one: '{title} segment, 1 card', other: '{title} segment, {count} cards' },
  },
  'boardDashboard.view.bucketLabel': {
    kind: 'plural',
    forms: { one: '{title}, 1 card', other: '{title}, {count} cards' },
  },
  'boardDashboard.view.emptyChart': { kind: 'text', value: 'No cards in this chart.' },
  'boardDashboard.mirror.title': { kind: 'text', value: 'Mirror {card}' },
  'boardDashboard.mirror.description': {
    kind: 'text',
    value:
      'Choose the same-workspace board and list where this shared card surface appears.',
  },
  'boardDashboard.mirror.close': { kind: 'text', value: 'Cancel mirror' },
  'boardDashboard.mirror.cancel': { kind: 'text', value: 'Cancel' },
  'boardDashboard.mirror.busy': { kind: 'text', value: 'Mirroring card...' },
  'boardDashboard.mirror.confirm': { kind: 'text', value: 'Mirror card' },
  'boardDashboard.mirror.loadingReason': {
    kind: 'text',
    value: 'Destination boards are still loading.',
  },
  'boardDashboard.mirror.chooseBoardReason': {
    kind: 'text',
    value: 'Choose another same-workspace board.',
  },
  'boardDashboard.mirror.needsListReason': {
    kind: 'text',
    value: 'Create a list on the destination board before mirroring this card.',
  },
  'boardDashboard.mirror.noticeTitle': { kind: 'text', value: 'Shared card' },
  'boardDashboard.mirror.noticeBody': {
    kind: 'text',
    value:
      'The mirror opens the source card. Removing the mirror later leaves the source card untouched.',
  },
  'boardDashboard.mirror.noDestinationTitle': {
    kind: 'text',
    value: 'No mirror destination',
  },
  'boardDashboard.mirror.noDestinationBody': {
    kind: 'text',
    value:
      'Mirrors can only be placed on another board in the same workspace where you can edit lists.',
  },
  'boardDashboard.mirror.boardLabel': { kind: 'text', value: 'Destination board' },
  'boardDashboard.mirror.boardOption': { kind: 'text', value: '{board} - {workspace}' },
  'boardDashboard.mirror.noListsTitle': { kind: 'text', value: 'No open lists' },
  'boardDashboard.mirror.noListsBody': {
    kind: 'text',
    value: 'Create a list on {board} before mirroring this card there.',
  },
  'boardDashboard.mirror.thatBoard': { kind: 'text', value: 'that board' },
  'boardDashboard.mirror.listLabel': { kind: 'text', value: 'Destination list' },
  'boardDashboard.mirror.positionLegend': { kind: 'text', value: 'Position' },
  'boardDashboard.mirror.position.top': { kind: 'text', value: 'Top of list' },
  'boardDashboard.mirror.position.bottom': { kind: 'text', value: 'Bottom of list' },
  'boardDashboard.mirror.position.afterCard': { kind: 'text', value: 'After {card}' },
  'boardDashboard.link.unavailableTitle': { kind: 'text', value: 'Unavailable card' },
  'boardDashboard.link.unavailableSubtitle': {
    kind: 'text',
    value: 'The target is unavailable.',
  },
  'boardDashboard.link.hiddenTarget': {
    kind: 'text',
    value: 'This preview does not reveal the target card.',
  },
  'boardDashboard.link.convert': { kind: 'text', value: 'Convert to card' },
  'boardDashboard.mobile.kind.automation': { kind: 'text', value: 'Automation' },
  'boardDashboard.mobile.kind.customFields': { kind: 'text', value: 'Custom fields' },
  'boardDashboard.mobile.kind.workspaceAdmin': { kind: 'text', value: 'Workspace admin' },
  'boardDashboard.mobile.kind.powerUps': { kind: 'text', value: 'Power-Ups' },
  'boardDashboard.mobile.kind.view': { kind: 'text', value: 'Board view' },
  'boardDashboard.mobile.kind.cardShare': { kind: 'text', value: 'Card share' },
  'boardDashboard.mobile.body.automation': {
    kind: 'text',
    value: 'Automation editing is available on web.',
  },
  'boardDashboard.mobile.body.customFields': {
    kind: 'text',
    value: 'Custom field setup and editing are available on web.',
  },
  'boardDashboard.mobile.body.workspaceAdmin': {
    kind: 'text',
    value: 'Workspace administration is available on web.',
  },
  'boardDashboard.mobile.body.powerUps': {
    kind: 'text',
    value: 'Power-Up setup and configuration are available on web.',
  },
  'boardDashboard.mobile.body.view': {
    kind: 'text',
    value: 'This board view is available on web and limited on mobile.',
  },
  'boardDashboard.mobile.body.cardShare': {
    kind: 'text',
    value: 'Card print and JSON export are available on web.',
  },
  'boardDashboard.mobile.openOnWeb': { kind: 'text', value: 'Open on web' },
  'boardDashboard.offline.cachedTitle': { kind: 'text', value: 'Viewing offline copy' },
  'boardDashboard.offline.cachedBody': {
    kind: 'text',
    value: 'This board is loaded from the offline copy on this device.',
  },
  'boardDashboard.offline.cachedBodyAt': {
    kind: 'text',
    value: 'This board is loaded from the offline copy saved at {time}.',
  },
  'boardDashboard.offline.retryConnection': { kind: 'text', value: 'Retry connection' },
  'boardDashboard.offline.pendingTitle': {
    kind: 'text',
    value: 'Changes waiting to sync',
  },
  'boardDashboard.offline.pendingBodyNone': {
    kind: 'text',
    value: 'Your offline changes will sync when this device is back online.',
  },
  'boardDashboard.offline.pendingBody': {
    kind: 'plural',
    forms: {
      one: '1 offline change is waiting to sync.',
      other: '{count} offline changes are waiting to sync.',
    },
  },
  'boardDashboard.offline.retrySync': { kind: 'text', value: 'Retry sync' },
  'boardDashboard.offline.unavailableTitle': {
    kind: 'text',
    value: 'Board is not available offline',
  },
  'boardDashboard.offline.unavailableBody': {
    kind: 'text',
    value: 'Open {address} while online before using it offline on this device.',
  },
  'boardDashboard.offline.thisBoard': { kind: 'text', value: 'this board' },
  'boardDashboard.offline.retry': { kind: 'text', value: 'Retry' },
  'boardDashboard.embed.errorTitle': {
    kind: 'text',
    value: 'This card cannot be embedded',
  },
  'boardDashboard.embed.errorBody': {
    kind: 'text',
    value: 'The card is unavailable, or this browser does not have board access.',
  },
  'boardDashboard.embed.eyebrow': { kind: 'text', value: 'Embedded card' },
  'boardDashboard.embed.inList': { kind: 'text', value: 'In list {list}' },
  'boardDashboard.embed.archivedTitle': { kind: 'text', value: 'Archived card' },
  'boardDashboard.embed.archivedBody': { kind: 'text', value: 'This card is read-only.' },
  'boardDashboard.embed.labels': { kind: 'text', value: 'Labels' },
  'boardDashboard.embed.members': { kind: 'text', value: 'Members' },
  'boardDashboard.embed.start': { kind: 'text', value: 'Start' },
  'boardDashboard.embed.due': { kind: 'text', value: 'Due' },
  'boardDashboard.embed.completed': { kind: 'text', value: 'Completed' },
  'boardDashboard.embed.yes': { kind: 'text', value: 'Yes' },
  'boardDashboard.embed.no': { kind: 'text', value: 'No' },
  'boardDashboard.embed.description': { kind: 'text', value: 'Description' },
  'boardDashboard.embed.noDescription': { kind: 'text', value: 'No description' },
  'boardDashboard.embed.checklists': { kind: 'text', value: 'Checklists' },
  'boardDashboard.embed.attachments': { kind: 'text', value: 'Attachments' },
  'cardBack.checklists.addTrigger': {
    kind: 'text',
    value: 'Checklist, add checklist to {card}',
  },
  'cardBack.checklists.addTriggerLabel': { kind: 'text', value: 'Checklist' },
  'cardBack.checklists.emptyTitle': { kind: 'text', value: 'No checklists' },
  'cardBack.checklists.emptyBody': {
    kind: 'text',
    value: 'Add a checklist to break this card into steps.',
  },
  'cardBack.checklists.cancel': { kind: 'text', value: 'Cancel' },
  'cardBack.checklists.add.title': { kind: 'text', value: 'Add checklist' },
  'cardBack.checklists.add.close': { kind: 'text', value: 'Close add checklist' },
  'cardBack.checklists.add.busy': { kind: 'text', value: 'Adding checklist' },
  'cardBack.checklists.add.submit': { kind: 'text', value: 'Add checklist' },
  'cardBack.checklists.add.titleLabel': { kind: 'text', value: 'Checklist title' },
  'cardBack.checklists.add.searchLabel': {
    kind: 'text',
    value: 'Search checklists to copy',
  },
  'cardBack.checklists.add.copyFromLabel': { kind: 'text', value: 'Copy items from' },
  'cardBack.checklists.add.noSource': { kind: 'text', value: 'No source' },
  // A whole option, not a stem plus a comma-joined tail: the three parts are
  // one phrase whose order a translator owns, and the count agrees with the
  // item total rather than with anything English.
  'cardBack.checklists.add.sourceOption': {
    kind: 'plural',
    forms: {
      one: '{checklist}, {card}, {count} item',
      other: '{checklist}, {card}, {count} items',
    },
  },
  'cardBack.checklists.add.noSourcesAvailable': {
    kind: 'text',
    value: 'No checklists on this board are available to copy.',
  },
  'cardBack.checklists.delete.title': { kind: 'text', value: 'Delete checklist?' },
  'cardBack.checklists.delete.titleNamed': {
    kind: 'text',
    value: 'Delete checklist {checklist}?',
  },
  'cardBack.checklists.delete.description': {
    kind: 'text',
    value: 'Deleting a checklist permanently removes all of its items.',
  },
  'cardBack.checklists.delete.confirm': { kind: 'text', value: 'Delete checklist' },
  'cardBack.checklists.delete.undoNotice': {
    kind: 'text',
    value: 'This action cannot be undone.',
  },
  'cardBack.checklists.panel.titleLabel': { kind: 'text', value: 'Checklist title' },
  'cardBack.checklists.panel.moveUp': {
    kind: 'text',
    value: 'Move checklist {checklist} up',
  },
  'cardBack.checklists.panel.moveDown': {
    kind: 'text',
    value: 'Move checklist {checklist} down',
  },
  'cardBack.checklists.panel.delete': {
    kind: 'text',
    value: 'Delete checklist {checklist}',
  },
  // `{checked}/{total}` renders in its own emphasised span, so the count and
  // the noun are two DOM nodes and this message is the tail only. That freezes
  // the number before the noun; merging the spans is a rendered-output change,
  // so it is recorded in the returns rather than taken here.
  'cardBack.checklists.panel.progressSuffix': {
    kind: 'plural',
    forms: { one: 'checklist item complete', other: 'checklist items complete' },
  },
  'cardBack.checklists.panel.progressBar': {
    kind: 'text',
    value: '{checklist} progress',
  },
  'cardBack.checklists.panel.showChecked': { kind: 'text', value: 'Show checked' },
  'cardBack.checklists.panel.hideChecked': { kind: 'text', value: 'Hide checked' },
  'cardBack.checklists.panel.showCheckedIn': {
    kind: 'text',
    value: 'Show checked items in {checklist}',
  },
  'cardBack.checklists.panel.hideCheckedIn': {
    kind: 'text',
    value: 'Hide checked items in {checklist}',
  },
  'cardBack.checklists.panel.hiddenCount': {
    kind: 'plural',
    forms: { one: '{count} item hidden', other: '{count} items hidden' },
  },
  'cardBack.checklists.panel.noItems': { kind: 'text', value: 'No items yet.' },
  'cardBack.checklists.panel.allHidden': {
    kind: 'text',
    value: 'All checked items are hidden.',
  },
  'cardBack.checklists.panel.addItemLabel': {
    kind: 'text',
    value: 'Add an item to {checklist}',
  },
  'cardBack.checklists.panel.addItem': { kind: 'text', value: 'Add item' },
  'cardBack.checklists.mention.newItemLabel': {
    kind: 'text',
    value: 'Mention a board member in new checklist item',
  },
  'cardBack.checklists.mention.itemLabel': {
    kind: 'text',
    value: 'Mention a board member in checklist item',
  },
  'cardBack.checklists.mention.group': { kind: 'text', value: 'Board members' },
  'cardBack.checklists.mention.typeTwo': { kind: 'text', value: 'Type two characters' },
  'cardBack.checklists.mention.noMatch': {
    kind: 'text',
    value: 'No board member matches',
  },
  'cardBack.checklists.convert.title': { kind: 'text', value: 'Convert item to card' },
  'cardBack.checklists.convert.close': {
    kind: 'text',
    value: 'Close convert checklist item',
  },
  'cardBack.checklists.convert.confirm': { kind: 'text', value: 'Convert to card' },
  'cardBack.checklists.convert.description': {
    kind: 'text',
    value: 'The item stays linked here after the new card is created.',
  },
  'cardBack.checklists.convert.listLabel': { kind: 'text', value: 'Destination list' },
  'cardBack.checklists.item.assignedTo': { kind: 'text', value: 'Assigned to {name}' },
  // `{status}` arrives from `lib/card-dates.ts`'s `dueStatusLabel`, now routed
  // to `primitives.cardDate.status.*`. The slot was authored ahead of that
  // slice so routing the leaf cost this message nothing -- it is the one
  // consumer of the six that absorbed the change without being re-authored.
  // Both halves are translated, so verify the composed sentence, not the label.
  'cardBack.checklists.item.due': {
    kind: 'text',
    value: 'Checklist item due {due}, {status}',
  },
  'cardBack.checklists.item.convertedBadge': { kind: 'text', value: 'Converted card' },
  'cardBack.checklists.item.openConverted': {
    kind: 'text',
    value: '{card}, open converted card',
  },
  'cardBack.checklists.item.assignLabel': {
    kind: 'text',
    value: 'Assign checklist item {item}',
  },
  'cardBack.checklists.item.noAssignee': { kind: 'text', value: 'No assignee' },
  'cardBack.checklists.item.setDueLabel': {
    kind: 'text',
    value: 'Set due date for checklist item {item}',
  },
  'cardBack.checklists.item.clearDueLabel': {
    kind: 'text',
    value: 'Clear due date for checklist item {item}',
  },
  'cardBack.checklists.item.convertLabel': {
    kind: 'text',
    value: 'Convert checklist item {item} to card',
  },
  'cardBack.checklists.item.moveUp': {
    kind: 'text',
    value: 'Move checklist item {item} up',
  },
  'cardBack.checklists.item.moveDown': {
    kind: 'text',
    value: 'Move checklist item {item} down',
  },
  'cardBack.checklists.item.moveToChecklist': {
    kind: 'text',
    value: 'Move checklist item {item} to checklist',
  },
  'cardBack.checklists.item.delete': {
    kind: 'text',
    value: 'Delete checklist item {item}',
  },
  // Lower-case `edit` is deliberate. `IconButton` renders the label as the
  // accessible name, and WCAG 2.5.3 wants that name to begin with the visible
  // text of the control it acts on; `FR-COLLAB-009.spec.ts` matches
  // /edit Checklist item text/.
  'cardBack.checklists.item.editLabel': {
    kind: 'text',
    value: 'edit Checklist item text {item}',
  },
  'cardBack.checklists.item.textLabel': { kind: 'text', value: 'Checklist item text' },
  'cardBack.checklists.item.save': { kind: 'text', value: 'Save item text' },
  'cardBack.checklists.item.saveFailed': {
    kind: 'text',
    value: 'Could not save. Try again.',
  },
  // Announcements. `lib/checklists.ts` takes a translator as a required first
  // parameter rather than reading the context itself, so `tsc` enumerates every
  // call site instead of letting one hook render English forever.
  'cardBack.checklists.announce.added': {
    kind: 'text',
    value: 'Checklist {checklist} added.',
  },
  'cardBack.checklists.announce.addFailed': {
    kind: 'text',
    value: 'Could not add the checklist.',
  },
  'cardBack.checklists.announce.renamed': {
    kind: 'text',
    value: 'Checklist renamed to {checklist}.',
  },
  'cardBack.checklists.announce.renameFailed': {
    kind: 'text',
    value: 'Could not rename the checklist.',
  },
  'cardBack.checklists.announce.moveFailed': {
    kind: 'text',
    value: 'Could not move the checklist.',
  },
  'cardBack.checklists.announce.deleted': {
    kind: 'text',
    value: 'Checklist {checklist} deleted.',
  },
  'cardBack.checklists.announce.deleteFailed': {
    kind: 'text',
    value: 'Could not delete the checklist.',
  },
  'cardBack.checklists.announce.hideCheckedFailed': {
    kind: 'text',
    value: 'Could not update checked item visibility.',
  },
  'cardBack.checklists.announce.itemsAdded': {
    kind: 'plural',
    forms: { one: 'Checklist item added.', other: '{count} checklist items added.' },
  },
  'cardBack.checklists.announce.itemAddFailed': {
    kind: 'text',
    value: 'Could not add the checklist item.',
  },
  'cardBack.checklists.announce.itemRenameFailed': {
    kind: 'text',
    value: 'Could not rename the checklist item.',
  },
  'cardBack.checklists.announce.itemUpdateFailed': {
    kind: 'text',
    value: 'Could not update the checklist item.',
  },
  'cardBack.checklists.announce.itemAssigned': {
    kind: 'text',
    value: 'Checklist item assigned.',
  },
  'cardBack.checklists.announce.itemAssignmentCleared': {
    kind: 'text',
    value: 'Checklist item assignment cleared.',
  },
  'cardBack.checklists.announce.itemAssignFailed': {
    kind: 'text',
    value: 'Could not update the checklist item assignment.',
  },
  'cardBack.checklists.announce.itemDueSet': {
    kind: 'text',
    value: 'Checklist item due date set.',
  },
  'cardBack.checklists.announce.itemDueCleared': {
    kind: 'text',
    value: 'Checklist item due date cleared.',
  },
  'cardBack.checklists.announce.itemDueFailed': {
    kind: 'text',
    value: 'Could not update the checklist item due date.',
  },
  'cardBack.checklists.announce.itemConverted': {
    kind: 'text',
    value: 'Checklist item converted to a card.',
  },
  'cardBack.checklists.announce.itemConvertedNamed': {
    kind: 'text',
    value: 'Checklist item converted to {card}.',
  },
  'cardBack.checklists.announce.itemConvertFailed': {
    kind: 'text',
    value: 'Could not convert the checklist item to a card.',
  },
  'cardBack.checklists.announce.itemMoveFailed': {
    kind: 'text',
    value: 'Could not move the checklist item.',
  },
  'cardBack.checklists.announce.itemDeleteFailed': {
    kind: 'text',
    value: 'Could not delete the checklist item.',
  },
  // FE-80.1 -- the card-back attachments section. Rendered by
  // `CardAttachmentSection.tsx`. Attachment display names, picked file names,
  // card titles and attachment URLs stay literal -- they are user data -- and
  // every message that shows one interpolates it as a named slot so a
  // translator owns the word order around it. One name per referent:
  // `{attachment}` is a stored attachment's display name, `{file}` a picked
  // `File.name` that is not an attachment yet, `{card}` a card title, `{size}`
  // and `{date}` already-formatted strings. Zero plurals: nothing in this
  // section renders a count.
  'cardBack.attachments.heading': { kind: 'text', value: 'Attachments' },
  'cardBack.attachments.add.fileTrigger': {
    kind: 'text',
    value: 'File, upload attachment to {card}',
  },
  'cardBack.attachments.add.fileTriggerLabel': { kind: 'text', value: 'File' },
  'cardBack.attachments.add.linkTrigger': {
    kind: 'text',
    value: 'Link, attach URL to {card}',
  },
  'cardBack.attachments.add.linkTriggerLabel': { kind: 'text', value: 'Link' },
  // `{size}` is fed `formatAttachmentSize(t, ATTACHMENT_MAX_BYTES)`, which
  // renders '250 MB' -- not '250.0 MB'. `ATTACHMENT_MAX_BYTES`
  // (`packages/shared/src/attachment.ts:176`) is 250 * 1024 * 1024 = 262144000;
  // 262144000 / 1024 = 256000, / 1024 = 250, unitIndex 1; `250 >= 10`, so the
  // `Math.round` branch runs and `toFixed(1)` never does; units[1] is
  // `primitives.attachment.size.megabytes` = '{size} MB' -> '250 MB'. The slot
  // rather than the literal because a hard-coded cap goes stale silently when
  // the constant moves, while a missing slot throws, and because
  // `primitives.attachment.size.megabytes` already owns the symbol `MB`.
  'cardBack.attachments.add.tooLarge': {
    kind: 'text',
    value: 'Files must be {size} or smaller.',
  },
  'cardBack.attachments.emptyTitle': { kind: 'text', value: 'No attachments' },
  'cardBack.attachments.emptyBody': {
    kind: 'text',
    value: 'Attach a file or link to keep evidence with this card.',
  },
  'cardBack.attachments.emptyBodyReadOnly': {
    kind: 'text',
    value: 'Attachments added by board members will appear here.',
  },
  // Hoisted to section level: the rename dialog and the delete dialog are the
  // same surface and the same generic control.
  'cardBack.attachments.cancel': { kind: 'text', value: 'Cancel' },
  'cardBack.attachments.add.file.fileLabel': { kind: 'text', value: 'Attachment file' },
  'cardBack.attachments.add.file.displayNameLabel': {
    kind: 'text',
    value: 'Display name',
  },
  'cardBack.attachments.add.file.noFileSelected': {
    kind: 'text',
    value: 'No file selected',
  },
  // U+00B7 MIDDLE DOT with one ASCII space either side, copied from the source
  // rather than retyped as a period or a bullet.
  'cardBack.attachments.add.file.selectedSummary': {
    kind: 'text',
    value: '{file} · {size}',
  },
  // A `busyLabel`, whereas `progress.valueText` is a progressbar's
  // `aria-valuetext`: byte-identical English, two roles, two keys.
  'cardBack.attachments.add.file.busy': { kind: 'text', value: 'Uploading attachment' },
  'cardBack.attachments.add.file.submit': { kind: 'text', value: 'Upload attachment' },
  'cardBack.attachments.add.link.urlLabel': { kind: 'text', value: 'URL' },
  'cardBack.attachments.add.link.displayNameLabel': {
    kind: 'text',
    value: 'Display name',
  },
  'cardBack.attachments.add.link.providerLabel': {
    kind: 'text',
    value: 'Cloud provider',
  },
  'cardBack.attachments.add.link.noProvider': { kind: 'text', value: 'No provider' },
  'cardBack.attachments.cloudProvider.googleDrive': {
    kind: 'text',
    value: 'Google Drive',
  },
  'cardBack.attachments.cloudProvider.dropbox': { kind: 'text', value: 'Dropbox' },
  'cardBack.attachments.cloudProvider.box': { kind: 'text', value: 'Box' },
  'cardBack.attachments.cloudProvider.oneDrive': { kind: 'text', value: 'OneDrive' },
  'cardBack.attachments.add.link.busy': { kind: 'text', value: 'Attaching link' },
  'cardBack.attachments.add.link.submit': { kind: 'text', value: 'Attach link' },
  // One indicator rendered in two layout positions -- inside the file form and
  // above the list -- so both progressbars share these two keys.
  'cardBack.attachments.progress.label': {
    kind: 'text',
    value: 'Attachment upload progress',
  },
  'cardBack.attachments.progress.valueText': {
    kind: 'text',
    value: 'Uploading attachment',
  },
  // The right-hand operand of a `??` only: the preview dialog's accessible name
  // is the raw `displayName` when there is one.
  'cardBack.attachments.preview.titleFallback': {
    kind: 'text',
    value: 'Attachment preview',
  },
  'cardBack.attachments.preview.close': {
    kind: 'text',
    value: 'Close attachment preview',
  },
  'cardBack.attachments.preview.previous': { kind: 'text', value: 'Previous attachment' },
  'cardBack.attachments.preview.next': { kind: 'text', value: 'Next attachment' },
  // Shared by the preview footer and the preview body, which render
  // simultaneously in one open dialog: two keys could diverge into two words
  // for one action in the same view.
  'cardBack.attachments.preview.download': { kind: 'text', value: 'Download' },
  'cardBack.attachments.preview.openLink': { kind: 'text', value: 'Open link' },
  'cardBack.attachments.preview.notPreviewable': {
    kind: 'text',
    value: 'This file cannot be previewed here. Download it to view the attachment.',
  },
  'cardBack.attachments.preview.frameTitle': {
    kind: 'text',
    value: '{attachment} preview',
  },
  // Names the Download button as bare prose, so it must be translated together
  // with `preview.download` and must stay one whole message -- feeding the
  // button's label in as a slot breaks in any locale that inflects the noun.
  'cardBack.attachments.preview.loadHint': {
    kind: 'text',
    value: 'If the preview does not load, use Download.',
  },
  // Dialog title vs. submit button: same English, two keys.
  'cardBack.attachments.rename.title': { kind: 'text', value: 'Rename attachment' },
  'cardBack.attachments.rename.busy': { kind: 'text', value: 'Renaming attachment' },
  'cardBack.attachments.rename.submit': { kind: 'text', value: 'Rename attachment' },
  'cardBack.attachments.rename.nameLabel': { kind: 'text', value: 'Attachment name' },
  'cardBack.attachments.delete.title': { kind: 'text', value: 'Delete attachment?' },
  'cardBack.attachments.delete.description': {
    kind: 'text',
    value: '{attachment} will be removed permanently.',
  },
  'cardBack.attachments.delete.busy': { kind: 'text', value: 'Deleting attachment' },
  'cardBack.attachments.delete.confirm': { kind: 'text', value: 'Delete attachment' },
  // Declared fresh rather than reusing `cardBack.checklists.delete.undoNotice`:
  // a translator must be able to phrase the two dialogs differently, and the
  // comment stream already says a third sentence for the same idea.
  'cardBack.attachments.delete.undoNotice': {
    kind: 'text',
    value: 'This action cannot be undone.',
  },
  // The row's kind noun in the size column, distinct from `add.linkTriggerLabel`
  // (the trigger's verb). It feeds `{size}` in `item.meta` for a link, so it
  // must never be passed there as a bare 'Link'.
  'cardBack.attachments.item.linkKind': { kind: 'text', value: 'Link' },
  'cardBack.attachments.item.label': { kind: 'text', value: '{attachment} attachment' },
  'cardBack.attachments.item.thumbnail': {
    kind: 'text',
    value: '{attachment} thumbnail',
  },
  // One message, not a stem concatenated with a locale-formatted date: the two
  // adjacent text nodes live in one `<p>`, so folding them leaves `textContent`
  // byte-identical. U+00B7 MIDDLE DOT, spaced. Both slots must be supplied --
  // `interfaceMessage` throws on a missing one.
  'cardBack.attachments.item.meta': {
    kind: 'text',
    value: '{size} · Added by {name} on {date}',
  },
  'cardBack.attachments.item.download': { kind: 'text', value: 'Download {attachment}' },
  'cardBack.attachments.item.open': { kind: 'text', value: 'Open {attachment}' },
  'cardBack.attachments.item.rename': { kind: 'text', value: 'Rename {attachment}' },
  'cardBack.attachments.item.delete': { kind: 'text', value: 'Delete {attachment}' },
  // The component-level fallbacks for a caught non-`Error`; the string a user
  // usually reads is the thrown message, routed at
  // `primitives.attachment.upload.*`.
  'cardBack.attachments.error.uploadFailed': {
    kind: 'text',
    value: 'Could not upload attachment.',
  },
  'cardBack.attachments.error.linkFailed': {
    kind: 'text',
    value: 'Could not attach link.',
  },
  // `{file}` is the picked `File.name`; the three below carry an attachment's
  // display name instead.
  'cardBack.attachments.announce.uploadComplete': {
    kind: 'text',
    value: 'Upload complete: {file}',
  },
  'cardBack.attachments.announce.linkAdded': {
    kind: 'text',
    value: 'Attachment link added: {attachment}',
  },
  'cardBack.attachments.announce.renameSaved': {
    kind: 'text',
    value: 'Rename saved: {attachment}',
  },
  'cardBack.attachments.announce.deleteComplete': {
    kind: 'text',
    value: 'Delete complete: {attachment}',
  },
  // FE-80.1 -- the card-back email-to-card section. Rendered by
  // `CardEmailToCardSection.tsx`, which lives under `components/integrations/`
  // but renders as a titled section of the card back, immediately below the
  // attachments -- the namespace follows the surface, not the file. The address
  // itself is user data and stays literal, interpolated bare into its own
  // field, never into a translated sentence; the load and reset errors are the
  // server's strings and have no key here.
  'cardBack.emailToCard.heading': { kind: 'text', value: 'Email to card' },
  // `BE-73.6` restored the attachments clause below in the SAME commit that
  // landed real ingestion (`external/email-to-card.ts` now inserts one
  // `attachments` row per part carrying base64 bytes, inside the comment's
  // own transaction). Fan-out 10 removed it because the path could not
  // deliver; the sentence is true again. Parts WITHOUT bytes still write no
  // row (`BE-74.1 F1` stands for them), so the promise covers forwarded files
  // the provider actually pushes, not metadata-only declarations.
  // See `docs/design/fe10-email-to-card-ingestion/AUDIT.md`.
  'cardBack.emailToCard.description': {
    kind: 'text',
    value:
      'Anyone holding this address can post comments and attachments to this card as you.',
  },
  // Three ASCII full stops, as the source has them -- not U+2026. Shared by the
  // disabled reason and the notice.
  'cardBack.emailToCard.checking': {
    kind: 'text',
    value: 'Checking this card email address...',
  },
  // Reached only when `disabledReason` is null, which the sole caller never
  // passes; declared because the prop type admits it and dropping it would be a
  // behaviour change.
  'cardBack.emailToCard.resetDisabledFallback': {
    kind: 'text',
    value: 'Only card members with comment access can reset this address.',
  },
  'cardBack.emailToCard.addressUnavailable': {
    kind: 'text',
    value: 'The card email address is not available.',
  },
  'cardBack.emailToCard.clipboardUnavailable': {
    kind: 'text',
    value: 'Clipboard access is not available.',
  },
  // Deliberately not `cardBack.reason.emailCommenter`, which says the same
  // thing in different words: reusing it would change rendered output. The
  // divergence is recorded for a later product pass, not collapsed here.
  'cardBack.emailToCard.lockedFallback': {
    kind: 'text',
    value: 'Only card members with comment access can use email-to-card.',
  },
  'cardBack.emailToCard.addressLabel': { kind: 'text', value: 'Email-to-card address' },
  'cardBack.emailToCard.inactive': {
    kind: 'text',
    value: 'This card email address is inactive. Reset it to issue a new address.',
  },
  'cardBack.emailToCard.copyLabel': { kind: 'text', value: 'Copy card email address' },
  'cardBack.emailToCard.copy': { kind: 'text', value: 'Copy' },
  // The trigger; `resetDialog.title` is the dialog it opens, and both accessible
  // names are asserted separately in `e2e/FR-AUTO-031`.
  'cardBack.emailToCard.reset': { kind: 'text', value: 'Reset card email address' },
  'cardBack.emailToCard.announce.copied': {
    kind: 'text',
    value: 'Card email address copied.',
  },
  'cardBack.emailToCard.announce.copyFailed': {
    kind: 'text',
    value: 'Card email address could not be copied.',
  },
  'cardBack.emailToCard.resetDialog.title': {
    kind: 'text',
    value: 'Reset card email address',
  },
  'cardBack.emailToCard.resetDialog.description': {
    kind: 'text',
    value: 'The current address will stop accepting mail for this card.',
  },
  'cardBack.emailToCard.resetDialog.cancel': { kind: 'text', value: 'Cancel' },
  'cardBack.emailToCard.resetDialog.confirm': { kind: 'text', value: 'Reset address' },
  'cardBack.emailToCard.resetDialog.body': {
    kind: 'text',
    value:
      'Reset only if the address was shared outside the people who should be able to comment by email.',
  },
  // FE-80.1 card-dates-section. `CardDatesSection` is the last card-back
  // section authoring English, and it was the file the locale-formatting
  // capture photographed rendering one Spanish sentence directly above one
  // English one inside a single error box.
  //
  // WARNING: four of these values are pinned as ACCESSIBLE NAMES by eight e2e
  // specs and `CardBack.test.tsx` - `cardBack.dates.trigger`,
  // `cardBack.dates.popoverTitle`, `cardBack.dates.removeLabel` and the three
  // field labels. Under the source-English fallback each must render
  // byte-identically to the literal it replaced or those specs go red for a
  // reason that reads as a routing bug rather than a copy change.
  'cardBack.dates.heading': { kind: 'text', value: 'Dates' },
  // The trigger is ONE message with a slot, not `heading` plus an English
  // suffix. WCAG 2.5.3 requires the accessible name to begin with the visible
  // label, and `CardDatesSection.test.tsx` asserts that relation rather than
  // encoding it in the key structure - a translator who reorders the clause is
  // making a real decision about their language and should see it fail loudly.
  'cardBack.dates.trigger': { kind: 'text', value: 'Dates, edit dates for {title}' },
  'cardBack.dates.popoverTitle': { kind: 'text', value: 'Card dates' },
  'cardBack.dates.closePopover': { kind: 'text', value: 'Close dates popover' },
  'cardBack.dates.remove': { kind: 'text', value: 'Remove' },
  'cardBack.dates.removeLabel': { kind: 'text', value: 'Remove dates' },
  'cardBack.dates.save': { kind: 'text', value: 'Save' },
  'cardBack.dates.saving': { kind: 'text', value: 'Saving...' },
  'cardBack.dates.startField': { kind: 'text', value: 'Start date and time' },
  'cardBack.dates.dueField': { kind: 'text', value: 'Due date and time' },
  'cardBack.dates.reminderField': { kind: 'text', value: 'Due date reminder' },
  'cardBack.dates.invertedTitle': {
    kind: 'text',
    value: 'Start date is after the due date',
  },
  'cardBack.dates.invertedBody': {
    kind: 'text',
    value: 'The card will still save; check that the range is intentional.',
  },
  'cardBack.dates.errorBody': { kind: 'text', value: 'Check the dates and try again.' },
  // The two catch fallbacks. They are reached only when the rejection is not an
  // `Error`; a real `ApiError` renders `cause.message`, which is why
  // `dateTimeLocalInZoneToIso`'s throw was routed by the locale-formatting
  // slice while `lib/card-dates.ts`'s byte-identical sibling stayed English.
  'cardBack.dates.saveFailed': { kind: 'text', value: 'Could not save card dates.' },
  'cardBack.dates.removeFailed': { kind: 'text', value: 'Could not remove card dates.' },
  // WARNING: the four composed lines below are WHOLE messages with slots, not
  // an English prefix concatenated with an already-routed value. `Starts`,
  // `Due`, `Reminder:` and the middle-dot join are authored copy: a language
  // that puts the date first, or joins with something else, can only say so if
  // it owns the whole sentence. This is the shared-primitives slice's
  // `Mention {name}, you were mentioned` decision applied to a summary row.
  //
  // `chip` and `dueWithStatus` differ only by that `Due ` - they are two
  // messages because the chip sits beside a calendar icon that already says
  // "due" and the summary row does not.
  'cardBack.dates.chip': { kind: 'text', value: '{date} · {status}' },
  'cardBack.dates.startsAt': { kind: 'text', value: 'Starts {date}' },
  'cardBack.dates.dueWithStatus': { kind: 'text', value: 'Due {date} · {status}' },
  'cardBack.dates.reminderSummary': { kind: 'text', value: 'Reminder: {reminder}' },
  'cardBack.dates.noDates': { kind: 'text', value: 'No dates set.' },
  // FE-80.1 card-back custom fields. Rendered by `CardCustomFieldsSection.tsx`,
  // which has held a `useInterfaceLanguage()` since the shared-primitives slice
  // threaded one through it and routed nothing of its own.
  //
  // WARNING: six of these values are pinned as ACCESSIBLE NAMES by
  // `CardCustomFieldsSection.test.tsx` and `FR-CARD-027.spec.ts` -
  // `heading`, `valueLabel`, `checkedLabel`, `save`, `clear` and
  // `invalidNumber`. Under the source-English fallback each must render
  // byte-identically to the literal it replaced, or those specs go red for a
  // reason that reads as a routing bug rather than a copy change.
  //
  // `cardBack.customFields.*` and NOT `boardMenu.customFields.*`, which already
  // holds thirteen keys including a byte-identical `'Custom fields'`: that one
  // titles the board menu's own panel, a different frame, and `FR-CARD-026`
  // pins it inside `getByRole('complementary')`. The namespace follows the
  // surface a person sees, not the word.
  'cardBack.customFields.heading': { kind: 'text', value: 'Custom fields' },
  // Flat `emptyTitle`/`emptyBody`, following the two most recent card-back
  // sections (`cardBack.checklists.*`, `cardBack.attachments.*`) rather than
  // the older nested `empty.title` that `cardBack.description.*` uses.
  'cardBack.customFields.emptyTitle': { kind: 'text', value: 'No custom fields' },
  'cardBack.customFields.emptyBody': {
    kind: 'text',
    value: 'Visible board custom fields will appear here.',
  },
  // ONE key for four call sites - the dropdown's own `<label>` and the three
  // `TextField label=` props - because all four are the same expression in the
  // same role on the same surface, repeated only because JSX cannot share one
  // element across three `return`s. The type-specific meaning is carried by
  // `cardBack.customFields.type.*` rendered directly above each control, so
  // four keys would encode a distinction the product does not make and give a
  // translator no way to know the four must stay identical. Splitting one key
  // later is a catalogue change; merging four drifted values is not.
  'cardBack.customFields.valueLabel': { kind: 'text', value: 'Value' },
  // ⚠ `Unset` and `Checked` are each TWO keys, and the discriminator is
  // mechanical: the two sites of each pair sit on OPPOSITE SIDES of the
  // `canEdit ?` ternary and can never appear on the same screen. The `Option`
  // and `Label` keys name affordances an editor operates; the `Value` keys are
  // states reported to a viewer who cannot edit. Scope differs too - the
  // `<option>` exists only for dropdown fields, the readout covers a null value
  // on all five types. Arguing for one key each means asserting that no
  // language distinguishes a null-choice option from a null-value readout.
  // Precedent: `cardBack.dates.remove` beside `cardBack.dates.removeLabel`.
  //
  // ⚠ The readout pair is `Checked`/`Unset`, NOT `Checked`/`Unchecked`. An
  // unticked checkbox is stored as null, so it falls through `valueLabel`'s
  // null branch to `unsetValue`. A translator given only the English will
  // otherwise supply a checked/unchecked pair and one of them will never render.
  'cardBack.customFields.unsetOption': { kind: 'text', value: 'Unset' },
  'cardBack.customFields.unsetValue': { kind: 'text', value: 'Unset' },
  'cardBack.customFields.checkedLabel': { kind: 'text', value: 'Checked' },
  'cardBack.customFields.checkedValue': { kind: 'text', value: 'Checked' },
  // A third card-back section's own `Save`, beside `cardBack.dates.save` and
  // `cardBack.comments.item.save`. Reuse would let one section's translator
  // decision silently retitle another section's button.
  'cardBack.customFields.save': { kind: 'text', value: 'Save' },
  'cardBack.customFields.clear': { kind: 'text', value: 'Clear' },
  'cardBack.customFields.invalidNumber': {
    kind: 'text',
    value: 'Enter a valid number.',
  },
  // ⚠ The THIRD key carrying this exact sentence, beside
  // `primitives.localeFormatting.invalidDateTime` (a leaf's thrown message) and
  // `inbox.card.invalidDate` (a panel's own copy). This is a surface's own
  // re-authored copy: `patchFromDraft` catches the leaf's throw BARE and
  // discards the message, so routing the leaf changed nothing a person reads
  // here - which is exactly why the locale-formatting slice deferred this one
  // by name rather than absorbing it.
  //
  // The rule at `primitives.localeFormatting.invalidDateTime` runs both ways: a
  // key a leaf owns must not sit inside a surface namespace, and a surface's
  // own copy must not be pinned to a leaf's key. A translator phrasing the
  // dates popover's error cannot be allowed to silently rewrite an inline field
  // error. Three byte-identical values mean no single-value assertion can tell
  // the three apart - `CardCustomFieldsSection.test.tsx` gives each a different
  // doctored value and asserts at its own site.
  'cardBack.customFields.invalidDate': {
    kind: 'text',
    value: 'Choose a valid date and time.',
  },
  // The five field-type nouns MOVED to `primitives.customFieldType.*` in the
  // board-frame slice, which became the third author of them and so met the
  // threshold this comment's previous revision set. Both frames now reach them
  // through `lib/custom-field-type.ts`; the namespace is earned by a function's
  // reach, and the function now exists.
  // FE-80.1 card-back location. `CardLocationSection.tsx` authored every one of
  // its strings as an English literal and held NO `useInterfaceLanguage()` at
  // all before this slice - unlike `CardCustomFieldsSection`, which at least had
  // a hook threaded through its leaf.
  //
  // WARNING: SEVEN of these values are pinned as ACCESSIBLE NAMES by
  // `FR-VIEW-029.spec.ts`. Lines 88 and 118 name `trigger`, 94 `save` and 121
  // `remove`, as `getByRole('button')` names; lines 90 and 120 name
  // `popoverTitle` as a `getByRole('dialog')` name, and lines 91-93 - scoped
  // INSIDE that dialog, so unambiguously this section - reach `addressField`,
  // `latitudeField` and `longitudeField` through `getByLabel`. (Lines 47-48
  // fill a Latitude and a Longitude too, but inside `getByRole('form', { name:
  // 'Add a card from Map view' })`: that is `BoardMapView`'s own add-card form,
  // which routes nothing and is NOT covered by these keys.)
  //
  // ⚠ None of those seven is a whole-string pin, and an earlier draft of this
  // comment claimed otherwise on the strength of two of them being written as
  // RegExp literals. Playwright's `getByRole` name option and `getByLabel` are
  // BOTH case-insensitive substring matches unless `exact: true` is passed, and
  // FR-VIEW-029 passes it exactly once, at line 180, on an unrelated
  // `getByText`. (`playwright-core` 1.62.1 `types.d.ts` states both defaults.)
  // So APPENDING to any of the seven - or changing its case - leaves this spec
  // GREEN. What actually guards these seventeen values against copy drift is
  // the `WebChromeProbe` run in `interface-language.test.tsx`, which this slice
  // extends; treat FR-VIEW-029 as a guard against a word CHANGING, not against
  // a word growing.
  //
  // `CardLocationSection.test.tsx` pins seven as well - the same list minus
  // `save` (it submits the form directly), plus `invalidCoordinates` through an
  // EXACT `getByText`. Under the source-English fallback each must render
  // byte-identically to the literal it replaced, or both suites go red for a
  // reason that reads as a routing bug rather than a copy change.
  //
  // ONE key across the `<h2>` and the trigger button's visible text, with a
  // separate slotted `trigger` for the aria-label - the shape
  // `CardDatesSection` uses for `cardBack.dates.heading` and
  // `cardBack.dates.trigger`. What the one key buys is that a TRANSLATOR cannot
  // give the heading and the button beside it two different words for one
  // thing. It does NOT encode WCAG 2.5.3, and cannot: the visible label comes
  // from `heading` and the accessible name from `trigger`, two independent
  // messages, so a catalogue that sets them to unrelated strings renders a
  // 2.5.3 violation no key structure can see. 2.5.3 is asserted by a TEST -
  // `CardLocationSection.test.tsx`, `begins the trigger accessible name with
  // its visible label` - exactly as the comment on `cardBack.dates.trigger`
  // above says of its own pair.
  //
  // ⚠ The word already appeared TWICE in that one header row BEFORE this slice
  // - a literal `<h2>Location</h2>` and the button's literal visible `Location`
  // - so the shared key does not create the duplication, it makes it GUARANTEED
  // rather than incidental. Consequence for whoever writes the test:
  // `getByText('Location')` is a strict-mode violation. Query by role.
  'cardBack.location.heading': { kind: 'text', value: 'Location' },
  'cardBack.location.trigger': {
    kind: 'text',
    value: 'Location, edit location for {title}',
  },
  // ONE key across the `Popover title=` prop and the inner `<form>`'s
  // `aria-label`, because both name the SAME popover. `Popover.tsx` renders
  // `title` as a visible `<h2>` carrying a `useId()` that the panel's
  // `aria-labelledby` points at, so the dialog and the form it contains are
  // announced with one string twice - nested accessible-name duplication, the
  // `cardBack.customFields.valueLabel` case. Two keys would let a translator
  // give the dialog and its form different names for one thing.
  'cardBack.location.popoverTitle': { kind: 'text', value: 'Card location' },
  'cardBack.location.closePopover': { kind: 'text', value: 'Close location popover' },
  'cardBack.location.addressField': { kind: 'text', value: 'Address text' },
  'cardBack.location.addressHint': {
    kind: 'text',
    value: 'Optional. Coordinates decide the Map pin.',
  },
  'cardBack.location.latitudeField': { kind: 'text', value: 'Latitude' },
  'cardBack.location.longitudeField': { kind: 'text', value: 'Longitude' },
  // The SECOND card-back section with a `Save`/`Remove` pair of its own, after
  // `cardBack.dates.save`/`.removeLabel`; the third to own a `save` at all,
  // after those and `cardBack.customFields.save` (which has no remove, as
  // `cardBack.labels.remove` has no save).
  // Reuse would let one section's translator decision silently retitle another
  // section's button.
  'cardBack.location.remove': { kind: 'text', value: 'Remove location' },
  'cardBack.location.save': { kind: 'text', value: 'Save location' },
  'cardBack.location.noLocation': { kind: 'text', value: 'No location' },
  // ⚠ BYTE-IDENTICAL to a literal at `components/board/BoardMapView.tsx:109` -
  // and it is not the only one. That same file authors `label="Latitude"` (line
  // 207) and `label="Longitude"` (line 221), byte-identical to `latitudeField`
  // and `longitudeField` above, so THREE of this block's values have a second,
  // un-routed author in one file. `grep -c useInterfaceLanguage
  // apps/web/src/components/board/BoardMapView.tsx` returns 0.
  //
  // Deliberately NOT adopted here: `BoardMapView` renders the board Map view, a
  // different frame - the same call `CardCustomFieldsSection` made about
  // `BoardCustomFieldsSection`'s byte-identical `TYPE_LABELS`. ⚠ But read the
  // count on the right axis before reusing that comment's rule of thumb: this
  // is ONE second author of THREE strings, not a third author of one. The
  // customFields threshold - a third independent author of the same string - is
  // NOT met. What the three do argue for is routing the whole Map-view frame in
  // its own slice, after which the duplication resolves itself.
  'cardBack.location.invalidCoordinates': {
    kind: 'text',
    value: 'Latitude and longitude must be valid numbers.',
  },
  // The two catch fallbacks. Reached ONLY when the rejection is not an `Error`;
  // a real `ApiError` renders `cause.message` and neither key is consulted.
  // Same footnote as `cardBack.dates.saveFailed` / `.removeFailed`, which is why
  // routing them changes nothing a person is likely to see.
  'cardBack.location.saveFailed': { kind: 'text', value: 'Could not save location.' },
  'cardBack.location.removeFailed': { kind: 'text', value: 'Could not remove location.' },
  // Section-scoped `announce.*`, following `cardBack.checklists.announce.*` and
  // `cardBack.comments.announce.*` rather than the surface-wide
  // `cardBack.announce.*` block: these fire from this section's own mutation
  // callbacks and nothing outside it can reach them.
  'cardBack.location.announce.saved': { kind: 'text', value: 'Card location saved.' },
  'cardBack.location.announce.removed': { kind: 'text', value: 'Card location removed.' },
  // A WHOLE message with two slots, not an English join of two already-routed
  // numbers. The `cardBack.dates.chip` / `.startsAt` / `.dueWithStatus`
  // decision: `, ` is authored copy, and a language that separates a coordinate
  // pair differently - or orders latitude after longitude - can only say so if
  // it owns the whole string.
  //
  // ONE key for two call sites: the `label` fallback and the muted readout
  // below it. ⚠ When a card has coordinates but NO address text, both sites
  // render from this key, so the pair appears TWICE in the same block as two
  // sibling `<p>`s with identical text - `getByText` on a doctored coordinates
  // value is a strict-mode violation in exactly that case.
  'cardBack.location.coordinates': { kind: 'text', value: '{lat}, {lng}' },
  // FE-80.1 card-back votes section. `CardVoteSection.tsx` authored every one of
  // its strings as an English literal and held NO `useInterfaceLanguage()` at
  // all before this slice - the `CardLocationSection` starting condition, not
  // the `CardCustomFieldsSection` one.
  //
  // ⚠ `cardBack.votes.*`, NOT `cardBack.vote.*`. Every card-back section
  // namespace is the camelCase of the heading a person reads, and its NUMBER
  // tracks the heading's: `dates`/`Dates`, `labels`/`Labels`, `checklists`,
  // `attachments`, `customFields`/`Custom fields`, `emailToCard`/`Email to
  // card`, and `location` singular BECAUSE its heading is `Location`. This
  // section's `<h2>` is `Votes`, so a singular namespace would be the first in
  // this file whose grammatical number disagrees with its own heading.
  //
  // WARNING: nine of these values are pinned as accessible names or exact text
  // by tests outside this block. `CardBack.test.tsx` names `heading`,
  // `voteLabel` and `countLabel`; `FR-CARD-033`, `FR-COLLAB-015` and
  // `FR-COLLAB-030` name `voteLabel`, `votedLabel`, both forms of `countLabel`,
  // `votersList`, `talliesHidden` and `lockedByBoardSetting`. Under the
  // source-English fallback each must render byte-identically to the literal it
  // replaced, or those specs go red for a reason that reads as a routing bug
  // rather than as a copy change.
  //
  // ⚠ The UNIT pins are the strong ones and the E2E pins are the weak ones -
  // the inverse of what earlier returns in this workstream assumed.
  // `@testing-library/dom`'s accessible-name matcher ends in
  // `normalizedText === String(matcher)`: whole-string and case-sensitive.
  // Playwright's `getByRole` name option is a case-insensitive SUBSTRING match
  // unless `exact: true`, which not one of the vote pins passes.
  'cardBack.votes.heading': { kind: 'text', value: 'Votes' },
  // PR-list approvals section (owner-directed, no requirement). The reason is
  // NOT a comment, and the copy says so.
  'cardBack.approvals.heading': { kind: 'text', value: 'Approvals' },
  'cardBack.approvals.summary': {
    kind: 'text',
    value: '{approved} of {required} approvals · {rejected} rejected',
  },
  'cardBack.approvals.met': { kind: 'text', value: 'Approved' },
  'cardBack.approvals.approve': { kind: 'text', value: 'Approve' },
  'cardBack.approvals.reject': { kind: 'text', value: 'Reject' },
  'cardBack.approvals.reasonLabel': { kind: 'text', value: 'Reason (optional)' },
  'cardBack.approvals.reasonHint': {
    kind: 'text',
    value: 'Saved with your verdict. This is not a comment.',
  },
  'cardBack.approvals.withdraw': { kind: 'text', value: 'Withdraw my verdict' },
  'cardBack.approvals.listLabel': { kind: 'text', value: 'Verdicts' },
  'cardBack.approvals.verdictApproved': { kind: 'text', value: 'Approved' },
  'cardBack.approvals.verdictRejected': { kind: 'text', value: 'Rejected' },
  'cardBack.approvals.notInPrList': {
    kind: 'text',
    value:
      'This card is no longer in the PR list. Earlier verdicts are kept for reference.',
  },
  'cardBack.approvals.announce.approved': {
    kind: 'text',
    value: 'You approved this card',
  },
  'cardBack.approvals.announce.rejected': {
    kind: 'text',
    value: 'You rejected this card',
  },
  'cardBack.approvals.announce.withdrawn': {
    kind: 'text',
    value: 'Your verdict was withdrawn',
  },
  // Reset clears EVERY member's verdict, so the copy says "all", never "your".
  // The confirm label deliberately does not contain the reveal label: a
  // Playwright name match is a case-insensitive substring.
  'cardBack.approvals.reset': { kind: 'text', value: 'Reset approvals' },
  'cardBack.approvals.resetPrompt': {
    kind: 'plural',
    forms: { one: 'Reset the 1 verdict?', other: 'Reset all {count} verdicts?' },
  },
  'cardBack.approvals.resetHint': {
    kind: 'text',
    value:
      'This removes every member’s verdict, not only yours. The card must be approved again.',
  },
  'cardBack.approvals.resetConfirm': {
    kind: 'plural',
    forms: { one: 'Yes, remove it', other: 'Yes, remove them' },
  },
  'cardBack.approvals.resetCancel': { kind: 'text', value: 'Cancel' },
  'cardBack.approvals.announce.reset': {
    kind: 'text',
    value: 'All verdicts on this card were reset',
  },
  'cardBack.approvals.resetFailed': {
    kind: 'text',
    value: 'Could not reset the verdicts. Try again.',
  },
  'cardBack.approvals.changeFailed': {
    kind: 'text',
    value: 'Could not save your verdict. Try again.',
  },
  'cardBack.approvals.conflict': {
    kind: 'text',
    value: 'This card cannot take a verdict right now.',
  },
  // TWO loading keys in one section, discriminated by their noun rather than
  // one bare `loading`: the shape `cardBack.comments.loadingActivity` and
  // `.loadingComments` already use, and for the same reason - a translator
  // seeing only `loading` beside `loadingVoters` cannot tell which surface the
  // bare one names.
  'cardBack.votes.loadingState': { kind: 'text', value: 'Loading vote state' },
  'cardBack.votes.loadFailed': { kind: 'text', value: 'Voting could not be loaded.' },
  'cardBack.votes.disabled': {
    kind: 'text',
    value: 'Voting is turned off on this board.',
  },
  // FOUR keys for ONE toggle, not two and not a shared pair. The precedent is
  // the one the `customFields` two-key comment names by name:
  // `cardBack.dates.remove` (`Remove`) beside `cardBack.dates.removeLabel`
  // (`Remove dates`) - a control's visible word beside its own longer
  // accessible name. The structural twin is `CardChecklistSection`'s toggle:
  // same `Button`, same `size="sm" variant="toggle"`, same `pressed`, a state
  // ternary in the `aria-label` over a state ternary in the children, already
  // routed as four keys.
  //
  // ⚠ `cardBack.location.heading`'s SHARED key does not reach this case, and
  // the reason is not "a different site class". It shares because the `<h2>`
  // and the button's visible text were THE SAME WORD, already duplicated in one
  // header row before that slice. Here the visible text and the accessible name
  // are different strings, so there is nothing to share; and the two visible
  // words are the two STATES of one control, mutually exclusive in time, so no
  // single value is ever both.
  //
  // The accessible name begins with the visible label in each state - `Vote` ⊂
  // `Vote for this card`, `Voted` ⊂ `Voted, remove your vote`. Like every
  // landed 2.5.3 pair that relation is NOT encoded here and cannot be; it is
  // asserted by `CardVoteSection.test.tsx`. ⚠ And `Vote` is a strict PREFIX of
  // `Voted`, so a translator who leaves those two equal ships a toggle whose
  // two states read identically with nothing red - which 2.5.3 does not cover,
  // so a separate distinctness assertion carries it.
  'cardBack.votes.vote': { kind: 'text', value: 'Vote' },
  'cardBack.votes.voteLabel': { kind: 'text', value: 'Vote for this card' },
  'cardBack.votes.voted': { kind: 'text', value: 'Voted' },
  'cardBack.votes.votedLabel': { kind: 'text', value: 'Voted, remove your vote' },
  // ⚠ THREE ASCII periods, not U+2026. That is this catalogue's own spelling -
  // `cardBack.dates.saving` is `Saving...` and `boardFace.live.reconnecting`
  // ends the same way. `Button`'s own default `busyLabel` is `Working…` with a
  // real ellipsis and is the outlier; this section overrides it, so the two
  // idioms sit side by side in one render, and the instinct to tidy these is
  // exactly the byte-identity break.
  //
  // Routed rather than skipped, unlike `cardBack.location`'s omission of a busy
  // label: this section AUTHORS both strings. They never enter the accessible
  // name - the explicit `aria-label` wins over content, and `Button` renders
  // `busyLabel` into a sibling sr-only live region BESIDE the label - so they
  // are invisible to every `getByRole({ name })` pin in either suite, and are
  // reachable only by the leak scan and `WebChromeProbe`.
  'cardBack.votes.adding': { kind: 'text', value: 'Adding vote...' },
  'cardBack.votes.removing': { kind: 'text', value: 'Removing vote...' },
  // The section's hand-rolled `countLabel(count, noun)` - `${count}
  // ${noun}${count === 1 ? '' : 's'}` - is DELETED, not routed. English
  // morphology in a helper is the thing `kind: 'plural'` exists for, and the
  // `noun` parameter was dead generality: the helper was module-private and
  // both of its call sites passed `'vote'`.
  //
  // The shape is `boardFace.list.cardCount`'s exactly - `{ one: '1 card',
  // other: '{count} cards' }` - so the singular form carries no slot, and
  // 0/1/2/3 all reproduce the deleted helper byte-for-byte.
  //
  // ⚠ BOTH of these keys must be called with the count TWICE: as the `{count}`
  // slot AND as `t`'s third positional argument. `interfaceMessage` defaults
  // `count` to 0 and does NOT read it out of `values`, so omitting the third
  // argument always selects `forms.other` and renders `1 votes` - nothing
  // throws, `count` is optional so `tsc` stays green, and no unit render before
  // this slice used a count of 1 at all.
  'cardBack.votes.count': {
    kind: 'plural',
    forms: { one: '1 vote', other: '{count} votes' },
  },
  // The tally trigger's WHOLE accessible name, `, show voters` included, and a
  // plural in its own right rather than `count` with an English suffix
  // concatenated on. The precedent is the pair
  // `cardBack.comments.reactions.count` beside `.countLabel`, which supplies
  // the key NAME as well as the shape: it is the one other place in this
  // catalogue where a plural already carries non-count English inside both
  // forms. The rule behind it is `cardBack.dates.trigger` - a trigger is one
  // message with a slot, never a routed value plus an English tail.
  //
  // ⚠ WCAG 2.5.3 must be asserted at BOTH arities here. A translator can
  // satisfy the visible-label prefix in `forms.one` and break it in
  // `forms.other`, and no key structure can see that. This is the first pair in
  // the file where both halves agree with a count.
  'cardBack.votes.countLabel': {
    kind: 'plural',
    forms: { one: '1 vote, show voters', other: '{count} votes, show voters' },
  },
  'cardBack.votes.popoverTitle': { kind: 'text', value: 'Vote tally' },
  'cardBack.votes.closePopover': { kind: 'text', value: 'Close vote tally' },
  'cardBack.votes.talliesHidden': {
    kind: 'text',
    value: 'Vote tallies are not visible.',
  },
  'cardBack.votes.loadingVoters': { kind: 'text', value: 'Loading voters' },
  'cardBack.votes.votersFailed': { kind: 'text', value: 'Voters could not be loaded.' },
  'cardBack.votes.noVoters': { kind: 'text', value: 'No one has voted yet.' },
  'cardBack.votes.votersList': { kind: 'text', value: 'Voters' },
  // TWO keys carrying ONE byte-identical sentence, on the MEANING limb of the
  // `cardBack.customFields.unsetOption`/`unsetValue` rule and on that limb
  // only. `lockedBoardClosed` is a standing prohibition on a control - it
  // reaches the muted `<p>` and `Button`'s `disabledReason`;
  // `conflictBoardClosed` reports that an action already submitted was refused,
  // in a `role="status"` error paragraph. Two speech acts, two channels, two
  // visual treatments.
  //
  // ⚠ The customFields comment's MECHANICAL limb - "can never appear on the
  // same screen" - is NOT claimed here, because here it is false: the two
  // paragraphs are siblings in one subtree, and a refetch that flips
  // `boardClosed` under an in-flight vote renders both with identical text.
  // Claiming it would repeat the over-claim the card-location block was
  // corrected for.
  //
  // Nothing guards the two against drifting apart. That is an editorial
  // exposure this split makes visible rather than one it creates: splitting one
  // key later is a catalogue change, merging two values that have already
  // drifted is not.
  'cardBack.votes.lockedBoardClosed': {
    kind: 'text',
    value: 'This board is closed. Reopen it to change votes.',
  },
  'cardBack.votes.lockedVoted': {
    kind: 'text',
    value: 'You can no longer change your vote on this card.',
  },
  'cardBack.votes.lockedByBoardSetting': {
    kind: 'text',
    value: "Voting is limited by this board's voting setting.",
  },
  'cardBack.votes.conflictBoardClosed': {
    kind: 'text',
    value: 'This board is closed. Reopen it to change votes.',
  },
  'cardBack.votes.changeFailed': {
    kind: 'text',
    value: 'Your vote change could not be saved.',
  },
  // Section-scoped `announce.*`, on the `cardBack.location.announce.*` shape:
  // these fire from this section's own mutation callback and nothing outside it
  // can reach them. They reach NO DOM node in a unit test, so the spy pins
  // their PLACEMENT and `WebChromeProbe` pins their VALUE, and neither alone
  // closes the gap.
  'cardBack.votes.announce.added': { kind: 'text', value: 'Vote added.' },
  'cardBack.votes.announce.removed': { kind: 'text', value: 'Vote removed.' },
  // FE-80.1 card-back stickers section. `CardStickerSection.tsx` held ZERO
  // `useInterfaceLanguage()` before this slice — the `CardVoteSection` /
  // `CardLocationSection` starting condition — and it is the FIRST card-back
  // section whose copy is authored in TWO files: `lib/stickers.ts` authors nine
  // of these values and the component the other twenty-nine.
  //
  // ⚠ `cardBack.stickers.*`, PLURAL, on the rule the `cardBack.votes.*` comment
  // states: a card-back section namespace is the camelCase of the heading a
  // person reads and agrees with its grammatical NUMBER. This section's `<h2>`
  // is `Stickers`.
  //
  // ⚠ `cardBack.stickers.*` and NOT `primitives.sticker.*`, on the REACH rule
  // the `primitives.attachment.upload.*` comment states in its own words — that
  // leaf surfaces on three call sites, so a `cardBack.attachments.*` key would
  // render inside the comment stream. `grep -rn 'lib/stickers' apps/web/src`
  // returns exactly ONE non-test consumer, so reach here is 1. ⚠ RIGHT BY
  // REACH, NOT FOREVER: the day a second surface consumes `lib/stickers.ts` — a
  // board-face sticker tray, or the card front once `GET /boards/:id/contents`
  // projects placements — these keys must MOVE to `primitives.sticker.*`.
  //
  // WARNING: eleven of these values are pinned as accessible names or exact
  // text by `CardStickerSection.test.tsx` and by `FR-CARD-032`. Under the
  // source-English fallback each must render byte-identically to the literal it
  // replaced. ⚠ The UNIT pins are the strong ones: `@testing-library/dom`'s
  // accessible-name matcher is whole-string and case-sensitive, while
  // Playwright's `getByRole` name option is a case-insensitive SUBSTRING match
  // unless `exact: true`, which no sticker pin passes.
  //
  // ZERO plurals, VERIFIED not assumed: `placed.length` is only compared
  // (`=== 0`) and used as a `zIndex`, and no message in either file agrees a
  // noun with a count.
  //
  // TWO keys for ONE byte-identical English word, `heading` and `pickerTitle`.
  // `cardBack.location.heading`'s SHARED key does not reach: it shares because
  // the `<h2>` and the trigger's visible text were the same word ALREADY
  // DUPLICATED IN ONE HEADER ROW, whereas `Popover`'s `title` renders through
  // `PopoverPrimitive.Portal` as the picker panel's OWN `<h2>` and the dialog's
  // accessible name — a different frame. All four shipped heading/picker pairs
  // are two keys AND two values (`Members`/`Card members`, `Votes`/`Vote
  // tally`, `Dates`/`Card dates`, `Location`/`Card location`); this is the
  // first whose two values are byte-identical, which argues for splitting, not
  // against — a translator wanting `Choose a sticker` for the picker must be
  // able to have it without retitling the section. ⚠ With the picker open two
  // nodes read `Stickers`, so `getByText('Stickers')` is a strict-mode
  // violation, and `WebChromeProbe` structurally CANNOT tell these two apart;
  // the load-bearing pin is the two-sentinel heading/dialog assertion in
  // `CardStickerSection.test.tsx`.
  //
  // `pickerTitle`/`closePicker`, not `popoverTitle`/`closePopover`, because
  // this file's own copy calls the object a picker — `cardBack.members.*`
  // exactly. FLAT, with no `picker.*` group: no card-back section in this
  // catalogue carries one, and `cardBack.members.*` is the structural twin.
  'cardBack.stickers.heading': { kind: 'text', value: 'Stickers' },
  'cardBack.stickers.pickerTitle': { kind: 'text', value: 'Stickers' },
  // Bare = the trigger's VISIBLE text, `…Label` = its accessible name, on
  // `cardBack.dates.remove`/`.removeLabel` and `cardBack.votes.vote`/
  // `.voteLabel`. NOT `cardBack.checklists.addTrigger`'s inversion: there the
  // visible text is a bare noun (`Checklist`) and the bare key holds a
  // comma-prefixed name; here the visible text is a full phrase the accessible
  // name EXTENDS.
  //
  // `addLabel` is a WHOLE message with a slot, never `t('…add') + ' to ' +
  // title` — the `cardBack.dates.chip` / `cardBack.location.coordinates` rule.
  // WCAG 2.5.3 (`Add sticker` ⊂ `Add sticker to {title}`) is asserted by a
  // TEST, never encoded here. ⚠ And because it IS a strict prefix, the visible
  // half must be asserted with `toBe` on trimmed `textContent`, never
  // `toHaveTextContent`.
  'cardBack.stickers.add': { kind: 'text', value: 'Add sticker' },
  'cardBack.stickers.addLabel': { kind: 'text', value: 'Add sticker to {title}' },
  'cardBack.stickers.closePicker': { kind: 'text', value: 'Close sticker picker' },
  // ⚠ THREE ASCII periods, not U+2026 — this catalogue's own spelling
  // (`cardBack.dates.saving`, `cardBack.votes.adding`). Do NOT harmonise it
  // with `upload.busy`, which carries no ellipsis at all, nor with `Button`'s
  // default `Working…`, which carries a real one: three idioms in one render.
  //
  // Discriminated by its noun rather than a bare `loading`, on the
  // `cardBack.comments.loadingActivity`/`.loadingComments` rule — this section
  // has two empty-ish surfaces (`libraryEmpty`, `empty`) and a bare `loading`
  // beside them names nothing a translator can place.
  'cardBack.stickers.loadingLibrary': { kind: 'text', value: 'Loading stickers...' },
  // The board sticker LIBRARY is empty, inside the picker — qualified from the
  // section's own vocabulary (`The sticker remains in the board library`) so it
  // cannot be confused with `empty`, the card having none placed. Two different
  // emptinesses; one key would be true of neither.
  'cardBack.stickers.libraryEmpty': { kind: 'text', value: 'No stickers are available.' },
  // Each library tile's `aria-label`: one whole message, TWO slots, both of
  // which must be supplied — `interfaceMessage` THROWS on a slot with no value.
  //
  // ⚠ The slot is `{sticker}`, not `{name}`, on the attachments rule (`one name
  // per referent`) and on a collision the name would cause:
  // `cardBack.checklists.item.assignedTo` already spends `{name}` on a PERSON.
  // `{title}` is the card title, following `cardBack.dates.trigger` and
  // `cardBack.location.trigger` rather than attachments' `{card}`.
  'cardBack.stickers.placeLabel': {
    kind: 'text',
    value: 'Place sticker {sticker} on {title}',
  },
  // A bare descriptive `<Notice tone="info">` key on the
  // `cardBack.powerUp.notInteractive` shape, not an `emptyTitle`/`emptyBody`
  // pair. FLAT rather than under `upload.*`: its subject is the sticker
  // FEATURE — card-front placement cannot render yet because `GET
  // /boards/:id/contents` projects no placed stickers — which is true whether
  // or not anyone uploads anything.
  //
  // ⚠⚠ RETIRED BY `FE-58.1`, AND STILL DECLARED — read the second half of this
  // note before routing it again. The sentence was true and is now FALSE:
  // `boardFaceCardSchema.stickers` projects `surface = 'front'` rows, so a
  // card-front sticker renders on the board face. `CardStickerSection` no
  // longer routes this key; the surface toggle
  // (`cardBack.stickers.surface.*`, below) stands where the notice stood.
  //
  // The DECLARATION is kept rather than deleted because it is still read by
  // three files this slice does not own — `lib/interface-language.test.tsx`
  // (twice, once inside a concatenated byte-identity chunk),
  // `e2e/fe-80-1-card-stickers.capture.ts`'s Spanish frame, and
  // `e2e/FR-CARD-032.spec.ts`, which asserts the notice VISIBLE in the picker
  // and therefore fails on this change. Retiring the key is one edit in each of
  // those, and it belongs to whoever takes them; deleting it here would break
  // them for a second, unrelated reason.
  'cardBack.stickers.frontUnsupported': {
    kind: 'text',
    value:
      'Card-front stickers need board payload support before they can render on the board.',
  },
  // `FE-58.1` — the placement-surface choice, three keys.
  //
  // ⚠ `surface.*`, NOT `frontUnsupported` reworded. A key whose NAME says
  // `unsupported` carrying a supported message is the drift the per-name
  // catalogue rules exist to stop, and the two strings have different jobs: one
  // reported a product gap, these three label a control.
  //
  // ⚠ ONE WHOLE MESSAGE PER SURFACE, on `cardBack.stickers.moveLeft`/
  // `.moveRight`'s recorded rule — not a routed stem plus a routed `front`/
  // `back` token. `Card front` is `Anverso`/`Vorderseite`, which no stem-plus-
  // token assembly reaches.
  //
  // ⚠ The two option values are each rendered at TWO sinks — the toggle inside
  // the picker and the caption above that surface's canvas — under ONE key
  // each, on `cardBack.stickers.upload.complete`'s rule: the two sinks name the
  // same surface by construction, and two keys would let a translator drift
  // them.
  'cardBack.stickers.surface.label': {
    kind: 'text',
    value: 'Place the sticker on',
  },
  'cardBack.stickers.surface.front': { kind: 'text', value: 'Card front' },
  'cardBack.stickers.surface.back': { kind: 'text', value: 'Card back' },
  // The `placed.length === 0` arm, byte-parallel to `cardBack.labels.empty`
  // (`No labels on this card.`) — same sentence frame, same position in the
  // section, so the same key name.
  'cardBack.stickers.empty': { kind: 'text', value: 'No stickers on this card.' },
  // ONE whole message per direction, NOT a routed stem plus a routed direction
  // token: `left` attaches to the verb with its own preposition in Spanish
  // (`hacia la izquierda`) and German (`nach links`). Shape from
  // `cardBack.checklists.item.moveUp`/`.moveDown`.
  //
  // FLAT rather than under an `item.*` group. `cardBack.attachments.item.*` and
  // `cardBack.checklists.item.*` group ROWS carrying their own metadata and
  // five-plus controls; the closer twin is `cardBack.labels.remove` (`Remove
  // {label} from this card`) — a flat per-row control key in a repeated list,
  // with the same sentence frame as `removeLabel` below.
  //
  // ⚠ Neither value is a substring of the other, but they share a long prefix,
  // so both are pinned by whole-string accessible-name matching, never by
  // `toHaveTextContent`. They also need a DISTINCTNESS assertion of their own:
  // a translator leaving the pair equal ships two adjacent buttons a person
  // cannot tell apart with nothing red — the `Vote`/`Voted` lesson.
  'cardBack.stickers.moveLeft': { kind: 'text', value: 'Move sticker {sticker} left' },
  'cardBack.stickers.moveRight': { kind: 'text', value: 'Move sticker {sticker} right' },
  // Icon-only, so this string IS the accessible name and there is no 2.5.3
  // pair. The 15° step and the 360 modulus stay in the component — arithmetic,
  // not copy.
  'cardBack.stickers.rotate': { kind: 'text', value: 'Rotate sticker {sticker}' },
  // Card-front stack controls added after the card back already owned rotation.
  // Whole messages rather than a `forward`/`backward` token: these are icon-only
  // accessible names, and the direction word sits after the object in English.
  'cardBack.stickers.bringForward': {
    kind: 'text',
    value: 'Bring sticker {sticker} forward',
  },
  'cardBack.stickers.sendBackward': {
    kind: 'text',
    value: 'Send sticker {sticker} backward',
  },
  // `…Label` on `cardBack.dates.removeLabel`: an accessible name for a remove
  // control with no visible text of its own. ⚠ `remove.confirm` (`Remove
  // sticker`) is a strict PREFIX of this value's render AND of
  // `remove.title`'s, so every assertion on it must be whole-string.
  // ⚠ It coexists safely with the `remove.*` dialog group because no BARE
  // `cardBack.stickers.remove` key is declared —
  // `cardBack.attachments.delete.*` already coexists with
  // `cardBack.attachments.item.delete` in one namespace.
  'cardBack.stickers.removeLabel': {
    kind: 'text',
    value: 'Remove sticker {sticker} from {title}',
  },
  // `stickerLabel`'s two fallbacks (`lib/stickers.ts`), TWO independent nouns
  // rather than one `Custom {noun}` key: English builds this by prefixing an
  // adjective, and freezing that composition is what a German compound or a
  // Spanish postposed adjective cannot follow. The `…Fallback` suffix is the
  // shipped idiom (`cardBack.attachments.preview.titleFallback`,
  // `cardBack.emailToCard.lockedFallback`).
  //
  // ⚠ `Sticker` is the single most exposed value in this block: a substring of
  // `Stickers`, `Sticker name`, both `Sticker {sticker} …` announcements, both
  // `Sticker images must be …` sentences and `Custom sticker`. And `Sticker` is
  // a strict PREFIX of `Custom sticker`, so a catalogue collapsing the two
  // ships a member's upload indistinguishable from a library sticker in seven
  // accessible names with nothing red — a distinctness assertion carries that,
  // as it does for `Vote`/`Voted`.
  'cardBack.stickers.nameFallback': { kind: 'text', value: 'Sticker' },
  'cardBack.stickers.customNameFallback': { kind: 'text', value: 'Custom sticker' },
  // The `upload.*` group is earned rather than decorative: FIVE of this
  // section's error values — `tooLarge`, `unsupportedType`, `bytesFailed`,
  // `refused` and (from `error.*`) `uploadFailed` — land in the SAME `<Notice
  // tone="bad">`, two of them authored in a different file, and a translator
  // reading the group top to bottom sees what can occupy that one box.
  //
  // `formLabel` names the `<form aria-label>`; a named `<form>` takes
  // `role="form"`, so it is assertable as `getByRole('form', { name })`.
  'cardBack.stickers.upload.formLabel': {
    kind: 'text',
    value: 'Custom sticker upload for {title}',
  },
  // The `<label htmlFor>` of the file input, shape from
  // `cardBack.attachments.add.file.fileLabel`. Not shared with `upload.submit`:
  // one names a field, the other commands an action, and both render at once in
  // one form. ⚠ The bare `<input type="file">` renders Chromium's own `Choose
  // File` beside it, which follows the BROWSER language and no key in this
  // workstream can reach.
  'cardBack.stickers.upload.fileLabel': { kind: 'text', value: 'Upload custom sticker' },
  // `TextField`'s visible `<label>`, shape from
  // `cardBack.attachments.rename.nameLabel`. ⚠ Superstring of `nameFallback`.
  'cardBack.stickers.upload.nameLabel': { kind: 'text', value: 'Sticker name' },
  // Routed rather than skipped on the AUTHORSHIP discriminator the
  // `cardBack.votes.adding`/`.removing` comment states: this section passes the
  // literal to `Button`'s `busyLabel` EXPLICITLY, so it is this file's copy.
  // Twin of `cardBack.attachments.add.file.busy` (`Uploading attachment`).
  // `Button`'s DEFAULT `busyLabel` (`Working…`) stays out of scope, sixth slice
  // running.
  //
  // ⚠ NO ellipsis of any kind here, while the sibling `loadingLibrary` carries
  // three ASCII periods.
  //
  // ⚠ CORRECTED (fan-out 16, `FE-58.1`), AND THE CORRECTION IS TO THE MECHANISM
  // RATHER THAN TO THE CONCLUSION. This comment used to read *"`busyLabel`
  // renders into a **sibling** sr-only live region and never enters the
  // accessible name"*. The conclusion is RIGHT about the shipped product and the
  // stated reason is WRONG about the DOM: `Button` renders `busyLabel` in an
  // sr-only `role="status"` span **inside** the `<button>`, not beside it, and
  // `sr-only` is a clip rather than a removal, so the span is rendered and is
  // part of the button's own subtree.
  //
  // It is excluded from the name by its ROLE. `status` is *Name From: author*,
  // and accname's name-from-content traversal does not descend into a child
  // whose role does not support it. Measured in Chromium over three buttons
  // differing in one attribute — `role="status"` + `sr-only`, `sr-only` alone,
  // `role="status"` alone:
  //
  //   role="status" + sr-only  ->  `Upload sticker`
  //   sr-only, no role         ->  `Upload sticker Uploading sticker`
  //   role="status", no sr-only->  `Upload sticker`
  //
  // So the role does it and the clip does not, and the shipped shape is the
  // first row: the name is `Upload sticker` in BOTH states.
  //
  // ⚠ AND THE TRAP THAT MADE A PAST SESSION READ THIS BACKWARDS: **jsdom does
  // not implement that traversal rule.** `dom-accessibility-api`, which every
  // unit suite in this package uses, descends into the `role="status"` span and
  // returns `Upload stickerUploading sticker` — no separating space. Fan-out 12
  // measured only there and concluded this comment was false about the product;
  // it is false only about jsdom. The generalisation is worth more than this
  // key: **a `toHaveAccessibleName` in a unit suite, over a subtree containing a
  // non-name-from-content role, is asserting an environment artefact.** Both
  // sides are pinned deliberately — `CardStickerSection.test.tsx` holds the jsdom
  // string and says so, `FR-EXP-027.spec.ts` and this chunk's capture hold the
  // Chromium one — so the divergence turns red if either engine moves rather
  // than silently rewriting this paragraph again.
  'cardBack.stickers.upload.busy': { kind: 'text', value: 'Uploading sticker' },
  // The submit button's visible text and, with no `aria-label`, its whole
  // accessible name. ⚠ Because `busy` is DECLARED on this `Button` the busy
  // slot is in the tree even when idle, so assert this with `toBe` on TRIMMED
  // `textContent` in the non-pending state only.
  'cardBack.stickers.upload.submit': { kind: 'text', value: 'Upload sticker' },
  // ONE key each across TWO author sites: `CardStickerSection.tsx`'s
  // module-scope `stickerUploadError` and `useUploadCustomSticker`'s
  // `mutationFn` in `lib/stickers.ts`. Both write the single `uploadError`
  // string that ONE `<Notice tone="bad">` renders, both are reports ABOUT THE
  // PICKED FILE in the same words, and the lib branch is unreachable through
  // this UI (the component guards on the same constant and the same predicate
  // and returns before `mutateAsync`). The `cardBack.customFields.valueLabel`
  // case — one key, several call sites, same surface, same role, same
  // expression — and NOT `cardBack.votes.lockedBoardClosed`/
  // `.conflictBoardClosed`, whose split rests on two speech acts in two sinks.
  // ⚠ A translator handed two keys with identical English has no way to know
  // they must stay identical; splitting one key later is a catalogue change,
  // merging two drifted values is not.
  //
  // ⚠ `1 MB` stays a LITERAL and does not become a `{size}` slot: the routed
  // value must be byte-identical to the literal it replaces, and
  // `formatAttachmentSize` would render `1.0 MB` for 1048576 bytes (verified
  // against the shipped arithmetic, not reconstructed). The staleness exposure
  // — `STICKER_UPLOAD_MAX_BYTES` can move and this sentence goes stale in every
  // locale silently — is recorded, not fixed.
  //
  // The `, ` joins and the ` or ` in the format sentence are AUTHORED ENGLISH
  // inside the message, which is why it is one whole sentence and not a routed
  // stem plus a joined array. `STICKER_UPLOAD_ACCEPT`'s MIME list is a wire
  // identifier list in an `accept=` attribute — vocabulary, not copy, correctly
  // untouched.
  'cardBack.stickers.upload.tooLarge': {
    kind: 'text',
    value: 'Sticker images must be 1 MB or smaller.',
  },
  'cardBack.stickers.upload.unsupportedType': {
    kind: 'text',
    value: 'Sticker images must be PNG, JPEG, WebP or GIF.',
  },
  // `putStickerBytes`' two throws, routed because the CATCH SITE RENDERS THE
  // MESSAGE: the `ApiError` propagates out of `useUploadCustomSticker`'s
  // `mutationFn`, `submitUpload` catches it and `setUploadError(cause.message)`
  // feeds the visible `<Notice tone="bad">`. That is the operative rule
  // `lib/card-dates.ts`' docblock states beside `dateTimeLocalToIso`, whose two
  // callers catch bare and DISCARD the message — the contrasting case, not this
  // one. Name and shape from `primitives.attachment.upload.bytesFailed` /
  // `.refused`, whose function this one mirrors line for line.
  //
  // ⚠ `{status}` is an HTTP status code — an opaque numeral that agrees with no
  // noun, so `kind: 'text'`, NOT a plural. The slot is load-bearing twice:
  // `interfaceMessage` throws on a slot with no value, so a call site that
  // drops `{ status }` fails loudly rather than rendering wrong copy.
  //
  // ⚠ `Could not upload the sticker.` is NOT a substring of `bytesFailed` — the
  // full stop after `sticker` breaks it — but only because both values carry
  // their period, so both still need `/` delimiters in the probe.
  'cardBack.stickers.upload.bytesFailed': {
    kind: 'text',
    value: 'Could not upload the sticker bytes.',
  },
  'cardBack.stickers.upload.refused': {
    kind: 'text',
    value: 'The sticker upload was refused with HTTP {status}.',
  },
  // ONE key, TWO adjacent sites: `announce()` and `setUploadMessage()`, built
  // from one `label` const in consecutive statements with no branch between
  // them — the `cardBack.location.coordinates` one-key-two-sites case. Two keys
  // would let a translator drift two strings the code guarantees identical.
  // Kept OUT of `announce.*` because it has a visible sink (`<p
  // role="status">`); named `complete` on
  // `cardBack.attachments.announce.uploadComplete`.
  //
  // ⚠ Finding recorded, not fixed: `role="status"` is itself a live region, so
  // a screen reader hears this sentence TWICE. One key survives that fix
  // untouched; two would leave a dead key.
  'cardBack.stickers.upload.complete': {
    kind: 'text',
    value: 'Custom sticker {sticker} uploaded.',
  },
  // `submitUpload`'s catch fallback for a rejection that is NOT an `Error` —
  // visible `<Notice>` copy. The precedent is exact and supplies the key name:
  // `cardBack.attachments.error.uploadFailed`, whose comment reads `The
  // component-level fallbacks for a caught non-Error; the string a user usually
  // reads is the thrown message`. Attachments deliberately did NOT fold its
  // component fallback into the upload group, and the naming carries MORE
  // weight here: this value and `announce.uploadFailed` are BYTE-IDENTICAL, so
  // the parallel sub-namespaces are the only thing in the key string that
  // separates them.
  //
  // Routed despite being effectively unreachable — every rejection path throws
  // an `ApiError`, which IS an `Error` — on the
  // `cardBack.location.saveFailed`/`.removeFailed` footnote: the branch admits
  // it and dropping it is a behaviour change.
  'cardBack.stickers.error.uploadFailed': {
    kind: 'text',
    value: 'Could not upload the sticker.',
  },
  // Section-scoped `announce.*`, on the `cardBack.location.announce.*` /
  // `cardBack.votes.announce.*` shape: these reach NO DOM node in a unit test,
  // so a spy pins their PLACEMENT and `WebChromeProbe` pins their VALUE, and
  // neither alone closes the gap.
  //
  // The two SUCCESS announcements fire from the component's own mutation
  // callbacks and carry the sticker slot because the component holds the label
  // at the call site. The four FAILURE announcements fire from `lib/stickers.ts`
  // and carry NO slot — ⚠ BY OBSERVATION, not by oversight: every mutation's
  // variables hold `stickerName` and TanStack does pass `variables` to
  // `onError`, so a `{sticker}` slot IS available. Adding one is a
  // rendered-output change and is out of scope; without this note the next
  // reader adds it thinking it was missed.
  //
  // Four verbs, four keys — VERIFIED distinct (`upload`/`place`/`move`/
  // `remove`), so no shared key was ever available. Shape from
  // `cardBack.checklists.announce.addFailed`/`.renameFailed`/`.moveFailed`/
  // `.deleteFailed`.
  //
  // ⚠ `announce.uploadFailed` is BYTE-IDENTICAL to `error.uploadFailed` above
  // and is deliberately a separate key, on the TRIGGER discriminator: this one
  // fires on EVERY upload failure into an sr-only live region, while the
  // visible `Notice` normally shows the specific `cause.message` and reaches
  // its own fallback only for a non-`Error`. Different triggers, frequencies
  // and channels — the `cardBack.votes` MEANING limb, which holds here where it
  // fails for the validation pair above.
  //
  // ⚠ `moveFailed` serves BOTH `nudge()` and `rotate()`, a pre-existing
  // conflation of two gestures under one verb — preserved, because the wire
  // failure is the same and splitting it is a rendered-output change.
  'cardBack.stickers.announce.added': { kind: 'text', value: 'Sticker {sticker} added.' },
  'cardBack.stickers.announce.removed': {
    kind: 'text',
    value: 'Sticker {sticker} removed.',
  },
  'cardBack.stickers.announce.uploadFailed': {
    kind: 'text',
    value: 'Could not upload the sticker.',
  },
  'cardBack.stickers.announce.placeFailed': {
    kind: 'text',
    value: 'Could not place the sticker.',
  },
  'cardBack.stickers.announce.moveFailed': {
    kind: 'text',
    value: 'Could not move the sticker.',
  },
  'cardBack.stickers.announce.removeFailed': {
    kind: 'text',
    value: 'Could not remove the sticker.',
  },
  // The confirm dialog. Grouped as `remove.*` on the VERB, the dominant
  // confirm-dialog idiom (`cardBack.attachments.delete.*`,
  // `cardBack.checklists.delete.*`, `cardBack.comments.item.delete.*`);
  // `cardBack.emailToCard.resetDialog.*` is the lone `…Dialog` outlier and
  // exists only because `cardBack.emailToCard.reset` already occupies the bare
  // key. Nothing occupies `cardBack.stickers.remove`, so the dominant idiom
  // applies — and the group scoping is what stops a future second dialog in
  // this section adopting the `Cancel` by accident.
  //
  // ⚠ `title`'s `{sticker}` slot is filled from a ternary that is LOAD-BEARING,
  // not tidy: JSX props evaluate eagerly, so `t()` fires on every render of the
  // section whether the dialog is open or closed, and `interfaceMessage` throws
  // on a slot whose value is `undefined` (the empty STRING is a value and does
  // not throw). The closed-state render is `Remove sticker ?` — one space
  // before the mark — byte-identical to what the template produces today, and
  // discarded unrendered because `Dialog` portals with no `forceMount`.
  // `cardBack.checklists.delete.title`/`.titleNamed` is the real two-key
  // precedent for a named/unnamed pair and is REJECTED on the contract: it
  // renders `Remove sticker?`, a one-space rendered-output change.
  //
  // The `?` is inside the value: Spanish opens with `¿` and a value stopping
  // short of the mark could not say so.
  //
  // `description` renders visibly AND is wired as `aria-describedby`, so it is
  // assertable both ways; `body` is the dialog's own `<p>` and a DIFFERENT
  // string, mirroring `cardBack.comments.item.delete.body`. ⚠ `body` is
  // authored across two source lines and JSX collapses the newline and indent
  // to ONE space, so the byte-identical value has a single space between
  // `board` and `library`. One key, not two sentences: the second clause
  // reassures against the first and a translator may need to merge or reorder
  // them.
  //
  // `cancel` is a NEW per-dialog key, not a reuse — `cardBack.checklists.cancel`,
  // `cardBack.attachments.cancel`, `cardBack.comments.item.delete.cancel` and
  // `cardBack.emailToCard.resetDialog.cancel` are four independent `Cancel`s
  // already, on the `cardBack.customFields.save` rule.
  //
  // ⚠ `confirm` (`Remove sticker`) is a strict PREFIX of both `title`'s render
  // and `removeLabel`'s, so every assertion on it must be whole-string and the
  // probe must give it a `/` on both sides.
  'cardBack.stickers.remove.title': {
    kind: 'text',
    value: 'Remove sticker {sticker}?',
  },
  'cardBack.stickers.remove.description': {
    kind: 'text',
    value: 'Removing a sticker from this card cannot be undone.',
  },
  'cardBack.stickers.remove.body': {
    kind: 'text',
    value:
      'This removes the sticker from this card only. The sticker remains in the board library.',
  },
  'cardBack.stickers.remove.cancel': { kind: 'text', value: 'Cancel' },
  'cardBack.stickers.remove.confirm': { kind: 'text', value: 'Remove sticker' },
  // FE-80.1 card-back related-cards section. `RelatedCardsSection.tsx` held
  // ZERO `useInterfaceLanguage()` before this slice — the `CardVoteSection` /
  // `CardLocationSection` / `CardStickerSection` starting condition — and,
  // unlike the stickers slice, it is ONE file: `lib/related-cards.ts` authors
  // no English at all, so the REACH question that sent `lib/stickers.ts`'s nine
  // values to `cardBack.stickers.*` does not arise here. There is nothing to
  // weigh.
  //
  // ⚠ `cardBack.relatedCards.*`, PLURAL, on the rule the `cardBack.votes.*`
  // comment states: a card-back section namespace is the camelCase of the
  // heading a person reads and agrees with its grammatical NUMBER. This
  // section's `<h2>` is `Related cards`.
  //
  // WARNING: FIVE of these values are pinned by `FR-TMPL-028.spec.ts` —
  // `addLabel`/`pickerTitle` (:548-549), `urlLabel` (:550), `reciprocalLabel`
  // (:551), `submit` (:552) and `list` (:554,:562) — and a sixth, the
  // `connected both ways` inside `meta.bothWays`, by the `/connected both
  // ways/` regex at :564. Under the source-English fallback each must render
  // byte-identically to the literal it replaced. ⚠ That regex is a
  // case-SENSITIVE substring, which is what forces the meta suffix to stay
  // lower-case where the standalone inaccessible value is capitalised — see
  // the `meta.*` comment.
  //
  // ZERO plurals, VERIFIED not assumed: `related.data.length` is only compared
  // (`=== 0`), `existingIds` is only membership-tested, and no message in this
  // file agrees a noun with a count.
  'cardBack.relatedCards.heading': { kind: 'text', value: 'Related cards' },
  // ⚠ SECOND key, BYTE-IDENTICAL English, and the `<ul aria-label>` — the
  // `cardBack.votes.votersList` precedent, where a list's accessible name got
  // its own key rather than borrowing the heading. It is the second such pair
  // in the catalogue after `cardBack.stickers.heading`/`.pickerTitle`, and it
  // splits for the same reason: the two render in DIFFERENT frames (a `<h2>`
  // in the header row, an `aria-label` on the list below it) and a translator
  // wanting a bare `Relacionadas` on the list must be able to have it without
  // retitling the section.
  //
  // ⚠ Neither `WebChromeProbe` nor any Playwright name match can tell these
  // two apart. The load-bearing pin is the two-sentinel assertion in
  // `RelatedCardsSection.test.tsx`, which reads the heading and the list
  // accessible name at their own sinks.
  'cardBack.relatedCards.list': { kind: 'text', value: 'Related cards' },
  // ⚠ THREE keys carrying the BYTE-IDENTICAL value `Add related card`, which
  // is the sharpest such case in the catalogue — stickers had a pair. They are
  // the icon trigger's accessible name, the picker's title (and so the
  // dialog's accessible name), and the submit button's visible text.
  //
  // ⚠ With the picker OPEN, two BUTTONS in the document are named `Add related
  // card`. `FR-TMPL-028.spec.ts:548,552` escapes a strict-mode violation only
  // because the second lookup is scoped `within(picker)`; the unscoped
  // `page.getByRole('button', { name: 'Add related card' })` at :548 runs while
  // the picker is still closed. A translator collapsing these three to one
  // value keeps that arrangement working and a translator splitting them can
  // break it — which is an argument for the split being VISIBLE, not for
  // sharing. Named `addLabel`/`pickerTitle`/`submit` on the stickers mapping
  // (`addLabel`/`pickerTitle`/`upload.submit`); there is no bare `add` because
  // the trigger is icon-only and has no visible text.
  'cardBack.relatedCards.addLabel': { kind: 'text', value: 'Add related card' },
  'cardBack.relatedCards.pickerTitle': { kind: 'text', value: 'Add related card' },
  'cardBack.relatedCards.submit': { kind: 'text', value: 'Add related card' },
  // `pickerTitle`/`closePicker`, not `popoverTitle`/`closePopover`, because
  // this file's own copy calls the object a picker (`Close related-card
  // picker`) — `cardBack.members.*` and `cardBack.stickers.*` exactly.
  'cardBack.relatedCards.closePicker': {
    kind: 'text',
    value: 'Close related-card picker',
  },
  // The two `<label>` texts and the `<select>`'s empty `<option>`. `chooseCard`
  // is the placeholder OPTION, not a label: it is the select's rendered text
  // while nothing is chosen and so reaches the combobox's own accessible
  // VALUE, which no `getByLabel` sees.
  'cardBack.relatedCards.boardCardLabel': { kind: 'text', value: 'Board card' },
  'cardBack.relatedCards.chooseCard': { kind: 'text', value: 'Choose a card' },
  'cardBack.relatedCards.urlLabel': { kind: 'text', value: 'Card URL or short link' },
  // ⚠ ROUTED, not left literal, and the decision is forced rather than
  // stylistic: `placeholder` is one of the seven name-bearing attributes the
  // leak scan reads, so `card-title` left literal fails
  // `not.toMatch(/[A-Za-z]/)` — the alternative was a documented EXCLUSION
  // from the scan, which weakens the strongest assertion in the test file to
  // spare one value.
  //
  // It is also genuinely copy rather than vocabulary, which is what settles it
  // against `STICKER_UPLOAD_ACCEPT`'s MIME list: `MG-0042` is an invented card
  // reference (docs/notes/card-keys.md — the canonical card URL segment, which
  // the field resolves exactly like a short link) and `card-title` is an
  // English SLUG of English words, so a Spanish reader should see
  // `titulo-de-tarjeta`. ⚠ The `/c/` prefix is a real route segment and a
  // translator moving or localising it produces an example URL that resolves
  // to nothing — recorded here because no assertion sees it.
  'cardBack.relatedCards.urlPlaceholder': {
    kind: 'text',
    value: '/c/MG-0042/card-title',
  },
  'cardBack.relatedCards.reciprocalLabel': {
    kind: 'text',
    value: 'Connect cards both ways',
  },
  // The `error.*` group is earned rather than decorative, on the
  // `cardBack.stickers.upload.*` rule: all FIVE of these land in the SAME
  // `<Notice tone="bad">` — the four sentences as its `title`, `body` as its
  // `body` beneath every one of them — and a translator reading the group top
  // to bottom sees what can occupy that one box.
  //
  // ⚠ `body` is a FIFTH key rather than four suffixed sentences because the
  // component passes one constant string to `Notice`'s `body` under all four
  // titles. Splitting it would let a translator drift four strings the code
  // guarantees identical.
  //
  // `invalidLink` is emitted by `extractCardShortLink` returning `null` —
  // reached both by an empty trimmed value and by one containing a `/` or a
  // space that is not a card path. One message for both, as shipped.
  'cardBack.relatedCards.error.invalidLink': {
    kind: 'text',
    value: 'Paste a card URL or short link.',
  },
  'cardBack.relatedCards.error.self': {
    kind: 'text',
    value: 'A card cannot be related to itself.',
  },
  'cardBack.relatedCards.error.duplicate': {
    kind: 'text',
    value: 'That card is already related.',
  },
  'cardBack.relatedCards.error.notFound': {
    kind: 'text',
    value: 'That card could not be found or is not accessible.',
  },
  'cardBack.relatedCards.error.body': { kind: 'text', value: 'Choose another card.' },
  // Section-scoped `announce.*`, on the `cardBack.location.announce.*` shape.
  // TWO keys and not one slotted message: they are two different FACTS about
  // what the mutation did (a mirror row was written, or it was not), chosen by
  // the `reciprocal` checkbox, and neither is a substring of the other.
  //
  // ⚠ Both reach `announce()` ONLY. There is no visible sink, so the leak scan
  // in the test file is structurally unable to see them and a spy is their
  // only call site in a unit test — the `cardBack.votes.announce.*` case.
  'cardBack.relatedCards.announce.connected': { kind: 'text', value: 'Cards connected.' },
  'cardBack.relatedCards.announce.added': { kind: 'text', value: 'Related card added.' },
  // The post-removal `<Notice tone="info">`. `removedOneSide`/`removed` are its
  // `title`, chosen by whether the removed relation was reciprocal; `body` is
  // the one string that sits under BOTH — the `error.body` arrangement again.
  //
  // ⚠ Finding recorded, NOT fixed: `body` tells the reader to `Check the other
  // card if you want to remove its side too.` under `removed` as well, where
  // the relation was one-way and there is no other side to check. That is a
  // rendered-output defect predating this slice; one key survives its fix
  // untouched, two would leave a dead key behind.
  'cardBack.relatedCards.notice.removedOneSide': {
    kind: 'text',
    value:
      'Removed this side only. The other card keeps its relation until someone removes it there.',
  },
  'cardBack.relatedCards.notice.removed': {
    kind: 'text',
    value: 'Related card removed.',
  },
  'cardBack.relatedCards.notice.body': {
    kind: 'text',
    value: 'Check the other card if you want to remove its side too.',
  },
  // `Notice`'s `title`/`body` pair for the query failure, and the `EmptyState`
  // title. ⚠ `loadFailedTitle` carries NO full stop and `loadFailedBody` does —
  // byte-identical to the literals, and the asymmetry is the shipped `Notice`
  // idiom (`cardBack.votes.loadFailed` is a sentence because it is a bare
  // `title` with no body beneath it).
  'cardBack.relatedCards.loadFailedTitle': {
    kind: 'text',
    value: 'Related cards could not be loaded',
  },
  'cardBack.relatedCards.loadFailedBody': {
    kind: 'text',
    value: 'Try again from the card back.',
  },
  'cardBack.relatedCards.empty': { kind: 'text', value: 'No related cards' },
  // The `accessible: false` variant's title. ⚠ The apostrophe is a STRAIGHT
  // `'`, not U+2019 — the literal it replaces is a double-quoted JSX string
  // carrying a straight one. Not normalised: byte-identity outranks typography
  // here, and `FR-COLLAB-005.spec.ts:73` quotes it in a docblock.
  'cardBack.relatedCards.inaccessibleTitle': {
    kind: 'text',
    value: "A card on a board you don't have access to",
  },
  // The muted meta line beneath each title, and the one decision in this slice
  // a find-and-replace gets wrong.
  //
  // ⚠⚠ WHAT SHIPPED BEFORE THIS SLICE WAS A DUPLICATION, MEASURED NOT
  // INFERRED. The component built `meta` in one ternary and then appended a
  // reciprocity suffix in the JSX unconditionally, so the inaccessible arm
  // said it twice: rendering `accessible: false, reciprocal: true` produced
  // `Connected both ways / connected both ways`, and the one-way arm produced
  // `One-way relation / one-way`. The shipped `keeps inaccessible targets
  // generic` test read the TITLE and the absence of `Kitchen board` and never
  // touched the meta line, so it was live and unpinned. Routing it as-is would
  // have frozen it into every locale and handed a translator a leading-slash
  // fragment; the suffix is now dropped on the inaccessible arm, which changes
  // no surviving string and deletes the repeat.
  //
  // ⚠ EACH RENDERED LINE IS ONE WHOLE VALUE WITH DATA-ONLY SLOTS, and the arm
  // count is set by how many distinct LINES the component can render — not by
  // how many fragments the JSX concatenated. That is the `cardBack.dates.chip`
  // / `cardBack.location.coordinates` rule at line scope: the ` / ` joins are
  // AUTHORED COPY, and a language that separates or orders a breadcrumb
  // differently can only say so if it owns the whole string. The rejected
  // alternative was two line keys plus a `{relation}` slot filled by a routed
  // value — fewer repeated words, but it invents COPY-IN-COPY nesting this
  // catalogue has no precedent for, and it hands a translator a slot whose
  // possible fillers are invisible from the value.
  //
  // The cost is stated rather than hidden: `connected both ways`, `one-way`
  // and `archived` are each authored in TWO keys, and a translator must keep
  // the pairs consistent. That is ordinary variant work and strictly more
  // expressive than a frozen composition.
  //
  // ⚠ THE CASE DIFFERENCE IS DELIBERATE AND LOAD-BEARING. `connected both
  // ways` is lower-case inside the four accessible lines because
  // `FR-TMPL-028.spec.ts:564` matches `/connected both ways/`, a case-SENSITIVE
  // regex; `Connected both ways` is capitalised standalone because it IS the
  // whole line. They are two frames, not one phrase in two casings — which is
  // exactly why they are separate keys rather than one value used twice.
  'cardBack.relatedCards.meta.bothWays': {
    kind: 'text',
    value: '{board} / {list} / connected both ways',
  },
  'cardBack.relatedCards.meta.oneWay': {
    kind: 'text',
    value: '{board} / {list} / one-way',
  },
  'cardBack.relatedCards.meta.archivedBothWays': {
    kind: 'text',
    value: '{board} / {list} / archived / connected both ways',
  },
  'cardBack.relatedCards.meta.archivedOneWay': {
    kind: 'text',
    value: '{board} / {list} / archived / one-way',
  },
  'cardBack.relatedCards.meta.inaccessibleBothWays': {
    kind: 'text',
    value: 'Connected both ways',
  },
  'cardBack.relatedCards.meta.inaccessibleOneWay': {
    kind: 'text',
    value: 'One-way relation',
  },
  // Bare = the button's VISIBLE text, `…Label` = its accessible name, on the
  // `cardBack.dates.remove`/`.removeLabel` and `cardBack.stickers.add`/
  // `.addLabel` idiom. ⚠ WCAG 2.5.3 applies HERE and at no other site in this
  // section: `removeLabel` is the only `aria-label` in the file whose element
  // also carries visible text, so the accessible name must BEGIN with `Remove`.
  // Asserted in the test file rather than encoded in the key structure — the
  // `cardBack.dates.trigger` / `cardBack.location.trigger` precedent, and the
  // reason `addLabel` needs no such guard is that its `IconButton` renders an
  // icon and nothing else.
  //
  // ⚠ `{title}` is filled with the title the row RENDERS, which for an
  // inaccessible card is `inaccessibleTitle` — a routed value reaching a slot.
  // That is the one place this section nests copy, it predates the slice, and
  // it is correct: the name must match what the reader sees.
  'cardBack.relatedCards.remove': { kind: 'text', value: 'Remove' },
  'cardBack.relatedCards.removeLabel': {
    kind: 'text',
    value: 'Remove related card {title}',
  },

  // ⚠ `cardBack.merge.*` — the CARD-BACK merge dialog, which is NOT the board's
  // multi-select merge that `FR-CARD-035.spec.ts` drives (`Merge 2 selected
  // cards`). Two surfaces, two copies; only this one is routed here, and the
  // e2e spec is therefore untouched by this slice.
  'cardBack.merge.title': { kind: 'text', value: 'Merge {card}' },
  'cardBack.merge.description': {
    kind: 'text',
    value: 'Choose another card on this board to merge with the open card.',
  },
  'cardBack.merge.close': { kind: 'text', value: 'Close merge dialog' },
  'cardBack.merge.cancel': { kind: 'text', value: 'Cancel' },
  // ⚠ ONE key for TWO sinks, deliberately. `submit` is the submit button's
  // visible text AND the `aria-label` it carries while nothing is selected, and
  // WCAG 2.5.3 requires those two to coincide. Two keys could drift apart in a
  // translation with nothing red; one key makes the coincidence structural.
  // `submitLabel` is the selected-state accessible name and must still START
  // with `submit` — `CardMergeDialog.test.tsx` asserts that as a prefix regex.
  'cardBack.merge.submit': { kind: 'text', value: 'Merge cards' },
  'cardBack.merge.submitLabel': {
    kind: 'text',
    value: 'Merge cards: {card} with {source}',
  },
  // ⚠ THREE ASCII full stops, not U+2026 — byte-identical to the literal it
  // replaced, and the same spelling `inbox.addingCard` uses. `Button`'s unowned
  // DEFAULT `busyLabel`, the one this dialog overrides, is the single-character
  // ellipsis; the two are not interchangeable to a screen reader.
  'cardBack.merge.busy': { kind: 'text', value: 'Merging...' },
  'cardBack.merge.noCandidatesReason': {
    kind: 'text',
    value: 'There are no other ordinary cards on this board.',
  },
  // ⚠ UNREACHABLE before this slice — see the `submitBlocked` docblock in
  // `CardMergeDialog.tsx`. The slice made it reachable rather than freezing a
  // string no reader could ever see into a catalogue sent to translators.
  'cardBack.merge.noSelectionReason': {
    kind: 'text',
    value: 'Choose another card to merge with this card.',
  },
  'cardBack.merge.consequencesTitle': { kind: 'text', value: 'Before merging' },
  // ⚠ ONE key, not one sentence per key. `CardBack.test.tsx:1373-1385` reads
  // FOUR separate substrings out of this body with `toHaveTextContent`;
  // splitting it puts all four at risk of a join-whitespace change for no gain,
  // and to a reader it is one paragraph.
  'cardBack.merge.consequencesBody': {
    kind: 'text',
    value:
      "The merged card will keep {card}'s board position, combine both descriptions, union labels and members, and add generated source checklists for both cards. Both original cards will be archived. Comments and activity history are not copied. Dates, completion state, cover, location, custom fields, votes, stickers and watch state are dropped. Attachment files stay on the archived source cards; attachment names are captured in the generated checklists.",
  },
  'cardBack.merge.failedTitle': { kind: 'text', value: 'Merge failed' },
  'cardBack.merge.emptyTitle': { kind: 'text', value: 'No cards to merge' },
  'cardBack.merge.emptyBody': {
    kind: 'text',
    value: 'This board has no other ordinary open card available.',
  },
  'cardBack.merge.searchLabel': { kind: 'text', value: 'Search same-board cards' },
  'cardBack.merge.legend': { kind: 'text', value: 'Card to merge into this one' },
  'cardBack.merge.noMatches': { kind: 'text', value: 'No same-board card matches.' },
  'cardBack.merge.footnote': {
    kind: 'text',
    value: 'The open card is the target; the selected card is the second source.',
  },

  // ⚠ `cardBack.print.*` — the CARD PRINT VIEW, reached only through
  // `SharePanel`'s print toggle (`SharePanel.tsx:268`, gated on `printOpen`),
  // never in the card dialog's default state. That gating is why routing
  // `Description`, `Labels` and `Members` here does NOT collide with
  // `FR-EXP-028.spec.ts`'s `heading … exact → count 0` assertions taken inside
  // the open card dialog.
  //
  // ⚠ ONE byte-identity collision the probe structurally cannot see:
  // `cardBack.print.inList` is `In list {list}`, byte-identical to
  // `cardBack.dialog.description` above. The two surfaces are unrelated and the
  // key is deliberately NOT shared — sharing would couple print copy to dialog
  // copy for no reason, and the merge slice's shared `submit` key was shared
  // only because WCAG 2.5.3 made that one coincidence a requirement. Reading
  // `In list 1` back out of the probe proves neither key on its own; the real
  // pin is the sentinel read at the print view's own sink.
  'cardBack.print.eyebrow': { kind: 'text', value: 'Card print view' },
  'cardBack.print.inList': { kind: 'text', value: 'In list {list}' },
  'cardBack.print.description': { kind: 'text', value: 'Description' },
  'cardBack.print.noDescription': { kind: 'text', value: 'No description' },
  'cardBack.print.labels': { kind: 'text', value: 'Labels' },
  'cardBack.print.noLabels': { kind: 'text', value: 'No labels' },
  'cardBack.print.dates': { kind: 'text', value: 'Dates' },
  'cardBack.print.start': { kind: 'text', value: 'Start' },
  'cardBack.print.due': { kind: 'text', value: 'Due' },
  'cardBack.print.completed': { kind: 'text', value: 'Completed' },
  // ⚠ `Yes` / `No` are the COMPLETION VALUE, not a confirmation prompt, and
  // they are the two shortest values in the catalogue. `No` is a substring of
  // six sibling values in this very section (`No description`, `No labels`,
  // `No dates`, `No members`, `No checklists`, `No attachments`), so it can
  // only be pinned BETWEEN separators in the probe — see the chunk carrying
  // `Completed/Yes/No/No dates`. The six siblings are not, in turn, at risk
  // from their headings: `Description` ⊄ `No description`, because the routed
  // heading is capitalised and the empty line is not.
  'cardBack.print.completedYes': { kind: 'text', value: 'Yes' },
  'cardBack.print.completedNo': { kind: 'text', value: 'No' },
  'cardBack.print.noDates': { kind: 'text', value: 'No dates' },

  /* ⚠ `signedOut.*` — the SIGN-IN SURFACE: `SignInPanel.tsx`, `lib/auth.ts`,
     `routes/sign-in.tsx` and `routes/board-invites.$token.tsx`. The only
     surface a person reaches BEFORE there is a session.

     ⚠ These 26 keys cannot yet resolve from a SERVED catalogue, and that is
     structural, not a defect. `__root.tsx` gates the
     `/me/message-catalogue` query on `!isSignedOut && !isPending &&
     profile !== null`, so every file above draws while the query is
     disabled, `InterfaceLanguageProvider` falls back to the empty default
     catalogue, and `t` resolves each key through `WEB_INTERFACE_MESSAGES`
     below — correct English on every path, in every locale, and no
     translation. The keys are declared and wired correctly and go live the
     moment the gate moves; lifting it belongs to the shell-shortcuts slice,
     not here. The one exception is `signedOut.invite.title`: the invite
     sheet redirects when signed out, so its error state renders for a
     SIGNED-IN member and is the single key of the 26 photographable under a
     non-English catalogue.

     Ruling 1 — `Sign in` takes TWO keys. `signIn.heading` is the `<h1>`,
     `signIn.submit` is the submit `<button>`; they are byte-identical in
     sign-in mode and the source already distinguishes them in the other
     mode (`Create an account` vs `Create account`). One key would make it
     impossible to translate a heading and its button differently, which
     this component already does in English.

     Ruling 2 — `Create an account` takes TWO keys, read the other way.
     `createAccount.heading` is the create-mode `<h1>`; `toggle.toCreateAccount`
     is the sign-in mode's quiet toggle. Different modes, different subjects,
     and the toggle's counterpart (`I already have an account`) is a heading
     nowhere.

     Ruling 3 — `Something went wrong. Try again.` takes ONE key for the
     sign-in sink (`error.generic`, asked for from `lib/auth.ts`'s switch and
     from `sign-in.tsx`'s non-`AuthError` catch — literally the same
     `Notice`) and a SECOND for the invite sheet (`invite.genericError`,
     another surface, another sheet). Collapsing the two would be the
     `boardMap.viewLabel` defect; splitting the first would let one
     translation land and the other not, with nothing red.

     Ruling 4 — `field.email` does NOT reuse `profile.panel.email`. That one
     names a read-only row on the profile page; this one labels an `<input>`
     the member types into.

     Ruling 5 — `invite.title` does NOT reuse `boardMenu.inviteLink`. That
     one names a menu item inside the board menu; this one is the `<h1>` of a
     standalone redemption sheet. A translation of a menu item must not
     rename a page heading.

     ⚠ Every entry here is `kind: 'text'`, including the two carrying a
     `{count}` slot (`error.passwordTooShort` = `MIN_PASSWORD_LENGTH`, 8;
     `name.tooLong` = `MAX_NAME_LENGTH`, 128). Neither number is ever 1, ever
     varies at runtime, or is ever derived from data — each sentence agrees
     with a module CONSTANT, not with a count. `text` also makes the failure
     loud: a missing `values` argument throws, where a plural would default
     `count` to 0, select `forms.other`, and render correctly at 8 and 128
     for ever.

     ⚠ There is NO key for `INVALID_DISPLAY_NAME`. That arm returns the
     server's own sentence verbatim (`apps/server/src/auth/better-auth.ts`),
     and a catalogue cannot translate a response body. Same for the three
     invite refusals authored in `apps/server/src/routes/board-invite-links.ts`
     that reach the invite sheet's `Notice`. Reported as contract bugs, not
     routed. */
  'signedOut.signIn.heading': { kind: 'text', value: 'Sign in' },
  'signedOut.signIn.submit': { kind: 'text', value: 'Sign in' },
  'signedOut.signIn.busy': { kind: 'text', value: 'Signing in…' },
  'signedOut.createAccount.heading': { kind: 'text', value: 'Create an account' },
  'signedOut.createAccount.submit': { kind: 'text', value: 'Create account' },
  'signedOut.createAccount.busy': { kind: 'text', value: 'Creating account…' },
  'signedOut.toggle.toCreateAccount': { kind: 'text', value: 'Create an account' },
  'signedOut.toggle.toSignIn': { kind: 'text', value: 'I already have an account' },
  'signedOut.field.email': { kind: 'text', value: 'Email' },
  'signedOut.field.password': { kind: 'text', value: 'Password' },
  'signedOut.field.name': { kind: 'text', value: 'Your name' },
  'signedOut.redirectNotice': { kind: 'text', value: 'Sign in to continue.' },
  'signedOut.announce.createAccount': {
    kind: 'text',
    value: 'Switched to create an account.',
  },
  'signedOut.announce.signIn': { kind: 'text', value: 'Switched to sign in.' },
  'signedOut.name.tooLong': {
    kind: 'text',
    value: 'Your name must be {count} characters or fewer.',
  },
  'signedOut.error.invalidEmailOrPassword': {
    kind: 'text',
    value: 'That email and password do not match an account.',
  },
  'signedOut.error.emailTaken': {
    kind: 'text',
    value: 'An account already exists for that email. Sign in instead.',
  },
  'signedOut.error.passwordTooShort': {
    kind: 'text',
    value: 'Your password must be at least {count} characters.',
  },
  'signedOut.error.passwordTooLong': {
    kind: 'text',
    value: 'That password is too long.',
  },
  'signedOut.error.invalidEmail': {
    kind: 'text',
    value: 'That does not look like an email address.',
  },
  'signedOut.error.missingField': { kind: 'text', value: 'Fill in every field.' },
  // ⚠ No trailing full stop, and every other sentence in this block has one.
  // Copied byte for byte from `lib/auth.ts`'s network arm; the asymmetry is
  // preserved rather than corrected, because this slice routes and does not
  // rewrite copy.
  'signedOut.error.network': { kind: 'text', value: 'Could not reach the server' },
  'signedOut.error.generic': { kind: 'text', value: 'Something went wrong. Try again.' },
  'signedOut.probeFailed': {
    kind: 'text',
    value:
      'We could not check whether you are already signed in. You can still sign in below.',
  },
  'signedOut.invite.title': { kind: 'text', value: 'Invite link' },
  'signedOut.invite.genericError': {
    kind: 'text',
    value: 'Something went wrong. Try again.',
  },

  'cardBack.print.members': { kind: 'text', value: 'Members' },
  'cardBack.print.noMembers': { kind: 'text', value: 'No members' },
  'cardBack.print.checklists': { kind: 'text', value: 'Checklists' },
  // ⚠ The two checklist marks ARE routed, and the reason is measured rather
  // than stylistic: the leak scan this slice's test file runs is
  // `not.toMatch(/[A-Za-z]/)` over the accumulated residue, and `x` is a Latin
  // letter. The only two ways to satisfy it are to route these or to add a
  // pass-scoped allowance, and the merge slice returned a blanket allowance as
  // "a standing hole in the file's strongest assertion". Routing is the
  // cleaner of the two here because — unlike `Button`'s unowned `busyLabel` —
  // these are the print view's OWN literals, so nothing outside this slice
  // owns them. A translator who needs `[✓]` can have it.
  'cardBack.print.itemChecked': { kind: 'text', value: '[x]' },
  'cardBack.print.itemUnchecked': { kind: 'text', value: '[ ]' },
  'cardBack.print.noChecklists': { kind: 'text', value: 'No checklists' },
  'cardBack.print.attachments': { kind: 'text', value: 'Attachments' },
  'cardBack.print.noAttachments': { kind: 'text', value: 'No attachments' },

  // ⚠ `share.*` — the SHARE PANEL (`components/share/SharePanel.tsx`), which is
  // NOT a card-back file. It is the frame the print view hangs in
  // (`SharePanel.tsx:268` mounts `CardPrintView`), and it is mounted twice with
  // different `kind`: by `CardBack.tsx:1494` as a card and by
  // `BoardMenuPanel.tsx:3116` as a board. Routing the print view without this
  // file left a translated print page inside an English panel.
  //
  // ⚠ TWO SUBSTRING HAZARDS against keys far above, both real and both pinned
  // only by the separators around them in `WebChromeProbe`:
  //   `share.copyLink`   `Copy link`    ⊂ `Copy link to comment`   (:2926)
  //   `share.copiedLink` `Link copied.` ⊂ `Comment link copied.`   (:2663)
  // Neither is a duplicate — no `share.*` value collides with any other value
  // in this catalogue — but a substring match cannot tell a prefix from a
  // whole, so every one of the 29 sits INTERIOR to an asserted probe chunk.

  // The eight `(kind × visibility)` branches, as a TOTAL map. `SharePanel.tsx`
  // resolves them through `visibilityMessageKey`, which returns a key rather
  // than a sentence so the mapping stays pure and testable — and so the eight
  // are pinned as eight, rather than as the two the file used to cover.
  // ⚠ The card arm says "still need board access" and the board arm says "with
  // board access": the card link is one level deeper than the board link, and a
  // translation that flattens the two says the wrong thing about permissions.
  'share.visibility.boardPublic': {
    kind: 'text',
    value: 'Anyone with the link can open this board.',
  },
  'share.visibility.boardPrivate': {
    kind: 'text',
    value: 'Only board members can open this link.',
  },
  'share.visibility.boardWorkspace': {
    kind: 'text',
    value: 'Workspace members with board access can open this link.',
  },
  'share.visibility.boardEnterprise': {
    kind: 'text',
    value: 'Enterprise members with board access can open this link.',
  },
  'share.visibility.cardPublic': {
    kind: 'text',
    value: 'Anyone with the link can open this card.',
  },
  'share.visibility.cardPrivate': {
    kind: 'text',
    value: 'Only board members can open this card link.',
  },
  'share.visibility.cardWorkspace': {
    kind: 'text',
    value: 'Workspace members still need board access to open this card link.',
  },
  'share.visibility.cardEnterprise': {
    kind: 'text',
    value: 'Enterprise members still need board access to open this card link.',
  },

  // ⚠ `share.heading` is the panel's ACCESSIBLE NAME, not decoration:
  // `SharePanel.tsx` labels its `<section>` from this `<h2>`, and
  // `FR-EXP-016.spec.ts:27` locates the whole panel by
  // `getByRole('region', { name: 'Share <title>' })`. Every assertion in that
  // spec is scoped inside that locator, so a change to this one value fails
  // several browser tests at once and none of them at this string.
  'share.heading': { kind: 'text', value: 'Share {title}' },
  'share.close': { kind: 'text', value: 'Close share panel' },
  'share.urlLabel': { kind: 'text', value: 'Share URL' },
  'share.manualCopyLabel': { kind: 'text', value: 'Manual copy value' },
  'share.copyLink': { kind: 'text', value: 'Copy link' },
  'share.copyReference': { kind: 'text', value: 'Copy reference' },
  'share.copyMarkdown': { kind: 'text', value: 'Copy Markdown link' },
  'share.copyEmbed': { kind: 'text', value: 'Copy embed snippet' },

  // ⚠ SIX keys for what used to be three nouns and two sentence templates.
  // `SharePanel` built `${source} copied.` and `${source} ready for manual
  // copy` from `'Link'` / `'Markdown link'` / `'Embed snippet'`, which is
  // fragment concatenation: it fixes English word order and hands a translator
  // a bare noun with no case, no gender and no agreement. Whole sentences cost
  // one extra key and can be translated. The three nouns are GONE — they never
  // reach the catalogue, and so were never substring hazards inside it.
  'share.copiedLink': { kind: 'text', value: 'Link copied.' },
  'share.copiedReference': { kind: 'text', value: 'Reference copied.' },
  'share.copiedMarkdown': { kind: 'text', value: 'Markdown link copied.' },
  'share.copiedEmbed': { kind: 'text', value: 'Embed snippet copied.' },
  'share.manualCopyLink': { kind: 'text', value: 'Link ready for manual copy' },
  'share.manualCopyReference': {
    kind: 'text',
    value: 'Reference ready for manual copy',
  },
  'share.manualCopyMarkdown': {
    kind: 'text',
    value: 'Markdown link ready for manual copy',
  },
  'share.manualCopyEmbed': {
    kind: 'text',
    value: 'Embed snippet ready for manual copy',
  },

  'share.exportJson': { kind: 'text', value: 'Export card JSON' },
  'share.exportBusy': { kind: 'text', value: 'Preparing export…' },
  // ⚠ Both `…Loading` values say "Card" whatever the panel's `kind` is. That is
  // the literal they replace, byte for byte, and it is a latent COPY defect
  // rather than a routing one — reported in the chunk's return, not fixed here.
  // It is unreachable from the board arm today because no board caller supplies
  // `exportAvailable` or `printBusy`; the day one does, a board panel says
  // "Card export data is still loading."
  'share.exportLoading': {
    kind: 'text',
    value: 'Card export data is still loading.',
  },
  'share.print': { kind: 'text', value: 'Print card' },
  'share.printBusy': { kind: 'text', value: 'Preparing print view…' },
  'share.printLoading': {
    kind: 'text',
    value: 'Card print data is still loading.',
  },
  'share.printPreview': { kind: 'text', value: 'Print preview for {title}' },
  'share.sendToPrinter': { kind: 'text', value: 'Send to printer' },
  /* FE-80.1 LABEL PICKER — 28 keys for 31 author sites.

     The asymmetry is measured, not designed around: THREE values are authored
     TWICE in the same file — the bare `Colour` caption above both colour
     selects, the bare `Cancel` on both the edit and the delete confirmation,
     and `Delete {label} label` on both the icon trigger and the confirm button.
     Each is one key at two call sites, which is why 28 covers 31.

     ⚠ `labelPicker.colourBlindMode` (`Colour-blind label mode`) is a proper
     PREFIX of `appearance.colourBlindOn` / `...Off` (`Colour-blind label mode
     on` / `off`, :563 and :567), the appearance menu's announcements. It is not
     a duplicate — checked one value at a time against the live catalogue — but
     a substring match cannot tell a prefix from a whole, so it is proved only
     by the separators around it in `WebChromeProbe`.

     ⚠ The bare verbs — `Create`, `Try again`, `Cancel`, `Filter`, `Edit`,
     `Delete` — are each substrings of values far above (`Filter labels`,
     `Delete board permanently`, `Something went wrong. Try again.`). Same
     reasoning: separators, not presence.

     The eight `{label}`-slotted values are the row actions. They are NOT
     interchangeable: all eight reach controls in the SAME row, so a swap
     renders a real, grammatical name on the wrong button. */
  'labelPicker.title': { kind: 'text', value: 'Card labels' },
  'labelPicker.trigger': { kind: 'text', value: 'Edit labels' },
  'labelPicker.close': { kind: 'text', value: 'Close labels picker' },
  'labelPicker.search': { kind: 'text', value: 'Search labels' },
  'labelPicker.colourBlindMode': {
    kind: 'text',
    value: 'Colour-blind label mode',
  },
  'labelPicker.newName': { kind: 'text', value: 'New label name' },
  'labelPicker.newNamePlaceholder': { kind: 'text', value: 'Unnamed label' },
  'labelPicker.colour': { kind: 'text', value: 'Colour' },
  'labelPicker.newColour': { kind: 'text', value: 'Colour for new label' },
  'labelPicker.create': { kind: 'text', value: 'Create' },
  'labelPicker.loadFailedTitle': {
    kind: 'text',
    value: 'Labels could not load',
  },
  'labelPicker.retry': { kind: 'text', value: 'Try again' },
  'labelPicker.loading': { kind: 'text', value: 'Loading labels.' },
  'labelPicker.noMatch': { kind: 'text', value: 'No labels match' },
  'labelPicker.nameFor': { kind: 'text', value: 'Name for {label}' },
  'labelPicker.colourFor': { kind: 'text', value: 'Colour for {label}' },
  'labelPicker.saveNamed': { kind: 'text', value: 'Save {label} label' },
  'labelPicker.save': { kind: 'text', value: 'Save label' },
  'labelPicker.cancelEdit': {
    kind: 'text',
    value: 'Cancel editing {label} label',
  },
  'labelPicker.cancel': { kind: 'text', value: 'Cancel' },
  'labelPicker.filterNamed': { kind: 'text', value: 'Filter board by {label}' },
  'labelPicker.filter': { kind: 'text', value: 'Filter' },
  'labelPicker.editNamed': { kind: 'text', value: 'Edit {label} label' },
  'labelPicker.edit': { kind: 'text', value: 'Edit' },
  'labelPicker.deleteNamed': { kind: 'text', value: 'Delete {label} label' },
  'labelPicker.delete': { kind: 'text', value: 'Delete' },
  'labelPicker.cancelDelete': {
    kind: 'text',
    value: 'Cancel deleting {label} label',
  },
  'labelPicker.deleteWarning': {
    kind: 'text',
    value: 'Deleting this label removes it from every card on this board.',
  },
  /* FE-80.1 THE PROFILE PAGE — 64 keys across five namespaces, for the route
     frame and the four sections it composes. This is the fe-plan queue's profile-page entry, the
     entry the rich-description-editor close-out advanced to after the last
     card-back file.

     ⚠ `LocalePreferencesSection` is the reason this slice has the shape it
     does. That file has imported `t` since the locale-formatting slice, for the
     three `lib/locale-formatting` leaves ONLY, and its own docstring names the
     six chrome values it deliberately left English until "the profile-page
     FE-80.1 slice" landed. Those six are `profile.locale.heading`, `.loading`,
     `.loadFailedTitle`, `.interfaceLanguage`, `.timezone` and `.datePreview`
     below. A `grep -rL useInterfaceLanguage` calls that file ROUTED and it is
     not — the -L form answers "does this file import the hook", never "does
     this file render unrouted English", and the merge-dialog slice recorded
     the same false positive from the other direction.

     ⚠ FIVE values here are BYTE-IDENTICAL to values that already exist under
     other namespaces, and every one is a deliberate refusal to share. MEASURED
     by grouping the whole catalogue by value rather than by grepping the ones
     that looked likely — the first pass sampled ten values, found four, and
     missed `Display name` entirely:
       `profile.page.signInAction`   `Sign in` — an EIGHTH copy, beside
         `shell.signIn`, `myCards.signInAction`, `inbox.signInAction`,
         `planner.signInAction`, `route.notFound.signIn`, `board.signIn.action`
         and `publicProfile.signInAction`.
       `profile.page.genericError`   `Something went wrong. Try again.` — a
         FOURTH copy, beside `myCards.genericError`, `inbox.genericError` and
         `planner.genericError`.
       `profile.lifecycle.cancel`    `Cancel` — a FOURTEENTH copy.
       `profile.connectedTools.never` `Never` — beside
         `notifications.preferences.frequency.never`.
       `profile.panel.displayName`   `Display name` — beside
         `cardBack.attachments.add.file.displayNameLabel` and
         `...add.link.displayNameLabel`.
     The cross-file share was settled against in the rich-description-editor
     slice and is settled the same way here: one namespace does not reach into
     another's key, because the two surfaces can diverge and a shared key turns
     that into a copy change to a shipped surface rather than an edit to one
     entry.

     ⚠ FIVE values are authored TWICE and are ONE key at two call sites, the
     label picker's asymmetry again:
       `profile.panel.displayName` — the caption above the editor AND the
         `InlineEdit` label, which is that control's accessible name.
       `profile.panel.bio` — the section heading AND the `TextField` label.
       `profile.connectedTools.heading` — the `h2` AND the grant list's
         `aria-label`. Both name the same region; telling them apart would be
         naming one region two ways.
       `profile.lifecycle.deactivate` / `.delete` — once on the section's
         trigger and once as the dialog's confirm label, through `actionLabel`.
     So 64 keys cover 69 author sites.

     ⚠ TWO sentences here were built by CONCATENATION and are now slotted, the
     `CardPrintView` `', '` defect class:
       `profile.connectedTools.scopeTrigger` was
         `${SCOPE_LABELS[scope]} — change connected-tool scope`
       `profile.connectedTools.revokedScope` was
         `{SCOPE_LABELS[grant.scope]}{revoked ? ' · Revoked' : ''}`
     A join is not translatable — word order and the separator are both
     language-specific. Both values are byte-identical to what the joins
     rendered, and `revokedScope` is a WHOLE second value rather than a
     `' · Revoked'` fragment, so a locale can put the state first.

     ⚠ `profile.connectedTools.scope.readOnly` / `.readWrite` reach the DOM
     through `scopeLabel(t, scope)`, the shape `languageLabel(t, language)` and
     `timezoneLabel(t, timezone)` already use one file away — module-level
     constants (`SCOPE_LABELS`, `scopeItems`) cannot call `t`, and turning them
     into functions of `t` is the nearest existing idiom rather than a new one.
     `ProfilePanel`'s two former module constants became plain `t` reads inside
     the component instead, because neither has a second call site.

     The slotted values are NOT interchangeable: `{toolName}` reaches the revoke
     button's accessible name AND its `disabledReason`, both in the SAME row, so
     a swap names a real tool on the wrong control. */
  'profile.page.title': { kind: 'text', value: 'Your profile' },
  'profile.page.signInTitle': {
    kind: 'text',
    value: 'Sign in to see your profile',
  },
  'profile.page.signInBody': {
    kind: 'text',
    value:
      'Your display name, avatar and bio are yours alone — there is nothing to show until we know who you are.',
  },
  'profile.page.signInAction': { kind: 'text', value: 'Sign in' },
  'profile.page.loadFailedTitle': {
    kind: 'text',
    value: 'We could not load your profile',
  },
  'profile.page.genericError': {
    kind: 'text',
    value: 'Something went wrong. Try again.',
  },
  'profile.panel.identity': { kind: 'text', value: 'Identity' },
  'profile.panel.displayName': { kind: 'text', value: 'Display name' },
  'profile.panel.username': { kind: 'text', value: 'Username' },
  'profile.panel.usernameHint': {
    kind: 'text',
    value: 'Usernames cannot be changed yet.',
  },
  'profile.panel.email': { kind: 'text', value: 'Email' },
  'profile.panel.emailHint': {
    kind: 'text',
    value: 'Email addresses cannot be changed yet.',
  },
  'profile.panel.bio': { kind: 'text', value: 'Bio' },
  'profile.panel.saveBio': { kind: 'text', value: 'Save bio' },
  'profile.panel.avatar': { kind: 'text', value: 'Avatar' },
  'profile.panel.avatarUrl': { kind: 'text', value: 'Avatar image URL' },
  // FE-44.2 -- this value USED to end "Uploading a file is not supported yet."
  // It was true of the interface and had been understating the server since
  // `BE-44.3` shipped `POST /me/avatar/presign`; it was rewritten in the same
  // commit that gave that route its first caller, and deliberately not one
  // commit earlier -- a hint promising an upload that does not exist is the same
  // lie pointing the other way.
  //
  // ⚠ IT NAMED "Upload an avatar" (`profile.avatarUpload.heading`) FOR AS LONG
  // AS THE PICKER WAS A SECTION OF THE PAGE, and the note here said: "If the
  // picker is ever folded into this panel's Avatar section, this sentence is
  // what has to change with it." The `fe11-avatar-panel-relocation` chunk folded
  // it in -- the picker is now an `h3` inside this panel's own Avatar section,
  // rendered directly beneath this field -- so the sentence points DOWN rather
  // than sideways, and no longer quotes a heading that is no longer a peer.
  //
  // ⚠ "To upload a file instead," is load-bearing and unchanged.
  // `ProfilePanel.test.tsx` asserts `/to upload a file instead/i`, and
  // `FR-NOTIF-033.spec.ts` asserts that "not supported yet" is absent from a
  // page that has a working picker on it. Both still hold.
  'profile.panel.avatarUrlHint': {
    kind: 'text',
    value:
      'A link to an image on the web. To upload a file instead, use the picker below.',
  },
  'profile.panel.avatarUrlInvalid': {
    kind: 'text',
    value: 'Enter a link that starts with http:// or https://.',
  },
  'profile.panel.saveAvatar': { kind: 'text', value: 'Save avatar' },
  'profile.panel.removeAvatar': { kind: 'text', value: 'Remove avatar' },
  'profile.panel.initialsPreview': { kind: 'text', value: 'Initials preview' },
  'profile.panel.initialsPreviewCaption': {
    kind: 'text',
    value: 'How you appear when you have no avatar image.',
  },
  'profile.lifecycle.heading': { kind: 'text', value: 'Account lifecycle' },
  'profile.lifecycle.body': {
    kind: 'text',
    value:
      'Closing your account revokes sign-in and removes you from workspace and board member lists. Work you already authored remains for collaborators.',
  },
  'profile.lifecycle.deactivate': { kind: 'text', value: 'Deactivate account' },
  'profile.lifecycle.delete': { kind: 'text', value: 'Delete account' },
  'profile.lifecycle.deactivateTitle': {
    kind: 'text',
    value: 'Deactivate account?',
  },
  'profile.lifecycle.deleteTitle': { kind: 'text', value: 'Delete account?' },
  'profile.lifecycle.dialogDescription': {
    kind: 'text',
    value: 'This closes your access without deleting authored work.',
  },
  'profile.lifecycle.acknowledgeReason': {
    kind: 'text',
    value: 'Acknowledge the account closure consequence first.',
  },
  'profile.lifecycle.cancel': { kind: 'text', value: 'Cancel' },
  'profile.lifecycle.deactivateNoticeTitle': {
    kind: 'text',
    value: 'Your account will be deactivated',
  },
  'profile.lifecycle.deleteNoticeTitle': {
    kind: 'text',
    value: 'Your account will be deleted',
  },
  'profile.lifecycle.consequences': {
    kind: 'text',
    value:
      'You will lose sign-in access, leave every workspace and board member list, and stop receiving notifications. Comments, activity, attachments and cards you created remain visible to collaborators under your inactive account.',
  },
  'profile.lifecycle.adminRule': {
    kind: 'text',
    value:
      'If this would leave any workspace or board without an admin, the server refuses the close and names the single-admin rule.',
  },
  'profile.lifecycle.acknowledge': {
    kind: 'text',
    value: 'I understand this will close my account',
  },
  'profile.connectedTools.heading': { kind: 'text', value: 'Connected tools' },
  'profile.connectedTools.description': {
    kind: 'text',
    value:
      'Review tools that can act with your permissions. Revoking access does not undo changes already made.',
  },
  'profile.connectedTools.grantHeading': { kind: 'text', value: 'Grant a tool' },
  'profile.connectedTools.toolName': { kind: 'text', value: 'Tool name' },
  'profile.connectedTools.scope.readOnly': { kind: 'text', value: 'Read only' },
  'profile.connectedTools.scope.readWrite': {
    kind: 'text',
    value: 'Read and write',
  },
  'profile.connectedTools.scopeTrigger': {
    kind: 'text',
    value: '{scope} — change connected-tool scope',
  },
  'profile.connectedTools.scopeHeading': {
    kind: 'text',
    value: 'Connected-tool scope',
  },
  'profile.connectedTools.grantExpiry': {
    kind: 'text',
    value: 'Grant expiry: no expiry unless you revoke it.',
  },
  'profile.connectedTools.create': {
    kind: 'text',
    value: 'Create connected tool token',
  },
  'profile.connectedTools.nameRequired': {
    kind: 'text',
    value: 'Name the tool before granting access.',
  },
  'profile.connectedTools.tokenLabel': {
    kind: 'text',
    value: 'New bearer token',
  },
  'profile.connectedTools.tokenHint': {
    kind: 'text',
    value: 'This token is shown once. Store it before leaving this page.',
  },
  'profile.connectedTools.loading': {
    kind: 'text',
    value: 'Checking connected tools...',
  },
  'profile.connectedTools.empty': {
    kind: 'text',
    value: 'No connected tools yet.',
  },
  'profile.connectedTools.never': { kind: 'text', value: 'Never' },
  'profile.connectedTools.revokedScope': {
    kind: 'text',
    value: '{scope} · Revoked',
  },
  'profile.connectedTools.revoke': { kind: 'text', value: 'Revoke {toolName}' },
  'profile.connectedTools.alreadyRevoked': {
    kind: 'text',
    value: '{toolName} is already revoked.',
  },
  'profile.connectedTools.granted': { kind: 'text', value: 'Granted' },
  'profile.connectedTools.lastActive': { kind: 'text', value: 'Last active' },
  'profile.connectedTools.expires': { kind: 'text', value: 'Expires' },
  // AT-13 (B85, `docs/notes/api-token-requirements.md`) — issued tokens on the
  // member's own list, and the reveal / regenerate dialog. Every value below
  // was measured against the whole catalogue before it was minted and has no
  // second author; the dialog's buttons are worded so none of them is `Cancel`,
  // `Close`, `Copy` or `Done`, which routing tests pin by exact author list.
  // The shell snippets the dialog shows are commands, not copy, and are not
  // here. `profile.connectedTools.grantExpiry` stays: it describes the grant
  // the self-service form creates, which still has no expiry.
  'profile.connectedTools.adminIssuedNote': {
    kind: 'text',
    value: 'API tokens are issued by a platform admin.',
  },
  'profile.connectedTools.unclaimedScope': {
    kind: 'text',
    value: '{scope} · Not yet revealed',
  },
  'profile.connectedTools.activeScope': { kind: 'text', value: '{scope} · Active' },
  'profile.connectedTools.expiredScope': { kind: 'text', value: '{scope} · Expired' },
  'profile.connectedTools.issuedBy': { kind: 'text', value: 'Issued by {name}' },
  'profile.connectedTools.tokenEndingLabel': { kind: 'text', value: 'Token' },
  'profile.connectedTools.tokenEnding': { kind: 'text', value: '…{hint}' },
  'profile.connectedTools.reveal': { kind: 'text', value: 'Reveal token' },
  'profile.connectedTools.revealFor': {
    kind: 'text',
    value: 'Reveal token for {toolName}',
  },
  'profile.connectedTools.regenerate': { kind: 'text', value: 'Regenerate' },
  'profile.connectedTools.regenerateFor': {
    kind: 'text',
    value: 'Regenerate token for {toolName}',
  },
  'profile.connectedTools.revealDialog.title': {
    kind: 'text',
    value: 'Reveal the {toolName} token',
  },
  'profile.connectedTools.revealDialog.description': {
    kind: 'text',
    value: 'Check what this token can do. It is shown once, when you reveal it.',
  },
  'profile.connectedTools.revealDialog.confirm': {
    kind: 'text',
    value: 'Reveal the token now',
  },
  'profile.connectedTools.revealDialog.dismiss': {
    kind: 'text',
    value: 'Reveal it later',
  },
  'profile.connectedTools.regenerateDialog.title': {
    kind: 'text',
    value: 'Regenerate the {toolName} token',
  },
  'profile.connectedTools.regenerateDialog.description': {
    kind: 'text',
    value:
      'The current token stops working at once. Every tool using it is cut off until you give it the new one.',
  },
  'profile.connectedTools.regenerateDialog.confirm': {
    kind: 'text',
    value: 'Regenerate the token now',
  },
  'profile.connectedTools.regenerateDialog.dismiss': {
    kind: 'text',
    value: 'Keep the current token',
  },
  'profile.connectedTools.revealed.title': {
    kind: 'text',
    value: 'Your {toolName} token',
  },
  'profile.connectedTools.revealed.warning': {
    kind: 'text',
    value:
      'This token will not be shown again. Copy it now; if you lose it, regenerate it.',
  },
  'profile.connectedTools.revealed.tokenLabel': { kind: 'text', value: 'API token' },
  'profile.connectedTools.revealed.copyToken': { kind: 'text', value: 'Copy token' },
  'profile.connectedTools.revealed.exportHeading': {
    kind: 'text',
    value: 'Export the token in the shell that starts your tools',
  },
  'profile.connectedTools.revealed.copyExport': {
    kind: 'text',
    value: 'Copy the export command',
  },
  'profile.connectedTools.revealed.mcpHeading': {
    kind: 'text',
    value: 'Connect an MCP client',
  },
  'profile.connectedTools.revealed.copyMcp': {
    kind: 'text',
    value: 'Copy the MCP command',
  },
  'profile.connectedTools.revealed.curlHeading': {
    kind: 'text',
    value: 'Call the API directly',
  },
  'profile.connectedTools.revealed.copyCurl': {
    kind: 'text',
    value: 'Copy the curl command',
  },
  'profile.connectedTools.revealed.close': {
    kind: 'text',
    value: 'Close and forget the token',
  },
  'profile.connectedTools.copied': { kind: 'text', value: 'Copied to the clipboard.' },
  'profile.connectedTools.copyFailed': {
    kind: 'text',
    value: 'Could not copy. Select the text and copy it by hand.',
  },
  'profile.connectedTools.copyUnavailable': {
    kind: 'text',
    value:
      'This browser does not allow copying here. Select the text and copy it by hand.',
  },
  'profile.connectedTools.notLive': {
    kind: 'text',
    value: 'This token can no longer be revealed: it was revoked or it has expired.',
  },
  'profile.connectedTools.revealFailed': {
    kind: 'text',
    value: 'The token could not be revealed. Try again.',
  },
  // The six values `LocalePreferencesSection`'s own docstring named as
  // deliberately-still-English until this slice. `.timezone` is `Timezone`,
  // which is a proper substring of NOTHING else in this catalogue but shares
  // its word with `profile.timezoneSaved` (`Timezone saved.`) — checked one
  // value at a time against the live record, not inferred from the prefix.
  'profile.locale.heading': { kind: 'text', value: 'Language and time' },
  /* FE-80.1 POWER-UPS — 157 keys in ONE namespace, across the six files that
     draw the whole Power-Up product surface: the manifest runtime, the enabled
     list inside the board menu, the directory, its detail panel, and the
     Enterprise policy page with its own route. All six held ZERO `t('…')` call
     sites before this slice. Every value here is BYTE-IDENTICAL to what the
     file rendered, with exactly one deliberate exception recorded at
     `powerUps.enterprise.policyEntryMeta`.

     ⚠ NINE VALUES COLLIDE WITH VALUES THAT ALREADY EXIST UNDER OTHER
     NAMESPACES, and all nine are declared SEPARATELY, knowingly, on the
     `boardTable.heading` ruling — a separate key is correct when the two name
     different DOM nodes on different surfaces. MEASURED by grouping the whole
     catalogue by value, not by grepping the ones that looked likely:
       `Cancel`                            → a 25th author
       `Sign in`                           → a 10th
       `Something went wrong. Try again.`  → a 6th
       `We could not load your account`    → a 2nd (`workspaceMembers.*`)
       `Automation`                        → the board menu's Automation SECTION
                                             against a marketplace CATEGORY
       `Search`                            → global search's control against
                                             THIS form's submit button
       `Settings`                          → the board menu's Settings section
                                             against one manifest line's `{group}`
       `{powerUp}: {label}` and
       `{powerUp}: {label} is not interactive in this frontend yet.`
                                           → byte-identical to
                                             `boardFace.powerUps.headerAction` and
                                             `cardBack.powerUp.notInteractive`;
                                             see the `powerUps.surface.*` note.

     ⚠ `Retry` IS PRESERVED, NOT NORMALISED. The house word is `Try again`,
     which nine keys already carry (`home.retry`, `boardFace.retry`,
     `route.error.retry`, …), and this slice's eight `Notice` actions all say
     `Retry`. Three test files OUTSIDE this slice press a button named exactly
     `Retry`; copy normalisation is not a routing change. Reported, not taken.

     ⚠ TWO KEYS ARE `kind: 'plural'` and the other 155 are `kind: 'text'`.
     Both plurals carry `{count}` in BOTH forms so the one-form still renders
     `1 board` byte-for-byte against the ternary they replace, and BOTH call
     sites pass the THIRD argument — `interfaceMessage` defaults `count = 0` and
     selects `forms.other` whenever `count !== 1`, so a call site that omits it
     renders `1 boards` and no assertion on the `other` form can see it.

     --- the eleven namespace-level singletons, each read from 2+ files ------
     A datum fallback is a per-namespace singleton (the catalogue's own rule:
     `Untitled workspace` once per namespace, `Untitled card` six times ACROSS
     namespaces). Two keys only when both nodes can be mounted at once — which
     is why `Pricing` and `Made by us` are each TWO keys below, and why the
     directory row badge and the detail banner share ONE. */
  'powerUps.untitledName': { kind: 'text', value: 'Untitled Power-Up' },
  'powerUps.unknownPublisher': { kind: 'text', value: 'Unknown publisher' },
  'powerUps.noDescription': { kind: 'text', value: 'No description provided.' },
  'powerUps.noPermissions': { kind: 'text', value: 'No board permissions requested.' },
  'powerUps.retry': { kind: 'text', value: 'Retry' },
  'powerUps.cancel': { kind: 'text', value: 'Cancel' },
  'powerUps.keepData': { kind: 'text', value: 'Keep all Power-Up data' },
  'powerUps.enabledBadge': { kind: 'text', value: 'Enabled on this board' },
  'powerUps.pricingTerm': { kind: 'text', value: 'Pricing' },
  'powerUps.permissionsTerm': { kind: 'text', value: 'Permissions' },
  'powerUps.entryMeta': { kind: 'text', value: '{category} · {publisher}' },
  /* The `PowerUpCategory` wire→label table, eleven arms. `PowerUpDirectory.tsx`
     held it as a module const, and a module-level constant CANNOT call `t()`;
     the file now holds `POWER_UP_CATEGORY_KEYS` (wire value → catalogue KEY)
     and resolves at render, the `WorkspaceSwitcher` / `BOARD_ROLE_LABELS`
     shape. The wire values (`file_management`, …) stay literal in `<option
     value=>`, in search params and as record keys: routing one sends a
     translated string to the server and the schema answers 422. */
  'powerUps.category.analytics': { kind: 'text', value: 'Analytics' },
  'powerUps.category.automation': { kind: 'text', value: 'Automation' },
  'powerUps.category.developerTools': { kind: 'text', value: 'Developer tools' },
  'powerUps.category.fileManagement': { kind: 'text', value: 'File management' },
  'powerUps.category.communication': { kind: 'text', value: 'Communication' },
  'powerUps.category.humanResources': { kind: 'text', value: 'HR' },
  'powerUps.category.it': { kind: 'text', value: 'IT' },
  'powerUps.category.marketing': { kind: 'text', value: 'Marketing' },
  'powerUps.category.productDesign': { kind: 'text', value: 'Product design' },
  'powerUps.category.projectManagement': { kind: 'text', value: 'Project management' },
  'powerUps.category.sales': { kind: 'text', value: 'Sales' },
  /* The `PowerUpPricingModel` wire→label table, same shape and same reason. */
  'powerUps.pricing.free': { kind: 'text', value: 'Free' },
  'powerUps.pricing.paid': { kind: 'text', value: 'Paid' },
  'powerUps.pricing.requiresSeparateSubscription': {
    kind: 'text',
    value: 'Requires a separate subscription',
  },
  /* `PowerUpSurfaceRuntime.tsx` — TWELVE KEYS WITH ZERO RUNTIME REACHABILITY.
     `PowerUpSurfaceRuntime`, `PowerUpRuntimeBadge` and `PowerUpRuntimeButton`
     are imported by `components/index.ts` and by their own test file and by
     NOTHING ELSE; `BoardFace.tsx` and `CardBack.tsx` import only the TYPE and
     RE-IMPLEMENTED these affordances, which is why the catalogue already holds
     `boardFace.powerUps.headerAction` (`{powerUp}: {label}`) and
     `cardBack.powerUp.notInteractive` byte-identically. No screenshot can show
     these twelve, because no route mounts the component; the pin is the only
     thing that will ever exercise them.

     ⚠ THE FORWARD NOTE: if this runtime is ever wired up it REPLACES CardBack's
     and BoardFace's hand-rolled versions, and the duplicate pairs must collapse
     AT THAT MOMENT. Cross-namespace reach is settled against, so they are
     declared here rather than borrowed from `boardFace.*` / `cardBack.*`. */
  'powerUps.surface.entryName': { kind: 'text', value: '{powerUp}: {label}' },
  'powerUps.surface.notInteractive': {
    kind: 'text',
    value: '{powerUp}: {label} is not interactive in this frontend yet.',
  },
  'powerUps.surface.boardHeaderButtons': {
    kind: 'text',
    value: 'Board header Power-Up buttons',
  },
  'powerUps.surface.cardFrontBadges': {
    kind: 'text',
    value: 'Card front Power-Up badges',
  },
  'powerUps.surface.cardBackBadges': { kind: 'text', value: 'Card back Power-Up badges' },
  'powerUps.surface.cardSections': { kind: 'text', value: 'Power-Up card sections' },
  'powerUps.surface.cardBackButtons': {
    kind: 'text',
    value: 'Card back Power-Up buttons',
  },
  'powerUps.surface.listMenuSurfaces': {
    kind: 'text',
    value: 'Power-Up list menu surfaces',
  },
  'powerUps.surface.actionsHeading': { kind: 'text', value: 'Power-Up actions' },
  'powerUps.surface.listMenuActions': {
    kind: 'text',
    value: 'Power-Up list menu actions',
  },
  'powerUps.surface.listMenuSorts': { kind: 'text', value: 'Power-Up list menu sorts' },
  'powerUps.surface.unavailableTitle': {
    kind: 'text',
    value: 'Power-Up surfaces unavailable',
  },
  /* `BoardPowerUpList.tsx` — the enabled rows inside the board menu.

     ⚠ `powerUps.board.heading` names the `<section>` AND its own `<h3>`, and
     `.disable` names the row trigger AND the dialog's confirm — ONE key each,
     the `profile.connectedTools.heading` ruling. Telling them apart would be
     naming one region two ways.

     ⚠ `.disableBody` is a WHOLE SENTENCE with a `{powerUp}` slot, split on a
     NUL sentinel at render so the name keeps its `<strong>`. Two fragment keys
     were rejected (a join fixes word order and separator) and dropping the
     `<strong>` was rejected (it removes emphasis from a destructive
     confirmation). The render carries a fallback arm for a translation that
     omits the slot, so the name can never silently vanish from the dialog.
     `BoardDangerConfirm.tsx:92`, `:104` and `WorkspaceDeleteDialog.tsx:58` have
     the same construct and are still unrouted — copy this, do not re-decide it.

     ⚠ `.enabledAt` interpolates a RAW POSTGRES TIMESTAMP
     (`2026-08-27 10:00:00.000+00`). The frame is routed and the value stays
     literal; formatting it is a behaviour change. Reported, not taken.

     ⚠ `.settingsInvalidJson` / `.settingsInvalidShape` are held in component
     STATE as catalogue KEYS and resolved at render — state set inside an event
     handler is the same problem as a module constant, one step later. */
  'powerUps.board.heading': { kind: 'text', value: 'Enabled Power-Ups' },
  'powerUps.board.description': {
    kind: 'text',
    value:
      'Board settings apply to every member. Connections are private to each member.',
  },
  'powerUps.board.loadFailedTitle': {
    kind: 'text',
    value: 'Could not load enabled Power-Ups',
  },
  'powerUps.board.emptyTitle': { kind: 'text', value: 'No Power-Ups enabled' },
  'powerUps.board.emptyBody': {
    kind: 'text',
    value: 'Enable one from the directory to add it to this board.',
  },
  'powerUps.board.rowLabel': { kind: 'text', value: '{powerUp} enabled Power-Up' },
  'powerUps.board.enabledAt': { kind: 'text', value: 'Enabled {date}' },
  'powerUps.board.headerVisibility': {
    kind: 'text',
    value: 'Show {powerUp} in board header',
  },
  'powerUps.board.settingsLabel': {
    kind: 'text',
    value: '{powerUp} board settings JSON',
  },
  'powerUps.board.saveSettings': { kind: 'text', value: 'Save {powerUp} settings' },
  'powerUps.board.clearSettings': { kind: 'text', value: 'Clear {powerUp} settings' },
  'powerUps.board.settingsInvalidJson': {
    kind: 'text',
    value: 'Settings must be valid JSON.',
  },
  'powerUps.board.settingsInvalidShape': {
    kind: 'text',
    value: 'Settings must be a JSON object within the Power-Up limits.',
  },
  'powerUps.board.noSettings': {
    kind: 'text',
    value: 'This Power-Up has no board settings.',
  },
  'powerUps.board.authNotRequired': {
    kind: 'text',
    value: 'No external account is required.',
  },
  'powerUps.board.connected': { kind: 'text', value: 'Connected for you' },
  'powerUps.board.disconnect': { kind: 'text', value: 'Disconnect {powerUp}' },
  'powerUps.board.reconnectBody': {
    kind: 'text',
    value: 'Reconnect your account to keep using this Power-Up.',
  },
  'powerUps.board.connectBody': {
    kind: 'text',
    value: 'Connect your account before this Power-Up can show your data.',
  },
  'powerUps.board.authorise': { kind: 'text', value: 'Authorise account for {powerUp}' },
  'powerUps.board.reconnect': { kind: 'text', value: 'Reconnect account for {powerUp}' },
  'powerUps.board.removalBody': {
    kind: 'text',
    value:
      'Remove this Power-Up from the board, or clear only your private Power-Up settings.',
  },
  'powerUps.board.disable': { kind: 'text', value: 'Disable {powerUp} Power-Up' },
  'powerUps.board.disableBusy': { kind: 'text', value: 'Disabling {powerUp} Power-Up' },
  'powerUps.board.removePersonal': {
    kind: 'text',
    value: 'Remove personal settings for {powerUp}',
  },
  'powerUps.board.removePersonalBusy': {
    kind: 'text',
    value: 'Removing personal settings for {powerUp}',
  },
  'powerUps.board.disableTitle': { kind: 'text', value: 'Disable {powerUp} Power-Up?' },
  'powerUps.board.disableDescription': {
    kind: 'text',
    value: 'This removes the Power-Up surfaces from this board.',
  },
  'powerUps.board.disableBody': {
    kind: 'text',
    value:
      'Disabling {powerUp} removes its board, list and card surfaces for everyone on this board.',
  },
  'powerUps.board.keepDataHint': {
    kind: 'text',
    value:
      'Leave stored board and card data so it can appear again if this Power-Up is re-enabled later. Native card names, labels, attachments and comments are never removed by this action.',
  },
  /* `PowerUpDirectory.tsx` — the searchable grid and its filters.

     ⚠ `.pricingLabel` (`Pricing`, the filter `<label>`) is a SECOND key beside
     `powerUps.pricingTerm` (`Pricing`, the row `<dt>`), and `.madeByUsFilter` a
     second beside `.madeByUsBadge`, because BOTH are on screen at once in one
     scroll view — the settled `boardFace.retry` discriminator. The directory
     row badge and the DETAIL banner share one key instead: `BoardMenuPanel`
     renders the directory OR the detail, never both. */
  'powerUps.directory.heading': { kind: 'text', value: 'Power-Up directory' },
  'powerUps.directory.boardContext': {
    kind: 'text',
    value: 'Showing enabled state for {board}.',
  },
  'powerUps.directory.searchLabel': { kind: 'text', value: 'Search Power-Ups' },
  'powerUps.directory.categoryLabel': { kind: 'text', value: 'Power-Up category' },
  'powerUps.directory.allCategories': { kind: 'text', value: 'All categories' },
  'powerUps.directory.pricingLabel': { kind: 'text', value: 'Pricing' },
  'powerUps.directory.allPricing': { kind: 'text', value: 'All pricing' },
  'powerUps.directory.madeByUsFilter': { kind: 'text', value: 'Made by us' },
  'powerUps.directory.search': { kind: 'text', value: 'Search' },
  'powerUps.directory.loadFailedTitle': {
    kind: 'text',
    value: 'Could not load Power-Ups',
  },
  'powerUps.directory.emptyTitle': { kind: 'text', value: 'No Power-Ups found' },
  'powerUps.directory.emptyBody': {
    kind: 'text',
    value: 'Try a different search or filter.',
  },
  'powerUps.directory.entryLabel': { kind: 'text', value: '{powerUp} Power-Up' },
  'powerUps.directory.madeByUsBadge': { kind: 'text', value: 'Made by us' },
  'powerUps.directory.viewDetails': { kind: 'text', value: 'View details' },
  /* `PowerUpDetailView.tsx` — one Power-Up's disclosure panel.

     ⚠ `.manifestLine` is `{group}: {entries}`. The `{group}` half resolves
     through the eleven `manifest.*` keys below; `{entries}` is a COMMA-JOIN of
     provider-authored labels, which no single slotted key can express.
     `Intl.ListFormat` is the real fix and is a behaviour change. Reported. */
  'powerUps.detail.regionLabel': { kind: 'text', value: 'Power-Up detail' },
  'powerUps.detail.back': { kind: 'text', value: 'Back to Power-Up directory' },
  'powerUps.detail.loadFailedTitle': { kind: 'text', value: 'Could not load Power-Up' },
  'powerUps.detail.emptyTitle': { kind: 'text', value: 'No Power-Up selected' },
  'powerUps.detail.emptyBody': {
    kind: 'text',
    value: 'Choose an entry from the directory to read what it adds.',
  },
  'powerUps.detail.articleLabel': { kind: 'text', value: '{powerUp} Power-Up detail' },
  'powerUps.detail.heading': { kind: 'text', value: '{powerUp} Power-Up' },
  'powerUps.detail.additionsTerm': { kind: 'text', value: 'What it adds' },
  'powerUps.detail.noAdditions': { kind: 'text', value: 'No declared surfaces.' },
  'powerUps.detail.manifestLine': { kind: 'text', value: '{group}: {entries}' },
  'powerUps.detail.manifest.cardFrontBadges': {
    kind: 'text',
    value: 'Card front badges',
  },
  'powerUps.detail.manifest.cardBackBadges': { kind: 'text', value: 'Card back badges' },
  'powerUps.detail.manifest.cardBackSections': {
    kind: 'text',
    value: 'Card back sections',
  },
  'powerUps.detail.manifest.cardBackButtons': {
    kind: 'text',
    value: 'Card back buttons',
  },
  'powerUps.detail.manifest.boardHeaderButtons': {
    kind: 'text',
    value: 'Board header buttons',
  },
  'powerUps.detail.manifest.listMenuActions': {
    kind: 'text',
    value: 'List menu actions',
  },
  'powerUps.detail.manifest.listMenuSorts': { kind: 'text', value: 'List menu sorts' },
  'powerUps.detail.manifest.attachmentSections': {
    kind: 'text',
    value: 'Attachment sections',
  },
  'powerUps.detail.manifest.attachmentThumbnails': {
    kind: 'text',
    value: 'Attachment thumbnails',
  },
  'powerUps.detail.manifest.settings': { kind: 'text', value: 'Settings' },
  'powerUps.detail.manifest.authorisation': { kind: 'text', value: 'Authorisation' },
  /* `EnterprisePowerUpAdmin.tsx` — posture, usage, affected boards, bulk
     disable. The largest family in the slice.

     ⚠ TWO ENGLISH VERB STEMS BECAME A CHOICE OF KEY, on the
     `POLICY_FIELD_LABELS` ruling. `policyCheckboxLabel()` concatenated `Allow`
     or `Block` before the name, and the save button lower-cased a posture LABEL
     mid-sentence — `Save ${POSTURE_LABELS[p].toLowerCase()} policy`.
     Lower-casing a translated label is a second bug independent of the slot:
     German capitalises nouns and Turkish lowercases `I` to a dotless letter.
     Both render byte-identically in English, which is why every existing
     assertion passes either way and why the ruling is written down here.

     ⚠ `{posture}` KEEPS its slot in `.policyListLabel`, filled from
     `.posture.*` — the OPPOSITE ruling on the neighbouring word, and
     deliberately: the slot is filled from the table that already owns the noun
     rather than from a second English author, which is what stops the list's
     name drifting from the radio's label in every language but English.

     ⚠ `.policyLoading` KEEPS THREE ASCII DOTS, not `…`. The
     `workspaceSwitcher.activity` precedent: byte-preservation is what keeps a
     sibling file's assertions green unedited, and the catalogue carries both.

     ⚠ `.affectedWorkspace` shows a RAW ULID to a human. Fixing it needs a
     workspace name on `PowerUpAffectedBoardsResponse` — a wire change.
     Reported, never patched. */
  'powerUps.enterprise.heading': {
    kind: 'text',
    value: 'Enterprise Power-Up administration',
  },
  'powerUps.enterprise.description': {
    kind: 'text',
    value:
      'Choose which Power-Ups members may enable. Existing installs stay in place until you disable them explicitly.',
  },
  'powerUps.enterprise.policyLoadFailedTitle': {
    kind: 'text',
    value: 'Could not load Enterprise Power-Up policy',
  },
  'powerUps.enterprise.directoryLoadFailedTitle': {
    kind: 'text',
    value: 'Could not load Power-Up directory',
  },
  'powerUps.enterprise.postureHeading': { kind: 'text', value: 'Policy posture' },
  'powerUps.enterprise.postureDescription': {
    kind: 'text',
    value: 'Enforcement applies when someone enables a Power-Up.',
  },
  'powerUps.enterprise.policyLoading': {
    kind: 'text',
    value: 'Loading Power-Up policy...',
  },
  'powerUps.enterprise.postureGroupLabel': {
    kind: 'text',
    value: 'Power-Up policy posture',
  },
  'powerUps.enterprise.posture.allowList': { kind: 'text', value: 'Allow-list' },
  'powerUps.enterprise.posture.blockList': { kind: 'text', value: 'Block-list' },
  'powerUps.enterprise.postureBody.allowList': {
    kind: 'text',
    value: 'Only named Power-Ups can be enabled.',
  },
  'powerUps.enterprise.postureBody.blockList': {
    kind: 'text',
    value: 'Every Power-Up except named ones can be enabled.',
  },
  'powerUps.enterprise.policyEmpty': {
    kind: 'text',
    value: 'No Power-Ups are available for this policy.',
  },
  'powerUps.enterprise.policyListLabel': { kind: 'text', value: '{posture} Power-Ups' },
  'powerUps.enterprise.policy.allowEntry': {
    kind: 'text',
    value: 'Allow {powerUp} Power-Up',
  },
  'powerUps.enterprise.policy.blockEntry': {
    kind: 'text',
    value: 'Block {powerUp} Power-Up',
  },
  /* ⚠ THE ONE VALUE IN THIS SLICE WHOSE RENDERED BYTES MOVE. The source drew
     `{entry.publisher ?? 'Unknown publisher'} · {entry.category}` — the WIRE
     value — so a policy row read `Molt · file_management` while the directory
     one panel away drew `File management` from the same field. FIXED, not
     merely routed: `{category}` is now filled from `powerUps.category.*`.
     Verified repo-wide before taking it: the three `file_management` hits in
     `apps/web/src` are all FIXTURE INPUT, never an assertion on rendered
     output. SLOT ORDER IS REVERSED against `powerUps.entryMeta`
     (`{category} · {publisher}`) because the source reverses it — different
     bytes, so sharing one key was never available. */
  'powerUps.enterprise.policyEntryMeta': {
    kind: 'text',
    value: '{publisher} · {category}',
  },
  'powerUps.enterprise.savePolicy.allowList': {
    kind: 'text',
    value: 'Save allow-list policy',
  },
  'powerUps.enterprise.savePolicy.blockList': {
    kind: 'text',
    value: 'Save block-list policy',
  },
  'powerUps.enterprise.usageHeading': { kind: 'text', value: 'Power-Ups in use' },
  'powerUps.enterprise.usageDescription': {
    kind: 'text',
    value: 'Counts come from boards currently carrying enabled Power-Ups.',
  },
  'powerUps.enterprise.usageLoadFailedTitle': {
    kind: 'text',
    value: 'Could not load Power-Up usage',
  },
  'powerUps.enterprise.usageEmptyTitle': { kind: 'text', value: 'No Power-Ups in use' },
  'powerUps.enterprise.usageEmptyBody': {
    kind: 'text',
    value: 'No Enterprise board currently has an enabled Power-Up.',
  },
  'powerUps.enterprise.usageListLabel': {
    kind: 'text',
    value: 'Enterprise Power-Up usage',
  },
  'powerUps.enterprise.usageRowLabel': {
    kind: 'text',
    value: '{powerUp} enterprise Power-Up usage',
  },
  /* ⚠ PLURAL. `{count}` in BOTH forms, so the one-form still renders `1 board`
     byte-for-byte against the `n === 1 ? 'board' : 'boards'` ternary it
     replaces — `boards.filterMatches` puts a literal `1` in its one-form and
     this family deliberately does not. The call site passes `count` as the
     THIRD argument as well as in the values object. */
  'powerUps.enterprise.boardCount': {
    kind: 'plural',
    forms: {
      one: '{count} board',
      other: '{count} boards',
    },
  },
  'powerUps.enterprise.viewAffected': {
    kind: 'text',
    value: 'View affected boards for {powerUp}',
  },
  'powerUps.enterprise.bulkDisable': {
    kind: 'text',
    value: 'Disable {powerUp} across affected boards',
  },
  'powerUps.enterprise.affectedHeading': { kind: 'text', value: 'Affected boards' },
  'powerUps.enterprise.affectedPrompt': {
    kind: 'text',
    value: 'Choose an in-use Power-Up to see the boards that would be affected.',
  },
  'powerUps.enterprise.affectedFor': {
    kind: 'text',
    value: 'Boards currently using {powerUp}.',
  },
  'powerUps.enterprise.affectedLoadFailedTitle': {
    kind: 'text',
    value: 'Could not load affected boards',
  },
  'powerUps.enterprise.affectedLoadFailedForTitle': {
    kind: 'text',
    value: 'Could not load affected boards for {powerUp}',
  },
  'powerUps.enterprise.affectedEmptyTitle': { kind: 'text', value: 'No affected boards' },
  'powerUps.enterprise.affectedEmptyBody': {
    kind: 'text',
    value: 'This Power-Up is not enabled on any active Enterprise board.',
  },
  'powerUps.enterprise.affectedWorkspace': {
    kind: 'text',
    value: 'Workspace {workspace}',
  },
  'powerUps.enterprise.thisPowerUp': { kind: 'text', value: 'this Power-Up' },
  'powerUps.enterprise.bulkTitle': {
    kind: 'text',
    value: 'Disable {powerUp} across affected boards?',
  },
  'powerUps.enterprise.bulkDescription': {
    kind: 'text',
    value: 'This removes the Power-Up from every affected Enterprise board.',
  },
  'powerUps.enterprise.bulkBusy': {
    kind: 'text',
    value: 'Disabling {powerUp} across affected boards',
  },
  'powerUps.enterprise.bulkConfirm': { kind: 'text', value: 'Disable {powerUp}' },
  'powerUps.enterprise.bulkBody': {
    kind: 'text',
    value:
      'Policy changes only affect future enablement. This action disables {powerUp} on every affected board now.',
  },
  'powerUps.enterprise.keepDataHint': {
    kind: 'text',
    value:
      'Leave stored board and card data so it can appear again if this Power-Up is re-enabled later.',
  },
  /* `routes/enterprises.$enterpriseId.power-ups.tsx` — the page frame and its
     three live-region sentences.

     ⚠ SEVEN OF THESE TEN CAN NEVER SPEAK ANYTHING BUT ENGLISH. `__root.tsx:243`
     gates the catalogue query on `!isSignedOut && !isPending && profile !== null`,
     so a signed-out visitor never fetches a catalogue and `.title`,
     `.description`, `.signInTitle`, `.signInBody`, `.signInAction`,
     `.accountErrorTitle` and `.genericError` fall back to
     `WEB_INTERFACE_MESSAGES` on every render they get. Not specific to this
     route — `workspaceMembers` returned the identical finding, and `__root.tsx`
     is still nobody's file. */
  'powerUps.directoryRoute.title': { kind: 'text', value: 'Power-Ups' },
  'powerUps.directoryRoute.description': {
    kind: 'text',
    value: 'Browse the Power-Ups that can add board, list and card capabilities.',
  },
  'powerUps.route.title': { kind: 'text', value: 'Enterprise Power-Ups' },
  'powerUps.route.description': {
    kind: 'text',
    value:
      'Manage which Power-Ups members may enable and remove disallowed installs across Enterprise boards.',
  },
  'powerUps.route.signInTitle': {
    kind: 'text',
    value: 'Sign in to administer Enterprise Power-Ups',
  },
  'powerUps.route.signInBody': {
    kind: 'text',
    value: 'Enterprise Power-Up policy is visible only to Enterprise admins.',
  },
  'powerUps.route.signInAction': { kind: 'text', value: 'Sign in' },
  'powerUps.route.accountErrorTitle': {
    kind: 'text',
    value: 'We could not load your account',
  },
  'powerUps.route.genericError': {
    kind: 'text',
    value: 'Something went wrong. Try again.',
  },
  'powerUps.route.allowListPolicySaved': {
    kind: 'text',
    value: 'Enterprise Power-Up allow-list policy saved.',
  },
  'powerUps.route.blockListPolicySaved': {
    kind: 'text',
    value: 'Enterprise Power-Up block-list policy saved.',
  },
  /* ⚠ PLURAL, and the second of exactly two in this family. Replaces
     `${n} Power-Up ${n === 1 ? 'install' : 'installs'} disabled.` — a live-region
     sentence, so an arity bug is announced to a screen reader and to nothing
     else. `announce(t(key, { count: n }, n))`: the third argument is load-bearing. */
  'powerUps.route.installsDisabled': {
    kind: 'plural',
    forms: {
      one: '{count} Power-Up install disabled.',
      other: '{count} Power-Up installs disabled.',
    },
  },
  'profile.locale.loading': {
    kind: 'text',
    value: 'Loading language and time settings',
  },
  'profile.locale.loadFailedTitle': {
    kind: 'text',
    value: 'Language and time settings could not be loaded',
  },
  'profile.locale.interfaceLanguage': {
    kind: 'text',
    value: 'Interface language',
  },
  'profile.locale.timezone': { kind: 'text', value: 'Timezone' },
  'profile.locale.datePreview': { kind: 'text', value: 'Date preview' },
  /* FE-80.1 `InlineEdit` — TWO keys, and they belong to the profile-page slice
     only because that slice's leak scan is what found them. `primitives.*`
     rather than `profile.*` on the established reach rule: `InlineEdit` renders
     on FOURTEEN surfaces (card title, list rename, board title, and eleven
     more), which is the widest reach any leaf in this catalogue has.

     ⚠ Unlike `Button`'s `busyLabel` — unowned for thirteen slices and still
     unowned after this one — neither of these has a prop a caller could
     override, so NO page slice could ever route them from outside. That is the
     whole difference, and it is why one shared primitive is taken here and the
     other is not: a caller can already fill `busyLabel` with a routed value and
     several do, while `, edit {label}` is unreachable from every call site.

     ⚠ `editSuffix` is a SUFFIX, not a whole accessible name. It follows the
     member's own value, which stays a visible text node — so a locale can
     change the words and the comma but cannot move the verb ahead of the value.
     Recorded as a finding; fixing it means restructuring the primitive.

     `saveFailed` is the non-`Error` rejection fallback, reachable only when a
     caller's `onCommit` promise rejects with something that is not an `Error`.

     ⚠ It is BYTE-IDENTICAL to `cardBack.checklists.item.saveFailed`, and the
     sixth such collision this slice adds. Measured by grouping the catalogue by
     value; a first pass asserted it was identical to nothing, which was a guess
     that read as a fact. Not shared, for the settled reason: the checklist
     item's failure and an inline editor's are different surfaces that a locale
     may word differently, and a shared key would make diverging a copy change
     to a shipped surface. */
  'primitives.button.busy': { kind: 'text', value: 'Working…' },
  'primitives.combobox.searching': { kind: 'text', value: 'Searching…' },
  'primitives.disclosure.summaryWithCount': {
    kind: 'plural',
    forms: { one: '{summary} ({count})', other: '{summary} ({count})' },
  },
  /* FE-80.1 automation-button strip — `components/automation/AutomationButtonStrip.tsx`.

     `primitives.*` on the reach rule: the same strip renders on the board face
     and the card back, while its caller supplies the already-routed section
     label and the server supplies literal command names. `failedActions` is a
     real plural because the old fallback used one English noun for every count.
     `outcome` deliberately keeps the wire outcome as a slot; changing that
     vocabulary is not a string-routing slice. */
  'primitives.automationButtonStrip.loading': {
    kind: 'text',
    value: 'Checking automation buttons...',
  },
  'primitives.automationButtonStrip.overflow': {
    kind: 'text',
    value: 'More {label}',
  },
  'primitives.automationButtonStrip.brokenReason': {
    kind: 'text',
    value: 'This automation has a broken reference.',
  },
  'primitives.automationButtonStrip.disabledReason': {
    kind: 'text',
    value: 'This automation is disabled.',
  },
  'primitives.automationButtonStrip.failedActions': {
    kind: 'plural',
    forms: {
      one: '{count} action failed.',
      other: '{count} actions failed.',
    },
  },
  'primitives.automationButtonStrip.outcome': {
    kind: 'text',
    value: 'Automation {outcome}.',
  },
  // FE-80.1 automation hook announcements. Outcome is a wire token and stays
  // literal slot data; only the sentence frame and frontend fallbacks route.
  'automationHooks.runOutcome': {
    kind: 'text',
    value: 'Automation {outcome}.',
  },
  'automationHooks.runOutcomeFallback': { kind: 'text', value: 'recorded' },
  'automationHooks.runCardButtonFailed': {
    kind: 'text',
    value: 'Could not run the card button.',
  },
  'automationHooks.runBoardButtonFailed': {
    kind: 'text',
    value: 'Could not run the board button.',
  },
  'automationHooks.createCommandFailed': {
    kind: 'text',
    value: 'Could not create the automation.',
  },
  'automationHooks.updateDefinitionFailed': {
    kind: 'text',
    value: 'Could not update the automation.',
  },
  'automationHooks.updateStateFailed': {
    kind: 'text',
    value: 'Could not change the automation state.',
  },
  'automationHooks.deleteCommandFailed': {
    kind: 'text',
    value: 'Could not delete the automation.',
  },
  'automationHooks.copyCommandFailed': {
    kind: 'text',
    value: 'Could not copy the automation.',
  },
  'automationHooks.adminDisableFailed': {
    kind: 'text',
    value: 'Could not disable the automation as administrator.',
  },
  // FE-80.1 — board-star hook announcements. The board title is not included:
  // the previous fallback did not name it, and the hook already keeps titles as
  // board data in its optimistic cache writes.
  'primitives.boardStars.starFailed': {
    kind: 'text',
    value: 'Could not star the board.',
  },
  'primitives.boardStars.unstarFailed': {
    kind: 'text',
    value: 'Could not unstar the board.',
  },
  'primitives.boardStars.reorderFailed': {
    kind: 'text',
    value: 'Could not reorder starred boards.',
  },
  'primitives.inlineEdit.empty': { kind: 'text', value: 'Empty' },
  'primitives.inlineEdit.editSuffix': { kind: 'text', value: ', edit {label}' },
  'primitives.inlineEdit.saveFailed': {
    kind: 'text',
    value: 'Could not save. Try again.',
  },
  /* FE-80.1 board frame — `lib/custom-field-type.ts`.

     `primitives.*` on the reach rule, and the THIRD author is what earned it:
     `card-back/CardCustomFieldsSection.tsx` had `cardBack.customFields.type.*`,
     `board/BoardCustomFieldsSection.tsx` had a byte-identical unrouted
     `TYPE_LABELS` record, and the card-custom-fields return wrote the rule that
     a third would be the argument for a leaf. The five `cardBack.*` keys were
     REMOVED, not aliased — `packages/shared`'s served catalogue carries only
     activity and notification templates, so a web-fallback key has no
     translation anywhere that a rename could orphan. Checked against that file,
     not assumed from the prefix. */
  'primitives.customFieldType.text': { kind: 'text', value: 'Text' },
  'primitives.customFieldType.number': { kind: 'text', value: 'Number' },
  'primitives.customFieldType.checkbox': { kind: 'text', value: 'Checkbox' },
  'primitives.customFieldType.date': { kind: 'text', value: 'Date' },
  'primitives.customFieldType.dropdown': { kind: 'text', value: 'Dropdown' },

  /* FE-80.1 board frame — the CARD FRONT (`board/CardTile.tsx` and
     `board/CardBadges.tsx`).

     ⚠ The two files render the same five facts twice, in two different voices,
     and every pair below is TWO keys by decision. `CardBadges` writes a badge's
     `aria-label` — a NOUN PHRASE with no full stop, read on its own when a
     screen reader lands on the badge. `CardTile`'s `cardAccessibleDescription`
     writes SENTENCES joined by a space into one `aria-describedby` paragraph.
     Byte-identical they are not (the full stop differs on every pair), and even
     where a locale would make them identical, sharing would let a translator's
     choice of badge wording silently rewrite a paragraph read as prose.

     ⚠ `board.card.description.labels` slots a list this catalogue does NOT own
     — `labelChipName` reads the 31 English colour names in `components/
     palette.ts`, held open as one slice needing a human to ratify that table.
     Same for the two colour arms of `BoardMapView`'s `pinStyle`. */
  'board.card.badge.description': {
    kind: 'text',
    value: 'This card has a description',
  },
  'board.card.badge.descriptionText': { kind: 'text', value: 'Description' },
  'board.card.badge.comments': {
    kind: 'plural',
    forms: { one: '{count} comment', other: '{count} comments' },
  },
  'board.card.badge.attachments': {
    kind: 'plural',
    forms: { one: '{count} attachment', other: '{count} attachments' },
  },
  'board.card.badge.votes': {
    kind: 'plural',
    forms: { one: '{count} vote', other: '{count} votes' },
  },
  'board.card.badge.approvals': {
    kind: 'text',
    value: '{approved} of {required} approvals',
  },
  'board.card.badge.approvalsMet': {
    kind: 'text',
    value: 'Approved: {approved} of {required} approvals',
  },
  'board.card.badge.rejections': {
    kind: 'plural',
    forms: { one: '{count} rejection', other: '{count} rejections' },
  },
  'board.card.badge.checklist': {
    kind: 'text',
    value: '{checked}/{total} checklist items complete',
  },
  'board.card.badge.due': { kind: 'text', value: 'Due {date}, {status}' },
  /* The card-front watch eye — `FR-BOARD-030` AC4 ("an eye indicator is shown
     to that viewer only") and `FR-COLLAB-022` ("an eye icon on the card back
     AND on the card front"). Its own key rather than a slot on
     `boardFace.watching` (`Watching {board}`) or `boardFace.list.watching`
     (`Watching {list}`): those two name their target because a board header
     and a list header carry many, while this badge sits INSIDE the card it
     speaks about, so the target is already identified and a `{title}` slot
     would read the card's name twice to a screen reader that just heard it.
     Second person, matching AC4's per-viewer meaning — `watching === null`
     (no session) and `false` both render nothing, so this string is only ever
     said to the one person it is true of. */
  'board.card.badge.watching': {
    kind: 'text',
    value: 'You are watching this card',
  },
  'board.card.badge.customField': { kind: 'text', value: '{name}: {value}' },
  // ⚠ Keeps a TRAILING SPACE: it is an `sr-only` prefix rendered immediately
  // before the value's own text node, so the space is the only thing between
  // two words. A translator who trims it joins them.
  'board.card.badge.customFieldPrefix': { kind: 'text', value: '{name}: ' },
  'board.card.badge.powerUp': { kind: 'text', value: '{powerUpName}: {label}' },
  // ⚠ Lower case, and NOT shared with `cardBack.customFields.checkedValue`
  // (`Checked`). This one is always the tail of a `{name}: {value}` badge; that
  // one is a standalone readout in a field row. Measured against the catalogue
  // rather than inferred from the shared word.
  'board.card.badge.checkedValue': { kind: 'text', value: 'checked' },
  'board.card.description.selected': { kind: 'text', value: 'Selected.' },
  'board.card.description.pendingSync': { kind: 'text', value: 'Pending sync.' },
  'board.card.description.mirrorFrom': {
    kind: 'text',
    value: 'Mirror from {board}.',
  },
  'board.card.description.sourceDeleted': {
    kind: 'text',
    value: 'The source card is deleted.',
  },
  'board.card.description.sourceArchived': {
    kind: 'text',
    value: 'The source card is archived.',
  },
  /* FE-80.1 mirror display controls — visible/control chrome for
     `board/MirrorDisplayControls.tsx`.

     ⚠ These do not reuse `board.card.description.*`. Those keys are the card
     link's prose `aria-describedby` paragraph; these keys are visible chips,
     icon-button names and disclosure body copy. Sharing would let a translator
     rewrite a screen-reader sentence by changing visible chip grammar, or the
     reverse. Card titles and source-board titles remain literal data slots. */
  'board.card.mirrorControls.fromSource': {
    kind: 'text',
    value: 'From {board}',
  },
  'board.card.mirrorControls.expand': {
    kind: 'text',
    value: 'Expand mirror {title}',
  },
  'board.card.mirrorControls.collapse': {
    kind: 'text',
    value: 'Collapse mirror {title}',
  },
  'board.card.mirrorControls.remove': {
    kind: 'text',
    value: 'Remove mirror for {title}',
  },
  'board.card.mirrorControls.sourceDeleted': {
    kind: 'text',
    value: 'Source deleted. This mirror will be removed.',
  },
  'board.card.mirrorControls.sourceArchived': {
    kind: 'text',
    value: 'Source archived. This mirror stays in place.',
  },
  'board.card.mirrorControls.deletedBody': {
    kind: 'text',
    value: 'The source card is gone; this mirror is no longer a shared card surface.',
  },
  'board.card.mirrorControls.activeBody': {
    kind: 'text',
    value:
      'Opens the shared source card from {board}. Removing this mirror leaves the source card untouched.',
  },
  /* FE-80.1 card mirror hooks — live-region result text authored by
     `lib/card-mirrors.ts`, consumed from both board face and card back.

     ⚠ These do not reuse `boardDashboard.mirror.*`, which is visible dialog
     chrome, nor `board.card.mirrorControls.*`, which is tile affordance chrome.
     They are mutation outcomes and carry board/list/card titles only as data
     slots. */
  'primitives.cardMirrors.createSucceeded': {
    kind: 'text',
    value: 'Card {title} mirrored to {list} on {board}.',
  },
  'primitives.cardMirrors.createFailed': {
    kind: 'text',
    value: 'Could not mirror {title}.',
  },
  'primitives.cardMirrors.removeSucceeded': {
    kind: 'text',
    value: 'Mirror for {title} removed.',
  },
  'primitives.cardMirrors.removeFailed': {
    kind: 'text',
    value: 'Could not remove the mirror for {title}.',
  },
  'primitives.cardMirrors.convertSucceeded': {
    kind: 'text',
    value: 'Link card {title} converted to an ordinary card.',
  },
  'primitives.cardMirrors.convertFailed': {
    kind: 'text',
    value: 'Could not convert {title}.',
  },
  'board.card.description.linkTargetUnavailable': {
    kind: 'text',
    value: 'Link card target unavailable. This preview does not reveal the target card.',
  },
  'board.card.description.linkPreview': {
    kind: 'text',
    value: 'Link card preview: {title} from {source}.',
  },
  'board.card.description.position': {
    kind: 'text',
    value: 'Card {index} of {count} in {list}.',
  },
  'board.card.description.labels': { kind: 'text', value: 'Labels: {labels}.' },
  'board.card.description.hasDescription': {
    kind: 'text',
    value: 'This card has a description.',
  },
  'board.card.description.members': { kind: 'text', value: 'Members: {members}.' },
  'board.card.description.comments': {
    kind: 'plural',
    forms: { one: '{count} comment.', other: '{count} comments.' },
  },
  'board.card.description.votes': {
    kind: 'plural',
    forms: { one: '{count} vote.', other: '{count} votes.' },
  },
  'board.card.description.attachments': {
    kind: 'plural',
    forms: { one: '{count} attachment.', other: '{count} attachments.' },
  },
  'board.card.description.checklist': {
    kind: 'text',
    value: '{checked}/{total} checklist items complete.',
  },
  'board.card.description.dueStatus': { kind: 'text', value: '{status}.' },
  'board.card.description.dueWithDate': {
    kind: 'text',
    value: 'Due {date}, {status}.',
  },
  'board.card.description.customFields': {
    kind: 'text',
    value: 'Custom fields: {fields}.',
  },
  // ⚠ A SPACE between name and value, where the badge's `customField` key uses
  // a colon. That difference is in the shipped English and is preserved rather
  // than tidied: changing it changes what a screen reader says, which is a copy
  // decision and not a routing one.
  'board.card.description.customFieldItem': { kind: 'text', value: '{name} {value}' },
  'board.card.description.powerUps': {
    kind: 'text',
    value: 'Power-Up badges: {badges}.',
  },
  'board.card.description.powerUpItem': {
    kind: 'text',
    value: '{powerUpName}: {label}',
  },
  'board.card.templateBadge': { kind: 'text', value: 'Template' },
  'board.card.hiddenFromListBadge': { kind: 'text', value: 'Hidden from list' },
  // Byte-identical to `board.card.description.pendingSync` minus its full stop
  // — the visible chip and the sentence a screen reader hears, two keys.
  'board.card.pendingSyncBadge': { kind: 'text', value: 'Pending sync' },
  'board.card.markComplete': { kind: 'text', value: 'Mark {title} complete' },
  'board.card.markIncomplete': { kind: 'text', value: 'Mark {title} incomplete' },
  'board.card.quickEdit': { kind: 'text', value: 'Quick edit {title}' },
  'board.card.showInList': { kind: 'text', value: 'Show {title} in list' },
  'board.card.hideFromList': { kind: 'text', value: 'Hide {title} from list' },
  /* ⚠ The board-frame slice's deferral, now taken. This key replaces
     `CardTile`'s `UNTITLED_CARD_NAME` module constant, which four files
     imported and which — being a constant — could not call `t`. It is reached
     through `lib/card-title.ts`'s `cardDisplayTitle`, never spelled at a call
     site, so that the title a card is DRAWN with and the title it is ANNOUNCED
     with cannot diverge: several `BoardFace` sites build a title and then match
     a rendered card against it, and routing this for one view alone would have
     broken that with nothing red. Byte-identical to `myCards.untitledCard` on
     the standing refusal to share across route namespaces. */
  'board.card.untitled': { kind: 'text', value: 'Untitled card' },

  /* FE-80.1 board frame — `board/BoardMapView.tsx` and `lib/board-map.ts`. */
  'boardMap.viewLabel': { kind: 'text', value: 'Map view' },
  'boardMap.heading': { kind: 'text', value: 'Map' },
  // ⚠ THREE keys for one sentence, because it carries TWO independent counts
  // and this catalogue's plural arm has exactly one `{count}`. The sub-keys are
  // what a locale with a third plural category needs; a single sentence key
  // could not express it, and four combination keys would multiply again the
  // next time a count is added.
  'boardMap.summary': {
    kind: 'text',
    value: '{cards} plotted across {pins} on {board}.',
  },
  'boardMap.summaryCards': {
    kind: 'plural',
    forms: { one: '{count} card', other: '{count} cards' },
  },
  'boardMap.summaryPins': {
    kind: 'plural',
    forms: { one: '{count} pin', other: '{count} pins' },
  },
  'boardMap.addCard': { kind: 'text', value: 'Add card at map point' },
  'boardMap.createDisabled.closed': {
    kind: 'text',
    value: 'Closed boards cannot create cards from Map.',
  },
  'boardMap.createDisabled.readonly': {
    kind: 'text',
    value: 'Only board editors can create cards from Map.',
  },
  'boardMap.createDisabled.noLists': {
    kind: 'text',
    value: 'Create a list before adding cards from Map.',
  },
  'boardMap.excludedTitle': {
    kind: 'plural',
    forms: {
      one: '{count} card is not shown in Map.',
      other: '{count} cards are not shown in Map.',
    },
  },
  // ⚠ A real plural whose forms carry NO `{count}` — the arity changes the verb
  // and the demonstrative, not a number. Declared `plural` because it is one;
  // declaring it `text` would make the singular unreachable.
  'boardMap.excludedBody': {
    kind: 'plural',
    forms: {
      one: 'This card has no complete stored location.',
      other: 'These cards have no complete stored location.',
    },
  },
  'boardMap.createFormLabel': { kind: 'text', value: 'Add a card from Map view' },
  'boardMap.cardTitle': { kind: 'text', value: 'Card title' },
  'boardMap.listLabel': { kind: 'text', value: 'List' },
  'boardMap.latitude': { kind: 'text', value: 'Latitude' },
  'boardMap.longitude': { kind: 'text', value: 'Longitude' },
  'boardMap.create': { kind: 'text', value: 'Create' },
  'boardMap.cancel': { kind: 'text', value: 'Cancel' },
  'boardMap.titleRequired': { kind: 'text', value: 'Card title is required.' },
  'boardMap.coordinatesInvalid': {
    kind: 'text',
    value: 'Latitude and longitude must be valid numbers.',
  },
  'boardMap.canvas': { kind: 'text', value: 'Map canvas' },
  'boardMap.pinsGroup': { kind: 'text', value: 'Map pins' },
  'boardMap.emptyTitle': { kind: 'text', value: 'No map pins' },
  'boardMap.emptyBody': {
    kind: 'text',
    value: 'Add a complete location to a card or create one from Map.',
  },
  'boardMap.asideLabel': { kind: 'text', value: 'Cards at this location' },
  'boardMap.selectPin': { kind: 'text', value: 'Select a pin to inspect cards.' },
  'boardMap.cardsAtLocation': {
    kind: 'plural',
    forms: {
      one: '{count} card at this location',
      other: '{count} cards at this location',
    },
  },
  // ⚠ `{lat}, {lng}` is a NUMBER PAIR, and the comma-space between them is a
  // formatting convention a locale may well write differently. It is a key
  // rather than a template literal for that reason alone — the two numbers
  // themselves stay wire values.
  'boardMap.coordinates': { kind: 'text', value: '{lat}, {lng}' },
  'boardMap.quickEditForm': { kind: 'text', value: 'Quick edit {title}' },
  // Its own key rather than `boardMap.cardTitle`: the create form and the
  // rename form can be open at once, and a locale that wants to distinguish
  // them must be able to.
  'boardMap.renameCardTitle': { kind: 'text', value: 'Card title' },
  'boardMap.save': { kind: 'text', value: 'Save' },
  'boardMap.renameCancel': { kind: 'text', value: 'Cancel' },
  'boardMap.pinAppearanceLine': { kind: 'text', value: 'Pin appearance: {appearance}' },
  'boardMap.openCard': { kind: 'text', value: 'Open {title}' },
  'boardMap.quickEditCard': { kind: 'text', value: 'Quick edit {title}' },
  'boardMap.quickEditDisabled': {
    kind: 'text',
    value: 'Only board editors can quick-edit cards from Map.',
  },
  // ⚠ TWO whole sentences, not one with an ` and {n} more` fragment spliced in.
  // The fragment shape needs a LEADING SPACE inside a translated value, which
  // is the defect `primitives.inlineEdit.editSuffix` is recorded for, and it
  // fixes English word order for every locale that reads the pin.
  'boardMap.pin.name': {
    kind: 'text',
    value: '{location}, {count}: {names}, {appearance}',
  },
  'boardMap.pin.nameWithOverflow': {
    kind: 'text',
    value: '{location}, {count}: {names} and {overflow} more, {appearance}',
  },
  'boardMap.pin.cardCount': {
    kind: 'plural',
    forms: { one: '{count} card', other: '{count} cards' },
  },
  // FE-80.1 board-map pin-colours. These five keys are the pin button
  // appearance phrases. The two colour arms use `labelColourDisplayName(t,
  // colour)` for the fixed colour vocabulary and whole messages here for the
  // subject noun; the selected-card row below has its own row wording.
  'boardMap.pin.appearance.colourCover': { kind: 'text', value: '{colour} cover' },
  'boardMap.pin.appearance.colourLabel': { kind: 'text', value: '{colour} label' },
  'boardMap.pin.appearance.photoCover': { kind: 'text', value: 'photo cover' },
  'boardMap.pin.appearance.attachmentCover': {
    kind: 'text',
    value: 'attachment cover',
  },
  'boardMap.pin.appearance.default': { kind: 'text', value: 'default pin' },
  // `lib/board-map.ts`'s `mapPinAppearanceLabel`, which is a DIFFERENT six-arm
  // vocabulary rendered in the card row rather than on the pin. Its cover
  // colour arm shares the fixed `primitives.labelColour.*` vocabulary, while
  // its label-name arm keeps stored label names literal.
  'boardMap.pinAppearance.coverColour': { kind: 'text', value: 'cover {colour}' },
  'boardMap.pinAppearance.coverPhoto': { kind: 'text', value: 'cover photo' },
  'boardMap.pinAppearance.coverAttachment': {
    kind: 'text',
    value: 'cover attachment',
  },
  'boardMap.pinAppearance.label': { kind: 'text', value: 'label' },
  'boardMap.pinAppearance.labelNamed': { kind: 'text', value: 'label {name}' },
  'boardMap.pinAppearance.default': { kind: 'text', value: 'default' },
  /* FE-80.1 workspace settings and access — the nine files that draw
     workspace ADMINISTRATION: `workspace/WorkspaceSettingsPanel.tsx`,
     `WorkspaceGuestAccessSection.tsx`,
     `WorkspaceBoardVisibilityPolicySection.tsx`,
     `WorkspaceJoinRequestsSection.tsx`, `WorkspaceActivityPage.tsx`,
     `WorkspaceMemberRemoveDialog.tsx`, `WorkspaceDeleteDialog.tsx`,
     `WorkspaceCreateForm.tsx` and `routes/w.$workspaceId.activity.tsx`.
     1,540 source lines, 136 keys declared here and 9 REUSED from
     `workspaceMembers.*`. (Counted by iterating this object, not by grepping
     it — `workspace-settings.routing.test.ts` asserts the 136.)

     ⚠ THE FOURTH WORKSPACE-SHAPED NAMESPACE, and the vocabularies stay
     apart. `workspaceSwitcher.*`, `workspaceMembers.*` and `workspaceViews.*`
     each already declare a `Private` and a `Workspace`. `workspace-members`
     ruled that two visibility vocabularies stay apart because one names a
     WORKSPACE's own visibility and the other names a BOARD's;
     `workspace-views` admitted a third on the same argument; this is the
     fourth, admitted on the same argument again. A collapsed key would make
     one translation of `Private` govern both subjects in every language whose
     adjective agrees with its noun.

     ⚠ NINE KEYS ARE REUSED, NOT REDECLARED, and the asymmetry is
     deliberate. `workspaceMembers.settings.visibility.private` / `.public`
     (the workspace's own visibility toggle),
     `workspaceMembers.guests.rule.anyone` / `.adminsOnly` / `.nobody` (the
     guest-invite rule) and `workspaceMembers.boardPolicy.visibility.private` /
     `.workspace` / `.enterprise` / `.public` (the four board visibilities) are
     spoken by the members page too — the catalogue says so at those keys,
     and this slice obeys rather than re-decides. The DESCRIPTIONS beside them
     are declared here, because only this file draws them:
     `guests.ruleDescription.*` and `boardPolicy.rule*.*` are ours while
     `guests.rule.*` and `boardPolicy.visibility.*` are theirs. A
     mixed-namespace pair of records over the same wire keys is what gets
     collapsed later, so the two records sit adjacent in the source with a
     comment, and the pin asserts each record's target namespace ARM BY ARM.

     ⚠ `type.*` REPLACES A STRING MUNGE, so its twelve values appear in no
     source file. `workspaceTypeLabel()` did
     `type.replaceAll('_', ' ').replace(/\b\w/g, upper)` — a raw wire
     value rendered to a human, and untranslatable in principle because the
     string does not exist until render. Deleted; `WORKSPACE_TYPE_KEYS` is a
     wire→KEY table resolved by `t()` at render. The English is
     byte-identical to what the munge produced, which is why no sibling test
     moves.

     ⚠ TWO PLURALS, both replacing a count ternary the source already
     carries. `guests.boardCount` is the WHOLE line with a `{type}` slot, not a
     bare count beside a label, so a locale may move the count before the type.
     `joinRequests.pendingCount` has IDENTICAL English arms and that is
     deliberate: `WorkspaceJoinRequestsSection.tsx:60` reads
     `{n} {n === 1 ? 'pending' : 'pending'}` — a count ternary whose two
     arms are the same word. Almost certainly a source defect, but correcting
     it is a COPY change that would move
     `WorkspaceJoinRequestsSection.test.tsx:96`. Declared `plural` so a locale
     that does distinguish can; `text` would bake English's accident into every
     language.

     ⚠ `joinRequests.status.*` IS AN IDENTITY MAP AND STAYS LOWER CASE.
     `STATUS_LABELS` mapped `approved: 'approved'`, so resolved requests render
     lower-case beside properly-cased copy. Five assertions in
     `WorkspaceJoinRequestsSection.test.tsx` bind the exact lower-case bytes.
     Routed, not case-corrected — routing is this slice's job; the case is
     a copy change for whoever also owns that test file.

     ⚠ `deleteDialog.body` IS THE SENTINEL-SPLIT CONSTRUCT, copied from
     `BoardPowerUpList.tsx` and not re-decided — the standing comment at
     `powerUps.board.*` (`interface-language.tsx:8428`) names
     `WorkspaceDeleteDialog.tsx:58` by line, alongside
     `BoardDangerConfirm.tsx:92` and `:104`. One whole
     sentence with a `{name}` slot, resolved with `String.fromCharCode(0)` and
     split at render, WITH THE FALLBACK ARM: a translation that omits `{name}`
     yields one part, and rendering `parts[0]` alone would silently delete the
     workspace name from a destructive confirmation.

     ⚠ ELLIPSES ARE NOT UNIFORM AND ARE PRESERVED AS FOUND.
     `boardPolicy.loading`, `guests.loading` and `activity.loadingMore` carry
     THREE ASCII DOTS; `joinRequests.loading`, `joinRequests.busy` and
     `create.submitBusy` carry `…` (U+2026). `activity.notFoundTitle`
     carries a HYPHEN, not an em dash, and the route test binds it. Normalising
     any of them is a copy change, not a routing fix.

     ⚠ TWO HEADINGS ARE DELIBERATELY REUSED AS THE ACCESSIBLE NAME OF THE
     CONTROL GROUP THEY INTRODUCE, and the collapse is the ruling rather than an
     oversight. `visibility.heading` is drawn twice by
     `WorkspaceSettingsPanel.tsx` — as the `<h2>` and as the
     `role="group"` `aria-label` over the two visibility buttons — and
     `guests.ruleHeading` is drawn twice by `WorkspaceGuestAccessSection.tsx`
     the same way. Two source literals, one key, on purpose: the heading and the
     group NAME THE SAME CONTROL, and a translator who rendered them
     differently would give a screen-reader user a group name that does not
     match the heading directly above it — a defect no static assertion can
     see. This is NOT the rule the same slice applied to `leave.heading` +
     `leave.action` and `delete.heading` + `delete.action`, which are also
     identical in English and are still TWO keys each. Those name different
     things — a section and a verb — and a language that wants an imperative
     for the button and a noun phrase for the heading must be able to have
     both. The discriminator is the referent, not the bytes.
     A THIRD collapse takes a third shape: `boardPolicy.heading` is resolved
     ONCE into a local and read at both its `<h2>` and its `<ul aria-label>`,
     so the `t('…')` anchor finds one call site rather than two.
     `workspace-settings.routing.test.ts` asserts all three collapses and both
     splits affirmatively, each by the mechanism it actually uses, so none can
     drift into another.

     ⚠ EVERY WIRE VALUE STAYS LITERAL. `<option value={type}>`,
     `onVisibilityChange(visibility)`, `onChangeGuestInviteRule(rule)`,
     `MenuItem.id`, `policyKey()` and every record KEY send `engineering`,
     `private`, `workspace_admins_only`, `any_member` and friends to the
     server, which `workspaceVisibilitySchema.parse` would answer with a 422
     — a 4xx that reads like an auth bug. In `PolicyRuleControl` the wire
     `id` and the copy `label` sit two lines apart in one object literal.

     ⚠ `removeMember.fallbackName` IS DECLARED BUT UNREACHABLE IN ENGLISH.
     The title is computed on every render, but `<Dialog open={member !== null}>`
     means the only branch producing `Remove this member?` is the branch that
     renders nothing at all. Declared because the computation exists and a
     translator must reach it; no capture can photograph it. */
  'workspaceSettings.profile.heading': { kind: 'text', value: 'Workspace profile' },
  'workspaceSettings.profile.nameLabel': { kind: 'text', value: 'Workspace name' },
  'workspaceSettings.profile.shortNameLabel': { kind: 'text', value: 'Short name' },
  'workspaceSettings.profile.shortNameHint': {
    kind: 'text',
    value:
      'Used in workspace URLs. Changing it does not make old URLs point at another workspace.',
  },
  'workspaceSettings.profile.descriptionLabel': { kind: 'text', value: 'Description' },
  'workspaceSettings.profile.websiteLabel': { kind: 'text', value: 'Website URL' },
  'workspaceSettings.profile.logoLabel': { kind: 'text', value: 'Logo URL' },
  'workspaceSettings.profile.logoHint': {
    kind: 'text',
    value: 'Uploading a logo file is not supported yet.',
  },
  'workspaceSettings.profile.typeLabel': { kind: 'text', value: 'Workspace type' },
  'workspaceSettings.profile.save': { kind: 'text', value: 'Save profile' },
  'workspaceSettings.profile.saveDisabled': {
    kind: 'text',
    value: 'Change a profile field before saving.',
  },
  'workspaceSettings.profile.locked': {
    kind: 'text',
    value: 'Only workspace admins can edit the workspace profile.',
  },
  'workspaceSettings.type.none': { kind: 'text', value: 'None' },
  'workspaceSettings.type.engineering': { kind: 'text', value: 'Engineering' },
  'workspaceSettings.type.marketing': { kind: 'text', value: 'Marketing' },
  'workspaceSettings.type.education': { kind: 'text', value: 'Education' },
  'workspaceSettings.type.sales': { kind: 'text', value: 'Sales' },
  'workspaceSettings.type.support': { kind: 'text', value: 'Support' },
  'workspaceSettings.type.humanResources': { kind: 'text', value: 'Human Resources' },
  'workspaceSettings.type.operations': { kind: 'text', value: 'Operations' },
  'workspaceSettings.type.finance': { kind: 'text', value: 'Finance' },
  'workspaceSettings.type.productManagement': {
    kind: 'text',
    value: 'Product Management',
  },
  'workspaceSettings.type.design': { kind: 'text', value: 'Design' },
  'workspaceSettings.type.legal': { kind: 'text', value: 'Legal' },
  'workspaceSettings.type.other': { kind: 'text', value: 'Other' },
  'workspaceSettings.visibility.heading': { kind: 'text', value: 'Workspace visibility' },
  'workspaceSettings.visibility.description': {
    kind: 'text',
    value:
      'This controls whether the workspace profile can be discovered. It does not change the visibility of any board inside the workspace.',
  },
  'workspaceSettings.visibility.locked': {
    kind: 'text',
    value: 'Only workspace admins can change workspace visibility.',
  },
  'workspaceSettings.leave.heading': { kind: 'text', value: 'Leave workspace' },
  'workspaceSettings.leave.description': {
    kind: 'text',
    value:
      'Leaving removes your workspace membership. Boards you were explicitly added to remain reachable as guest boards.',
  },
  'workspaceSettings.leave.action': { kind: 'text', value: 'Leave workspace' },
  'workspaceSettings.leave.pending': {
    kind: 'text',
    value: 'Checking your workspace membership.',
  },
  'workspaceSettings.delete.heading': { kind: 'text', value: 'Delete workspace' },
  'workspaceSettings.delete.description': {
    kind: 'text',
    value: 'Deleting this workspace deletes its boards too.',
  },
  'workspaceSettings.delete.action': { kind: 'text', value: 'Delete workspace' },
  'workspaceSettings.boardPolicy.heading': {
    kind: 'text',
    value: 'Board visibility policy',
  },
  /* ⚠ THE ENTERPRISE ARM OF THE PROVENANCE SENTENCE — `FE-67.2`, `FR-PERM-033`'s
     edge case (*"the UI must attribute the restriction to the right level"*).

     ⚠ IT IS A THIRD SIBLING OF `sourceWorkspace` / `sourceDefault` AND NOT A
     REPLACEMENT FOR EITHER. Those two answer a ROW-level question — did a
     `workspace_board_visibility_policies` row exist? — and the live catalogue
     is what makes that row-level rather than per-field: that table has ONE row
     per `(workspace_id, visibility)` carrying BOTH rules, and no
     `workspace_admin_can_control` column at all. Only the Enterprise can
     govern one field and not the other, so only THIS key is resolved per
     field. Deleting either sibling to "unify" the three would also falsify
     `packages/shared/src/workspace.ts:346-354`, which states as contract that
     `workspaceBoardVisibilityPolicySourceSchema` may never gain a third arm
     BECAUSE the section reads `source` through a binary ternary.

     ⚠ THE `{rule}` SLOT IS THE EFFECTIVE RULE, NOT THE ONE ON THE BUTTON, and
     that is the whole content of the sentence. The button shows the WORKSPACE
     rule (what the PATCH writes); this names what is actually enforced over
     it. The two are provably different exactly here and nowhere else:
     `fold()` resolves ties TO THE WORKSPACE (`>`, never `>=`), so
     `createRuleSource === 'enterprise'` holds if and only if the Enterprise
     rule is STRICTLY more restrictive. That is why no sentence is rendered on
     the other three arms — there, the effective rule IS the button. */
  'workspaceSettings.boardPolicy.sourceEnterprise': {
    kind: 'text',
    value: 'Source: Enterprise policy, which enforces {rule}',
  },
  /* ⚠ THE LOCK REASON THAT ATTRIBUTES ITSELF TO THE RIGHT LEVEL, and it exists
     on the written instruction of the assertion it changes.
     `WorkspaceBoardVisibilityPolicySection.test.tsx` asserted that the
     Enterprise lock stated NO reason, and said in its own docstring: *"When the
     key lands, THIS assertion is the one that must change, and it should be
     changed to a positive Enterprise reason — never deleted."*

     ⚠ IT MUST NOT SAY "workspace admins". `boardPolicy.locked` below reads
     *"Only workspace admins can change board visibility policy."*, and speaking
     that to a workspace ADMIN who has just been refused is the wrong-level
     attribution `FR-PERM-033`'s edge case exists to forbid. Two separate keys
     for that reason; they must not be collapsed, and the pin asserts their
     texts differ. */
  'workspaceSettings.boardPolicy.lockedEnterprise': {
    kind: 'text',
    value: 'Enterprise policy controls this setting for every workspace.',
  },
  'workspaceSettings.boardPolicy.description': {
    kind: 'text',
    value:
      'Choose who can create or delete boards at each visibility. These rules do not change existing boards.',
  },
  'workspaceSettings.boardPolicy.loading': {
    kind: 'text',
    value: 'Checking board visibility policy...',
  },
  'workspaceSettings.boardPolicy.empty': {
    kind: 'text',
    value: 'No board visibility policy is available.',
  },
  'workspaceSettings.boardPolicy.sourceWorkspace': {
    kind: 'text',
    value: 'Source: Workspace policy',
  },
  'workspaceSettings.boardPolicy.sourceDefault': {
    kind: 'text',
    value: 'Source: Default policy',
  },
  'workspaceSettings.boardPolicy.createLabel': {
    kind: 'text',
    value: 'Create {visibility} boards',
  },
  'workspaceSettings.boardPolicy.deleteLabel': {
    kind: 'text',
    value: 'Delete {visibility} boards',
  },
  'workspaceSettings.boardPolicy.ruleControl': {
    kind: 'text',
    value: '{rule} - {field}',
  },
  'workspaceSettings.boardPolicy.rule.anyMember': {
    kind: 'text',
    value: 'Any workspace member',
  },
  'workspaceSettings.boardPolicy.rule.adminsOnly': {
    kind: 'text',
    value: 'Workspace admins only',
  },
  'workspaceSettings.boardPolicy.rule.nobody': { kind: 'text', value: 'Nobody' },
  'workspaceSettings.boardPolicy.ruleDescription.anyMember': {
    kind: 'text',
    value: 'Any workspace member can use this visibility.',
  },
  'workspaceSettings.boardPolicy.ruleDescription.adminsOnly': {
    kind: 'text',
    value: 'Only workspace admins can use this visibility.',
  },
  'workspaceSettings.boardPolicy.ruleDescription.nobody': {
    kind: 'text',
    value: 'No one can use this visibility.',
  },
  'workspaceSettings.boardPolicy.locked': {
    kind: 'text',
    value: 'Only workspace admins can change board visibility policy.',
  },
  'workspaceSettings.boardPolicy.busy': {
    kind: 'text',
    value: 'Updating board visibility policy.',
  },
  'workspaceSettings.guests.heading': { kind: 'text', value: 'Guest access' },
  'workspaceSettings.guests.description': {
    kind: 'text',
    value:
      'Control who can invite guests and remove guest memberships across this workspace.',
  },
  'workspaceSettings.guests.ruleHeading': { kind: 'text', value: 'Guest invite rule' },
  'workspaceSettings.guests.ruleControl': {
    kind: 'text',
    value: '{rule} - guest invite rule',
  },
  'workspaceSettings.guests.ruleDescription.anyone': {
    kind: 'text',
    value: 'Any workspace member can invite guests to boards.',
  },
  'workspaceSettings.guests.ruleDescription.adminsOnly': {
    kind: 'text',
    value: 'Only workspace admins can invite guests to boards.',
  },
  'workspaceSettings.guests.ruleDescription.nobody': {
    kind: 'text',
    value: 'Guest invitations are turned off for this workspace.',
  },
  'workspaceSettings.guests.listHeading': { kind: 'text', value: 'Guests' },
  'workspaceSettings.guests.loading': {
    kind: 'text',
    value: 'Checking workspace guests...',
  },
  'workspaceSettings.guests.empty': { kind: 'text', value: 'No workspace guests.' },
  'workspaceSettings.guests.listLabel': { kind: 'text', value: 'Workspace guests' },
  'workspaceSettings.guests.type.multiBoard': {
    kind: 'text',
    value: 'Multi-board guest',
  },
  'workspaceSettings.guests.type.singleBoard': {
    kind: 'text',
    value: 'Single-board guest',
  },
  'workspaceSettings.guests.boardCount': {
    kind: 'plural',
    forms: { one: '{type} - {count} board', other: '{type} - {count} boards' },
  },
  'workspaceSettings.guests.remove': { kind: 'text', value: 'Remove' },
  'workspaceSettings.guests.removeControl': { kind: 'text', value: 'Remove {name}' },
  'workspaceSettings.guests.removeLocked': {
    kind: 'text',
    value: 'Only workspace admins can remove workspace guests.',
  },
  'workspaceSettings.guests.removeBusy': {
    kind: 'text',
    value: 'Removing another workspace guest.',
  },
  'workspaceSettings.guests.ruleLocked': {
    kind: 'text',
    value: 'Only workspace admins can change guest access.',
  },
  'workspaceSettings.guests.rulePending': {
    kind: 'text',
    value: 'Guest invite rule is still loading.',
  },
  'workspaceSettings.guests.ruleBusy': {
    kind: 'text',
    value: 'Updating guest invite rule.',
  },
  'workspaceSettings.joinRequests.heading': { kind: 'text', value: 'Join requests' },
  'workspaceSettings.joinRequests.loading': {
    kind: 'text',
    value: 'Checking join requests…',
  },
  'workspaceSettings.joinRequests.empty': { kind: 'text', value: 'No pending requests.' },
  /* ⚠ `kind: 'plural'`, NOT `kind: 'text'`, WITH TWO BYTE-IDENTICAL ENGLISH
     ARMS — the single most fragile declaration in this block, and the one
     place where the house rule against identical plural arms is deliberately
     spent rather than obeyed.

     The source it replaces read
     `{pending.length} {pending.length === 1 ? 'pending' : 'pending'}` — a
     count ternary whose two arms are the SAME word. A scan of every
     `kind: 'plural'` ENTRY in this file (count the entries by iterating this
     object, not the occurrences of the string `kind: 'plural'` — several of
     those are comment prose) found this is the only one whose forms are
     identical.

     `text` was the tempting reading and it is the wrong one. English's
     `pending` here is an invariant adjectival status, but that is a fact about
     ENGLISH: Spanish and French inflect exactly this adjective for number
     (`1 pendiente` / `2 pendientes`), so the phrase does agree with the count
     in the languages this catalogue exists to serve. Declaring it `text` would
     bake English's own accident into every one of them, with no key left for a
     translator to reach. That is the opposite shape from
     `1 of 20 boards selected`, which agrees with the CAP rather than the count
     and is correctly `text`.

     The cost is that the two arms render byte-identically at every arity, so
     NOTHING — not `getByText('2 pending')`, not a screenshot — can see the
     arity→form wiring. `interfaceMessage` picks `forms.one` from its THIRD
     POSITIONAL ARGUMENT alone and never reads the count out of `values`, so a
     call site passing only `{ count }` would silently take `forms.other` at
     every arity. `WorkspaceJoinRequestsSection.tsx` therefore passes BOTH —
     `t(key, { count: pending.length }, pending.length)` — and
     `workspace-settings.routing.test.ts` resolves this key through the shipped
     resolver at 0, 1 and 2, which is the only assertion in the repository that
     can tell the wiring from the bytes. */
  'workspaceSettings.joinRequests.pendingCount': {
    kind: 'plural',
    forms: { one: '{count} pending', other: '{count} pending' },
  },
  'workspaceSettings.joinRequests.pendingListLabel': {
    kind: 'text',
    value: 'Pending join requests',
  },
  'workspaceSettings.joinRequests.resolvedListLabel': {
    kind: 'text',
    value: 'Resolved join requests',
  },
  'workspaceSettings.joinRequests.status.pending': { kind: 'text', value: 'pending' },
  'workspaceSettings.joinRequests.status.approved': { kind: 'text', value: 'approved' },
  'workspaceSettings.joinRequests.status.declined': { kind: 'text', value: 'declined' },
  'workspaceSettings.joinRequests.approve': { kind: 'text', value: 'Approve {name}' },
  'workspaceSettings.joinRequests.decline': { kind: 'text', value: 'Decline {name}' },
  'workspaceSettings.joinRequests.approveLocked': {
    kind: 'text',
    value: 'Only workspace admins can approve join requests.',
  },
  'workspaceSettings.joinRequests.declineLocked': {
    kind: 'text',
    value: 'Only workspace admins can decline join requests.',
  },
  'workspaceSettings.joinRequests.busy': { kind: 'text', value: 'Updating request…' },
  'workspaceSettings.removeMember.fallbackName': { kind: 'text', value: 'this member' },
  'workspaceSettings.removeMember.title': { kind: 'text', value: 'Remove {name}?' },
  'workspaceSettings.removeMember.description': {
    kind: 'text',
    value: 'Choose whether explicit board memberships stay in place.',
  },
  'workspaceSettings.removeMember.confirm': { kind: 'text', value: 'Remove member' },
  'workspaceSettings.removeMember.cancel': { kind: 'text', value: 'Cancel' },
  'workspaceSettings.removeMember.role.admin': { kind: 'text', value: 'Workspace admin' },
  'workspaceSettings.removeMember.role.normal': {
    kind: 'text',
    value: 'Workspace member',
  },
  'workspaceSettings.removeMember.lastAdminWarning': {
    kind: 'text',
    value:
      'If this would leave the workspace or any affected board without an admin, the server will refuse the removal.',
  },
  'workspaceSettings.removeMember.scopeWorkspaceOnly': {
    kind: 'text',
    value:
      'Remove from workspace only. Explicit board memberships remain, so {name} may still reach those boards as a guest.',
  },
  'workspaceSettings.removeMember.scopeAllBoards': {
    kind: 'text',
    value: 'Remove from the workspace and all boards in it.',
  },
  'workspaceSettings.deleteDialog.title': { kind: 'text', value: 'Delete workspace?' },
  'workspaceSettings.deleteDialog.description': {
    kind: 'text',
    value: 'This cannot be undone.',
  },
  'workspaceSettings.deleteDialog.body': {
    kind: 'text',
    value: 'Deleting {name} deletes every board in this workspace too.',
  },
  'workspaceSettings.deleteDialog.nameLabel': { kind: 'text', value: 'Workspace name' },
  'workspaceSettings.deleteDialog.confirm': { kind: 'text', value: 'Delete workspace' },
  'workspaceSettings.deleteDialog.confirmDisabled': {
    kind: 'text',
    value: 'Type {name} exactly to delete this workspace.',
  },
  'workspaceSettings.deleteDialog.cancel': { kind: 'text', value: 'Cancel' },
  'workspaceSettings.create.nameLabel': { kind: 'text', value: 'Workspace name' },
  'workspaceSettings.create.nameHint': {
    kind: 'text',
    value: 'You can rename it later. Its web address is chosen for you.',
  },
  'workspaceSettings.create.submit': { kind: 'text', value: 'Create workspace' },
  'workspaceSettings.create.submitBusy': { kind: 'text', value: 'Creating workspace…' },
  'workspaceSettings.create.cancel': { kind: 'text', value: 'Cancel' },
  'workspaceSettings.activity.workspaceFallback': { kind: 'text', value: 'Workspace' },
  'workspaceSettings.activity.eyebrow': { kind: 'text', value: 'Workspace' },
  'workspaceSettings.activity.title': { kind: 'text', value: 'Workspace activity' },
  'workspaceSettings.activity.memberFilterHeading': {
    kind: 'text',
    value: 'Filter activity by member',
  },
  'workspaceSettings.activity.memberFilterDescription': {
    kind: 'text',
    value: 'Select a member to show only actions they performed.',
  },
  'workspaceSettings.activity.allMembers': { kind: 'text', value: 'All members' },
  'workspaceSettings.activity.memberFilterControl': {
    kind: 'text',
    value: 'Filter activity by {name}',
  },
  'workspaceSettings.activity.feedLabel': {
    kind: 'text',
    value: 'Workspace activity feed',
  },
  'workspaceSettings.activity.filterAll': { kind: 'text', value: 'All' },
  'workspaceSettings.activity.filterComments': { kind: 'text', value: 'Comments' },
  'workspaceSettings.activity.showingMember': {
    kind: 'text',
    value: 'Showing actions by {name}',
  },
  'workspaceSettings.activity.loadFailedTitle': {
    kind: 'text',
    value: 'Workspace activity could not be loaded',
  },
  'workspaceSettings.activity.loadFailedBody': {
    kind: 'text',
    value: 'Try again from this page.',
  },
  'workspaceSettings.activity.retry': { kind: 'text', value: 'Retry' },
  'workspaceSettings.activity.emptyCommentsTitle': {
    kind: 'text',
    value: 'No comments yet',
  },
  'workspaceSettings.activity.emptyActivityTitle': {
    kind: 'text',
    value: 'No activity yet',
  },
  'workspaceSettings.activity.emptyBody': {
    kind: 'text',
    value: 'Workspace board changes will appear here.',
  },
  'workspaceSettings.activity.emptyMemberBody': {
    kind: 'text',
    value: '{name} has no matching activity on boards you can read.',
  },
  'workspaceSettings.activity.loadingMore': {
    kind: 'text',
    value: 'Loading activity...',
  },
  'workspaceSettings.activity.loadMore': { kind: 'text', value: 'Load more activity' },
  'workspaceSettings.activity.signInTitle': {
    kind: 'text',
    value: 'Sign in to see workspace activity',
  },
  'workspaceSettings.activity.signInBody': {
    kind: 'text',
    value: 'Workspace activity is visible only to the people in the workspace.',
  },
  'workspaceSettings.activity.signInAction': { kind: 'text', value: 'Sign in' },
  'workspaceSettings.activity.accountErrorTitle': {
    kind: 'text',
    value: 'We could not load your account',
  },
  'workspaceSettings.activity.genericError': {
    kind: 'text',
    value: 'Something went wrong. Try again.',
  },
  'workspaceSettings.activity.notFoundTitle': {
    kind: 'text',
    value: 'No such workspace - or it is not yours to see.',
  },
  'workspaceSettings.activity.loadFailedMembersTitle': {
    kind: 'text',
    value: 'We could not load the member list',
  },

  /* FE-80.1 table view — `board/BoardTableView.tsx`, the whole file. 540 lines,
     ZERO `t(` call sites before this slice, 28 distinct English strings at 32
     sites, and the surface `FR-VIEW-012`–`FR-VIEW-015` are written against.

     ⚠ WHY A NEW TOP-LEVEL FAMILY AND NOT `boardFace.table.*`, which already
     exists twelve hundred lines above. The precedent is exact and structural,
     not a matter of taste: `boardFace.map.cardCreated` (what `BoardFace.tsx`
     ANNOUNCES about the map) coexists with the top-level `boardMap.*` (what
     `BoardMapView.tsx` DRAWS). `boardFace.table.cardMoved` and
     `boardFace.table.cardCreated` are the same thing for this view — sentences
     `BoardFace.tsx` speaks into the live region — and `boardTable.*` is this
     view file's own chrome. Sibling view, sibling file, sibling family.

     ⚠ AND THE ONE KEY THIS BLOCK DOES NOT DECLARE IS THE POINT OF THE SLICE.
     The `<section>`'s `aria-label` was the literal `'Table view'`, while
     `BoardFace.tsx:4919` and `:4941` — the ERROR and LOADING branches of the
     SAME conditional, rendering the SAME `id="board-view-content"` region —
     already name it as
     `t('boardFace.viewRegion', { view: boardViewLabel(t, 'table') })`, which
     composes to those exact bytes. One node, one accessible name, three
     branches, two authors. Served a catalogue, the loading state spoke the
     translation and the loaded table said `Table view`, with nothing red. So
     the loaded branch REUSES the composition; there is deliberately no
     `boardTable.viewLabel` here, and `Table view` is declared flat nowhere in
     this catalogue. `board-table.routing.test.ts` asserts both halves.

     ⚠ `boardMap.viewLabel` (`Map view`, above) IS THAT DEFECT, STILL LIVE, and
     is NOT precedent to copy. `BoardFace.tsx:5184`/`:5206` compose the map's
     region name from `boardFace.viewRegion` while `BoardMapView.tsx:138`
     declares its own. Reported by this slice as a residual; not taken, because
     fixing it deletes a key and edits another view's file. */
  /* The `<h2>`, and it is NOT a reuse of `boardFace.view.table` even though the
     bytes match — a THIRD `Table` in the catalogue, declared knowingly. The
     switcher's `Table` names a VIEW IN A MENU; this names THIS PAGE.
     `boardMap.heading` sits apart from `boardFace.view.map` on exactly that
     reading, and `RichDescriptionEditor.test.tsx:551–563` records the ruling
     for the editor's own third `Table`. Read together with the region label
     above, the two answers look opposite and are one rule: THE NAMESPACE
     DECIDES, NOT THE BYTES. The region's name belongs to the board face because
     the board face already names that node in two of three states; the page's
     heading belongs to the page. */
  'boardTable.heading': { kind: 'text', value: 'Table' },
  /* ⚠ ONE key for the whole sentence, not a frame plus a hard-coded
     `card`/`cards` fragment — the standing rule stated above the `boardMap`
     summary and in `lib/dnd.ts`. The English carries NO trailing full stop
     (the JSX rendered `{n} {n === 1 ? 'card' : 'cards'} on {title}`); do not
     add one. */
  'boardTable.summary': {
    kind: 'plural',
    forms: { one: '{count} card on {board}', other: '{count} cards on {board}' },
  },
  'boardTable.addCard': { kind: 'text', value: 'Add card' },
  /* ⚠ THREE KEYS THAT NO `t('…')` ANCHOR CAN SEE, and that is deliberate.
     `dragRefusalKey` is React state that outlives the render which set it, so
     it holds a KEY and the sentence is resolved at render — `t(refusalKey)`,
     with the key in a variable. `BoardMapView.tsx:80–84` states the rule and
     `localErrorKey` is the worked example: hold the English of the moment and
     the catalogue query resolving underneath leaves the previous language on
     screen, which no test in this repository can see. The pin holds these three
     by asserting the key CONSTANTS appear in the source plus a rendering
     assertion in `BoardTableView.test.tsx`, never by the anchor.

     ⚠ `lockedReason` is near-parallel to `boardMap.createDisabled.readonly`
     (`Only board editors can create cards from Map.`) and NOT byte-identical.
     Neither was bent to match the other: `FR-VIEW-013.spec.ts:66` binds this
     one exactly. */
  'boardTable.lockedReason': {
    kind: 'text',
    value: 'Only board editors can change cards from Table view.',
  },
  'boardTable.dragDisabled.sort': {
    kind: 'text',
    value: 'Row drag is unavailable while a sort is active.',
  },
  'boardTable.dragDisabled.filter': {
    kind: 'text',
    value: 'Row drag is unavailable while filters are active.',
  },
  /* ⚠ The same FRAME as `boardMap.createFormLabel` (`Add a card from Map view`)
     with a different view noun, and deliberately NOT unified into
     `'Add a card from {view} view'`. A noun routed apart from the sentence it
     sits in does not decline with the frame around it — the refusal
     `boardView.pin`/`boardView.unpin` records. Both are form `aria-label`s bound
     by a spec (`FR-VIEW-015.spec.ts:28,46,54`), so neither may drift. */
  'boardTable.createFormLabel': { kind: 'text', value: 'Add a card from Table view' },
  /* An EIGHTH `Card title` product-wide, and right to declare. Seven other
     surfaces carry their own; `FR-BOARD-020.spec.ts:783` records that two of
     them share the accessible name BY DESIGN and scopes its queries
     accordingly, and `FR-VIEW-015.spec.ts:30` scopes ours to the composer form.
     Sharing one key would let one surface's translation rename a field on
     seven others. */
  'boardTable.cardTitle': { kind: 'text', value: 'Card title' },
  /* ⚠ TWO KEYS FOR `List`, and the discriminator is co-rendering rather than
     bytes: this is the composer's `<select>` label and `boardTable.column.list`
     below is the sortable COLUMN HEADER. Both are on screen together whenever
     the composer is open. `FR-VIEW-015.spec.ts:31` scopes `getByLabel('List')`
     to the composer form for precisely that reason, while
     `BoardTableView.test.tsx` relies on the header being a BUTTON rather than a
     label — so both accessible names must stay byte-identical. */
  'boardTable.listLabel': { kind: 'text', value: 'List' },
  'boardTable.create': { kind: 'text', value: 'Create' },
  /* ⚠ TWO `Cancel`s in this one file, on the same co-rendering discriminator
     (`boardMap.cancel` / `boardMap.renameCancel` is the precedent). NOT because
     the composer survives its own submit — `submitCreate` closes it
     unconditionally once the title is non-blank — but because `createOpen` and
     `renameCardId` are INDEPENDENT state: open the composer, then a row's
     rename pencil, and both buttons are on screen at once. A twenty-first and a
     twenty-second `Cancel` in this catalogue; each family declares its own. */
  'boardTable.cancel': { kind: 'text', value: 'Cancel' },
  /* The column headings, under their own sub-namespace so the composer's `List`
     field and the `List` column can never be confused at a call site. Each is a
     bare English noun some other surface also says (`Move` ×4, `Card` ×1,
     `Labels` ×3, `Members` ×4, `Due date` ×5 before this block); the standing
     ruling applies to every one of them — each family declares its own.

     ⚠ NOT routed, and named so a later slice does not "finish the job":
     `SortableHeading` appends ` ↑` / ` ↓` to the button text when its column is
     active. Those are GLYPHS, and `aria-sort` already carries the semantics.
     Rebuilding the header name as a key with a `{glyph}` slot would break
     `FR-VIEW-014.spec.ts:33` and `FR-VIEW-015.spec.ts:44`, which query
     `{ name: 'Due date', exact: true }` and `{ name: 'Card', exact: true }`
     and match only PRE-sort. */
  'boardTable.column.move': { kind: 'text', value: 'Move' },
  'boardTable.column.card': { kind: 'text', value: 'Card' },
  'boardTable.column.list': { kind: 'text', value: 'List' },
  'boardTable.column.labels': { kind: 'text', value: 'Labels' },
  'boardTable.column.members': { kind: 'text', value: 'Members' },
  'boardTable.column.dueDate': { kind: 'text', value: 'Due date' },
  /* ⚠ THE CUSTOM-FIELD COLUMNS DECLARE NO HEADER KEY, and the absence is
     deliberate. `FR-VIEW-012` gives the board table "an optional column for each
     custom field defined on that board"; the header of such a column is the
     field's NAME, which is user data a member typed, not copy — routing it would
     mean declaring a catalogue key per board. Only the three CELL states below
     are copy, and all three exist because a cell that renders nothing is
     indistinguishable from a cell that failed to render.

     ⚠ `customFieldEmpty` is NOT `boardTable.noDueDate` reused. That key names a
     specific absent field; this one names any of them, on a column whose header
     already says which. Separate keys, as the standing per-family ruling
     requires.

     ⚠ THE CHECKBOX PAIR IS TWO KEYS RATHER THAN A REUSE OF `customFieldEmpty`,
     and the reason is a real distinction rather than a shade. `toValue` returns
     `null` for an unticked checkbox — the storage cannot tell "unticked" from
     "never touched" — but the COLUMN can, because the definition's type is
     known: an unticked checkbox has an answer, and it is No. Saying "No value"
     there would report a fact the field does not have. */
  'boardTable.column.customFieldEmpty': { kind: 'text', value: 'No value' },
  'boardTable.column.customFieldTicked': { kind: 'text', value: 'Ticked' },
  'boardTable.column.customFieldUnticked': { kind: 'text', value: 'Unticked' },
  /* ⚠ ONE key across TWO call sites — the rename `IconButton`'s name and the
     rename `TextField`'s label — and this does NOT contradict the
     `boardMap.cardTitle` / `boardMap.renameCardTitle` split above. That split
     exists because the map's create form and rename form CAN be open at once
     (its own comment says so). Here the ternary in the card cell swaps one
     control for the other, so they never co-render, and
     `FR-VIEW-013.spec.ts:49–51` clicks
     `getByRole('button', { name: 'Rename …' })` and then fills
     `getByLabel('Rename …')` with the same string. Two keys would let a
     translation break that spec silently.

     ⚠ `boardCustomFields.renameLabel` (`Rename {name}`) and
     `cardBack.attachments.item.rename` (`Rename {attachment}`) are the same
     sentence with a differently-named slot. Do not unify: the slot name is part
     of the contract at each call site. */
  'boardTable.renameCard': { kind: 'text', value: 'Rename {title}' },
  'boardTable.save': { kind: 'text', value: 'Save' },
  'boardTable.renameCancel': { kind: 'text', value: 'Cancel' },
  'boardTable.changeList': { kind: 'text', value: 'Change list for {title}' },
  /* ⚠ `labelPicker.trigger` is `Edit labels`, a proper PREFIX of this value.
     That matters only to `WebChromeProbe`-style substring sweeps, which cannot
     tell a prefix from a whole — the class documented at
     `interface-language.test.tsx:1533–1552`. It is a reason not to add either
     to the probe without a separator, not a reason to change either string. */
  'boardTable.editLabels': { kind: 'text', value: 'Edit labels for {title}' },
  'boardTable.editMembers': { kind: 'text', value: 'Edit members for {title}' },
  'boardTable.setDueDate': { kind: 'text', value: 'Set due date for {title}' },
  /* ⚠ ONE key each across TWO call sites, on the discriminator above: the
     read-only arm and the `canEdit` arm of the SAME cell, which are mutually
     exclusive render branches rather than two controls. `noDueDate`'s second
     site is `dueDateLabel()`'s return, which is why that module-level helper
     now takes `t` as its first parameter (`BoardMapView.tsx:543`'s idiom).

     ⚠ `dueDateLabel`'s other return is
     `new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' })` — `undefined`
     meaning the BROWSER's locale, which ignores the user's locale preferences
     entirely. REPORTED, NOT TAKEN: it is the `calendarDateLabel` residual's
     class, and `lib/locale-formatting.ts` already holds the fix. Wiring it needs
     locale-preferences plumbing into a module-level helper, which is locale
     work rather than catalogue routing. */
  'boardTable.noLabels': { kind: 'text', value: 'No labels' },
  'boardTable.noMembers': { kind: 'text', value: 'No members' },
  'boardTable.noDueDate': { kind: 'text', value: 'No due date' },

  /* FE-80.1 calendar view — `board/BoardCalendarView.tsx`, the whole file. 580
     lines, ZERO `t(` call sites before this slice, and the surface
     `FR-VIEW-018`–`FR-VIEW-022` are written against. `boardTimeline.*` follows
     immediately below: the two views were routed together because they are the
     same shape — a date grid, a read-only refusal held in state, and a bank of
     nudge controls whose disabled name is the enabled one plus a suffix — so
     every ruling below had to be made twice, and where the two answers differ
     the difference is stated rather than left to be inferred.

     ⚠ AND, AS IN THE TABLE SLICE, THE KEY THIS BLOCK DOES NOT DECLARE IS THE
     POINT OF IT. The `<section>`'s `aria-label` was a DEFAULT PARAMETER,
     `label = 'Calendar view'`, while `BoardFace.tsx:4992` and `:5016` — the
     ERROR and LOADING branches of the same conditional, rendering the same
     `id="board-view-content"` region — already name it
     `t('boardFace.viewRegion', { view: boardViewLabel(t, 'calendar') })`,
     which composes through `boardFace.view.calendar` = `Calendar` to exactly
     those bytes. One node, one accessible name, three branches, two authors:
     served a catalogue, two states spoke the translation and the loaded
     calendar said `Calendar view`, with nothing red. The loaded branch REUSES
     the composition; there is deliberately no `boardCalendar.viewLabel`, and
     `Calendar view` is declared flat nowhere in this catalogue.

     ⚠ THE DEFAULT PARAMETER IS THE PART UNIQUE TO THIS VIEW, and it is why the
     table slice's fix does not transfer as a copy. A default parameter cannot
     call `t` — the same wall as `ALL_WORKSPACES_LABEL` and `SCOPE_LABELS` — so
     `label` stays `string | undefined` and the fallback is resolved in the
     component body. `label` is a REAL prop with a REAL second caller:
     `BoardFace.tsx:5358` renders a SECOND `BoardCalendarView` inside the
     Calendar Power-Up dialog with `label={t('boardFace.powerUps.calendar')}`,
     which must keep naming that dialog's region. The pin asserts the
     composition is the FALLBACK, never that the file always composes.

     ⚠ `boardMap.viewLabel` (`Map view`, above) IS that defect still live and is
     NOT precedent to copy. Reported by this slice as a residual and not taken:
     fixing it deletes a key and edits another view's file. */
  /* ⚠ TWO KEYS FOR `Month` AND TWO FOR `Week`, BOTH PAIRS DECLARED BY THIS ONE
     SLICE, which a reader will otherwise read as an oversight. These name the
     calendar's mode TOGGLE BUTTONS; `boardTimeline.zoom.month` and
     `boardTimeline.zoom.week` below name `<option>`s in the timeline's zoom
     `<select>`. Different views, different files, different families,
     different affordances — the namespace decides, not the bytes, exactly as
     `boardTable.heading` sits apart from `boardFace.view.table`. Neither pair
     may reach into the other: a locale that shortened the timeline's zoom
     options would otherwise silently rename the calendar's toggle.

     ⚠ NOT ROUTED TOGETHER WITH `showMonth`/`showWeek` below, and the reason is
     that they are NOT the same string: the toggle's visible label is `Month`
     and its `aria-label` is `Show month calendar`. That pair fails WCAG 2.5.3
     (label in name) TODAY, in English, before any translation — the accessible
     name does not contain the visible label. REPORTED, NOT FIXED: correcting
     it changes rendered bytes that `BoardCalendarView.test.tsx:144` and the
     `FR-VIEW-022` specs bind, which is a copy change rather than routing. */
  'boardCalendar.mode.month': { kind: 'text', value: 'Month' },
  'boardCalendar.mode.week': { kind: 'text', value: 'Week' },
  'boardCalendar.showMonth': { kind: 'text', value: 'Show month calendar' },
  'boardCalendar.showWeek': { kind: 'text', value: 'Show week calendar' },
  /* ⚠ ONE key for the whole sentence, not a frame plus hard-coded `card is` /
     `cards are` and `it has` / `they have` fragments — the JSX chose BOTH from
     one English plural rule it owned, which no catalogue can translate. The
     standing rule above `boardTable.summary` and in `lib/dnd.ts`. */
  'boardCalendar.excluded': {
    kind: 'plural',
    forms: {
      one: '{count} card is not shown because it has no due date.',
      other: '{count} cards are not shown because they have no due date.',
    },
  },
  'boardCalendar.reviewExcluded': { kind: 'text', value: 'Review cards' },
  /* ⚠ TWO WHOLE SENTENCES, not one `'{mode} calendar for {month}'` with a
     `{mode}` slot, and this is where the `boardTable.createFormLabel` refusal
     DOES belong: `Month` in that frame is a noun that must decline with the
     sentence around it, and Spanish wants `Calendario mensual`, not
     `Mes calendario`. The two branches are mutually exclusive, so this is not
     the one-key-two-call-sites case either. `FR-VIEW-022.spec.ts:242` binds
     `getByRole('grid', { name: /^Month calendar for/ })` on the Power-Up
     calendar, so these bytes are already load-bearing.

     ⚠ `{month}` is a formatted DATE, not chrome — see the formatter residual
     recorded at the foot of this block. */
  'boardCalendar.gridMonth': { kind: 'text', value: 'Month calendar for {month}' },
  'boardCalendar.gridWeek': { kind: 'text', value: 'Week calendar for {month}' },
  'boardCalendar.createCard': { kind: 'text', value: 'Create card on {date}' },
  /* ⚠ ONE ACTION KEY PER CONTROL PLUS ONE FRAME, NOT TWO INDEPENDENT KEYS PER
     CONTROL. Both views build a disabled control's accessible name by
     appending a fixed suffix to the enabled one — fourteen controls, twenty-
     eight labels between the two files. The reason for the frame is not key
     economy: THE DISABLED NAME MUST CONTAIN THE ENABLED NAME BY CONSTRUCTION,
     not by a translator's discipline. Every spec queries the enabled name; the
     disabled name is reachable only in the permission-reduced state, so a
     locale that wrote two unrelated sentences would be green everywhere and
     wrong only where nobody looks. External evidence, not taste:
     `FR-VIEW-024.spec.ts:157` binds the composed bytes
     `Move {title} later one column unavailable: Only board editors can change
     timeline cards` exactly, and `FR-VIEW-018.spec.ts:432` binds the
     calendar's.

     ⚠ AND THE OBVIOUS TIDY IS REFUSED HERE AND IN THE PIN. Fourteen controls
     × two branches is verbose, and the tempting cleanup is a local
     `controlLabel(t, key, values, readonly)` helper. That single edit would
     make all twenty-eight labels INVISIBLE to the `t('…')` anchor —
     `dragRefusalKey`'s problem multiplied by fourteen, with the pin still
     green. The verbose inline nested form is anchor-visible at BOTH levels and
     is therefore the correct shape rather than the lazy one.

     ⚠ AND THIS IS NOT `boardTable.createFormLabel`'s refusal. That one is
     about a NOUN that must decline with the frame around it. Here `{action}`
     is a whole imperative clause and `{reason}` a whole sentence; neither
     declines, so the frame is safe.

     ⚠ `boardTimeline.controlUnavailable` below carries BYTE-IDENTICAL English
     and is a deliberate second declaration, not an oversight. Two views, two
     files, two families, and neither family may reach into the other's
     namespace — the refusal `lib/card-title.ts:33-40` records for
     `board.card.untitled` against `myCards.untitledCard`. This is NOT the
     `boardCustomFields.loading` / `.loadingNotice` case, which is two ROLES
     co-rendering inside ONE family. There is no third author, so the
     third-author extraction threshold `lib/custom-field-type.ts` was taken on
     is not met either. */
  'boardCalendar.readonlyReason': {
    kind: 'text',
    value: 'Only board editors can reschedule calendar cards',
  },
  'boardCalendar.controlUnavailable': {
    kind: 'text',
    value: '{action} unavailable: {reason}',
  },
  /* ⚠ THREE KEYS NO `t('…')` ANCHOR CAN SEE, and that is deliberate. `refusal`
     was React state holding a RESOLVED SENTENCE across renders, so a catalogue
     query resolving underneath left the previous language on screen — the
     defect `BoardMapView.tsx:80–84` states and `BoardTableView.tsx`'s
     `dragRefusalKey` is the worked example. The state now holds a KEY and the
     sentence is resolved at render, `t(refusalKey)`. Held by the pin through
     the key CONSTANTS appearing in the source plus a rendering assertion,
     never by the anchor. */
  'boardCalendar.refusal.readonly': {
    kind: 'text',
    value: 'This calendar is read only. Dates were not changed.',
  },
  'boardCalendar.refusal.dueBeforeStart': {
    kind: 'text',
    value: 'The due date cannot be moved before the start date.',
  },
  'boardCalendar.refusal.startAfterDue': {
    kind: 'text',
    value: 'The start date cannot be moved after the due date.',
  },
  /* ⚠ `Untitled item` is DECLARED (a checklist item has no shared fallback
     helper) while `Untitled card` beside it in the same function is REUSED
     through `cardDisplayTitle(t, …)` → `board.card.untitled`. Six authors of
     `Untitled card` already exist and `board.card.untitled` is the one meant
     for board card surfaces, reached only through the helper so a card's drawn
     title and its announced title cannot diverge. Becoming a seventh author
     here would break that at three sites. */
  'boardCalendar.untitledChecklistItem': { kind: 'text', value: 'Untitled item' },
  /* ⚠ The `/` join lives INSIDE the value, at all three joins this slice
     routed (here, `boardTimeline.summary`, `boardTimeline.cellLabel`). The
     `profile.connectedTools` comment states the rule: a join is not
     translatable — word order and the separator are both language-specific,
     and a locale that needs `{card} — elemento de lista` cannot express it if
     the separator is JSX. */
  'boardCalendar.checklistItemParent': { kind: 'text', value: 'Checklist item / {card}' },
  /* Lower-case on purpose: it is spliced into the middle of the two `Open …`
     names below, never rendered alone. */
  'boardCalendar.unknownDate': { kind: 'text', value: 'unknown date' },
  'boardCalendar.openChecklistItem': {
    kind: 'text',
    value: 'Open checklist item {item} on {card}, due {date}',
  },
  'boardCalendar.openCard': { kind: 'text', value: 'Open {title}, due {date}' },
  /* The six nudge controls. Each is asked for TWICE by design — once bare and
     once as the `{action}` of `controlUnavailable` — which is the shape the
     frame ruling above requires. */
  'boardCalendar.moveEarlier': { kind: 'text', value: 'Move {title} earlier one day' },
  'boardCalendar.moveLater': { kind: 'text', value: 'Move {title} later one day' },
  'boardCalendar.moveStartEarlier': {
    kind: 'text',
    value: 'Move start of {title} earlier one day',
  },
  'boardCalendar.moveStartLater': {
    kind: 'text',
    value: 'Move start of {title} later one day',
  },
  'boardCalendar.moveDueEarlier': {
    kind: 'text',
    value: 'Move due date of {title} earlier one day',
  },
  'boardCalendar.moveDueLater': {
    kind: 'text',
    value: 'Move due date of {title} later one day',
  },
  /* ⚠ NOT ROUTED, and named here so a later slice does not "finish the job":
     the calendar's `<h2>` is `monthLabel`, a formatted DATE, so there is
     deliberately no `boardCalendar.heading` beside `boardTable.heading` and
     `boardMap.heading`. Inventing one would be authoring copy. Its weekday
     column headers and every `gridcell` name are `Intl.DateTimeFormat` output
     for the same reason.

     ⚠ AND THE FORMATTERS THEMSELVES ARE A REPORTED RESIDUAL, in the class of
     `boardTable.noDueDate`'s `dueDateLabel` and `BoardFace.tsx:935`'s
     `calendarDateLabel`: `new Intl.DateTimeFormat(undefined, …)` means the
     BROWSER's locale, which ignores the user's locale preferences. What
     settles it here is measured rather than argued — `lib/locale-formatting.ts`
     holds NO formatter with this option set (`weekday: 'long'`,
     `month: 'long'`, `day: 'numeric'`, `year: 'numeric'`);
     `formatLocaleCardDate` is `month: 'short'` with a time, and
     `formatLocaleDateTimeLabel` is `dateStyle: 'medium'`. Adopting either is
     not a substitution, it CHANGES RENDERED BYTES that fourteen `FR-VIEW-*`
     specs bind. That is a behaviour change, not catalogue routing. */

  /* FE-80.1 timeline view — `board/BoardTimelineView.tsx`, the whole file. 873
     lines, ZERO `t(` call sites before this slice, the largest unrouted view
     file in the product, and the surface `FR-VIEW-023`–`FR-VIEW-026` are
     written against. Sibling of `boardCalendar.*` above; the rulings the two
     share are stated there and referred to here rather than repeated.

     ⚠ THE REGION LABEL IS REUSED HERE TOO, and this file held the table
     slice's defect VERBATIM: a literal `aria-label="Timeline view"` on the
     very `id="board-view-content"` node that `BoardFace.tsx:5054` and `:5078`
     already name through `boardFace.viewRegion` + `boardFace.view.timeline`.
     No `boardTimeline.viewLabel`, and `Timeline view` is declared flat nowhere
     in this catalogue. Unlike the calendar there is no default parameter and
     no second caller: this view's region name is ALWAYS the composition. */
  /* ⚠ ONE key, TWO call sites. `BoardTimelineView.tsx` computes this ONCE into
     `const title` and renders it into both the `<h2>` and the `role="grid"`
     `aria-label`. The identity is AUTHORED, not coincidental, and
     `BoardTimelineView.test.tsx:242` records it —
     `getByRole('grid', { name: 'Launch board timeline' })` finds the grid by
     the heading directly above it. Two keys would let a locale name the grid
     differently from its own heading with nothing red.

     ⚠ `{board}` is the board's own title and stays user data. */
  'boardTimeline.heading': { kind: 'text', value: '{board} timeline' },
  /* `kind: 'text'`, NOT plural, and measured rather than assumed: the English
     noun that follows `{count}` here is the invariant `unscheduled`, not a
     count-inflected noun. The `/` join is inside the value — see
     `boardCalendar.checklistItemParent`. */
  'boardTimeline.summary': { kind: 'text', value: '{column} / {count} unscheduled' },
  /* ⚠ DECLARED HERE RATHER THAN BESIDE THE OTHER `notifications.*` KEYS, and
     the misplacement is deliberate. This registry is one of five that every
     chunk of fan-out 9 edits, and the merge rule is INSERT ONLY, ONE HUNK PER
     FILE, at the chunk's assigned anchor — `boardTimeline.summary` for the
     Notification Fold chunk. Six concurrent branches inserting at their own
     anchors collide only by insertion, so whoever merges takes the union; six
     branches each inserting into the `notifications.` family would collide on
     one line and a resolution that keeps one side drops a key, which is a
     sentence that renders as its own dotted key name. Move it up beside its
     family in a later, single-branch pass — not now.

     ⚠ NOT `notifications.sourceUnavailable`, which is what `NotificationReply`
     reused before this. That sentence — 'This source is no longer available.' —
     describes the OBJECT, and `FR-NOTIF-009` AC3 asks for a sentence about the
     READER: "then they see a 'you no longer have access' message rather than a
     blank board". The two states are genuinely different and one row can be in
     the second without being in the first: a former member of a live, open
     board whose card is right there and perfectly available. Reuse was the
     right call for a chunk that did not own this file; it is the wrong one for
     a chunk that does. */
  'notifications.replyNoAccess': {
    kind: 'text',
    value: 'You no longer have access to this board.',
  },
  'boardTimeline.groupByLabel': { kind: 'text', value: 'Group Timeline by' },
  /* The four grouping `<option>`s. Each bare noun is one some other family
     already says — `None` ×1 (`primitives.cardDate.reminder.none`), `List` ×5,
     `Member` ×4, `Label` ×1 before this block — and the standing ruling
     applies to every one: each family declares its own. Sharing
     `boardTable.column.list` here would let the table's column heading rename
     the timeline's grouping option. */
  'boardTimeline.groupBy.none': { kind: 'text', value: 'None' },
  'boardTimeline.groupBy.list': { kind: 'text', value: 'List' },
  'boardTimeline.groupBy.member': { kind: 'text', value: 'Member' },
  'boardTimeline.groupBy.label': { kind: 'text', value: 'Label' },
  /* The `<label>` of the zoom `<select>`, then its five `<option>`s. `Month`
     and `Week` are the second half of the pair declared above the calendar's
     mode toggle; the reason they are two keys and not one is stated there. */
  'boardTimeline.zoomLabel': { kind: 'text', value: 'Timeline zoom' },
  'boardTimeline.zoom.day': { kind: 'text', value: 'Day' },
  'boardTimeline.zoom.week': { kind: 'text', value: 'Week' },
  'boardTimeline.zoom.month': { kind: 'text', value: 'Month' },
  'boardTimeline.zoom.quarter': { kind: 'text', value: 'Quarter' },
  'boardTimeline.zoom.year': { kind: 'text', value: 'Year' },
  /* A SECOND `Today` (`planner.today` is the first). Same ruling: a route
     namespace and a view family do not share a word. */
  'boardTimeline.today': { kind: 'text', value: 'Today' },
  'boardTimeline.openDrawer': { kind: 'text', value: 'Open unscheduled drawer' },
  /* One sentence, one plural key — the `card has` / `cards have` fragment the
     JSX chose was an English plural rule the call site owned. */
  'boardTimeline.unscheduledSummary': {
    kind: 'plural',
    forms: {
      one: '{count} card has no start or due date.',
      other: '{count} cards have no start or due date.',
    },
  },
  /* ⚠ ONE key, TWO call sites, like `boardTimeline.heading` above: the
     `<aside>`'s `aria-label` and the `<h3>` INSIDE it. A region's accessible
     name and its own visible heading, co-rendered on the same panel — two keys
     is the second-author defect one level down, and green under every
     rendering test. `BoardTimelineView.test.tsx:234` finds the drawer by the
     name and reads the heading inside it with one string. */
  'boardTimeline.drawerLabel': { kind: 'text', value: 'Unscheduled cards' },
  /* ⚠ A LIVE ENGLISH BUG THIS SLICE FIXES, named so nobody reads it as drift.
     The JSX was `{count} filtered cards` with NO plural conditional at all, so
     the drawer read `1 filtered cards`. Routed as a plural key, the `one` arm
     reads `1 filtered card`. The visible bytes change in exactly one state.
     `boardTimeline.drawerOverflow` below had the same defect and gets the same
     treatment. */
  'boardTimeline.drawerCount': {
    kind: 'plural',
    forms: { one: '{count} filtered card', other: '{count} filtered cards' },
  },
  'boardTimeline.drawerEmpty': {
    kind: 'text',
    value: 'Every filtered card has a date.',
  },
  'boardTimeline.drawerList': { kind: 'text', value: 'Unscheduled card list' },
  'boardTimeline.scheduleCard': {
    kind: 'text',
    value: 'Schedule {title} from {list}',
  },
  'boardTimeline.drawerOverflow': {
    kind: 'plural',
    forms: {
      one: '{count} more filtered card remains in the drawer.',
      other: '{count} more filtered cards remain in the drawer.',
    },
  },
  /* The grid's fixed first `columnheader`. */
  'boardTimeline.laneColumn': { kind: 'text', value: 'Lane' },
  /* ⚠ THE FRAME ONLY, and its two slots have DIFFERENT fates — the reason this
     docblock is worth its length.

     `{column}` WAS the seam this key reported: `lib/board-timeline.ts` composed
     it in English (`Week of …`, `Q3 2026`) and no slice owned that file.
     ⚠ CORRECTED 2026-09-05 by T15-2 — it is routed now, through
     `boardTimeline.column.*` immediately below, and the value spliced into this
     frame is a resolved catalogue string. The slot stays a slot; nothing about
     THIS key changed.

     `{lane}` is still SERVER-authored English — `apps/server/src/routes/
     timeline-view.ts` writes `Cards`, `Unassigned` and `Unlabelled` into
     `TimelineLane.title`, which reaches this cell name and the `rowheader`
     beside it. NO SERVED CATALOGUE CAN TRANSLATE A STRING THAT ARRIVES IN A
     RESPONSE BODY. Still reported as a contract bug; `apps/server` is out of
     bounds for a routing slice. */
  'boardTimeline.cellLabel': { kind: 'text', value: '{lane}, {column}' },
  /* ── The timeline's COLUMN CHROME, resolved by `labelBoardTimelineColumns`
     (`lib/board-timeline.ts`) and drawn at three nodes: the summary line under
     the heading, every `columnheader`, and every `gridcell` name through
     `cellLabel` above.

     ⚠ ONLY TWO OF THE FIVE ZOOMS ARE HERE, and the absence of the other three
     is the ruling. `day`, `month` and `year` delegate to `Intl.DateTimeFormat`,
     which is already locale-correct — a key for them would AUTHOR copy where
     the platform authors none, exactly as `boardCalendar.heading` is left
     undeclared for a formatted date.

     ⚠ `Q` IS COPY, NOT A NUMERAL CONVENTION, and this was argued rather than
     assumed. `Intl.DateTimeFormat` exposes no `quarter` option at all, so
     unlike every other arm there is no locale-correct path to delegate to; and
     the abbreviation is language-specific — French and Spanish write `T1`
     (*trimestre*), German `1. Quartal`. The digit stays a SLOT so a locale can
     reletter or reorder it; `{year}` likewise stays a bare numeral. */
  'boardTimeline.column.week': { kind: 'text', value: 'Week of {date}' },
  /* The long form, on the summary line and in every `gridcell` name. */
  'boardTimeline.column.quarter': { kind: 'text', value: 'Q{quarter} {year}' },
  /* ⚠ A SECOND KEY, not `column.quarter` reused with the year omitted. The
     `columnheader` row is width-constrained and drops the year; a locale that
     shortened one form must not be forced to shorten the other, and a shared
     key with an optional slot would make `interfaceMessage` throw at whichever
     call site stopped passing it. */
  'boardTimeline.column.quarterShort': { kind: 'text', value: 'Q{quarter}' },
  'boardTimeline.readonlyReason': {
    kind: 'text',
    value: 'Only board editors can change timeline cards',
  },
  /* Byte-identical to `boardCalendar.controlUnavailable`, deliberately — the
     two-families ruling is stated in full above that key. */
  'boardTimeline.controlUnavailable': {
    kind: 'text',
    value: '{action} unavailable: {reason}',
  },
  /* ⚠ FOUR KEYS NO ANCHOR CAN SEE, on the calendar's `refusal` ruling: `status`
     was state holding a resolved sentence and now holds a KEY. ⚠ `string |
     null`, NOT `''`: the old `setStatus('')` clearing idiom makes a
     key-or-empty-string union, which is exactly the type that invites
     `setStatusKey(k === null ? null : t(k))` — the mutation that survived the
     board-table pin's anchored regex, which is why this slice's pin reads the
     setter's arguments with BALANCED PARENTHESES instead.

     ⚠ AND `status.dueBeforeStart` IS A KNOWING SECOND AUTHOR of
     `boardFace.timeline.rangeRefused`, which carries these exact bytes. This
     is the `boardFace.table.cardMoved` ↔ `boardTable.*` split in a new place
     and it is TWO NODES, not one: `BoardFace.tsx:3202` speaks its copy into
     the board face's `announce()` live region after a resize the view PASSED
     ON, while this one is the view's own `role="status"` and fires only when
     the view REFUSES the resize itself and never calls back. They cannot both
     speak for one gesture. Sharing would let the board face's announcement
     rename this view's status line. ⚠ Note the asymmetry rather than
     "correcting" it: `boardFace.*` has no counterpart for the start-edge
     sentence, and the calendar's own refusals are worded differently again
     (`boardFace.calendar.rangeRefused` is `Calendar range refused because …`
     while `boardCalendar.refusal.dueBeforeStart` is `The due date cannot be
     moved …`). Three wordings for one family of refusals is pre-existing copy
     drift, reported and not bent to match. */
  'boardTimeline.status.readonlyDates': {
    kind: 'text',
    value: 'This timeline is read only. Dates were not changed.',
  },
  'boardTimeline.status.readonlyDatesLanes': {
    kind: 'text',
    value: 'This timeline is read only. Dates and lanes were not changed.',
  },
  'boardTimeline.status.startAfterDue': {
    kind: 'text',
    value: 'Timeline range refused because the start date would be after the due date.',
  },
  'boardTimeline.status.dueBeforeStart': {
    kind: 'text',
    value: 'Timeline range refused because the due date would be before the start date.',
  },
  /* The bar's sub-label. `{start}` and `{due}` are `Intl` output — the
     formatter residual recorded at the foot of the calendar block applies to
     this file's `longDateLabel` too. `rangeUnscheduled` is lower-case because
     it stands where a formatted date would. */
  'boardTimeline.rangeSpan': { kind: 'text', value: '{start} to {due}' },
  'boardTimeline.rangeUnscheduled': { kind: 'text', value: 'unscheduled' },
  'boardTimeline.openCard': { kind: 'text', value: 'Open {title}, {range}' },
  /* The eight nudge controls, each asked for twice — bare, and as the
     `{action}` of `controlUnavailable`. ⚠ `moveStart` / `moveDue` are the
     DRAG HANDLES and carry no direction; the six below them are the keyboard
     nudges and do. Not the same control, not the same name. */
  'boardTimeline.moveStart': { kind: 'text', value: 'Move start of {title}' },
  'boardTimeline.moveDue': { kind: 'text', value: 'Move due date of {title}' },
  'boardTimeline.moveEarlier': {
    kind: 'text',
    value: 'Move {title} earlier one column',
  },
  'boardTimeline.moveLater': { kind: 'text', value: 'Move {title} later one column' },
  'boardTimeline.moveStartEarlier': {
    kind: 'text',
    value: 'Move start of {title} earlier one column',
  },
  'boardTimeline.moveStartLater': {
    kind: 'text',
    value: 'Move start of {title} later one column',
  },
  'boardTimeline.moveDueEarlier': {
    kind: 'text',
    value: 'Move due date of {title} earlier one column',
  },
  'boardTimeline.moveDueLater': {
    kind: 'text',
    value: 'Move due date of {title} later one column',
  },

  /* FE-80.1 board frame — `board/BoardCustomFieldsSection.tsx`, the board menu
     panel's field manager. The five type nouns it used to own privately are now
     `primitives.customFieldType.*` above. */
  'boardCustomFields.heading': { kind: 'text', value: 'Custom fields' },
  // TWO keys, one value. `loading` is a control's `disabledReason` (a
  // tooltip-shaped explanation of why a button will not respond);
  // `loadingNotice` is the body of a `Notice` occupying the panel. They render
  // at the same moment, in different roles, and a locale that shortens one
  // should not be forced to shorten the other.
  'boardCustomFields.loading': { kind: 'text', value: 'Checking custom fields...' },
  'boardCustomFields.loadingNotice': {
    kind: 'text',
    value: 'Checking custom fields...',
  },
  'boardCustomFields.noPermission': {
    kind: 'text',
    value: 'Only board members with edit access can manage custom fields.',
  },
  'boardCustomFields.createHeading': { kind: 'text', value: 'Create custom field' },
  'boardCustomFields.nameLabel': { kind: 'text', value: 'New field name' },
  'boardCustomFields.typeLabel': { kind: 'text', value: 'Field type' },
  'boardCustomFields.dropdownOptionsLabel': {
    kind: 'text',
    value: 'Dropdown options',
  },
  'boardCustomFields.dropdownOptionsHint': {
    kind: 'text',
    value: 'One option per line.',
  },
  'boardCustomFields.createField': { kind: 'text', value: 'Create field' },
  'boardCustomFields.definitionsHeading': { kind: 'text', value: 'Field definitions' },
  'boardCustomFields.empty': { kind: 'text', value: 'No custom fields yet.' },
  // ⚠ FOUR whole sentences rather than a type noun with two English fragments
  // (`' - Hidden'`, `' - Shows on card front'`) concatenated onto it. The
  // suffix shape cannot be reordered, cannot lose the separator, and needs a
  // leading space inside a translated value. Four keys is the cost of the 2x2;
  // a third flag would argue for a different shape, and this comment is where
  // that argument starts.
  'boardCustomFields.meta.plain': { kind: 'text', value: '{type}' },
  'boardCustomFields.meta.hidden': { kind: 'text', value: '{type} - Hidden' },
  'boardCustomFields.meta.showsOnFront': {
    kind: 'text',
    value: '{type} - Shows on card front',
  },
  'boardCustomFields.meta.hiddenAndShowsOnFront': {
    kind: 'text',
    value: '{type} - Hidden - Shows on card front',
  },
  'boardCustomFields.renameLabel': { kind: 'text', value: 'Rename {name}' },
  'boardCustomFields.saveName': { kind: 'text', value: 'Save name' },
  'boardCustomFields.unhide': { kind: 'text', value: 'Unhide' },
  'boardCustomFields.hide': { kind: 'text', value: 'Hide' },
  'boardCustomFields.showOnFront': { kind: 'text', value: 'Show on front' },
  'boardCustomFields.moveUp': { kind: 'text', value: 'Move {name} up' },
  'boardCustomFields.moveDown': { kind: 'text', value: 'Move {name} down' },
  'boardCustomFields.up': { kind: 'text', value: 'Up' },
  'boardCustomFields.down': { kind: 'text', value: 'Down' },
  'boardCustomFields.alreadyFirst': {
    kind: 'text',
    value: 'This field is already first.',
  },
  'boardCustomFields.alreadyLast': { kind: 'text', value: 'This field is already last.' },
  'boardCustomFields.delete': { kind: 'text', value: 'Delete' },
  'boardCustomFields.deleteTitle': { kind: 'text', value: 'Delete custom field?' },
  'boardCustomFields.deleteDescription': {
    kind: 'text',
    value:
      '{name} will be removed from this board. Values on every card will be removed.',
  },
  'boardCustomFields.deleteClose': {
    kind: 'text',
    value: 'Cancel custom field deletion',
  },
  'boardCustomFields.cancel': { kind: 'text', value: 'Cancel' },
  'boardCustomFields.deleteField': { kind: 'text', value: 'Delete field' },
  'boardCustomFields.deleteBody': {
    kind: 'text',
    value: 'This cannot be undone from the board menu.',
  },
  // ⚠ The glyphs are ROUTED. Three of the five are English words rather than
  // symbols (`Aa` is the Latin alphabet, `Yes` and `List` are words), and a
  // locale that can change `primitives.customFieldType.checkbox` but not the
  // badge beside it renders a mixed row. `123` and `31` translate to themselves
  // and cost one key each to keep the five together.
  'boardCustomFields.typeGlyph.text': { kind: 'text', value: 'Aa' },
  'boardCustomFields.typeGlyph.number': { kind: 'text', value: '123' },
  'boardCustomFields.typeGlyph.checkbox': { kind: 'text', value: 'Yes' },
  'boardCustomFields.typeGlyph.date': { kind: 'text', value: '31' },
  'boardCustomFields.typeGlyph.dropdown': { kind: 'text', value: 'List' },
  // The option editor's own namespace. `Up`, `Down` and the two ordinal
  // refusals are byte-identical to the definition-level pairs above and are
  // deliberately NOT shared: they operate different objects, and a locale that
  // wants `Move up` on one and an icon-only label on the other must be able to.
  'boardCustomFields.options.heading': { kind: 'text', value: 'Dropdown options' },
  'boardCustomFields.options.empty': { kind: 'text', value: 'No options yet.' },
  'boardCustomFields.options.renameLabel': {
    kind: 'text',
    value: 'Rename option {label}',
  },
  'boardCustomFields.options.save': { kind: 'text', value: 'Save option' },
  'boardCustomFields.options.moveUp': {
    kind: 'text',
    value: 'Move option {label} up',
  },
  'boardCustomFields.options.moveDown': {
    kind: 'text',
    value: 'Move option {label} down',
  },
  'boardCustomFields.options.up': { kind: 'text', value: 'Up' },
  'boardCustomFields.options.down': { kind: 'text', value: 'Down' },
  'boardCustomFields.options.alreadyFirst': {
    kind: 'text',
    value: 'This option is already first.',
  },
  'boardCustomFields.options.alreadyLast': {
    kind: 'text',
    value: 'This option is already last.',
  },
  'boardCustomFields.options.delete': { kind: 'text', value: 'Delete option' },
  'boardCustomFields.options.newLabel': {
    kind: 'text',
    value: 'New option for {name}',
  },
  'boardCustomFields.options.add': { kind: 'text', value: 'Add option' },

  /* FE-80.1 board view switcher — `board/BoardViewSwitcher.tsx`,
     `board/BoardViewShell.tsx` and the `lib/view-state.ts` leaf underneath
     them.

     These three had ZERO `t(` call sites between them and rendered directly
     beside a board face that was already fully routed: the board-face slice's
     screenshot pass photographed the switcher trigger reading `Board` and its
     menu reading `Calendar` on an otherwise entirely Spanish board.

     ⚠ THE SIX VIEW NOUNS ARE NOT DECLARED HERE. `boardFace.view.*` above
     already declares them, and `lib/view-state.ts` was a SECOND English author
     of the same six words through `BOARD_VIEW_OPTIONS[].label`. That field is
     deleted rather than left unread; the keys below are only the words no key
     already owns.

     ⚠ AND THE EXCLUSION SENTENCES ARE PER-REASON PLURALS, not a fragment. The
     leaf used to hand out the English `'without due dates'` and its two
     consumers spliced it into different frames — `{count} cards {reason}` in
     the switcher and `These cards are {reason}.` in the shell. `lib/dnd.ts`
     states the rule these now follow: a key per sentence, never one sentence
     assembled from a fragment and a plural rule the call site hard-codes. */

  // Two keys, one value, and it is the settled discriminator rather than an
  // oversight: the mobile-limited note appears on the option row AND on that
  // same view's pin row, and the mobile fixture has both on screen at once.
  // They also sit in different frames — joined with ` · ` here and with `, `
  // there — which is exactly the case the ruling was written for.
  'boardView.option.mobileLimited': {
    kind: 'text',
    value: 'available on web and desktop, limited on mobile',
  },
  'boardView.pin.mobileLimited': {
    kind: 'text',
    value: 'available on web and desktop, limited on mobile',
  },
  // TWO keys rather than one with an `{action}` slot. A verb routed apart from
  // the sentence it sits in is the `palette.ts` shape: the frame agrees with
  // the catalogue and the word inside it does not decline with the frame.
  'boardView.pin': { kind: 'text', value: '{view}, pin view' },
  'boardView.unpin': { kind: 'text', value: '{view}, unpin view' },
  'boardView.pinnedHeading': { kind: 'text', value: 'Pinned views' },
  'boardView.trigger': { kind: 'text', value: '{view} view, change view' },
  // The `busyLabel` this switcher PASSES. `Button`'s busyLabel DEFAULT
  // (`Working...`) is still unowned, for the sixteenth slice running.
  'boardView.saving': { kind: 'text', value: 'Saving view' },
  // ⚠ Both plurals, and both forms must differ — the kind audit in
  // `board-view.routing.test.ts` reads `.kind` off these declarations, because
  // a `plural` whose two forms are identical renders byte-identically at every
  // arity and its singular is unreachable with nothing red.
  'boardView.excluded.noDueDate': {
    kind: 'plural',
    forms: { one: '1 card without due dates', other: '{count} cards without due dates' },
  },
  'boardView.excluded.noDates': {
    kind: 'plural',
    forms: {
      one: '1 card without start or due dates',
      other: '{count} cards without start or due dates',
    },
  },
  'boardView.shell.description': {
    kind: 'text',
    value:
      'Showing the filtered cards from {board} that {view} can place. Cards that need more data are listed below.',
  },
  'boardView.shell.notShown': {
    kind: 'plural',
    forms: {
      one: '1 card is not shown in {view}.',
      other: '{count} cards are not shown in {view}.',
    },
  },
  'boardView.shell.reason.noDueDate': {
    kind: 'plural',
    forms: {
      one: 'This card is without due dates.',
      other: 'These cards are without due dates.',
    },
  },
  'boardView.shell.reason.noDates': {
    kind: 'plural',
    forms: {
      one: 'This card is without start or due dates.',
      other: 'These cards are without start or due dates.',
    },
  },
  'boardView.shell.hiddenList': { kind: 'text', value: 'Cards not shown in {view}' },
  'boardView.shell.allShown': {
    kind: 'text',
    value: 'Every filtered card can be accounted for by this view.',
  },

  /*
   * FE-80.1 card-template create dialog — `components/board/CardTemplateCreateDialog.tsx`,
   * 207 lines that held 22 English strings and ZERO `t(` call sites.
   *
   * ⚠ WHY THIS IS A TOP-LEVEL FAMILY AND NOT `boardFace.*`. The dialog has TWO
   * callers in TWO route namespaces: `ListColumn.tsx:702` (a `boardFace.*`
   * file) and `CardBack.tsx:2064` (a `cardBack.*` file). Measured across
   * `apps/web/src`, no product-surface family is routed from both
   * `components/board/` and `components/card-back/`; the single crossing is
   * `undo.*` below, which is a cross-cutting concern and not a surface. So
   * `boardFace.cardTemplate.*` would make `CardBack.tsx` the first card-back
   * file ever to route a board-face key, and `cardBack.templateCreate.*` would
   * make `ListColumn.tsx` reach into the card back's namespace — the same
   * reach-across `board.card.untitled` records against `myCards.untitledCard`.
   * A family named for the COMPONENT is the settled escape: `labelPicker.*`,
   * `quickEdit.*`, `undo.*`, `dnd.*` and `primitives.*` are all already that.
   *
   * ⚠ THIS SETS A PRECEDENT FOR FIVE FILES. `CardCopyDialog`,
   * `CrossBoardMoveDialog`, `MirrorCardDialog`, `ArchiveDeleteConfirm` and
   * `ArchiveRestoreDestinationDialog` all live in `components/board/`, are all
   * imported by `components/card-back/`, and all have zero routed call sites.
   * They take `cardCopy.*`, `crossBoardMove.*` and so on — not `boardFace.*`.
   *
   * ⚠ THREE DECLINES TO SHARE, each ruled on the namespace and not the bytes:
   *
   * - `.title` is byte-identical to `cardBack.actions.createFromTemplate` and
   *   stays separate. That key is the card back's Actions-MENU BUTTON; this is
   *   the DIALOG TITLE of the dialog that button opens, and
   *   `FR-CARD-031.spec.ts` queries a `button` and a `dialog` of that same name
   *   in the SAME test at `:493`/`:509`. Different namespace AND different
   *   role. This is also the third member of the near-miss cluster the comment
   *   above `boardFace.card.composer.fromTemplate` warns about — `Create from
   *   template` (the button that opens THIS dialog) is NOT byte-identical to
   *   `Create card from template`, and the only place the three could be
   *   confused is a grep.
   * - `.cancel` is a twenty-first `Cancel`. Each family declares its own, so
   *   one surface's translation cannot rename a button on twenty others.
   * - `.submit` is a second `Create card`, beside
   *   `boardFace.calendarCreate.submit` — the calendar view's create dialog, a
   *   different surface reached by a different gesture.
   *
   * ⚠ AND `.resultTitle` IS NOT A THIRD AUTHOR OF THE PINNED PAIR.
   * `Card created from template` (a `Notice title=` heading: no slot, no card
   * name, no full stop) is a DIFFERENT SENTENCE from `Card {title} created
   * from template.`, which `boardFace.card.createdFromTemplate` and
   * `cardBack.announce.createdFromTemplate` share byte-identically and which
   * `card-composer.routing.test.ts` pins as EXACTLY TWO. Merging the heading
   * into that sentence is not merely unwise, it is impossible without changing
   * the rendered bytes. They are close enough to matter:
   * `CardTemplateCreateDialog.test.tsx` reads this Notice through
   * `getByRole('status')`, so both are live-region text about the same event —
   * which is the reason they must stay separate keys, not a reason to merge.
   *
   * ⚠ `.destinationFallback` is a fallback for the CALLER's
   * `destinationListTitle` prop and its branch is probably unreachable today
   * (both callers pass a real list title). It is routed anyway, on the
   * precedent the list-header slice set for `boardFace.list.expand`: an
   * unreachable-today branch that renders English tomorrow is the same defect,
   * discovered later.
   *
   * ⚠ `.hiddenOption` is ONE slotted label, not a name with ` - hidden from
   * list` glued after it. The file rendered two adjacent JSX expression
   * children — the two-fragment shape `lib/undo.ts` was emptied of, in which a
   * translator cannot move the name. The empty-title fallback feeds the slot,
   * so untitled-and-hidden still renders both halves.
   *
   * ⚠ `TemplateCardVisibilityAction.tsx` carries its OWN `Untitled template`
   * and `Template card`, byte-identical to two keys here and unrouted. Under
   * the namespace rule those stay SEPARATE keys when that file is taken — do
   * not reach for these.
   */
  'cardTemplateCreate.title': { kind: 'text', value: 'Create card from template' },
  'cardTemplateCreate.description': {
    kind: 'text',
    value: 'Choose a template and create the new card in {list}.',
  },
  'cardTemplateCreate.close': { kind: 'text', value: 'Cancel create from template' },
  'cardTemplateCreate.destinationFallback': { kind: 'text', value: 'the selected list' },
  'cardTemplateCreate.titleLabel': { kind: 'text', value: 'New card title' },
  'cardTemplateCreate.titleRequired': { kind: 'text', value: 'Enter a new card title.' },
  'cardTemplateCreate.templateLabel': { kind: 'text', value: 'Template card' },
  'cardTemplateCreate.untitledTemplate': { kind: 'text', value: 'Untitled template' },
  'cardTemplateCreate.hiddenOption': {
    kind: 'text',
    value: '{title} - hidden from list',
  },
  'cardTemplateCreate.loadingTitle': { kind: 'text', value: 'Loading template cards' },
  'cardTemplateCreate.loadingBody': {
    kind: 'text',
    value: 'Template cards for this board will be available here.',
  },
  'cardTemplateCreate.loadingReason': {
    kind: 'text',
    value: 'Template cards are still loading.',
  },
  'cardTemplateCreate.noTemplatesTitle': { kind: 'text', value: 'No template cards' },
  'cardTemplateCreate.noTemplatesBody': {
    kind: 'text',
    value: 'Make a card into a template before creating one here.',
  },
  'cardTemplateCreate.submit': { kind: 'text', value: 'Create card' },
  'cardTemplateCreate.busy': { kind: 'text', value: 'Creating card...' },
  'cardTemplateCreate.cancel': { kind: 'text', value: 'Cancel' },
  'cardTemplateCreate.done': { kind: 'text', value: 'Done' },
  'cardTemplateCreate.resultTitle': { kind: 'text', value: 'Card created from template' },
  'cardTemplateCreate.resultBody': {
    kind: 'text',
    value: 'The new card is ready in {list}.',
  },
  'cardTemplateCreate.openCreated': { kind: 'text', value: 'Open created card' },

  /*
   * FE-80.1 card-template hook announcements — `lib/card-templates.ts`.
   *
   * These five failure sentences live in the hook layer rather than the
   * card-back or board-face components. The same hooks are consumed by
   * `CardBack.tsx` and `ListColumn.tsx`, so a `cardBack.*` or `boardFace.*`
   * namespace would make one caller speak another caller's surface family. The
   * title slot remains literal card/template data; the whole sentence routes
   * so languages can move it around that slot.
   */
  'cardTemplateHooks.markFailed': {
    kind: 'text',
    value: 'Could not mark {title} as a template.',
  },
  'cardTemplateHooks.unmarkFailed': {
    kind: 'text',
    value: 'Could not remove the template flag from {title}.',
  },
  'cardTemplateHooks.hideFailed': {
    kind: 'text',
    value: 'Could not hide {title} from its list.',
  },
  'cardTemplateHooks.unhideFailed': {
    kind: 'text',
    value: 'Could not return {title} to its list.',
  },
  'cardTemplateHooks.createFailed': {
    kind: 'text',
    value: 'Could not create a card from {title}.',
  },

  /*
   * FE-80.1 label hook announcements — `lib/labels.ts`.
   *
   * The six fixed failure sentences live in shared label mutation hooks used by
   * the card-back picker and board label-management paths. Label names, colour
   * wire values and server error bodies stay literal; these messages are
   * product chrome and route as whole sentences.
   */
  'primitives.labels.applyFailed': {
    kind: 'text',
    value: 'Could not apply the label.',
  },
  'primitives.labels.removeFailed': {
    kind: 'text',
    value: 'Could not remove the label.',
  },
  'primitives.labels.createFailed': {
    kind: 'text',
    value: 'Could not create the label.',
  },
  'primitives.labels.renameFailed': {
    kind: 'text',
    value: 'Could not rename the label.',
  },
  'primitives.labels.recolourFailed': {
    kind: 'text',
    value: 'Could not recolour the label.',
  },
  'primitives.labels.deleteFailed': {
    kind: 'text',
    value: 'Could not delete the label.',
  },

  /*
   * FE-80.1 label-chip palette vocabulary — `palette.ts`, `LabelChip.tsx`,
   * `label-display.ts`, `CardTile.tsx` and `LabelPicker.tsx`.
   *
   * These are fixed product words used when label data has no stored name of
   * its own, and in the label picker's colour controls/search haystack. Stored
   * label names, colour wire values and swatch fills stay literal data.
   */
  'primitives.labelChip.unnamed': {
    kind: 'text',
    value: '{colour} label, no name',
  },
  'primitives.labelChip.namedColour': {
    kind: 'text',
    value: '{colour} label',
  },
  'primitives.labelChip.remove': {
    kind: 'text',
    value: 'Remove {label}',
  },
  'primitives.labelColour.colourless': { kind: 'text', value: 'Colourless' },
  'primitives.labelColour.greenSubtle': { kind: 'text', value: 'Subtle green' },
  'primitives.labelColour.green': { kind: 'text', value: 'Green' },
  'primitives.labelColour.greenBold': { kind: 'text', value: 'Bold green' },
  'primitives.labelColour.yellowSubtle': { kind: 'text', value: 'Subtle yellow' },
  'primitives.labelColour.yellow': { kind: 'text', value: 'Yellow' },
  'primitives.labelColour.yellowBold': { kind: 'text', value: 'Bold yellow' },
  'primitives.labelColour.orangeSubtle': { kind: 'text', value: 'Subtle orange' },
  'primitives.labelColour.orange': { kind: 'text', value: 'Orange' },
  'primitives.labelColour.orangeBold': { kind: 'text', value: 'Bold orange' },
  'primitives.labelColour.redSubtle': { kind: 'text', value: 'Subtle red' },
  'primitives.labelColour.red': { kind: 'text', value: 'Red' },
  'primitives.labelColour.redBold': { kind: 'text', value: 'Bold red' },
  'primitives.labelColour.purpleSubtle': { kind: 'text', value: 'Subtle purple' },
  'primitives.labelColour.purple': { kind: 'text', value: 'Purple' },
  'primitives.labelColour.purpleBold': { kind: 'text', value: 'Bold purple' },
  'primitives.labelColour.blueSubtle': { kind: 'text', value: 'Subtle blue' },
  'primitives.labelColour.blue': { kind: 'text', value: 'Blue' },
  'primitives.labelColour.blueBold': { kind: 'text', value: 'Bold blue' },
  'primitives.labelColour.skySubtle': { kind: 'text', value: 'Subtle sky' },
  'primitives.labelColour.sky': { kind: 'text', value: 'Sky' },
  'primitives.labelColour.skyBold': { kind: 'text', value: 'Bold sky' },
  'primitives.labelColour.limeSubtle': { kind: 'text', value: 'Subtle lime' },
  'primitives.labelColour.lime': { kind: 'text', value: 'Lime' },
  'primitives.labelColour.limeBold': { kind: 'text', value: 'Bold lime' },
  'primitives.labelColour.pinkSubtle': { kind: 'text', value: 'Subtle pink' },
  'primitives.labelColour.pink': { kind: 'text', value: 'Pink' },
  'primitives.labelColour.pinkBold': { kind: 'text', value: 'Bold pink' },
  'primitives.labelColour.blackSubtle': { kind: 'text', value: 'Subtle black' },
  'primitives.labelColour.black': { kind: 'text', value: 'Black' },
  'primitives.labelColour.blackBold': { kind: 'text', value: 'Bold black' },
  /*
   * FE-80.1 FilterChip — the primitive fallback only. BoardFace supplies
   * richer explicit labels (`boardFace.filters.remove.*`) for active board
   * filters, and those labels must continue to win.
   */
  'primitives.filterChip.remove': {
    kind: 'text',
    value: 'Remove {facet} filter',
  },

  /*
   * FE-80.1 undo — the FRAME, which was a second author standing behind eight
   * already-routed labels.
   *
   * `lib/undo.ts` built every toast and every announcement in English from a
   * `label` its callers had translated: `punctuate` appended a full stop,
   * `completionMessage` appended ` undone.` / ` redone.`, and the action button
   * read a module-literal `'Undo'`. A Spanish board therefore said
   * `Tarjeta Comprar leche archivada undone.` — the exact half-translated
   * sentence this workstream exists to remove, and the one shape no fixture
   * caught because every fixture asserted the English it was reading.
   *
   * ⚠ `undone` and `redone` are TWO WHOLE SENTENCES, not one frame with a
   * `{mode}` slot. `lib/dnd.ts` states the rule (six keys for six sentences)
   * and `boardView.pin`/`boardView.unpin` is the precedent: a verb routed apart
   * from the sentence it sits in does not decline with the frame around it, and
   * in a language that inflects the participle for the label's gender the slot
   * form cannot be written at all.
   */
  'undo.toast.recorded': { kind: 'text', value: '{label}.' },
  'undo.toast.undone': { kind: 'text', value: '{label} undone.' },
  'undo.toast.redone': { kind: 'text', value: '{label} redone.' },
  /*
   * The toast's own button, and it is NOT `boardFace.undo.dismiss` beside it —
   * the two render side by side in the same `Notice`, which is the settled
   * discriminator for declaring separate keys.
   */
  'undo.action.undo': { kind: 'text', value: 'Undo' },
  'undo.action.redo': { kind: 'text', value: 'Redo' },
  'undo.busy.undo': { kind: 'text', value: 'Undo is already in progress.' },
  'undo.busy.redo': { kind: 'text', value: 'Redo is already in progress.' },
  'undo.empty.undo': { kind: 'text', value: 'Nothing to undo.' },
  'undo.empty.redo': { kind: 'text', value: 'Nothing to redo.' },
  'undo.error.undo': { kind: 'text', value: 'Could not undo the last action.' },
  'undo.error.redo': {
    kind: 'text',
    value: 'Could not redo the last undone action.',
  },
  /*
   * ⚠ `undo.source.*`, RENAMED from `cardBack.undo.*` and shared with the board
   * face, rather than a `boardFace.undo.*` family declared beside it.
   *
   * `BoardFace.tsx` held `Card ${title} moved` and `Card ${title} archived` as
   * raw template literals while `cardBack.undo.moved` and `.archived` declared
   * the same two sentences, byte for byte. Declaring board-face copies would
   * have made this slice a second author of four sentences while removing one
   * from the frame above — the mistake the board-view-switcher slice refused
   * when it reused `boardFace.viewRegion`.
   *
   * There is exactly ONE undo toast per board, so a card-back label and a
   * board-face label can never be on screen at once: the discriminator that
   * licenses two keys with one value does not apply, and one key is correct.
   * The prefix is surface-neutral because the sentence has two surfaces.
   *
   * ⚠ The merge slot is `{target}`, where the card back's key said `{title}`.
   * Both call sites name a target card and a source card; `{title}` beside
   * `{source}` reads as though only one of the two were the card.
   */
  'undo.source.cardArchived': { kind: 'text', value: 'Card {title} archived' },
  'undo.source.cardMoved': { kind: 'text', value: 'Card {title} moved' },
  'undo.source.cardsMerged': {
    kind: 'text',
    value: 'Cards {target} and {source} merged',
  },
  'undo.source.listMoved': { kind: 'text', value: 'List {title} moved' },
  /* FE-80.1 list header. ⚠ TWO KEYS FOR WHAT LOOKS LIKE ONE STRING, and the
     difference is the terminal full stop. `boardFace.list.archived` is what
     `announce` says (`List {title} archived.`); THIS is the
     `UndoSourceRegistration.label` that reaches `undo.toast.recorded`'s
     `{label}` slot, which `lib/undo.ts`'s `stripTerminal` expects to arrive
     WITHOUT one — exactly as every sibling in this family does. Collapsing them
     either puts a stray period mid-toast or drops it from the live region.

     ⚠ It belongs HERE, not beside the header's other keys. `ListHeader.tsx` was
     the LAST unrouted undo label — `CardBack.tsx` and `BoardFace.tsx` author a
     dozen more and were routed into this family already — so declaring it in
     `boardFace.list.*` would have split one vocabulary across two families for
     the sake of the one file that happened to be late. */
  'undo.source.listArchived': { kind: 'text', value: 'List {title} archived' },
  'undo.source.cardRenamed': { kind: 'text', value: 'Card renamed to {title}' },
  'undo.source.memberAdded': { kind: 'text', value: 'Card member added' },
  'undo.source.memberRemoved': { kind: 'text', value: 'Card member removed' },
  'undo.source.labelAdded': { kind: 'text', value: 'Card label added' },
  'undo.source.labelRemoved': { kind: 'text', value: 'Card label removed' },
  /* FE-80.1 workspace members — `routes/w.$workspaceId.members.tsx` (918 lines)
     and the list it composes, `workspace/WorkspaceMemberList.tsx` (278 lines).
     BOTH files held ZERO `t(` call sites before this slice, measured with the
     pin's own anchor and not with `grep -c 't('`, which answered 6 and 12 and
     was counting the tail of `useState(`, `expect(` and `preventDefault(`.

     ⚠ ONE FAMILY FOR TWO FILES, because the route and the list name ONE
     surface: /w/:workspaceId/members. The route owns the session, every
     mutation and every `announce()`; the list is purely presentational. Two
     families would let a translation rename the page's heading and the row's
     role word out of step with each other.

     ⚠ WHY THE THREE SUB-NAMESPACES ARE `workspaceMembers.settings.*`,
     `.boardPolicy.*` AND `.guests.*` — NOT top-level `workspaceSettings.*` /
     `workspaceGuests.*`. The route ANNOUNCES about the settings panel, the
     board-visibility policy table and guest access; it DRAWS none of them.
     `WorkspaceSettingsPanel.tsx`, `WorkspaceBoardVisibilityPolicySection.tsx`
     and `WorkspaceGuestAccessSection.tsx` draw them and are all still
     unrouted. The precedent is exact: `boardFace.map.cardCreated` (what
     `BoardFace.tsx` ANNOUNCES about the map) coexists with the top-level
     `boardMap.*` (what `BoardMapView.tsx` DRAWS). Claiming `workspaceSettings.*`
     for a sentence this route speaks would squat on the name the panel's own
     slice needs and push it into a worse one.

     ⚠ NO PLURAL IN THIS SLICE, stated affirmatively rather than skipped.
     Neither file holds a count sentence — no `{n} members`, no
     `? 'member' : 'members'`, no summary line. The only numeric literal in
     either file is `<Skeleton count={3} />`, a geometry prop that renders no
     text. Every entry below is `kind: 'text'`. If a later slice adds a member
     count to this page, `boardTable.summary` is the shape to copy.

     ⚠ NOTHING BELOW IS SHARED WITH `BoardMemberList.tsx`, and the evidence is
     that file's own docstring (`:76–80`): its role trigger is named
     `Member — change <name>'s role`, DELIBERATELY not this list's
     `Role of <name>: <label>`, because `D4-BRIEF.md` §7 fixed the pattern
     differently from `D2-BRIEF.md` §7 and the e2e specs locate by the literal
     strings. It also holds its own `(you)` (`:198`), its own
     `Email or username` (`:363`) and an EXPORTED `BOARD_ROLE_LABELS` over a
     THREE-value set (`Admin`/`Member`/`Observer`) against this file's two. Two
     member lists, two vocabularies, by decision. That file needs its own
     family; it must not reach into this one. */

  /* --- the page, and the four states that wrap it ------------------------- */

  /* ⚠ A SECOND `Workspace members` in this catalogue, declared knowingly.
     The existing one is `cardBack.comments.composer.mention.workspaceMembersHeading`
     — a HEADING OVER A GROUP INSIDE A MENTION PICKER. This one names THIS
     PAGE, and `MembersPage` renders it as the route's `<h1>` in all four
     states. `boardTable.heading`'s third `Table` is the settled precedent:
     the namespace decides, not the bytes. It is also the FALLBACK for the
     workspace name — `:655` prefers `workspaceName` when the cache holds one —
     and §5.6 of `D2-BRIEF.md` fixes it as this page's fallback rather than the
     workspace list's `Untitled workspace`, which names a workspace among
     workspaces. */
  'workspaceMembers.title': { kind: 'text', value: 'Workspace members' },
  /* ⚠ `FR-PERM-034` AC3 — THE STATE WORD, AND NEVER A REDACTION. A deactivated
     account keeps its name, its avatar and its role on this row, because the
     requirement keeps that account's authored content "visible and attributed";
     the row GAINS a word rather than losing one. Deactivation is a distinct
     action from removal and the row says so.

     ⚠ NOT A REUSE of `enterpriseAdmin.members.deactivated`, which holds these
     exact bytes for the Enterprise-admin console. `boardMembers.role.option.admin`
     and `workspaceMembers.role.admin` are byte-identical and stay SEPARATE
     declarations because "a locale must be free to render them differently"
     (`workspace-members.routing.test.ts`, the `Admin` author list); an
     Enterprise-admin console and a workspace member list are two audiences by
     exactly that argument. Reuse is also outside this chunk's granted family,
     and reaching into another family's keys is the shape
     `lets no other file reach into workspaceMembers.*` forbids in the other
     direction.

     ⚠ NO `authorLists` ROW IS ARMED for these bytes, deliberately. That
     assertion shape is a GLOBAL `toEqual` over every key carrying the value, and
     that suite's own comment calls those rows "provisional against a pre-merge
     tree"; twelve branches are inserting into this file right now, so the ruling
     above lives in this docstring rather than in somebody else's merge
     failure. */
  'workspaceMembers.deactivated': { kind: 'text', value: 'Deactivated' },
  /* The signed-out branch. ⚠ NOT a reuse of `route.notFound.signIn`: that key
     belongs to `RouteNotFound.tsx`, the router's own 404 page, and reusing it
     would let the 404 page rename this button. Six per-page sign-in actions
     already exist on exactly that reading (`myCards`, `inbox`, `planner`,
     `board`, the public profile and `profile.page`), so the per-page key is the
     settled pattern here, not a duplication. */
  'workspaceMembers.signInTitle': {
    kind: 'text',
    value: 'Sign in to see this workspace',
  },
  'workspaceMembers.signInBody': {
    kind: 'text',
    value: "A workspace's members are visible only to the people in it.",
  },
  'workspaceMembers.signInAction': { kind: 'text', value: 'Sign in' },
  /* Not pending, not signed out, and still no profile: the `['me']` probe
     failed for some other reason. `tone="error"`, never neutral —
     `FR-EXP-034`'s second edge case. */
  'workspaceMembers.accountErrorTitle': {
    kind: 'text',
    value: 'We could not load your account',
  },
  /* ⚠ The FALLBACK body only — `sessionError?.message` wins when the server
     sent one, and a server message is never chrome. A fifth author of these
     bytes, and every one of the four before it is a PER-PAGE generic body
     (`myCards.genericError`, `inbox.genericError`, `planner.genericError`,
     `profile.page.genericError`). ⚠ Do NOT "simplify" this into
     `route.error.genericBody`: that is a DIFFERENT sentence
     (`Something on this page broke. Reloading might fix it.`) belonging to
     `RouteError.tsx`'s boundary, not to a page state. */
  'workspaceMembers.genericError': {
    kind: 'text',
    value: 'Something went wrong. Try again.',
  },
  /* The members query answered 404 — a non-member and a soft-deleted workspace
     are answered identically by route 4, deliberately, so a 403 there cannot
     become an existence oracle.

     ⚠ NOT a reuse of `route.notFound.body`
     (`There is nothing at this address — or it is not yours to see.`), which is
     the closest bytes in the catalogue. Same rhetorical shape, DIFFERENT
     sentence and DIFFERENT node kind: this is an `EmptyState` TITLE inside this
     page's own `<h1>` frame; that is a `<p>` BODY under the 404 page's `<h1>`.
     Sharing would put the router's wording on a page state and let one
     translation rename both. */
  'workspaceMembers.notFoundTitle': {
    kind: 'text',
    value: 'No such workspace — or it is not yours to see.',
  },
  /* ⚠ THE FOURTH OUTCOME, and it must stay a separate sentence from the 404
     above. Folding them would tell a member their workspace does not exist
     because the server 500'd — `FR-EXP-034`'s "a failed load must never render
     the empty copy", inverted. The route splits on `error.code === 'not_found'`
     for exactly this, and the body here is `members.error.message`, verbatim,
     which is the server's and not chrome. */
  'workspaceMembers.loadFailedTitle': {
    kind: 'text',
    value: 'We could not load the member list',
  },

  /* --- the list ----------------------------------------------------------- */

  /* Rendered as a sibling span, not appended into the name's text node, so
     `getByText` on a bare display name still matches exactly one node. */
  'workspaceMembers.you': { kind: 'text', value: '(you)' },
  /* ⚠ THE ROLE NOUNS ARE DISPLAY ONLY. `WORKSPACE_ROLES = ['admin', 'normal']`
     is the closed set `ck_workspace_members_role` enforces and the value
     `PATCH /workspaces/:id/members/:userId/role` parses. In `RoleControl` the
     section's `value`, each item's `id` and the argument handed to
     `onChangeRole` all stay the LITERAL wire value; only the `label` is copy.
     They sit two lines apart in one object literal. Route the wire side and
     `workspaceRoleSchema.parse` answers 422 — a 4xx that reads like an auth
     bug. `ROLE_LABEL_KEYS` in `WorkspaceMemberList.tsx` maps the wire value to
     the key below and is resolved with `t()` at render, because a module-level
     constant cannot call `t()`.

     `Member` here is a fifth author of those bytes; the four before it are a
     filter facet, a filter fallback, a notification fallback and the
     anonymous-avatar name, and none of them is a workspace role. */
  'workspaceMembers.role.admin': { kind: 'text', value: 'Admin' },
  'workspaceMembers.role.normal': { kind: 'text', value: 'Member' },
  /* ⚠ ONE key, TWO slots, and `{role}` is filled from the two keys above so the
     page never spells the noun a second way. `{name}` is USER DATA and stays
     literal inside the slot.

     ⚠ The resolved string feeds TWO NODES from ONE computation — the trigger's
     `aria-label` and its visible text — and it must keep doing so. WCAG 2.5.3
     *Label in Name*: the accessible name must CONTAIN the visible label, and
     resolving the two separately lets them drift in any language but English,
     silently. `w.$workspaceId.members.test.tsx:440` locates the control by
     these exact bytes. */
  'workspaceMembers.roleControl': { kind: 'text', value: 'Role of {name}: {role}' },
  /* The remove `IconButton`'s accessible name — the whole sentence in one key
     so a translator can move the name inside it, never an English fragment
     concatenated to a display name. */
  'workspaceMembers.remove': {
    kind: 'text',
    value: 'Remove {name} from this workspace',
  },
  /* The add form. ⚠ `BoardMemberList.tsx:363` holds byte-identical English for
     its own field and must NOT share this key — see the family note above. */
  'workspaceMembers.addLabel': { kind: 'text', value: 'Email or username' },
  'workspaceMembers.addAction': { kind: 'text', value: 'Add' },

  /* --- what the route ANNOUNCES about membership -------------------------- */

  /* ⚠ TWO WHOLE SENTENCES, not one frame plus a hard-coded `an admin` /
     `a normal member` fragment chosen by a ternary. The ternary now chooses the
     KEY, and both arms are written as `t('…')` literals so the pin's anchor can
     see both. It branches on the SETTLED row (`updated.role`), not the
     requested one: `announce()` reports what happened. */
  'workspaceMembers.roleChangedToAdmin': {
    kind: 'text',
    value: '{name} is now an admin.',
  },
  'workspaceMembers.roleChangedToNormal': {
    kind: 'text',
    value: '{name} is now a normal member.',
  },
  /* ⚠ `{workspace}` IS USER DATA in a slot, and the announcement gets its OWN
     fallback rather than the heading's. Sharing `workspaceMembers.title` as the
     fallback would announce `Ada Lovelace added to Workspace members.` on a
     cache miss — a page title read aloud as a workspace name. Both arms are
     `t('…')` literals for the same anchor reason as above. */
  'workspaceMembers.added': { kind: 'text', value: '{name} added to {workspace}.' },
  'workspaceMembers.addedFallback': {
    kind: 'text',
    value: '{name} added to this workspace.',
  },
  /* ⚠ THE NO-OP RE-ADD. `POST /workspaces/:id/members` answers 200 for both a
     real add and a re-add; `FR-PERM-021` AC4 wants the second STATED, not
     reported as a success and not as a failure, so the route reads
     `result.added` rather than `response.ok`.

     ⚠ RULE 4 — the state that used to hold this SENTENCE now holds the NAME.
     `addNotice` was `useState<string | null>` set to resolved English at
     `:420`; routed naively it would hold a resolved TRANSLATION, and a
     catalogue arriving underneath leaves the previous language on screen, which
     no test in this repository can see (`BoardMapView.tsx:80–84` is the
     precedent, `dragRefusalKey` the worked example). It cannot hold a bare key
     because the sentence carries user data — so it holds the DATA
     (`alreadyMemberName`) and this key is resolved at render.

     ⚠ This one is deliberately NOT also announced: `Notice tone="info"` IS a
     `role="status"` node, so announcing as well put the identical sentence into
     the accessibility tree twice. One channel per event.
     `FR-PERM-021.spec.ts:720` binds these exact bytes. */
  'workspaceMembers.alreadyMember': {
    kind: 'text',
    value: '{name} is already a member of this workspace.',
  },
  'workspaceMembers.removed': {
    kind: 'text',
    value: '{name} was removed from this workspace.',
  },
  /* ⚠ `joinRequestApproved` and `addedFallback` are TWO SENTENCES FOR TWO
     EVENTS — an admin adding by handle, and an admin approving a pending
     request — and they are not byte-identical (`added to` against
     `was added to`). Recorded so a later reader does not "unify" them.

     ⚠ The possessive `'s` in `joinRequestDeclined` lives INSIDE the key, where
     a translator can move it, rather than being glued to the display name at
     the call site. */
  'workspaceMembers.joinRequestApproved': {
    kind: 'text',
    value: '{name} was added to this workspace.',
  },
  'workspaceMembers.joinRequestDeclined': {
    kind: 'text',
    value: "{name}'s join request was declined.",
  },

  /* --- the leave-workspace dialog ----------------------------------------- */

  'workspaceMembers.leave.title': { kind: 'text', value: 'Leave workspace?' },
  'workspaceMembers.leave.description': {
    kind: 'text',
    value:
      'This removes your workspace membership. Boards you were explicitly added to remain reachable as guest boards.',
  },
  /* ⚠ A per-dialog `Cancel`, like every other `Cancel` in this catalogue. It is
     one of many authors of those five bytes and that is the settled shape: a
     shared `Cancel` would let one dialog's translation rename every other
     dialog's escape hatch. */
  'workspaceMembers.leave.cancel': { kind: 'text', value: 'Cancel' },
  'workspaceMembers.leave.confirm': { kind: 'text', value: 'Leave workspace' },
  'workspaceMembers.leave.announced': {
    kind: 'text',
    value: 'You left this workspace.',
  },

  /* --- what the route ANNOUNCES about the settings panel ------------------- */

  /* A PANEL-level state inside the loaded page, not a route state — the members
     query has already succeeded when this renders. Body is
     `workspace.error.message`, the server's. */
  'workspaceMembers.settings.loadFailedTitle': {
    kind: 'text',
    value: 'We could not load the workspace settings',
  },
  'workspaceMembers.settings.profileSaved': {
    kind: 'text',
    value: 'Workspace profile saved.',
  },
  /* ⚠ A DEFECT THIS SLICE FIXES, not a pure routing change, and it is stated
     rather than smuggled in. `:776` announced
     `Workspace visibility set to ${updated.visibility}.` — the RAW WIRE VALUE
     spoken as prose, so a screen reader heard "set to public." about a control
     one panel away (`WorkspaceSettingsPanel.tsx:176`) that had just drawn
     `Public`. That is `boardFace.template.visibilitySet`'s recorded defect
     verbatim, in a second file: `BoardFace` had been building the same noun
     with `value.replace('_', ' ')`, producing `project management` where the
     control said `Project management`.

     ⚠ SO THE RENDERED ENGLISH CHANGES HERE: `set to public.` becomes
     `set to Public.` Nothing in `src/` or `e2e/` binds the old sentence
     (checked); every other value this slice routes is byte-identical. */
  'workspaceMembers.settings.visibilitySaved': {
    kind: 'text',
    value: 'Workspace visibility set to {visibility}.',
  },
  /* ⚠ TWO SEPARATE VOCABULARIES, and this slice declares `Private` and `Public`
     TWICE on purpose. `WORKSPACE_VISIBILITIES = ['public', 'private']`
     (`packages/shared/src/workspace.ts:86`) describes THE WORKSPACE ITSELF;
     `BoardVisibility` below is a FOUR-value set describing A BOARD'S AUDIENCE.
     `packages/shared/src/board.ts:261` and `workspace.ts:108–128` argue at
     length that these two vocabularies stay separate DECLARATIONS rather than
     being merged, because merging types a field as the union of two CHECK
     constraints; the catalogue takes the same shape for the same reason. A
     single shared `Private` would let a translation that reads well for "a
     private workspace" rename "boards only members can see".

     ⚠ `WorkspaceSettingsPanel.tsx:176` DRAWS these same two words for the same
     control. When that file is routed it must REUSE these keys, not declare a
     third pair — otherwise the word the page speaks and the word the control
     draws can disagree in every language but English, which is the whole point
     of the fix above. */
  'workspaceMembers.settings.visibility.private': { kind: 'text', value: 'Private' },
  'workspaceMembers.settings.visibility.public': { kind: 'text', value: 'Public' },
  'workspaceMembers.settings.deleted': { kind: 'text', value: 'Workspace deleted.' },

  /* --- what the route ANNOUNCES about the board-visibility policy ---------- */

  /* ⚠ TWO WHOLE SENTENCES, because `POLICY_FIELD_LABELS` IS DELETED. It mapped
     `createRule → 'create'` and `deleteRule → 'delete'` and `:530` dropped the
     result into the MIDDLE of a sentence. An English verb stem slotted
     mid-sentence is untranslatable: it fixes word order, it fixes that the word
     is a bare stem, and it fixes that the sentence needs exactly one there. The
     field is now the CHOICE OF KEY and neither word gets a key of its own.

     ⚠ The `{visibility}` NOUN keeps its slot, and that is the opposite ruling
     on the neighbouring word for a stated reason:
     `boardFace.template.visibilitySet` settles this exact question on this
     exact subject — the slot is filled from the table that already owns the
     noun rather than from a fourth English author. Eight standalone sentences
     (4 visibilities × 2 fields) would also let the word the page SPEAKS drift
     from the word the `<select>` DRAWS, which is the defect that comment was
     written about. Both designs render byte-identically, so
     `w.$workspaceId.members.test.tsx:782` passes either way; the ruling is
     recorded because it is invisible in the output. */
  'workspaceMembers.boardPolicy.createSaved': {
    kind: 'text',
    value: '{visibility} create policy saved.',
  },
  'workspaceMembers.boardPolicy.deleteSaved': {
    kind: 'text',
    value: '{visibility} delete policy saved.',
  },
  /* ⚠ The FOUR-value board audience, keyed by `BoardVisibility`'s wire values:
     `private`, `workspace_visible`, `enterprise_visible`, `public`.
     `POLICY_VISIBILITY_KEYS` in the route maps the wire value to the key and is
     resolved with `t()` at render; the value handed to
     `policyMutation.mutate({ visibility, … })` stays the literal wire string.

     ⚠ `WorkspaceBoardVisibilityPolicySection.tsx:23–26` holds a BYTE-IDENTICAL
     copy of this table and DRAWS what this route SPEAKS. When that file is
     routed it must REUSE these four keys, not declare a second set. Same
     ruling, same reason, as the settings pair above. */
  'workspaceMembers.boardPolicy.visibility.private': { kind: 'text', value: 'Private' },
  'workspaceMembers.boardPolicy.visibility.workspace': {
    kind: 'text',
    value: 'Workspace',
  },
  'workspaceMembers.boardPolicy.visibility.enterprise': {
    kind: 'text',
    value: 'Enterprise',
  },
  'workspaceMembers.boardPolicy.visibility.public': { kind: 'text', value: 'Public' },

  /* --- what the route ANNOUNCES about guest access ------------------------ */

  'workspaceMembers.guests.ruleSaved': {
    kind: 'text',
    value: 'Guest invite rule set to {rule}.',
  },
  /* ⚠ Keyed by `WorkspaceGuestInviteRule`'s wire values — `anyone`,
     `workspace_admins_only`, `nobody` — through `GUEST_INVITE_RULE_KEYS`. The
     rule handed to `guestInviteRuleMutation.mutate(rule)` stays literal.
     ⚠ `WorkspaceGuestAccessSection.tsx:26–28` holds a byte-identical copy and
     must REUSE these three keys when it is routed. */
  'workspaceMembers.guests.rule.anyone': { kind: 'text', value: 'Anyone' },
  'workspaceMembers.guests.rule.adminsOnly': {
    kind: 'text',
    value: 'Workspace admins only',
  },
  'workspaceMembers.guests.rule.nobody': { kind: 'text', value: 'Nobody' },
  /* The fallback arm is reached when the removed guest has already fallen out
     of the cached list, so there is no name to put in the slot — a separate
     sentence, not the same one with an empty `{name}`. Both arms are `t('…')`
     literals so the anchor sees both. */
  'workspaceMembers.guests.removed': {
    kind: 'text',
    value: '{name} was removed from guest access.',
  },
  'workspaceMembers.guests.removedFallback': {
    kind: 'text',
    value: 'Guest access was removed.',
  },
  /* ⚠ THE OVERRIDE ARM OF THE SAVE ANNOUNCEMENT — `FE-67.2`, `FR-PERM-033`'s
     edge case (*"the more restrictive must win, and the UI must attribute the
     restriction to the right level"*), on the one channel a screenshot cannot
     photograph.

     ⚠ ONE KEY PER FIELD CARRYING BOTH FACTS, AND THAT IS FORCED RATHER THAN
     PREFERRED. `announce()` is LAST-WRITE-WINS — `lib/announce.ts` holds one
     module-level `region` and one `pendingMessage`, and `write()` assigns
     `textContent` — so a second `announce()` for the override would overwrite
     the save sentence before a screen reader reached either. The two facts
     therefore share one string and one slot each.

     ⚠ THE `{rule}` SLOT IS THE EFFECTIVE RULE, NEVER THE ONE ON THE BUTTON.
     Same ruling, same reason, as `workspaceSettings.boardPolicy.sourceEnterprise`,
     which DRAWS on the row what these two SPEAK on the save: the control shows
     the WORKSPACE rule the PATCH writes, and this names what is actually
     enforced over it. `fold()` in
     `apps/server/src/routes/workspace-board-visibility-policies.ts:125-134`
     compares with `>` and never `>=`, so `createRuleSource === 'enterprise'`
     holds if and only if the Enterprise rule is STRICTLY more restrictive —
     which is exactly when the two words differ and the sentence carries
     information. On the other three arms the plain `createSaved`/`deleteSaved`
     pair above is spoken, unchanged.

     ⚠ THE `{rule}` WORD IS REUSED FROM `workspaceSettings.boardPolicy.rule.*`,
     NOT REDECLARED HERE, and the route's `POLICY_RULE_KEYS` is where the reuse
     is written down. A second `Any workspace member` / `Workspace admins only`
     / `Nobody` set under this family is the drift
     `workspaceMembers.boardPolicy.visibility.private` above forbids in as many
     words, and the reuse runs in both directions already:
     `WorkspaceBoardVisibilityPolicySection.tsx:43` reuses this family's four
     board-audience nouns, and `WorkspaceGuestAccessSection.tsx:58-60` reuses
     its three guest-rule nouns.

     ⚠ THE FIELD IS THE CHOICE OF KEY, not a word in a slot — `POLICY_FIELD_LABELS`
     was deleted for exactly that reason (`w.$workspaceId.members.tsx:276-284`)
     and is not being reintroduced by the back door. */
  'workspaceMembers.boardPolicy.createSavedEnterprise': {
    kind: 'text',
    value: '{visibility} create policy saved. Enterprise policy enforces {rule}.',
  },
  'workspaceMembers.boardPolicy.deleteSavedEnterprise': {
    kind: 'text',
    value: '{visibility} delete policy saved. Enterprise policy enforces {rule}.',
  },
  /* FE-80.1 copy-move dialogs — the nine components through which a card, a
     list or a board is copied, moved, restored, pasted or bulk-acted on
     (`CardCopyDialog`, `BoardCopyDialog`, `ListCopyDialog`,
     `CrossBoardMoveDialog`, `MoveReconciliationReport`,
     `ArchiveRestoreDestinationDialog`, `PasteCardsDialog`,
     `BulkCardActionsDialog`, `ListOperationsDialog`). 165 keys, and ZERO `t(`
     call sites in those files before this block existed.

     ⚠ `copyMove.*` WITH NINE SUB-FAMILIES, not the `cardCopy.*` /
     `crossBoardMove.*` the comment at the five-file cluster predicts. The
     precedent's REASON is satisfied either way — the family is named for a
     component cluster, not for `boardFace.*` or `cardBack.*`, so neither frame
     reaches into the other's namespace — and `copyMove.card.*` IS the predicted
     `cardCopy.*`, one segment deeper. Recorded so a later slice does not
     "correct" it back and orphan 165 keys.

     ⚠ `copyMove.list.` DOES NOT PREFIX-MATCH `copyMove.listOps.`, and
     `copyMove.board.` does not match `copyMove.boardOption` — the trailing dot
     is what separates them. Every prefix in
     `copy-move-dialogs.interface-language.test.ts` carries its trailing dot for
     exactly that reason, and the pin asserts the disjointness rather than
     assuming it.

     THE COLLISIONS, each measured against the live record rather than guessed:
     `Untitled card` keeps its SEVEN authors and gains none here — the three
     dialogs that authored it reach `board.card.untitled` through
     `cardDisplayTitle`, and `BulkCardActionsDialog.printableCardTitle` reuses
     the KEY (it must keep its own trim, because its value leaves the file on a
     mutation payload). `Untitled list` / `Untitled board` /
     `Untitled workspace` / `Untitled item` each gain one author, one per
     namespace, which is this catalogue's own settled convention. `Send to
     board` gains a THIRD author knowingly: `FR-EXP-010` clicks the archived
     ROW's button and then asserts the DIALOG's, so the two are on screen at
     once. `Cancel`, `Done`, `Board`, `List`, `Position`, `Top of list`,
     `Bottom of list`, `Destination board`, `Destination list`, `No open lists`,
     `Archive`, `Move`, `Copy`, `Merge`, `Private`, `Public`, `Workspace`,
     `Visibility`, `Labels`, `Members`, `Attachments`, `Dates`, `Checklists`,
     `Custom fields` and `Due date` are all separate keys over separate DOM
     nodes — the `boardTable.heading` ruling, not the `boardMap.viewLabel`
     defect. `inbox.file.*` in particular is a ROUTE namespace these components
     must not reach into, however identical `InboxFileDialog`'s controls look.

     ⚠ `copyMove.bulk.title` and `copyMove.listOps.bulkRegion` are TWO keys for
     one English string INSIDE this namespace, declared on purpose: a `Dialog`
     accessible name in one file and a `<section aria-label>` in another. The
     pin asserts exactly two `copyMove.*` authors of `Bulk actions`, so the
     duplicate cannot silently become three.

     ⚠ FOUR JOINS BECAME WHOLE SLOTTED VALUES, on the
     `profile.connectedTools.scopeTrigger` ruling that word order and separators
     are language-specific: `copyMove.boardOption` (an ASCII hyphen-minus with
     one space either side — NOT an en dash; eight e2e specs read those
     `<option>` bytes), `copyMove.bulk.willMove` / `.willCopy` (the source
     interpolated the PARTICIPLE into the sentence), and
     `copyMove.reconciliation.fieldMatchedNames` / `.fieldConflict`.

     Every value below is byte-preserved from the source it replaces: three
     ASCII dots in the four `...ing` busy labels, one `U+2026` in
     `copyMove.restore.busy`, ASCII double quotes in `copyMove.restore.description`
     and an ASCII apostrophe in `copyMove.board.visibility.privateBody`. */
  'copyMove.cancel': { kind: 'text', value: 'Cancel' },
  'copyMove.done': { kind: 'text', value: 'Done' },
  'copyMove.closeCopy': { kind: 'text', value: 'Cancel copy' },
  'copyMove.titleRequired': { kind: 'text', value: 'Enter a title for the copy.' },
  'copyMove.copyTitleLabel': { kind: 'text', value: 'Copy title' },
  'copyMove.loadingBoards': {
    kind: 'text',
    value: 'Destination boards are still loading.',
  },
  'copyMove.chooseBoardFirst': {
    kind: 'text',
    value: 'Choose a destination board first.',
  },
  'copyMove.destinationBoardLabel': { kind: 'text', value: 'Destination board' },
  'copyMove.destinationListLabel': { kind: 'text', value: 'Destination list' },
  /* ⚠ ONE SPACE, ASCII HYPHEN-MINUS, ONE SPACE. */
  'copyMove.boardOption': { kind: 'text', value: '{board} - {workspace}' },
  'copyMove.noBoards.title': { kind: 'text', value: 'No destination boards' },
  'copyMove.noBoards.body': {
    kind: 'text',
    value: 'Boards where you are not a member are not offered here.',
  },
  'copyMove.noOpenLists': { kind: 'text', value: 'No open lists' },
  'copyMove.positionLabel': { kind: 'text', value: 'Position' },
  'copyMove.position.topOfList': { kind: 'text', value: 'Top of list' },
  'copyMove.position.bottomOfList': { kind: 'text', value: 'Bottom of list' },
  /* `{card}` carries a card title — product data, never translated. The copy
     dialogs give it the `board.card.untitled` fallback through
     `cardDisplayTitle`; `CrossBoardMoveDialog` passes the raw title, which is
     what it did before this block and is preserved rather than fixed. */
  'copyMove.position.afterCard': { kind: 'text', value: 'After {card}' },
  'copyMove.untitledList': { kind: 'text', value: 'Untitled list' },
  'copyMove.untitledBoard': { kind: 'text', value: 'Untitled board' },
  'copyMove.untitledWorkspace': { kind: 'text', value: 'Untitled workspace' },
  'copyMove.untitledItem': { kind: 'text', value: 'Untitled item' },
  /* `CardCopyDialog.tsx`. ⚠ `Copy {title}` is THREE keys across this block, one
     per subject kind: German wants *Karte kopieren* / *Board kopieren* /
     *Liste kopieren*, and the slot carries an instance name, never the noun. */
  'copyMove.card.title': { kind: 'text', value: 'Copy {title}' },
  'copyMove.card.description': {
    kind: 'text',
    value: 'Choose where the new card goes and what it keeps.',
  },
  'copyMove.card.busy': { kind: 'text', value: 'Copying card...' },
  'copyMove.card.submit': { kind: 'text', value: 'Copy card' },
  'copyMove.card.needsList': {
    kind: 'text',
    value: 'Create a list on the destination board before copying this card.',
  },
  'copyMove.card.resultSummary': { kind: 'text', value: 'After copying' },
  'copyMove.card.copiedTitle': { kind: 'text', value: 'Card copied' },
  'copyMove.card.copiedBody': { kind: 'text', value: 'The new card is ready.' },
  'copyMove.card.copyFailed': {
    kind: 'text',
    value: 'Could not copy the card {title}.',
  },
  'copyMove.card.noListsBody': {
    kind: 'text',
    value: 'Create a list on {board} before copying this card there.',
  },
  /* ⚠ UNREACHABLE TODAY, and routed anyway. The `EmptyState` that carries the
     `{board}` slot renders only when a destination board IS selected, so the
     `??` arm never runs. A deleted site is a site the pin's count guard cannot
     hold — `boardSettings.selfJoin.privateBody`'s precedent. */
  'copyMove.card.thatBoard': { kind: 'text', value: 'that board' },
  'copyMove.card.keepLegend': { kind: 'text', value: 'Keep choices' },
  'copyMove.card.keepChecklists': { kind: 'text', value: 'Keep checklists' },
  'copyMove.card.keepLabels': { kind: 'text', value: 'Keep labels' },
  'copyMove.card.keepMembers': { kind: 'text', value: 'Keep members' },
  'copyMove.card.keepAttachments': { kind: 'text', value: 'Keep attachments' },
  'copyMove.card.keepDates': { kind: 'text', value: 'Keep dates' },
  /* `BoardCopyDialog.tsx`. */
  'copyMove.board.title': { kind: 'text', value: 'Copy {title}' },
  'copyMove.board.description': {
    kind: 'text',
    value: 'Choose the destination workspace, visibility and card contents.',
  },
  'copyMove.board.busy': { kind: 'text', value: 'Copying board...' },
  'copyMove.board.submit': { kind: 'text', value: 'Copy board' },
  'copyMove.board.titleLabel': { kind: 'text', value: 'Board copy title' },
  'copyMove.board.loadingWorkspaces': {
    kind: 'text',
    value: 'Destination workspaces are still loading.',
  },
  /* One key at two mutually exclusive sites in one file: the submit button's
     `disabledReason` and the `EmptyState` body that replaces the select. */
  'copyMove.board.needsWorkspace': {
    kind: 'text',
    value: 'Join or create a workspace before copying this board.',
  },
  'copyMove.board.copiedTitle': { kind: 'text', value: 'Board copied' },
  'copyMove.board.copiedReady': { kind: 'text', value: 'The copied board is ready.' },
  'copyMove.board.copiedCount': {
    kind: 'plural',
    forms: { one: 'Copied 1 card.', other: 'Copied {count} cards.' },
  },
  'copyMove.board.copyFailed': {
    kind: 'text',
    value: 'Could not copy the board {title}.',
  },
  'copyMove.board.openCopy': { kind: 'text', value: 'Open copied board' },
  'copyMove.board.workspaceLabel': { kind: 'text', value: 'Destination workspace' },
  'copyMove.board.noWorkspaces': { kind: 'text', value: 'No destination workspaces' },
  /* The visibility the COPY will be created with — a different enum arm on a
     different surface from `boardSettings.heading.visibility` (a live board's
     own setting) and from `workspaceMembers.*` (a workspace-wide policy). Each
     label names both a radio `aria-label` and its visible span, one key at two
     co-rendered nodes, which is WCAG 2.5.3 rather than a duplicate. */
  'copyMove.board.visibility': { kind: 'text', value: 'Visibility' },
  'copyMove.board.visibility.private': { kind: 'text', value: 'Private' },
  'copyMove.board.visibility.privateBody': {
    kind: 'text',
    value: "Only people added to the copied board's member list can see it.",
  },
  'copyMove.board.visibility.workspace': { kind: 'text', value: 'Workspace' },
  'copyMove.board.visibility.workspaceBody': {
    kind: 'text',
    value: 'Every member of the destination workspace can see the copied board.',
  },
  'copyMove.board.visibility.public': { kind: 'text', value: 'Public' },
  'copyMove.board.visibility.publicBody': {
    kind: 'text',
    value: 'Anyone with the link can read the copied board.',
  },
  'copyMove.board.keepCards': { kind: 'text', value: 'Keep cards' },
  'copyMove.board.keepCardsBody': {
    kind: 'text',
    value: 'Copy open cards with titles and descriptions only.',
  },
  /* `ListCopyDialog.tsx`. */
  'copyMove.list.title': { kind: 'text', value: 'Copy {title}' },
  'copyMove.list.description': {
    kind: 'text',
    value: 'Choose the copied list title, destination board, and position.',
  },
  'copyMove.list.busy': { kind: 'text', value: 'Copying list...' },
  'copyMove.list.submit': { kind: 'text', value: 'Copy list' },
  'copyMove.list.copying': { kind: 'text', value: 'The list is being copied.' },
  'copyMove.list.copiedTitle': { kind: 'text', value: 'List copied' },
  'copyMove.list.copyFailed': {
    kind: 'text',
    value: 'Could not copy the list {title}.',
  },
  /* ⚠ TWO NODE KINDS, MUTUALLY EXCLUSIVE: the reconciliation report's `summary`
     (which becomes a `Notice` TITLE) when a report is rendered, and a `Notice`
     BODY when it is not. One sentence, one key. */
  'copyMove.list.copiedSummary': {
    kind: 'plural',
    forms: { one: '1 card copied', other: '{count} cards copied' },
  },
  'copyMove.list.position.first': { kind: 'text', value: 'First list on board' },
  'copyMove.list.position.top': { kind: 'text', value: 'Top of board' },
  'copyMove.list.position.bottom': { kind: 'text', value: 'Bottom of board' },
  'copyMove.list.position.after': { kind: 'text', value: 'After {list}' },
  /* `CrossBoardMoveDialog.tsx`. ⚠ `Move {title}` is TWO keys, byte-identical
     today, and that is deliberately the opposite of "preserve the source's
     shape": the file already branches on card-versus-list mode at three
     adjacent nodes, so a fourth branch costs one ternary, changes no English
     byte, and keeps the subject-noun rule readable in one direction only. */
  'copyMove.move.cardTitle': { kind: 'text', value: 'Move {title}' },
  'copyMove.move.listTitle': { kind: 'text', value: 'Move {title}' },
  'copyMove.move.cardDescription': {
    kind: 'text',
    value: 'Choose the board, list, and card position.',
  },
  'copyMove.move.listDescription': {
    kind: 'text',
    value: 'Choose the board that should receive this list. The list will be appended.',
  },
  'copyMove.move.close': { kind: 'text', value: 'Cancel move' },
  'copyMove.move.busyCard': { kind: 'text', value: 'Moving card...' },
  'copyMove.move.busyList': { kind: 'text', value: 'Moving list...' },
  'copyMove.move.submitCard': { kind: 'text', value: 'Move card' },
  'copyMove.move.submitList': { kind: 'text', value: 'Move list' },
  'copyMove.move.needsList': {
    kind: 'text',
    value: 'Create a list on the destination board before moving this card.',
  },
  'copyMove.move.differentBoard': {
    kind: 'text',
    value: 'Choose a different board for this list.',
  },
  'copyMove.move.previewFailed': {
    kind: 'text',
    value: 'Move preview could not be loaded. Try again.',
  },
  'copyMove.move.previewUnavailable': { kind: 'text', value: 'Preview unavailable' },
  'copyMove.move.previewSummary': { kind: 'text', value: 'Before moving' },
  'copyMove.move.search': { kind: 'text', value: 'Search destination boards' },
  'copyMove.move.noMatchTitle': { kind: 'text', value: 'No boards match' },
  'copyMove.move.noMatchBody': {
    kind: 'text',
    value: 'Widen the board search to choose a destination.',
  },
  'copyMove.move.noListsBody': {
    kind: 'text',
    value: 'Create a list on {board} before this card can move there.',
  },
  'copyMove.move.createList': { kind: 'text', value: 'Create list' },
  'copyMove.move.listAppended': {
    kind: 'text',
    value:
      'This list will be appended to the destination board. No list position is chosen in this release.',
  },
  /* `MoveReconciliationReport.tsx`. The three group headings are `<h3>`s in a
     report, which is why they are not `copyMove.bulk.keep.*` (checkbox labels
     for what a copy carries) and not `cardBack.*` (card-back sections). */
  'copyMove.reconciliation.complete': { kind: 'text', value: 'Move complete' },
  'copyMove.reconciliation.cardsMoved': {
    kind: 'plural',
    forms: { one: '1 card moved', other: '{count} cards moved' },
  },
  'copyMove.reconciliation.nothingChanged': {
    kind: 'text',
    value: 'No labels, members, or custom fields needed to change.',
  },
  'copyMove.reconciliation.labels': { kind: 'text', value: 'Labels' },
  'copyMove.reconciliation.members': { kind: 'text', value: 'Members' },
  'copyMove.reconciliation.customFields': { kind: 'text', value: 'Custom fields' },
  'copyMove.reconciliation.labelApplied': {
    kind: 'text',
    value: 'Applied existing destination label',
  },
  'copyMove.reconciliation.labelCreated': {
    kind: 'text',
    value: 'Created destination label',
  },
  'copyMove.reconciliation.labelDropped': { kind: 'text', value: 'Dropped source label' },
  'copyMove.reconciliation.memberRemoved': { kind: 'text', value: 'Removed card member' },
  'copyMove.reconciliation.fieldMatched': { kind: 'text', value: 'Matched field' },
  /* Two custom-field NAMES joined by an English preposition — a join, so the
     whole sentence is one value with two slots. */
  'copyMove.reconciliation.fieldMatchedNames': {
    kind: 'text',
    value: '{source} to {destination}',
  },
  'copyMove.reconciliation.fieldCreated': { kind: 'text', value: 'Created field' },
  'copyMove.reconciliation.optionCreated': { kind: 'text', value: 'Created option' },
  'copyMove.reconciliation.fieldNotApplied': {
    kind: 'text',
    value: 'Could not apply source field',
  },
  'copyMove.reconciliation.fieldConflict': {
    kind: 'text',
    value: '{source} conflicts with {existing}',
  },
  /* `ArchiveRestoreDestinationDialog.tsx`. */
  'copyMove.restore.title': { kind: 'text', value: 'Choose a list' },
  /* ⚠ ASCII DOUBLE QUOTES around the slot, verbatim from the source. */
  'copyMove.restore.description': {
    kind: 'text',
    value: 'Pick where "{title}" should return.',
  },
  'copyMove.restore.close': { kind: 'text', value: 'Cancel restore' },
  /* ⚠ THE THIRD AUTHOR of `Send to board`, closing a residual another slice
     returned: the archived ROW's button and this DIALOG's button are on screen
     at once, so one key would rename both. */
  'copyMove.restore.submit': { kind: 'text', value: 'Send to board' },
  'copyMove.restore.chooseList': {
    kind: 'text',
    value: 'Choose a destination list first.',
  },
  /* ⚠ ONE `U+2026`, not the three ASCII dots its `Copying …` siblings carry. */
  'copyMove.restore.busy': { kind: 'text', value: 'Restoring…' },
  'copyMove.restore.noListsBody': {
    kind: 'text',
    value: 'Create or restore a list before this card can return to the board.',
  },
  /* `PasteCardsDialog.tsx`. */
  'copyMove.paste.title': { kind: 'text', value: 'Create cards from pasted text' },
  'copyMove.paste.description': { kind: 'text', value: 'Paste into {list}.' },
  'copyMove.paste.close': { kind: 'text', value: 'Cancel pasted cards' },
  'copyMove.paste.singleCard': { kind: 'text', value: 'Create one card' },
  'copyMove.paste.submit': {
    kind: 'plural',
    forms: { one: 'Create 1 card', other: 'Create {count} cards' },
  },
  'copyMove.paste.needsLine': {
    kind: 'text',
    value: 'Paste at least one nonblank line.',
  },
  'copyMove.paste.errorTitle': { kind: 'text', value: 'Cards were not created' },
  'copyMove.paste.body': {
    kind: 'text',
    value: 'Create one card for each nonblank line, or keep the paste as one card.',
  },
  /* `BulkCardActionsDialog.tsx`. */
  'copyMove.bulk.title': { kind: 'text', value: 'Bulk actions' },
  'copyMove.bulk.selectedCount': {
    kind: 'plural',
    forms: { one: '1 selected card', other: '{count} selected cards' },
  },
  'copyMove.bulk.close': { kind: 'text', value: 'Close bulk actions' },
  'copyMove.bulk.errorTitle': { kind: 'text', value: 'Bulk action failed' },
  /* The four mode buttons sit UNDER the selection toolbar that opened this
     dialog, so `boardFace.selection.*` and these are in the DOM at once. */
  'copyMove.bulk.mode.archive': { kind: 'text', value: 'Archive' },
  'copyMove.bulk.mode.move': { kind: 'text', value: 'Move' },
  'copyMove.bulk.mode.copy': { kind: 'text', value: 'Copy' },
  'copyMove.bulk.mode.merge': { kind: 'text', value: 'Merge' },
  /* ⚠ Each of the four names the primary footer button AND the consequence
     `Notice` title — two co-rendered nodes the source already builds from one
     expression. They are resolved by an explicit four-arm ternary, never by
     `t('copyMove.bulk.action.' + mode)`: a concatenated key type-checks,
     renders correctly and hides all four from the `t('…')` anchor. */
  'copyMove.bulk.action.archive': {
    kind: 'plural',
    forms: { one: 'Archive 1 selected card', other: 'Archive {count} selected cards' },
  },
  'copyMove.bulk.action.move': {
    kind: 'plural',
    forms: { one: 'Move 1 selected card', other: 'Move {count} selected cards' },
  },
  'copyMove.bulk.action.copy': {
    kind: 'plural',
    forms: { one: 'Copy 1 selected card', other: 'Copy {count} selected cards' },
  },
  'copyMove.bulk.action.merge': {
    kind: 'plural',
    forms: { one: 'Merge 1 selected card', other: 'Merge {count} selected cards' },
  },
  'copyMove.bulk.archiveBody': {
    kind: 'text',
    value: 'Every selected card will be archived and the selection will clear.',
  },
  /* One key at two SIMULTANEOUS sites — the `Notice` body and the submit
     `disabledReason` — which `BulkCardActionsDialog.test.tsx` asserts by
     counting two matches. */
  'copyMove.bulk.mergeNeedsTwo': {
    kind: 'text',
    value: 'Select exactly two cards to merge.',
  },
  'copyMove.bulk.mergeBody': {
    kind: 'text',
    value:
      'The new card will combine {target} and {source}. Both original cards will be archived. Comments, dates, attachments, cover, location, custom fields, votes, stickers and watch state are not carried to the merged card.',
  },
  'copyMove.bulk.chooseDestination': {
    kind: 'text',
    value: 'Choose a destination before continuing.',
  },
  /* ⚠ TWO WHOLE SENTENCES, and the finding of this file. The source built
     `The selected cards will be ${moved|copied} to ${list}.` — a participle
     interpolated into a sentence, which no catalogue can translate, because
     agreement, word order and the participle's own form are language-specific. */
  'copyMove.bulk.willMove': {
    kind: 'text',
    value: 'The selected cards will be moved to {list}.',
  },
  'copyMove.bulk.willCopy': {
    kind: 'text',
    value: 'The selected cards will be copied to {list}.',
  },
  'copyMove.bulk.needsSelection': {
    kind: 'text',
    value: 'Select at least one card first.',
  },
  /* ⚠ A FULL STOP AND NO `first` — deliberately NOT `copyMove.chooseBoardFirst`,
     whose bytes differ. */
  'copyMove.bulk.needsBoard': { kind: 'text', value: 'Choose a destination board.' },
  'copyMove.bulk.needsList': {
    kind: 'text',
    value: 'Create a list on the destination board first.',
  },
  'copyMove.bulk.boardLabel': { kind: 'text', value: 'Board' },
  'copyMove.bulk.listLabel': { kind: 'text', value: 'List' },
  'copyMove.bulk.keepLegend': { kind: 'text', value: 'Copy contents' },
  'copyMove.bulk.keep.checklists': { kind: 'text', value: 'Checklists' },
  'copyMove.bulk.keep.labels': { kind: 'text', value: 'Labels' },
  'copyMove.bulk.keep.members': { kind: 'text', value: 'Members' },
  'copyMove.bulk.keep.attachments': { kind: 'text', value: 'Attachments' },
  'copyMove.bulk.keep.dates': { kind: 'text', value: 'Dates' },
  /* `ListOperationsDialog.tsx`. */
  'copyMove.listOps.title': { kind: 'text', value: 'List operations for {list}' },
  'copyMove.listOps.description': {
    kind: 'plural',
    forms: {
      one: '1 open card in this list.',
      other: '{count} open cards in this list.',
    },
  },
  'copyMove.listOps.close': { kind: 'text', value: 'Cancel list operations' },
  'copyMove.listOps.noticeTitle': { kind: 'text', value: 'Sorting rewrites card order' },
  'copyMove.listOps.noticeBody': {
    kind: 'text',
    value: 'Previous manual order is not kept as a saved state.',
  },
  'copyMove.listOps.sortRegion': { kind: 'text', value: 'Sort cards' },
  'copyMove.listOps.sortHeading': { kind: 'text', value: 'Sort by' },
  /* ⚠ A WIRE→LABEL MAP WITH NO RECORD BEHIND IT. `title`, `due_date`,
     `created_newest` and `created_oldest` are `ListCardSortBody['mode']` and
     stay LITERAL in the `onSort` argument; only the button text is routed. */
  'copyMove.listOps.sort.title': { kind: 'text', value: 'Card name' },
  'copyMove.listOps.sort.dueDate': { kind: 'text', value: 'Due date' },
  'copyMove.listOps.sort.newest': { kind: 'text', value: 'Newest first' },
  'copyMove.listOps.sort.oldest': { kind: 'text', value: 'Oldest first' },
  /* ⚠ THE SECOND `Bulk actions` IN THIS BLOCK, on purpose — a `<section
     aria-label>` here against `copyMove.bulk.title`'s `Dialog` name. */
  'copyMove.listOps.bulkRegion': { kind: 'text', value: 'Bulk actions' },
  'copyMove.listOps.archiveAll': {
    kind: 'plural',
    forms: { one: 'Archive 1 card', other: 'Archive {count} cards' },
  },
  'copyMove.listOps.moveAll': {
    kind: 'plural',
    forms: { one: 'Move 1 card', other: 'Move {count} cards' },
  },
  'copyMove.listOps.noOpenCards': {
    kind: 'text',
    value: 'This list has no open cards.',
  },
  'copyMove.listOps.announce.sorted': {
    kind: 'text',
    value: 'Cards in {list} sorted.',
  },
  'copyMove.listOps.announce.archived': {
    kind: 'plural',
    forms: {
      one: '1 card archived from {list}.',
      other: '{count} cards archived from {list}.',
    },
  },
  'copyMove.listOps.announce.moved': {
    kind: 'plural',
    forms: {
      one: '1 card moved to {list}.',
      other: '{count} cards moved to {list}.',
    },
  },
  'copyMove.listOps.announce.added': {
    kind: 'plural',
    forms: {
      one: '1 card added to {list}.',
      other: '{count} cards added to {list}.',
    },
  },
  /* ==================================================================
     FE-80.1 — BOARD ADMIN CHROME. `BoardTitle.tsx`, `ArchiveDeleteConfirm.tsx`,
     `BoardDangerConfirm.tsx`, `BoardInviteLinkSection.tsx`,
     `BoardActivitySection.tsx`, `BoardBackgroundPicker.tsx`.
     94 keys, 93 `text` and exactly ONE `plural`
     (`boardAdmin.archiveDelete.body.list`).
     ⚠ 92 -> 94 at fan-out 13: `boardAdmin.activity.signInTitle` and
     `…signInBody`, the signed-out state `FE-61.2` returned as a residual.
     ⚠ NOTHING under `boardAdmin.visibility.*` — board visibility is
     `BoardVisibilityControl.tsx`, which belongs to the board-templates slice.
     ================================================================== */
  /* `BoardTitle.tsx`. */
  'boardAdmin.title.label': { kind: 'text', value: 'Board title' },
  /* `ArchiveDeleteConfirm.tsx` — permanent deletion FROM the archive. Distinct
     from `boardAdmin.danger.*` below, which destroys the whole board; the two
     dialogs share the words Cancel and `Deleting…` and share NO key. */
  'boardAdmin.archiveDelete.untitledList': { kind: 'text', value: 'Untitled list' },
  'boardAdmin.archiveDelete.untitledCard': { kind: 'text', value: 'Untitled card' },
  'boardAdmin.archiveDelete.title.list': {
    kind: 'text',
    value: 'Delete archived list?',
  },
  'boardAdmin.archiveDelete.title.card': {
    kind: 'text',
    value: 'Delete archived card?',
  },
  /* ⚠ THE ONLY PLURAL IN THIS BLOCK, and the sentence really does agree with
     the count — it reproduces `cardCountText`'s `1 card` / `{count} cards`.
     ⚠ BOTH ARGUMENT CHANNELS ARE REQUIRED AT THE CALL SITE:
     `t(key, { name, count }, count)`. `interfaceMessage` THROWS on an unfilled
     slot, so passing `{ name }` alone throws on every list whose card count is
     not 1 — inside a destructive confirmation. */
  'boardAdmin.archiveDelete.body.list': {
    kind: 'plural',
    forms: {
      one: 'This permanently deletes "{name}" and 1 card. This cannot be undone.',
      other:
        'This permanently deletes "{name}" and {count} cards. This cannot be undone.',
    },
  },
  'boardAdmin.archiveDelete.body.card': {
    kind: 'text',
    value: 'This permanently deletes "{name}". This cannot be undone.',
  },
  'boardAdmin.archiveDelete.close': { kind: 'text', value: 'Cancel deletion' },
  'boardAdmin.archiveDelete.cancel': { kind: 'text', value: 'Cancel' },
  'boardAdmin.archiveDelete.confirm': { kind: 'text', value: 'Delete permanently' },
  'boardAdmin.archiveDelete.busy': { kind: 'text', value: 'Deleting…' },
  'boardAdmin.archiveDelete.note': {
    kind: 'text',
    value: 'The archive has no undo or support recovery path for this deletion.',
  },
  /* `BoardDangerConfirm.tsx` — close and delete THE BOARD.
     ⚠ The two bodies take the SENTINEL-SPLIT IDIOM set by `BoardPowerUpList.tsx`,
     whose own comment names `BoardDangerConfirm.tsx:92` and `:104` as the next
     two sites and says: copy this, do not re-decide it. One whole-sentence key
     with a `{board}` slot, split at render on `String.fromCharCode(0)` so the
     `<strong>` emphasis survives — and the ONE-PART FALLBACK ARM IS NOT
     OPTIONAL: a translation that drops `{board}` would otherwise silently
     delete the board's name from a destructive confirmation. */
  'boardAdmin.danger.close.title': { kind: 'text', value: 'Close board?' },
  'boardAdmin.danger.close.body': {
    kind: 'text',
    value:
      'Close {board}? It moves to Closed boards and keeps every list and card — a board admin can reopen it at any time.',
  },
  'boardAdmin.danger.close.confirm': { kind: 'text', value: 'Close board' },
  'boardAdmin.danger.close.busy': { kind: 'text', value: 'Closing…' },
  'boardAdmin.danger.delete.title': {
    kind: 'text',
    value: 'Delete board permanently?',
  },
  'boardAdmin.danger.delete.body': {
    kind: 'text',
    value:
      'Permanently delete {board}? This removes every list and card on it for everyone, and cannot be undone.',
  },
  'boardAdmin.danger.delete.confirm': {
    kind: 'text',
    value: 'Delete board permanently',
  },
  'boardAdmin.danger.delete.busy': { kind: 'text', value: 'Deleting…' },
  'boardAdmin.danger.cancel': { kind: 'text', value: 'Cancel' },
  /* `BoardInviteLinkSection.tsx`.
     ⚠ `role.*` IS THE INVITE ROLE, NOT A VISIBILITY. These are the lowercase
     mid-sentence forms `ROLE_LABELS` holds, read only by `invite.urlHint`. The
     capitalised standalone role words are `boardMembers.role.option.*` and are
     a different slice's. */
  'boardAdmin.invite.role.admin': { kind: 'text', value: 'board admin' },
  'boardAdmin.invite.role.normal': { kind: 'text', value: 'board member' },
  'boardAdmin.invite.role.observer': { kind: 'text', value: 'observer' },
  'boardAdmin.invite.checking': {
    kind: 'text',
    value: 'Checking this board invite link…',
  },
  'boardAdmin.invite.reason.cannotManage': {
    kind: 'text',
    value: 'Only board members with invite rights can manage invite links.',
  },
  'boardAdmin.invite.reason.clipboard': {
    kind: 'text',
    value: 'Clipboard access is not available.',
  },
  'boardAdmin.invite.copied': { kind: 'text', value: 'Invite link copied.' },
  'boardAdmin.invite.copyFailed': {
    kind: 'text',
    value: 'Invite link could not be copied.',
  },
  /* ⚠ ONE KEY, TWO SPELLINGS OF THE SAME REGION NAME. `showHeading` picks
     `aria-labelledby` (the `<h2>`) or `aria-label` for the SAME `<section>`. */
  'boardAdmin.invite.heading': { kind: 'text', value: 'Invite link' },
  'boardAdmin.invite.description': {
    kind: 'text',
    value: 'Anyone holding the active invite link can join this board.',
  },
  'boardAdmin.invite.loading': {
    kind: 'text',
    value: 'Checking the active invite link…',
  },
  'boardAdmin.invite.none': { kind: 'text', value: 'No active invite link.' },
  'boardAdmin.invite.urlLabel': { kind: 'text', value: 'Invite URL' },
  /* ⚠ A SHIPPED COPY DEFECT, PRESERVED. `{role}` resolves to `observer` for an
     observer link, so this reads "can join as a observer" today. Correcting the
     article is a COPY change, not a routing one; reported as a residual. */
  'boardAdmin.invite.urlHint': {
    kind: 'text',
    value: 'Anyone holding this link can join as a {role}.',
  },
  'boardAdmin.invite.copy': { kind: 'text', value: 'Copy link' },
  'boardAdmin.invite.revoke': { kind: 'text', value: 'Revoke link' },
  'boardAdmin.invite.generate': { kind: 'text', value: 'Generate link' },
  'boardAdmin.invite.generateObserver': {
    kind: 'text',
    value: 'Generate observer link',
  },
  /* ⚠ `revokeDialog.title` and `revokeDialog.confirm` carry the SAME words in
     two roles — a `Dialog` name and the button that performs it. Two keys. */
  'boardAdmin.invite.revokeDialog.title': { kind: 'text', value: 'Revoke invite link' },
  'boardAdmin.invite.revokeDialog.description': {
    kind: 'text',
    value:
      'The current URL will stop admitting new members. People who already joined keep their board membership.',
  },
  'boardAdmin.invite.revokeDialog.cancel': { kind: 'text', value: 'Cancel' },
  'boardAdmin.invite.revokeDialog.confirm': {
    kind: 'text',
    value: 'Revoke invite link',
  },
  'boardAdmin.invite.revokeDialog.none': {
    kind: 'text',
    value: 'There is no active invite link to revoke.',
  },
  'boardAdmin.invite.revokeDialog.note': {
    kind: 'text',
    value:
      'Revoke this invite link only after everyone who still needs access has joined another way.',
  },
  /* `BoardActivitySection.tsx` — the board's feed, not the card's.
     ⚠ SEVEN OF THE FOURTEEN, NOT ALL FOURTEEN, pair with the card back.
     `error.title`, `empty.commentsTitle`, `empty.activityTitle`, `loadingMore`,
     `loadMore`, `linkCopied` and `linkCopyFailed` each have a co-author under
     `cardBack.comments.*`, and not one of the seven shares its key: two
     surfaces, two translators' choices. The other seven do not pair with the
     card back at all — `landmark` shares `Activity` with four keys outside it,
     `error.retry` shares `Retry` with two, `filter.comments` shares `Comments`
     with one, and `filter.all`, `error.body`, `empty.commentsBody` and
     `empty.activityBody` are the sole author of their value. Every count in
     this paragraph is asserted in `board-admin.routing.test.ts` rather than
     described here, because the first version of this comment claimed all
     fourteen and was half wrong. */
  'boardAdmin.activity.landmark': { kind: 'text', value: 'Activity' },
  /* ⚠ TWO KEYS, NOT THREE, AND THE MISSING THIRD IS THE POINT.
     `w.$workspaceId.activity.tsx` ships this same state as
     `workspaceSettings.activity.{signInTitle,signInBody,signInAction}`; the
     names here match it deliberately, and the ACTION is deliberately absent.
     Its value would be `'Sign in'`, which already has seventeen authors and an
     EXACT author list asserted in two pin files this slice does not own
     (`workspace-settings.routing.test.ts`, `workspace-members.routing.test.ts`).
     The shell's banner offers `Sign in` on this very page, so the affordance is
     not lost — only the second copy of it. See `BoardActivitySection.tsx`. */
  'boardAdmin.activity.signInTitle': {
    kind: 'text',
    value: 'Sign in to see board activity',
  },
  /* ⚠ Deliberately NOT a paraphrase of the workspace body ("Workspace activity
     is visible only to the people in the workspace"), because the board case is
     the opposite shape and saying so is the whole value of the sentence: this
     board IS world-readable — `FR-PERM-010` AC1 — and the feed is the one part
     of it that is not, because it names every actor of every change. */
  'boardAdmin.activity.signInBody': {
    kind: 'text',
    value:
      'Board activity names who changed what, so it is shown only to signed-in visitors.',
  },
  /* ⚠ WIRE VALUES STAY LITERAL. `'all'` and `'comments'` are `ActivityFilter`
     and remain unrouted in `setFilter`; only the button text is routed. */
  'boardAdmin.activity.filter.all': { kind: 'text', value: 'All' },
  'boardAdmin.activity.filter.comments': { kind: 'text', value: 'Comments' },
  'boardAdmin.activity.error.title': {
    kind: 'text',
    value: 'Activity could not be loaded',
  },
  'boardAdmin.activity.error.body': {
    kind: 'text',
    value: 'Try again from the board menu.',
  },
  'boardAdmin.activity.error.retry': { kind: 'text', value: 'Retry' },
  'boardAdmin.activity.empty.commentsTitle': {
    kind: 'text',
    value: 'No comments yet',
  },
  'boardAdmin.activity.empty.activityTitle': {
    kind: 'text',
    value: 'No activity yet',
  },
  'boardAdmin.activity.empty.commentsBody': {
    kind: 'text',
    value: 'Comments from across the board will appear here.',
  },
  'boardAdmin.activity.empty.activityBody': {
    kind: 'text',
    value: 'Board changes will appear here.',
  },
  /* ⚠ THREE ASCII DOTS, NOT U+2026 — unlike `Closing…` and `Deleting…`
     above. Both spellings ship today; both are preserved byte for byte. */
  'boardAdmin.activity.loadingMore': { kind: 'text', value: 'Loading activity...' },
  'boardAdmin.activity.loadMore': { kind: 'text', value: 'Load more activity' },
  'boardAdmin.activity.linkCopied': { kind: 'text', value: 'Activity link copied.' },
  'boardAdmin.activity.linkCopyFailed': {
    kind: 'text',
    value: 'Could not copy activity link.',
  },
  'boardAdmin.background.error.contentType': {
    kind: 'text',
    value: 'Upload a PNG, JPEG, WebP or GIF background.',
  },
  'boardAdmin.background.error.tooLarge': {
    kind: 'text',
    value: 'Board background uploads are limited to 10 MB.',
  },
  'boardAdmin.background.error.noFile': {
    kind: 'text',
    value: 'Choose a PNG, JPEG, WebP or GIF background up to 10 MB.',
  },
  /* ⚠ THE COLON IS PART OF THE VALUE and the space after it is NOT — the label
     is followed by a `<span>` holding `boardBackgroundLabel(t, background)`;
     the shared fallback label itself lives in `primitives.boardBackground.*`. */
  'boardAdmin.background.current': { kind: 'text', value: 'Current background:' },
  'boardAdmin.background.textGroup': { kind: 'text', value: 'Board text' },
  'boardAdmin.background.text.light': { kind: 'text', value: 'Use light board text' },
  'boardAdmin.background.text.dark': { kind: 'text', value: 'Use dark board text' },
  /* One key each for `colours`, `customOptions`, `photos` and `customs`: the
     `role="group"` `aria-label` and the `<h3>` beneath it are one group name. */
  'boardAdmin.background.colours': { kind: 'text', value: 'Solid colours' },
  'boardAdmin.background.swatch': { kind: 'text', value: 'Use {name} background' },
  'boardAdmin.background.customOptions': {
    kind: 'text',
    value: 'Custom background options',
  },
  /* ⚠ FOUR KEYS FOR TWO WORDS, ON PURPOSE. `renderMode.*` labels the BOARD'S
     CURRENT custom background (group `Custom background options`);
     `upload.*` below labels the PENDING UPLOAD (group `Upload render mode`).
     Two different controls under two different group names. */
  'boardAdmin.background.renderMode.fullCover': { kind: 'text', value: 'Full cover' },
  'boardAdmin.background.renderMode.tiled': { kind: 'text', value: 'Tiled' },
  'boardAdmin.background.photoSearch': { kind: 'text', value: 'Search stock photos' },
  'boardAdmin.background.photos': { kind: 'text', value: 'Stock photos' },
  'boardAdmin.background.photo': {
    kind: 'text',
    value: 'Use {name} photo background',
  },
  'boardAdmin.background.customs': { kind: 'text', value: 'Custom backgrounds' },
  'boardAdmin.background.customsNote': {
    kind: 'text',
    value: 'Uploaded images are visible to every member of boards that use them.',
  },
  'boardAdmin.background.uploadLabel': {
    kind: 'text',
    value: 'Upload custom background',
  },
  'boardAdmin.background.uploadRenderMode': {
    kind: 'text',
    value: 'Upload render mode',
  },
  'boardAdmin.background.upload.fullCover': { kind: 'text', value: 'Full cover' },
  'boardAdmin.background.upload.tiled': { kind: 'text', value: 'Tiled' },
  'boardAdmin.background.uploadTextGroup': {
    kind: 'text',
    value: 'Upload board text',
  },
  'boardAdmin.background.upload.lightText': { kind: 'text', value: 'Light text' },
  'boardAdmin.background.upload.darkText': { kind: 'text', value: 'Dark text' },
  'boardAdmin.background.uploadSubmit': { kind: 'text', value: 'Upload and use' },
  'boardAdmin.background.customsLoading': {
    kind: 'text',
    value: 'Loading custom backgrounds...',
  },
  'boardAdmin.background.customsEmpty': {
    kind: 'text',
    value: 'No custom backgrounds yet.',
  },
  /* `{date}` is `new Date(custom.createdAt).toLocaleDateString()`, already
     locale-aware; the sentence around it was not. */
  'boardAdmin.background.customLabel': {
    kind: 'text',
    value: 'Use custom background uploaded {date}',
  },
  'boardAdmin.background.customUploaded': { kind: 'text', value: 'Uploaded {date}' },
  /* ⚠ FOUR WHOLE SUMMARIES RATHER THAN A JOIN. The source composes
     `{mode} -{' '}{treatment}` from three nodes; a two-key join would fix the
     separator and the word order for every locale, which is the
     `POLICY_FIELD_LABELS` defect. The separator is a PLAIN HYPHEN here, not the
     em dash used in `boardAdmin.danger.close.body`; preserved as found. */
  'boardAdmin.background.customSummary.tiledLight': {
    kind: 'text',
    value: 'Tiled - light text',
  },
  'boardAdmin.background.customSummary.tiledDark': {
    kind: 'text',
    value: 'Tiled - dark text',
  },
  'boardAdmin.background.customSummary.fullCoverLight': {
    kind: 'text',
    value: 'Full cover - light text',
  },
  'boardAdmin.background.customSummary.fullCoverDark': {
    kind: 'text',
    value: 'Full cover - dark text',
  },
  /* ------------------------------------------------------------------------
     FE-58.1 — the board-menu sticker tray (`FR-CARD-032` AC1, `FR-EXP-027`
     AC1). TWO keys, and the count is the point: `BoardMenuPanel.tsx` reuses
     `boardMenu.reason.closedReadonly` and `boardMenu.reason.checkingBoardAccess`
     for the other two arms of the same ternary, exactly as the background and
     custom-field gates beside it do.

     ⚠ ONE KEY FOR THE ROOT ENTRY *AND* THE `<h2>`, on the
     `boardMenu.background` precedent. Both nodes live in the same panel, so a
     `getByText('Stickers')` is a strict-mode violation and every selector is
     by role — `FR-EXP-025.spec.ts:203`/`:204` is the shipped shape. A second
     key would hand a translator two words for one noun.

     ⚠ COLLISION-CHECKED against all fourteen pre-existing section headings and
     against the three strings `BoardMenuPanel.tsx`'s docstring names for
     Playwright's substring matching (`Close board`, `Delete board
     permanently`, `Back to board menu`): `Stickers` is a substring of none of
     them and none is a
     substring of it. It IS byte-identical to `cardBack.stickers.heading` and
     `cardBack.stickers.pickerTitle`, which is harmless and deliberate — those
     render in the card back's dialog, never in this panel, and no test pins
     the author count of the value `Stickers`.

     ⚠ NOT `cardBack.stickers.*`, though a value there already reads
     `Stickers`. That family's own comment above states the reach rule and its
     expiry — "the day a second surface consumes `lib/stickers.ts` — a
     board-face sticker tray … these keys must MOVE to `primitives.sticker.*`".
     That move is a cross-file rename over `CardStickerSection.tsx`, which this
     chunk's step does not own; borrowing a `cardBack.*` key into the board
     menu would deepen the debt rather than pay it. Recorded as a residual.

     ⚠ `editorStickers` and NOT a reuse of `editorBackground` /
     `editorCustomFields`: each names its own act, and a translator given
     "…can change the background." for a sticker refusal has been handed a
     wrong sentence rather than a terse one. */
  'boardMenu.stickers': { kind: 'text', value: 'Stickers' },
  'boardMenu.reason.editorStickers': {
    kind: 'text',
    value: 'Only a board member who can edit this board can place stickers.',
  },
  /* SA-9 — the platform admin console (`routes/admin.tsx`,
     `routes/admin.users.$userId.tsx`, `components/admin/*`) and its account-menu
     entry, as ONE contiguous run at the tail, each key on one line, so a sibling
     branch appending its own block beside this one merges without a conflict.
     `components/admin/admin-console.routing.test.ts` pins the run.

     ⚠ EVERY VALUE HAS DISTINCT BYTES from every other value in the catalogue,
     measured before the block was written. Several existing pins count authors
     of a value (`['Boards', 6]` in `workspace-views.interface-language.test.ts`,
     `authorsOf('Board title')`, `authorsOf('Enterprise')`), so a console column
     reading `Boards` or `Board title` would turn a file this block does not own
     red. That is why the boards tab reads `All boards` and the columns carry a
     noun each.

     ⚠ `admin.audit.action.*` is enumerated against `SUPERADMIN_AUDIT_ACTIONS`
     and `admin.visibility.*` against `BOARD_VISIBILITIES`, key suffix = wire
     value, so the console resolves both as `t(`admin.visibility.${value}`)`. The
     pin iterates the two shared lists, so an action added to the constraint
     without a label here reds there rather than rendering its raw key.

     ⚠ No 409 copy. The two refusals the server can send (own account, another
     platform admin, and D11's last-admin rule) arrive as the server's own
     sentence and render as the body under `admin.user.refusedTitle`. */
  'account.admin': { kind: 'text', value: 'Admin console' },
  'admin.route.title': { kind: 'text', value: 'Platform administration' },
  'admin.route.description': {
    kind: 'text',
    value:
      'Find any account, workspace or board on this platform. Every account action is recorded in the audit log.',
  },
  'admin.tabs.label': { kind: 'text', value: 'Admin console sections' },
  'admin.tabs.users': { kind: 'text', value: 'Users' },
  'admin.tabs.workspaces': { kind: 'text', value: 'Workspaces' },
  'admin.tabs.boards': { kind: 'text', value: 'All boards' },
  'admin.tabs.audit': { kind: 'text', value: 'Audit' },
  'admin.search.usersLabel': { kind: 'text', value: 'Search accounts' },
  'admin.search.usersHint': {
    kind: 'text',
    value: 'Email, username, display name or account id.',
  },
  'admin.search.workspacesLabel': { kind: 'text', value: 'Search workspaces by name' },
  'admin.search.workspacesHint': {
    kind: 'text',
    value: 'Name, short name or workspace id.',
  },
  'admin.search.boardsLabel': { kind: 'text', value: 'Search boards by title' },
  'admin.search.boardsHint': { kind: 'text', value: 'Title, short link or board id.' },
  'admin.search.submit': { kind: 'text', value: 'Find' },
  'admin.filter.visibilityLabel': { kind: 'text', value: 'Board visibility' },
  'admin.filter.visibilityAny': { kind: 'text', value: 'Any visibility' },
  'admin.filter.closedLabel': { kind: 'text', value: 'Open or closed' },
  'admin.filter.closedAny': { kind: 'text', value: 'Open and closed' },
  'admin.filter.closedFalse': { kind: 'text', value: 'Open boards only' },
  'admin.filter.closedTrue': { kind: 'text', value: 'Closed boards only' },
  'admin.filter.actionLabel': { kind: 'text', value: 'Audit action' },
  'admin.filter.actionAny': { kind: 'text', value: 'Any action' },
  'admin.filter.actor': { kind: 'text', value: 'Actor' },
  'admin.filter.target': { kind: 'text', value: 'Target' },
  'admin.visibility.private': { kind: 'text', value: 'Private board' },
  'admin.visibility.workspace_visible': {
    kind: 'text',
    value: 'Visible to its workspace',
  },
  'admin.visibility.enterprise_visible': {
    kind: 'text',
    value: 'Visible to its Enterprise',
  },
  'admin.visibility.public': { kind: 'text', value: 'Public board' },
  'admin.users.tableLabel': { kind: 'text', value: 'Accounts on this platform' },
  'admin.users.column.account': { kind: 'text', value: 'Account holder' },
  'admin.users.column.email': { kind: 'text', value: 'Sign-in email' },
  'admin.users.column.status': { kind: 'text', value: 'Account status' },
  'admin.users.column.workspaces': { kind: 'text', value: 'Workspace memberships' },
  'admin.users.column.created': { kind: 'text', value: 'Joined' },
  'admin.users.status.active': { kind: 'text', value: 'Active account' },
  'admin.users.status.deactivated': { kind: 'text', value: 'Deactivated account' },
  'admin.users.status.deleted': { kind: 'text', value: 'Deleted account record' },
  'admin.users.status.superadmin': { kind: 'text', value: 'Platform admin access' },
  'admin.workspaces.tableLabel': { kind: 'text', value: 'Workspaces on this platform' },
  'admin.workspaces.column.name': { kind: 'text', value: 'Workspace title' },
  'admin.workspaces.column.shortName': { kind: 'text', value: 'Workspace short name' },
  'admin.workspaces.column.members': { kind: 'text', value: 'Member count' },
  'admin.workspaces.column.boards': { kind: 'text', value: 'Live boards' },
  'admin.workspaces.column.enterprise': { kind: 'text', value: 'Enterprise link' },
  'admin.workspaces.column.created': { kind: 'text', value: 'Created on' },
  'admin.workspaces.inEnterprise': { kind: 'text', value: 'In an Enterprise' },
  'admin.workspaces.noEnterprise': { kind: 'text', value: 'No Enterprise' },
  'admin.boards.tableLabel': { kind: 'text', value: 'Boards on this platform' },
  'admin.boards.column.title': { kind: 'text', value: 'Board name' },
  'admin.boards.column.workspace': { kind: 'text', value: 'Owning workspace' },
  'admin.boards.column.visibility': { kind: 'text', value: 'Who can see it' },
  'admin.boards.column.status': { kind: 'text', value: 'Status' },
  'admin.boards.column.members': { kind: 'text', value: 'Board member count' },
  'admin.boards.column.lastActivity': { kind: 'text', value: 'Last activity' },
  'admin.boards.status.open': { kind: 'text', value: 'Open to edits' },
  'admin.boards.status.closed': { kind: 'text', value: 'Closed to edits' },
  'admin.boards.noActivity': { kind: 'text', value: 'Never active' },
  'admin.audit.tableLabel': { kind: 'text', value: 'Platform admin audit log' },
  'admin.audit.column.when': { kind: 'text', value: 'When' },
  'admin.audit.column.action': { kind: 'text', value: 'Audited action' },
  'admin.audit.column.actor': { kind: 'text', value: 'Performed by' },
  'admin.audit.column.target': { kind: 'text', value: 'Target of the action' },
  'admin.audit.column.reason': { kind: 'text', value: 'Stated reason' },
  'admin.audit.operator': { kind: 'text', value: 'Operator {operator}' },
  'admin.audit.noActor': { kind: 'text', value: 'No recorded actor' },
  'admin.audit.target.user': { kind: 'text', value: 'Account {id}' },
  'admin.audit.target.board': { kind: 'text', value: 'Board {id}' },
  'admin.audit.target.workspace': { kind: 'text', value: 'Workspace {id}' },
  'admin.audit.target.enterprise': { kind: 'text', value: 'Enterprise {id}' },
  'admin.audit.target.none': { kind: 'text', value: 'No target' },
  'admin.audit.noReason': { kind: 'text', value: 'No reason recorded' },
  'admin.audit.action.super.granted': {
    kind: 'text',
    value: 'Platform admin access granted',
  },
  'admin.audit.action.super.revoked': {
    kind: 'text',
    value: 'Platform admin access revoked',
  },
  'admin.audit.action.super.board_opened': {
    kind: 'text',
    value: 'Board opened by a platform admin',
  },
  'admin.audit.action.super.mutation': {
    kind: 'text',
    value: 'Change made by a platform admin',
  },
  'admin.audit.action.super.user_deactivated': {
    kind: 'text',
    value: 'Account deactivated by a platform admin',
  },
  'admin.audit.action.super.user_reactivated': {
    kind: 'text',
    value: 'Account reactivated by a platform admin',
  },
  'admin.audit.action.super.sessions_revoked': {
    kind: 'text',
    value: 'Sessions ended by a platform admin',
  },
  'admin.audit.action.super.bearer_refused': {
    kind: 'text',
    value: 'Connected app refused platform admin reach',
  },
  'admin.audit.action.super.api_token_issued': {
    kind: 'text',
    value: 'API token issued by a platform admin',
  },
  'admin.audit.action.super.api_token_revoked': {
    kind: 'text',
    value: 'API token revoked by a platform admin',
  },
  'admin.audit.action.super.user_created': {
    kind: 'text',
    value: 'Account created by a platform admin',
  },
  'admin.audit.action.super.user_initials_set': {
    kind: 'text',
    value: 'Member initials set by a platform admin',
  },
  'admin.loadMore': { kind: 'text', value: 'Load more results' },
  'admin.loadingMore': { kind: 'text', value: 'Loading more results' },
  'admin.loadMoreFailed': { kind: 'text', value: 'More results could not load.' },
  'admin.loading': { kind: 'text', value: 'Loading the admin console' },
  'admin.empty.users': { kind: 'text', value: 'No accounts match this search' },
  'admin.empty.workspaces': { kind: 'text', value: 'No workspaces match this search' },
  'admin.empty.boards': { kind: 'text', value: 'No boards match this search' },
  'admin.empty.audit': { kind: 'text', value: 'No audit entries match these filters' },
  'admin.empty.body': {
    kind: 'text',
    value: 'Try another search, or clear the filters.',
  },
  'admin.error.title': { kind: 'text', value: 'The admin console could not load' },
  'admin.error.retry': { kind: 'text', value: 'Load the admin console again' },
  'admin.user.back': { kind: 'text', value: 'Back to the admin console' },
  'admin.user.eyebrow': { kind: 'text', value: 'Platform account' },
  'admin.user.loading': { kind: 'text', value: 'Loading the account' },
  'admin.user.loadFailed': { kind: 'text', value: 'This account could not load' },
  'admin.user.field.email': { kind: 'text', value: 'Email on file' },
  'admin.user.field.username': { kind: 'text', value: 'Username on file' },
  'admin.user.field.id': { kind: 'text', value: 'Account id' },
  'admin.user.field.created': { kind: 'text', value: 'Account created' },
  'admin.user.viewAudit': { kind: 'text', value: "View this account's audit entries" },
  'admin.user.memberships': { kind: 'text', value: 'Memberships' },
  'admin.user.workspaces': {
    kind: 'plural',
    forms: {
      one: '{count} workspace membership',
      other: '{count} workspace memberships',
    },
  },
  'admin.user.boards': {
    kind: 'plural',
    forms: { one: '{count} board membership', other: '{count} board memberships' },
  },
  'admin.user.enterprises': {
    kind: 'plural',
    forms: {
      one: '{count} Enterprise membership',
      other: '{count} Enterprise memberships',
    },
  },
  'admin.user.noMemberships': { kind: 'text', value: 'None.' },
  'admin.user.truncated': {
    kind: 'text',
    value: 'Showing the newest {shown} of {count}.',
  },
  'admin.role.admin': { kind: 'text', value: 'Admin role' },
  'admin.role.normal': { kind: 'text', value: 'Normal role' },
  'admin.role.observer': { kind: 'text', value: 'Observer role' },
  'admin.role.member': { kind: 'text', value: 'Member role' },
  'admin.user.actions': { kind: 'text', value: 'Account actions' },
  'admin.user.deactivate': { kind: 'text', value: 'Deactivate this account' },
  'admin.user.reactivate': { kind: 'text', value: 'Reactivate this account' },
  'admin.user.revokeSessions': { kind: 'text', value: 'Sign out of every session' },
  'admin.user.refused.self': {
    kind: 'text',
    value: 'You cannot act on your own account from the admin console.',
  },
  'admin.user.refused.superadmin': {
    kind: 'text',
    value: "Revoke this account's platform admin access before acting on it.",
  },
  'admin.user.refused.deleted': {
    kind: 'text',
    value: 'This account has been deleted, so there is nothing to act on.',
  },
  'admin.user.confirm.deactivateTitle': { kind: 'text', value: 'Deactivate {name}?' },
  'admin.user.confirm.deactivateBody': {
    kind: 'text',
    value:
      'Every session ends now and the account cannot sign in until it is reactivated. Its memberships are kept.',
  },
  'admin.user.confirm.reactivateTitle': { kind: 'text', value: 'Reactivate {name}?' },
  'admin.user.confirm.reactivateBody': {
    kind: 'text',
    value: 'The account can sign in again. Nothing else changes.',
  },
  'admin.user.confirm.revokeTitle': {
    kind: 'text',
    value: 'Sign {name} out of every session?',
  },
  'admin.user.confirm.revokeBody': {
    kind: 'text',
    value: 'Every session ends now. The account can sign straight back in.',
  },
  'admin.user.confirm.reasonLabel': { kind: 'text', value: 'Reason for this action' },
  'admin.user.confirm.reasonHint': {
    kind: 'text',
    value: 'Required. Recorded in the platform admin audit log.',
  },
  'admin.user.confirm.close': { kind: 'text', value: 'Close this confirmation' },
  'admin.user.confirm.cancel': { kind: 'text', value: 'Leave the account as it is' },
  'admin.user.confirm.deactivate': { kind: 'text', value: 'Deactivate now' },
  'admin.user.confirm.reactivate': { kind: 'text', value: 'Reactivate now' },
  'admin.user.confirm.revoke': { kind: 'text', value: 'End every session now' },
  'admin.user.confirm.working': { kind: 'text', value: 'Applying the account action' },
  'admin.user.refusedTitle': {
    kind: 'text',
    value: 'The server refused this account action',
  },
  'admin.user.announce.deactivated': {
    kind: 'text',
    value: '{name} is now deactivated.',
  },
  'admin.user.announce.reactivated': { kind: 'text', value: '{name} can sign in again.' },
  'admin.user.announce.sessionsRevoked': {
    kind: 'plural',
    forms: {
      one: '{count} session ended for {name}.',
      other: '{count} sessions ended for {name}.',
    },
  },
  'boardFace.platformAdmin.notice': {
    kind: 'text',
    value: 'You are viewing this board as a platform admin.',
  },
  'boardFace.platformAdmin.dismiss': { kind: 'text', value: 'Hide notice' },
  'cardBack.activityEntry.platformAdminBadge': { kind: 'text', value: 'Platform admin' },
  // B85, AT-12 — the admin account page's API tokens section
  // (`components/admin/ApiTokensAdminSection.tsx`), pinned by
  // `components/admin/api-tokens-admin.routing.test.ts`.
  'admin.apiTokens.heading': { kind: 'text', value: 'API tokens' },
  'admin.apiTokens.description': {
    kind: 'text',
    value:
      'A token issued here is shown to you once, so you can hand it to the account holder. An older token listed as not yet revealed can be revealed only by its account holder.',
  },
  'admin.apiTokens.tableLabel': {
    kind: 'text',
    value: 'API tokens held by this account',
  },
  'admin.apiTokens.column.name': { kind: 'text', value: 'Token name' },
  'admin.apiTokens.column.scope': { kind: 'text', value: 'Access scope' },
  'admin.apiTokens.column.status': { kind: 'text', value: 'Token status' },
  'admin.apiTokens.column.issuedBy': { kind: 'text', value: 'Issued by' },
  'admin.apiTokens.column.actions': { kind: 'text', value: 'Token actions' },
  'admin.apiTokens.status.unclaimed': { kind: 'text', value: 'Not yet revealed' },
  'admin.apiTokens.status.active': { kind: 'text', value: 'Active' },
  'admin.apiTokens.status.expired': { kind: 'text', value: 'Expired' },
  'admin.apiTokens.status.revoked': { kind: 'text', value: 'Revoked' },
  'admin.apiTokens.issuedBy.account': { kind: 'text', value: 'The account holder' },
  'admin.apiTokens.noHint': { kind: 'text', value: 'Not revealed' },
  'admin.apiTokens.loading': { kind: 'text', value: 'Loading API tokens' },
  'admin.apiTokens.loadFailed': { kind: 'text', value: 'API tokens could not load' },
  'admin.apiTokens.retry': { kind: 'text', value: 'Load the API tokens again' },
  'admin.apiTokens.empty': { kind: 'text', value: 'This account holds no API tokens.' },
  'admin.apiTokens.revokeConfirm.title': {
    kind: 'text',
    value: 'Revoke the {name} token?',
  },
  'admin.apiTokens.revokeConfirm.body': {
    kind: 'text',
    value:
      'It stops working at once, and a revoked token cannot be restored. Issue a new one if the account still needs access.',
  },
  'admin.apiTokens.revokeConfirm.cancel': { kind: 'text', value: 'Keep the token' },
  'admin.apiTokens.revokeConfirm.confirm': {
    kind: 'text',
    value: 'Revoke the token now',
  },
  'admin.apiTokens.revokeConfirm.working': { kind: 'text', value: 'Revoking the token' },
  'admin.apiTokens.revokeRefusedTitle': {
    kind: 'text',
    value: 'The server refused to revoke this token',
  },
  'admin.apiTokens.announce.revoked': {
    kind: 'text',
    value: 'The {name} token is revoked.',
  },
  'admin.apiTokens.issue.heading': { kind: 'text', value: 'Issue an API token' },
  'admin.apiTokens.issue.nameHint': {
    kind: 'text',
    value: 'Shown in activity as the tool behind each change.',
  },
  'admin.apiTokens.issue.nameRequired': {
    kind: 'text',
    value: 'Name the token before issuing it.',
  },
  'admin.apiTokens.issue.expiryLabel': { kind: 'text', value: 'Expires after' },
  'admin.apiTokens.expiry.7d': { kind: 'text', value: '7 days' },
  'admin.apiTokens.expiry.30d': { kind: 'text', value: '30 days' },
  'admin.apiTokens.expiry.90d': { kind: 'text', value: '90 days' },
  'admin.apiTokens.expiry.1y': { kind: 'text', value: '1 year' },
  'admin.apiTokens.issue.neverWarning': {
    kind: 'text',
    value: 'This token will work until someone revokes it.',
  },
  'admin.apiTokens.issue.submit': { kind: 'text', value: 'Issue token' },
  'admin.apiTokens.issue.working': { kind: 'text', value: 'Issuing the token' },
  'admin.apiTokens.issue.created': {
    kind: 'text',
    value:
      'Token created. Copy it now and hand it to {name}: it will not be shown again.',
  },
  'admin.apiTokens.issue.refusedTitle': {
    kind: 'text',
    value: 'The server refused to issue this token',
  },
  'admin.apiTokens.refused.deleted': {
    kind: 'text',
    value: 'This account has been deleted, so it cannot be issued a token.',
  },
  'admin.apiTokens.refused.superadmin': {
    kind: 'text',
    value: 'A platform admin account cannot hold an API token, your own included.',
  },
  'admin.apiTokens.refused.deactivated': {
    kind: 'text',
    value: 'Reactivate this account before issuing it a token.',
  },
  'admin.apiTokens.issued.title': { kind: 'text', value: 'Hand over the {name} token' },
  'admin.apiTokens.issued.close': { kind: 'text', value: 'Close and forget this token' },
  'admin.apiTokens.issued.closeControl': {
    kind: 'text',
    value: 'Close the token hand-over',
  },
  'admin.createUser.open': { kind: 'text', value: 'New account' },
  'admin.createUser.title': {
    kind: 'text',
    value: 'Create an account that can sign in at once',
  },
  'admin.createUser.description': {
    kind: 'text',
    value:
      'Nobody is emailed. You hand the sign-in details to the account holder yourself.',
  },
  'admin.createUser.email': { kind: 'text', value: 'Account email' },
  'admin.createUser.displayName': { kind: 'text', value: 'Account holder name' },
  'admin.createUser.passwordLegend': {
    kind: 'text',
    value: 'How the first password is chosen',
  },
  'admin.createUser.passwordGenerate': {
    kind: 'text',
    value: 'Generate a password for me',
  },
  'admin.createUser.passwordSet': { kind: 'text', value: 'Set the password myself' },
  'admin.createUser.password': { kind: 'text', value: 'Password for the account' },
  'admin.createUser.passwordHint': {
    kind: 'text',
    value: 'Between {min} and {max} characters.',
  },
  'admin.createUser.error.email': {
    kind: 'text',
    value: 'Enter a valid email address for the account.',
  },
  'admin.createUser.error.emailTaken': {
    kind: 'text',
    value: 'That email already has an account.',
  },
  'admin.createUser.error.displayName': {
    kind: 'text',
    value: 'Give the account holder a name of 1 to {max} characters.',
  },
  'admin.createUser.error.password': {
    kind: 'text',
    value: 'The password must be {min} to {max} characters long.',
  },
  'admin.createUser.refusedTitle': {
    kind: 'text',
    value: 'The server refused to create this account',
  },
  'admin.createUser.cancel': { kind: 'text', value: 'Close without creating' },
  'admin.createUser.submit': { kind: 'text', value: 'Create the account' },
  'admin.createUser.working': { kind: 'text', value: 'Creating the account' },
  'admin.createUser.closeControl': { kind: 'text', value: 'Close the new account form' },
  'admin.handover.title': { kind: 'text', value: 'Sign-in details for {name}' },
  'admin.handover.warning': {
    kind: 'text',
    value:
      'Shown once. Copy these details now: close this and the password cannot be read again.',
  },
  'admin.handover.warningTyped': {
    kind: 'text',
    value: 'Shown once. The password is the one you set, and it is not shown here.',
  },
  'admin.handover.signInUrl': { kind: 'text', value: 'Sign-in address' },
  'admin.handover.email': { kind: 'text', value: 'Email to sign in with' },
  'admin.handover.password': { kind: 'text', value: 'Password to sign in with' },
  'admin.handover.passwordTyped': { kind: 'text', value: 'The password you set' },
  'admin.handover.copySignInUrl': { kind: 'text', value: 'Copy the sign-in address' },
  'admin.handover.copyEmail': { kind: 'text', value: 'Copy the email' },
  'admin.handover.copyPassword': { kind: 'text', value: 'Copy the password' },
  'admin.handover.all': { kind: 'text', value: 'All the details, to paste in one go' },
  'admin.handover.allText': {
    kind: 'text',
    value:
      'Sign in at {url}\nEmail: {email}\nPassword: {password}\nSign in on the site once before using an API token: that first visit sets up your workspace.',
  },
  'admin.handover.allTextTyped': {
    kind: 'text',
    value:
      'Sign in at {url}\nEmail: {email}\nPassword: sent to you separately\nSign in on the site once before using an API token: that first visit sets up your workspace.',
  },
  'admin.handover.copyAll': { kind: 'text', value: 'Copy all the details' },
  'admin.handover.issueToken': { kind: 'text', value: 'Issue an API token for {name}' },
  'admin.handover.close': { kind: 'text', value: 'Close and forget these details' },
  'admin.handover.closeControl': { kind: 'text', value: 'Close the sign-in details' },
  /* Member initials (owner-directed; `docs/notes/member-initials.md`) — the
     account page's "Avatar initials" control (`components/admin/
     AdminUserInitials.tsx`) and the route's two announcements. ONE contiguous
     run at the tail, after the hand-over block, each value with distinct bytes;
     `components/admin/admin-user-initials.routing.test.ts` pins it. The card
     front's hover names the member through `primitives.avatar.tooltip`, which
     already exists, so no key is owed there. */
  'admin.initials.heading': { kind: 'text', value: 'Avatar initials' },
  'admin.initials.current.custom': {
    kind: 'text',
    value: 'Shows {initials}, set by a platform admin.',
  },
  'admin.initials.current.derived': {
    kind: 'text',
    value: 'Shows {initials}, derived from the display name.',
  },
  'admin.initials.current.none': {
    kind: 'text',
    value:
      'Shows a person icon: the display name has no letter, number or symbol to derive initials from.',
  },
  'admin.initials.label': { kind: 'text', value: 'Custom initials' },
  'admin.initials.hint': {
    kind: 'text',
    value:
      "One to four characters. Shown in this account's avatar circle on every card, comment and member list.",
  },
  'admin.initials.invalid': {
    kind: 'text',
    value: 'Use one to four characters, at least one of them visible.',
  },
  'admin.initials.save': { kind: 'text', value: 'Save these initials' },
  'admin.initials.saving': { kind: 'text', value: 'Saving the initials' },
  'admin.initials.clear': { kind: 'text', value: 'Go back to derived initials' },
  'admin.initials.clearRefused': {
    kind: 'text',
    value: 'These initials are already derived from the display name.',
  },
  'admin.initials.refusedTitle': {
    kind: 'text',
    value: 'The server refused these initials',
  },
  'admin.initials.announce.set': {
    kind: 'text',
    value: '{name} now shows {initials} in their avatar.',
  },
  'admin.initials.announce.cleared': {
    kind: 'text',
    value: '{name} now shows initials derived from their display name.',
  },
};

export interface InterfaceLanguageContextValue {
  readonly catalogue: InterfaceLanguageCatalogueResponse;
  readonly textDirection: InterfaceLanguageCatalogueResponse['textDirection'];
  readonly locale: InterfaceLanguageCatalogueResponse['resolvedLocale'];
  readonly t: (
    key: string,
    values?: Record<string, string | number>,
    count?: number,
  ) => string;
}

export const InterfaceLanguageContext = createContext<InterfaceLanguageContextValue>(
  contextValue(fallbackCatalogue),
);

export function getMessageCatalogue(): Promise<InterfaceLanguageCatalogueResponse> {
  return get('/me/message-catalogue', interfaceLanguageCatalogueResponseSchema);
}

export function interfaceLanguageQueryOptions(): UseQueryOptions<
  InterfaceLanguageCatalogueResponse,
  ApiError
> {
  return {
    queryKey: queryKeys.meMessageCatalogue(),
    queryFn: getMessageCatalogue,
  };
}

export function InterfaceLanguageProvider({
  catalogue,
  children,
}: {
  catalogue: InterfaceLanguageCatalogueResponse | null | undefined;
  children: ReactNode;
}): ReactElement {
  const resolved = catalogue ?? fallbackCatalogue;
  useEffect(() => {
    document.documentElement.lang = resolved.resolvedLocale;
    document.documentElement.dir = resolved.textDirection;
  }, [resolved.resolvedLocale, resolved.textDirection]);

  /**
   * ⚠ MEMOISED at the board-face slice, and it is a correctness fix rather than
   * a performance one.
   *
   * `contextValue` builds a fresh arrow for `t` on every call, so calling it
   * inline in `createElement` handed every consumer a NEW `t` identity on every
   * render of this provider. That was invisible while nothing depended on the
   * identity — and the board-face slice made twelve `useCallback`/`useMemo`
   * dependency lists depend on it, because fe-plan residual 11 is right that a
   * callback closing over `t` without declaring it announces the PREVIOUS
   * language after a switch.
   *
   * Add `t` to those lists against an unmemoised context and the two failures
   * trade places: the stale announcement goes away and every one of those
   * callbacks is rebuilt on every provider render instead. `BoardFace` feeds
   * one of them (`announcements`) straight into `DndContext.accessibility`,
   * where a new object identity mid-drag is precisely the class of churn
   * dnd-kit is sensitive to — and no static-render assertion can see it.
   *
   * One `useMemo` keyed on the resolved catalogue makes `t` stable for as long
   * as the catalogue is, which is exactly the lifetime those dependency lists
   * are trying to express.
   */
  const value = useMemo(() => contextValue(resolved), [resolved]);

  // Descendant query hooks can enter `request()` as soon as this provider's
  // subtree is rendered, so install the formatter before handing the context
  // to children. The effect below owns only cleanup.
  configureApiErrorMessages(value.t);
  useEffect(() => {
    return () => configureApiErrorMessages(null);
  }, [value.t]);

  return createElement(InterfaceLanguageContext.Provider, { value }, children);
}

export function useInterfaceLanguage(): InterfaceLanguageContextValue {
  return useContext(InterfaceLanguageContext);
}

export function interfaceMessage(
  message: InterfaceMessage,
  values: Record<string, string | number> = {},
  count = 0,
): string {
  const template =
    message.kind === 'text'
      ? message.value
      : count === 1
        ? message.forms.one
        : message.forms.other;
  return template.replace(/\{([A-Za-z0-9_]+)\}/g, (_match, slot: string) => {
    const value = values[slot];
    if (value === undefined) {
      throw new Error(`interface message needs a value for {${slot}}`);
    }
    return String(value);
  });
}

function contextValue(
  catalogue: InterfaceLanguageCatalogueResponse,
): InterfaceLanguageContextValue {
  return {
    catalogue,
    textDirection: catalogue.textDirection,
    locale: catalogue.resolvedLocale,
    t: (key, values, count) => {
      const message = catalogue.messages[key] ?? WEB_INTERFACE_MESSAGES[key];
      if (message === undefined) return key;
      return interfaceMessage(message, values, count);
    },
  };
}
